/**
 * webcodecs-pipeline.js — full hardware video pipeline:
 *
 *   File → MP4Box demux → VideoDecoder (hw) → WebGL filter passes →
 *     VideoEncoder (hw) → mp4-muxer → File
 *
 * Replaces FFmpeg WASM x264 encode for re-encode tools (enhance-video,
 * video-crop, video-flip, etc) on browsers that support WebCodecs +
 * WebGL2. Falls back transparently to FFmpeg WASM when:
 *   - WebCodecs API absent (Firefox <130, older Safari)
 *   - codec not supported by hardware decoder/encoder
 *   - WebGL2 context unavailable
 *   - container is not mp4-family (mkv/avi/ts → demux mismatch)
 *
 * The reason this exists is that single-thread WASM x264 hits its ceiling
 * at ~10-20 fps for 1080p on a modern desktop, ~3-5 fps on mobile. Hardware
 * decode+encode runs at 60-200 fps. For a 30 s 1080p clip the difference is
 * 30 s vs 3-5 min on mobile — the gap that drove the original
 * "плохое качество" / "застрял на 11%" feedback cluster.
 *
 * API (callers — primarily enhance-video, but designed for any re-encode tool):
 *
 *   const pipe = window.WebCodecsPipeline;
 *
 *   const cap = await pipe.isSupported({ inputCodec: 'avc1.640028',
 *                                        outputCodec: 'avc1.640028' });
 *   // → { ok: true } or { ok: false, reason: 'no-webgl2' / 'no-encoder-avc' / ... }
 *
 *   const result = await pipe.run({
 *       file:  File,
 *       params: { sharpness, denoise, brightness, contrast, saturation,
 *                 gamma, highlights, shadows, warmth, vignette, grain,
 *                 resolution },
 *       crf: 18..30,           // mapped to bitrate for VideoEncoder
 *       speed: 'fast'|'balanced'|'slow',
 *       onProgress: (pct) => void,    // 0..1
 *       abortSignal: AbortSignal,     // optional
 *       // Audio edits (optional). Any of these switches the audio track from
 *       // frame passthrough to decode → edit → AAC re-encode; omitting them
 *       // all keeps the byte-identical passthrough. `speed` ≠ 1 additionally
 *       // requires AudioPipeline/PitchEngine to be loaded by the caller.
 *       audioGain: 1,                 // linear multiplier
 *       audioFadeIn: 0,               // seconds, from silence at output start
 *       audioFadeOut: 0,              // seconds, to silence at output end
 *       videoBitrate: 0,              // bits/s. Explicit ABR target — bypasses
 *                                     // the crf→bitrate heuristic AND its
 *                                     // source-aware ceiling. For size-targeted
 *                                     // callers (video-compressor) only.
 *       maxVideoBitrate: 0,           // bits/s. A CEILING on what any encoder
 *                                     // rung is asked for (the software rung's
 *                                     // ×2.2 included); a low source keeps its own
 *                                     // lower number. For a picture only a model
 *                                     // looks at — the /video-to-sound proxy, whose
 *                                     // 85 % source floor made «480p» of a 10 Mbit/s
 *                                     // clip 3.9 Mbit/s: 87.6 MB per 3 min, over the
 *                                     // upload cap (2026-10-03).
 *       encoderLatency: 'realtime',   // 'quality' asks the encoder for B-frames
 *       h264Profile: 'baseline'       // 'baseline' | 'main' | 'high'. Навязывает
 *                                     // ПОРЯДОК каскада профилей, когда у выдачи
 *                                     // есть второй адресат со своими правилами
 *                                     // (автомагнитола играет только Constrained
 *                                     // Baseline). Опущено — прежнее поведение:
 *                                     // High → Main → Baseline.
 *   });
 *   // → { blob: Blob, frames: N, encode_sec: float, ms_per_frame: float,
 *   //     decoder: 'hw' / 'sw', encoder: 'hw' / 'sw' }
 *
 * Dependencies:
 *   - window.MP4Box (mp4box.js — vendored at /vendor/mp4box/)
 *   - window.Mp4Muxer (mp4-muxer — vendored at /vendor/mp4-muxer/)
 *   - window.Mp4EditList (public/js/mp4-edit-list.js — loaded by the demuxer;
 *     maps both tracks onto the presentation timeline, see demuxMp4)
 *   - WebCodecs (VideoDecoder / VideoEncoder) — natively in browser
 *   - WebGL2 — natively in browser
 */
(function(){
'use strict';
if (window.WebCodecsPipeline) return;

// ─────────────────────────────────────────────────────────────────────
// LIBRARY LOADERS
// ─────────────────────────────────────────────────────────────────────

// timeoutMs — только для НЕОБЯЗАТЕЛЬНЫХ загрузок: запрос, который сервер
// принял и не отдаёт (перегруженный FPM за /jsv/…), не зовёт ни onload, ни
// onerror, и без срока ожидание длится, пока жива вкладка. Поймано стендом
// /eye-contact 24.09.2026: сохранение стояло на «441 из 443 кадров» 10+ минут
// — все кадры готовы, а сборка ждала mp4-tags.js.
function loadScript(src, timeoutMs) {
    return new Promise(function(resolve, reject){
        var s = document.createElement('script');
        var timer = 0;
        s.src = src;
        s.onload = function(){ if (timer) clearTimeout(timer); resolve(); };
        s.onerror = function(){ if (timer) clearTimeout(timer); reject(new Error('load: ' + src)); };
        if (timeoutMs > 0) {
            timer = setTimeout(function(){ reject(new Error('load-timeout: ' + src)); }, timeoutMs);
        }
        document.head.appendChild(s);
    });
}

async function ensureMp4Box() {
    if (window.MP4Box) return;
    if (window.__mp4boxLoadingPromise) return window.__mp4boxLoadingPromise;
    window.__mp4boxLoadingPromise = loadScript('/vendor/mp4box/mp4box.all.min.js')
        .catch(function(){ return loadScript('https://cdn.jsdelivr.net/npm/mp4box@0.5.4/dist/mp4box.all.min.js'); });
    return window.__mp4boxLoadingPromise;
}

async function ensureMp4Muxer() {
    if (window.Mp4Muxer) return;
    if (window.__mp4muxerLoadingPromise) return window.__mp4muxerLoadingPromise;
    /* ⚠️ VERSIONED. nginx on the intl shard serves /vendor, /js and /css with
       `Cache-Control: immutable, max-age=15552000` — a promise that the bytes at
       this URL never change. A blade tag goes through @light and gets a stamp, so
       it is honest there; a script inserted BY JavaScript does not, and whoever
       already has the file keeps it for 180 days while every deploy reports
       success. Measured on the live intl domain 2026-09-02: the unversioned
       /js/timbrica-core.js came back `cf-cache-status: HIT`, `Age: 168847`, four
       days stale, while the versioned copy the page actually loads was current.
       `__jsSrc` is the house primitive for exactly this (layouts/app.blade.php);
       it is absent only in a bare test page, hence the fallback. */
    var _muxUrl = (typeof window.__jsSrc === 'function')
        ? window.__jsSrc('/vendor/mp4-muxer/mp4-muxer.js')
        : '/vendor/mp4-muxer/mp4-muxer.js';
    window.__mp4muxerLoadingPromise = loadScript(_muxUrl)
        .catch(function(){ return loadScript('https://cdn.jsdelivr.net/npm/mp4-muxer@5.2.2/build/mp4-muxer.js'); });
    return window.__mp4muxerLoadingPromise;
}

/* AacAccessUnit (public/js/aac-access-unit.js) — the one door for AAC that an
   AudioEncoder produced on its way into the MP4 track: strips the ADTS framing
   Chromium 109–111 put on every chunk, guarantees the esds ASC. Same runtime,
   versioned loading as the muxer above. */
function ensureAacAccessUnit() {
    if (window.AacAccessUnit) return Promise.resolve();
    /* Same contract as canvas-video-export.js loadOnce(), and the SAME cache key
       (either module may load it first): one retry on a fresh URL, and a
       refusal is never cached — a rejected promise left in this slot killed
       the fast lane for every later export on the page (15.09.2026). */
    if (!window.__aacAccessUnitLoading) {
        var primary = typeof window.__jsSrc === 'function' ? window.__jsSrc('/js/aac-access-unit.js') : '/js/aac-access-unit.js';
        var retry = '/js/aac-access-unit.js?v=' + (window.__JS_VER || '') + '&retry=1';
        window.__aacAccessUnitLoading = loadScript(primary)
            .then(function () { if (!window.AacAccessUnit) throw new Error('aac-access-unit-not-loaded'); })
            .catch(function () {
                return new Promise(function (r) { setTimeout(r, 400); }).then(function () { return loadScript(retry); });
            })
            .then(function () { if (!window.AacAccessUnit) throw new Error('aac-access-unit-not-loaded'); })
            .catch(function (e) { window.__aacAccessUnitLoading = null; throw e; });
    }
    return window.__aacAccessUnitLoading;
}

/* Mp4EditList (public/js/mp4-edit-list.js) — the one reader of a container's
   edit lists. The demuxer hands every lane PRESENTATION timestamps through it,
   which is the only way the two tracks of a phone clip land on one timeline
   (see the block in demuxMp4). Same runtime, versioned loading as above. */
/* The HDR → SDR door (hdr-to-sdr.js, docs/hdr-to-sdr.md). Requested as soon as
   this file runs: renderStill and the 2D lane are synchronous, and an HDR
   frame that reaches the rig before the door has loaded goes in as the raw
   coded signal. run() awaits it too; a failed load leaves the browser's own
   picture (fail-open). */
function ensureHdrToSdr() {
    if (window.HdrToSdr) return Promise.resolve();
    if (!window.__hdrToSdrLoading) {
        window.__hdrToSdrLoading = loadScript(typeof window.__jsSrc === 'function'
            ? window.__jsSrc('/js/hdr-to-sdr.js') : '/js/hdr-to-sdr.js')
            .catch(function () { window.__hdrToSdrLoading = null; });
    }
    return window.__hdrToSdrLoading;
}
try { ensureHdrToSdr(); } catch (_) {}

function ensureMp4EditList() {
    if (window.Mp4EditList) return Promise.resolve();
    if (!window.__mp4EditListLoading) {
        window.__mp4EditListLoading = loadScript(typeof window.__jsSrc === 'function'
            ? window.__jsSrc('/js/mp4-edit-list.js') : '/js/mp4-edit-list.js');
    }
    return window.__mp4EditListLoading.then(function () {
        if (!window.Mp4EditList) throw new Error('mp4-edit-list-not-loaded');
    });
}

/* One line of telemetry per run about what the edit lists did, only when they
   did something a plain file would not show: frames hidden at either end, an
   audio track that must start late, a shape the reader does not model, or the
   reader missing altogether. Priming alone is every AAC file and stays quiet. */
function _aeEditList(slug, edit) {
    if (typeof window === 'undefined' || typeof window.__ae !== 'function' || !edit) return;
    try {
        var v = edit.video || {}, a = edit.audio || {};
        var noteworthy = !edit.applied || edit.hiddenHead > 0 || edit.hiddenTail > 0 || edit.audioLeadUs > 0
            || edit.presStartUs > 0 || edit.audioDropped > 1
            || v.shape === 'unsupported' || a.shape === 'unsupported';
        if (!noteworthy) return;
        window.__ae('feature_use', {
            slug: slug || 'enhance-video', action: 'edit_list_applied', applied: edit.applied ? 1 : 0,
            v: v.shape || '', a: a.shape || '', why: (v.reason || a.reason || edit.error || '').slice(0, 40),
            head: edit.hiddenHead || 0, tail: edit.hiddenTail || 0,
            v_lead_ms: Math.round((edit.presStartUs || 0) / 1000),
            a_res_ms: Math.round((edit.audioResidualUs || 0) / 1000),
            a_lead_ms: Math.round((edit.audioLeadUs || 0) / 1000)
        });
    } catch (_) {}
}

/**
 * The video TIMEBASE we write. This is the canonical explanation; the other
 * muxer call sites (canvas-video-export.js, screen-recorder/recorder.js,
 * webcam-recorder/recorder.js) repeat the number and point here, and
 * Mp4MuxerTimebaseTest keeps the copies from drifting apart.
 *
 * ⚠️ It is passed as mp4-muxer's `frameRate` option because that option IS the
 * track timescale — `timescale: video.frameRate ?? 57600` — and nothing else.
 * Four of its five mentions in the vendored bundle are argument validation.
 * Two comments in this file used to claim it drives «the trex / mvex setup»
 * and «the container metadata … playback rate»; both were wrong, and the
 * second one is what kept the value pinned to the frame rate.
 *
 * Why not the frame rate (what we shipped until 2026-08-30). mp4-muxer derives
 * each sample duration as `round(exactTicks(dts) − runningTotal)`, which
 * compensates its own error — so a timescale equal to the frame rate does NOT
 * drift, it HITCHES: the residue accumulates until one frame is a whole tick
 * longer. Replayed over the muxer's own algorithm:
 *
 *     29.97 fps @ timescale 30      → 217 duration changes per hour,
 *                                     each a 33.33 ms stutter
 *     23.976 fps @ timescale 24     → 173 per hour, 41.67 ms each
 *     25 / 30 / 50 / 60 fps         → exact, no hitch (this is why it went
 *                                     unnoticed: PAL and integer rates are fine)
 *
 * Why not mp4-muxer's own default either. 57600 removes the hitch (0.02 ms)
 * but is not a multiple of 1001, so NTSC durations alternate and the run-length
 * `stts` explodes: one hour of 29.97 fps costs 17 263 entries (138 KB of moov),
 * and 23.976 fps costs 69 052 (552 KB). Deleting the option would have traded a
 * visible stutter for half a megabyte of header.
 *
 * 120000 is exact for every rate we actually meet — 10/12/15/20/24/25/30/48/50/
 * 60/100/120 and 24000⁄1001, 30000⁄1001, 60000⁄1001 — so `stts` stays ONE entry
 * and there is no rounding at all. No ceiling risk: the muxer promotes mdhd /
 * mvhd / tkhd to 64-bit fields on its own when a value passes 2³² (`V = e =>
 * e >= 0 && e < 2**32`), so long videos widen the field instead of wrapping.
 *
 * ⚠️ NOT the encoder's rate. VideoEncoder takes WebCodecs' lower-case
 * `framerate`, which stays the real frame rate — it is a rate-control hint.
 *
 * ⚠️ The FFmpeg side pins a DIFFERENT canonical timescale — 90000, in
 * ffmpeg-processor.js's trim/concat remux (incident #1330). That is deliberate,
 * not drift: 90000 is exact for 24/25/30/50/60 and 29.97 but not for 23.976 or
 * 59.94, which is fine there (it only has to survive a rescale between two
 * halves of one file) and not fine here (we write the source's own rate).
 * Neither number should be changed to match the other without re-measuring.
 */
var MUX_VIDEO_TIMEBASE = 120000;

// ─────────────────────────────────────────────────────────────────────
// MUX OUTPUT TARGET — RAM for small results, OPFS stream for big ones
// ─────────────────────────────────────────────────────────────────────
// mp4-muxer's `fastStart:'in-memory'` keeps EVERY encoded sample's bytes on
// the JS heap until finalize (verified in our bundled copy: the 'in-memory'
// branch of finalizeCurrentChunk does `offset = 0; return` WITHOUT writing —
// the streaming branch writes and nulls `sample.data` as chunks fill). With
// ArrayBufferTarget the finalize then materialises one more full-size buffer,
// and the caller's `new Blob([buffer])` copies it again.
//
// MEASURED on a 300 MB output (synthetic samples, fresh page, forced GC):
//   ArrayBufferTarget + 'in-memory'          peak heap 1395 MB  (×4.65)
//   OPFS stream + fastStart:false            peak heap  315 MB, moov at END
//   OPFS stream + fastStart:{expectedChunks} peak heap  348 MB, moov at HEAD
// Extrapolated to the 2 h export from support conv #553 (549 MB output) the
// current mode needs ~2.5 GB and the renderer dies — reproduced twice here,
// mid-encode, on a 32 GB machine.
//
// Why NOT simply copy screen-recorder's `fastStart:false` (its own fix for
// the same class, memory reference_long_recording_patterns): a live recording
// cannot know its length, so it has no choice. A TRANSCODE does — the source
// sample count is known before the first frame — so we can reserve the moov
// at the head and still stream. That matters for our own ecosystem: our
// Tier-0 "moov missing" probe reads a 512 KB TAIL window, so a big
// moov-at-end file we produced can be false-rejected by our own tools
// (the reason FFmpeg outputs carry +faststart, see ffmpeg-processor).
// Over-reserving is safe (measured: 2×+500 expected chunks → still
// moov-at-head, same output size), under-reserving throws mid-run — so the
// estimate is deliberately generous, and any doubt falls back to `false`.
var MUX_STREAM_MIN_BYTES = 120 * 1024 * 1024;   // below this, RAM is simpler and fine
var MUX_TMP_PREFIX = '_muxtmp_';
var MUX_TMP_TTL_MS = 6 * 60 * 60 * 1000;

// Stale temp sweeper — a crashed/aborted run leaves its file behind. Pattern
// lifted from /join's __jnOpfsDir + screen-recorder's sweepStaleOpfsRecordings.
async function sweepStaleMuxTemps() {
    try {
        if (!(navigator.storage && navigator.storage.getDirectory)) return;
        var root = await navigator.storage.getDirectory();
        if (!root.values) return;
        var now = Date.now();
        for await (var entry of root.values()) {
            if (entry.kind !== 'file' || entry.name.indexOf(MUX_TMP_PREFIX) !== 0) continue;
            // An owner-tagged file goes with its page (window.__opfsOwner) — a live
            // page's result must not be taken for being six hours old.
            if (window.__opfsOwner && window.__opfsOwner.ownerOf(entry.name)) continue;
            var ts = parseInt(entry.name.slice(MUX_TMP_PREFIX.length), 10);
            if (isFinite(ts) && (now - ts) < MUX_TMP_TTL_MS) continue;
            try { await root.removeEntry(entry.name); } catch (_) {}
        }
    } catch (_) {}
}

// → { target, fastStart, opfs|null, mode }.  Never throws: any failure to open
// OPFS degrades to the in-RAM target the pipeline has always used.
//
// reserveBytes — the room the disk lane takes BEFORE frame 0 (__opfsMuxLane in
// timbrica-core.js). A grant that refuses it sends the run to RAM now, instead
// of letting it encode for minutes and refusing the bytes at the end: a nearly
// full system disk gives a site a few MB of grant or none, and
// navigator.storage.estimate() hides it behind a static usage + 10 GiB on most
// Chromium builds (support chat 647; mode `memory_refused` counts it).
/* The largest file the in-memory lane takes when it holds the file as BLOCKS
   (window.__memoryMuxTarget): what the device's RAM allows. 0 — no block
   target here (an older core): memory is then the one growing buffer, as before. */
function memoryBlocksCeiling() {
    try {
        if (typeof window.__memoryMuxTarget === 'function' && typeof window.__memoryMuxCeiling === 'function') return +window.__memoryMuxCeiling() || 0;
    } catch (_) {}
    return 0;
}

async function openMuxTarget(Ns, estOutBytes, expVideoChunks, expAudioChunks, hasAudio, reserveBytes, where) {
    var refused = false, unreserved = '';
    var memCeiling = memoryBlocksCeiling();
    where = where || {};
    function headOrTail() {
        // The moov reserved at the head when the chunk counts are known, else at the end.
        if (expVideoChunks > 0 && (!hasAudio || expAudioChunks > 0)) {
            var fs = { expectedVideoChunks: expVideoChunks };
            if (hasAudio) fs.expectedAudioChunks = expAudioChunks;
            return fs;
        }
        return false;
    }
    /* The person's own file (opts.outputHandle — «choose where to save» after
       the browser's storage refused): the muxer writes straight into it, the
       site's grant is not asked at all (__pickedMuxLane, docs/why/primitives/
       storage-room.md). No room THERE is an answer for the person, never a
       quiet fall into memory behind their choice. */
    if (where.outputHandle && typeof window.__pickedMuxLane === 'function' && Ns.StreamTarget) {
        var picked = await window.__pickedMuxLane(Ns, where.outputHandle, { lane: 'webcodecs-pipeline', reserveBytes: reserveBytes || 0 });
        if (!picked) {
            var noRoom = new Error('picked-disk-refused:' + Math.round((reserveBytes || 0) / 1048576) + 'MB');
            noRoom.diskFull = true; noRoom.picked = true; noRoom.needBytes = reserveBytes || 0;
            throw noRoom;
        }
        var fsPicked = headOrTail();
        return { target: picked.target, fastStart: fsPicked, mode: 'picked', opfs: { name: picked.name, handle: picked.handle, writable: picked.writable, lane: picked } };
    }
    if (!where.noDiskTarget && estOutBytes >= MUX_STREAM_MIN_BYTES &&
        navigator.storage && navigator.storage.getDirectory &&
        Ns.StreamTarget && typeof window.__opfsMuxLane === 'function') {
        try {
            var lane = await window.__opfsMuxLane(Ns, {
                prefix: MUX_TMP_PREFIX, ext: 'mp4', lane: 'webcodecs-pipeline', reserveBytes: reserveBytes || 0
            });
            /* A lane that could not hold its room is where writes are refused
               minutes in (measured 14.10.2026: a write inside a held reservation
               never is; 74 sessions in 30 days came from lanes without one — an
               off-the-record profile, or a reservation that failed). When memory
               can take the file, memory it is. The same on a SHORT disk
               (lane.diskShort): Chromium evicts the site's storage under the
               open file there (21 sessions in 30 days: «NotFoundError … could
               not be found»), and memory cannot be evicted. */
            var laneDoubt = lane ? ((!lane.reserved && lane.unreservedWhy) ? String(lane.unreservedWhy) : (lane.diskShort ? 'short' : '')) : '';
            if (laneDoubt && reserveBytes > 0 && reserveBytes <= memCeiling) {
                unreserved = laneDoubt.slice(0, 40);
                try { lane.abort(); } catch (_) {}
                lane = null;
            } else if (!lane) {
                refused = true;
            }
            if (lane) {
                var fastStart = headOrTail(), mode = fastStart ? 'opfs_head' : 'opfs_tail';
                return {
                    // Exact-size writes: WebKit's writable stream writes the whole
                    // buffer behind a view (padding + seek-back corruption on Safari
                    // 26) — the lane's shared target copies first; see timbrica-core.js.
                    target: lane.target,
                    fastStart: fastStart, mode: mode,
                    opfs: { root: lane.root, name: lane.name, handle: lane.handle, writable: lane.writable, lane: lane }
                };
            }
        } catch (_) { /* fall through to RAM */ }
    }
    var memMode = refused ? 'memory_refused' : 'memory';
    /* A stream-sized file in memory is BLOCKS, not the muxer's one doubling
       buffer: that one dies at ~1 GiB with RangeError at finalize — after the
       whole encode — and peaks at ~4x the file (measured 14.10.2026: 1000 MB →
       2.9–4.0 GB; blocks → 1.5 GB, 1900 MB whole). The mode name stays — the
       dashboards and the guards read it; `blocks` says which target it was. */
    if (reserveBytes > 0 && reserveBytes <= memCeiling && Ns.StreamTarget) {
        try {
            var mem = window.__memoryMuxTarget(Ns);
            return { target: mem.target, fastStart: headOrTail(), mode: memMode, opfs: null, memory: mem, blocks: 1, unreserved: unreserved };
        } catch (_) { /* the one buffer below */ }
    }
    return { target: new Ns.ArrayBufferTarget(), fastStart: 'in-memory', mode: memMode, opfs: null, unreserved: unreserved };
}

/* __mp4Tags — перенос описательных тегов источника в наш контейнер. Грузится
   по требованию, как muxer и AacAccessUnit: потребителей у пайплайна три
   десятка, и обязывать каждый блейд помнить про ещё один тег <script> — это
   ровно тот способ, которым правило теряется у следующего тула. */
async function ensureMp4Tags() {
    if (window.__mp4Tags) return window.__mp4Tags;
    if (!ensureMp4Tags._p) {
        var src = (typeof window.__jsSrc === 'function') ? window.__jsSrc('/js/mp4-tags.js') : '/js/mp4-tags.js';
        // Теги — удобство, выданный файл — обязанность: 10 с и без тегов.
        // Опоздавший скрипт всё равно определит __mp4Tags для СЛЕДУЮЩЕГО экспорта.
        ensureMp4Tags._p = loadScript(src, 10000).catch(function (e) {
            try {
                if (e && /load-timeout/.test(e.message) && typeof window.__ae === 'function') {
                    window.__ae('tool_diagnostic', { issue: 'wcp_tags_load_timeout' });
                }
            } catch (_) { }
            /* Отказ не запоминается навсегда: иначе один сетевой обрыв оставлял ВСЕ
               следующие выдачи этой страницы без тегов и без ключа «как играть». */
            if (!window.__mp4Tags) ensureMp4Tags._p = null;
        });
    }
    try { await ensureMp4Tags._p; } catch (_) { }
    return window.__mp4Tags || null;
}

/* The disk refused a write. The muxer never awaits its writes: a refusal is
   seen in lane.stats.failed — by the encode loop on the next frame (it stops
   the run there, instead of encoding to the end: report 19.10.2026, a 71-min
   1080p file, the first write refused at 96 MB and the run went on for 13
   minutes to fail at «98 %»), or by finish(). One error for both, keeping the
   `opfs-write-failed:` prefix older callers match; `needBytes` — the room the
   whole file wanted, `memFits` — whether the in-memory lane can take it. */
function diskWriteError(lane, cause, needBytes) {
    var c = (lane && lane.stats && lane.stats.failed) || cause;
    var e = new Error('opfs-write-failed:' + ((c && c.message) || 'stream-errored'));
    e.diskFull = !!(c && (c.name === 'QuotaExceededError' || c.code === 22)) || /quota/i.test(String(c && c.message));
    if (lane && lane.picked) e.picked = true;
    e.needBytes = +needBytes || 0;
    var ceil = memoryBlocksCeiling();
    e.memFits = !!(ceil && e.needBytes > 0 && e.needBytes <= ceil);
    return e;
}
function diskWriteFailed(muxTarget) {
    return !!(muxTarget && muxTarget.opfs && muxTarget.opfs.lane && muxTarget.opfs.lane.stats && muxTarget.opfs.lane.stats.failed);
}

// Turn a finalized muxer into { blob, file } without copying the bytes.
// For the OPFS path the file on disk IS the output: getFile() hands back a
// disk-backed File, and File([thatFile]) references rather than copies.
//
// ЗДЕСЬ ЖЕ переезжают теги источника (название, исполнитель, альбом…). Это
// единственная точка пайплайна, где на руках одновременно исходный файл и
// готовый контейнер, — и точка, общая ОБОИМ выходам (run и runInterpolation).
// Нужна она потому, что mp4-muxer не умеет `udta`/`ilst` вовсе, а FFmpeg-лейн,
// которым те же тулы падают назад, переносит их сам: без этой строки один и
// тот же тул отвечает по-разному в зависимости от браузера (обращение #8219,
// /video-watermark). Врезка правит только `moov`, mdat остаётся ссылками.
//
// tagOpts.playbackIntent (1 | 0) — выход сам знает, как его играть, и говорит это
// плеерам Apple (ключ full-frame-rate-playback-intent, см. mp4-tags.js): без него
// «Фото» на iPhone и QuickTime играют середину файла ≥ 120 к/с как slow-mo. Не
// задан — ключ переезжает из источника, как и описательные теги.
async function collectMuxOutput(muxTarget, muxer, outName, srcFile, tagOpts) {
    var blob;
    var picked = !!(muxTarget.opfs && muxTarget.opfs.lane && muxTarget.opfs.lane.picked);
    if (muxTarget.opfs) {
        var f;
        try {
            // finish(): trims the reservation to the written end, closes, and
            // drops the temp file if the close fails.
            f = await muxTarget.opfs.lane.finish();
        } catch (closeErr) {
            /* The muxer never awaits its writes, so a refused one shows up only
               here, as "Cannot close a ERRORED writable stream". Name the real
               cause — the disk, not the encoder (see openMuxTarget). */
            throw diskWriteError(muxTarget.opfs.lane, closeErr, muxTarget.needBytes);
        }
        if (!f || !f.size) throw new Error('muxer-empty');
        blob = f;
    } else if (muxTarget.memory) {
        // The block target: the Blob is the blocks themselves, nothing is copied into one buffer.
        blob = muxTarget.memory.finish('video/mp4');
        if (!blob || !blob.size) throw new Error('muxer-empty');
    } else {
        var buffer = muxer.target.buffer;
        if (!buffer || !buffer.byteLength) throw new Error('muxer-empty');
        blob = new Blob([buffer], { type: 'video/mp4' });
    }
    // The person's own file is already the delivered file — the tags would make
    // a second, composite copy that never reaches their disk.
    var _intent = (tagOpts && (tagOpts.playbackIntent === 0 || tagOpts.playbackIntent === 1)) ? tagOpts.playbackIntent : undefined;
    if ((srcFile || _intent !== undefined) && !picked) {
        try {
            var T = await ensureMp4Tags();
            if (T) blob = await T.carry(srcFile, blob, { slug: 'webcodecs-pipeline', playbackIntent: _intent });
        } catch (_) { /* fail-open: теги важны, выданный файл важнее */ }
    }
    // opfsName — the raw output file in the OPFS root, before the tags. A caller
    // that keeps the result for later (ResumableRender, resumable-render.js) MOVES
    // that file instead of copying the bytes — with tags carried, `blob` is a
    // composite with no name, and the temp would stay on a phone's disk twice.
    var out = { blob: blob, file: new File([blob], outName, { type: 'video/mp4' }), opfsName: (muxTarget.opfs && muxTarget.opfs.name) || '' };
    // __savedToPicked — the convention of CanvasVideoExport: the file is on the person's disk, delivery is done
    // (and it is no OPFS temp a caller could move).
    if (picked) { out.savedToPicked = true; out.pickedName = out.opfsName; out.opfsName = ''; }
    return out;
}

// ─────────────────────────────────────────────────────────────────────
// AUDIO SPECIFIC CONFIG (AAC) — build the 2-byte AudioSpecificConfig
// that VideoEncoder/AudioEncoder + mp4-muxer expect as `description`.
// Layout per ISO/IEC 14496-3:
//   audioObjectType (5 bits) | samplingFrequencyIndex (4 bits) | channelConfiguration (4 bits)
// = 13 bits, padded to 2 bytes.
// AAC LC (audioObjectType=2) covers 99 % of mp4a.40.2 streams.
// ─────────────────────────────────────────────────────────────────────
var AAC_SAMPLE_RATE_TABLE = [
    96000, 88200, 64000, 48000, 44100, 32000, 24000, 22050,
    16000, 12000, 11025, 8000, 7350
];

function buildAacAsc(sampleRate, channels) {
    var sri = AAC_SAMPLE_RATE_TABLE.indexOf(sampleRate);
    if (sri < 0) sri = 4;            // 44100 fallback
    var aot = 2;                     // AAC LC
    var cc = Math.max(1, Math.min(7, channels || 2));
    return new Uint8Array([
        (aot << 3) | (sri >> 1),
        ((sri & 1) << 7) | (cc << 3)
    ]);
}

// Audio passthrough copies source frames VERBATIM under an AAC-LC
// AudioSpecificConfig: mp4-muxer guesses one from options.audio, and our
// first-chunk `description` (buildAacAsc) overwrites it — either way the track
// ships an esds that says AAC-LC, so only real AAC-LC frames may take that
// route. MP4Box spells the esds objectTypeIndication into the codec string:
// AAC is `mp4a.40.2`, but MP3-in-MP4 is `mp4a.6b`, MPEG-2 audio `mp4a.69`,
// MP3-as-MPEG-4-audio `mp4a.40.34`, HE-AAC `mp4a.40.5/.29` — every one of
// them passed the old bare /^mp4a/ gate and got copied under a fabricated
// AAC-LC header. Report 2026-08-02 (/ru/profile, KMPlayer refused the whole
// file): the attached output held 1152-sample/836-byte frames (MP3 @256 kbps)
// declared AAC-LC; ffmpeg answers "Error decoding AAC frame header" on every
// frame. Tolerant players (Windows «Кино и ТВ», VLC) show the video and drop
// the audio, which is why this stayed invisible — and it fires no telemetry
// of its own. Anything not AAC-LC now takes the decode → AAC re-encode path.
// A bare `mp4a` (esds not parsed) is assumed AAC-LC, same as
// webcodecs-audio-decode.js does.
function isAacPassthroughCodec(codec) {
    if (!codec) return true;
    return /^mp4a(\.40(\.2)?)?$/i.test(String(codec).trim());
}

/* ЗВУК, КОТОРЫЙ НАЧИНАЕТСЯ ПОЗЖЕ КАРТИНКИ — копией, а не декодом (22.10.2026).
   Телефон (Android MediaMuxer, камеры iPhone) пишет у звуковой дорожки пустую
   правку в начале: звук вступает через 22–2 400 мс после первого кадра. MP4 без
   списка правок не умеет начать дорожку позже нуля, поэтому прежде такой AAC-LC
   декодировался ЦЕЛИКОМ и кодировался заново с тишиной впереди (muxSpeedAudio
   `leadSec`): весь файл в кучу (`_decodeWholeAudio`), а на Яндексе нативный
   decodeAudioData отдавал обрубок — 79 с из 122, 68 из 202, и прогон падал с
   `audio-decode-length-mismatch` (второй провал сборки /video-stabilizer;
   30 д — ~400 сессий конвейера с такой задержкой, почти все телефоны).
   Теперь задержка — N беззвучных кадров AAC-LC перед байтами источника. N — с
   округлением ВВЕРХ, излишек (< кадра) снимает та же задержка первого кадра
   видео, что держит остаток копии (`_videoDelayUs`), — синхрон точный. Только
   AAC-LC без SBR, моно/стерео, без обрезки; иначе — прежний путь. */
function _silentAacFrame(channels) {
    // SCE (моно) или CPE с общим окном (стерео): ONLY_LONG, max_sfb 0 — ни
    // одного спектрального коэффициента, — затем ID_END. Моно 4 байта, стерео 6.
    var bits = [];
    function put(v, n) { for (var i = n - 1; i >= 0; i--) bits.push((v >> i) & 1); }
    function icsInfo() { put(0, 1); put(0, 2); put(0, 1); put(0, 6); put(0, 1); }
    function ics(withInfo) { put(0, 8); if (withInfo) icsInfo(); put(0, 1); put(0, 1); put(0, 1); }
    if (channels === 1) { put(0, 3); put(0, 4); ics(true); }
    else { put(1, 3); put(0, 4); put(1, 1); icsInfo(); put(0, 2); ics(false); ics(false); }
    put(7, 3);
    while (bits.length % 8) bits.push(0);
    var out = new Uint8Array(bits.length / 8);
    for (var b = 0; b < bits.length; b++) out[b >> 3] |= bits[b] << (7 - (b & 7));
    return out;
}
// AudioSpecificConfig → {aot, sampleRate, channels, sbr}; null — не прочесть.
function _aacAscInfo(asc) {
    if (!asc || asc.length < 2) return null;
    var pos = 0;
    function rd(n) { var v = 0; for (var i = 0; i < n; i++, pos++) { if ((pos >> 3) >= asc.length) return -1; v = (v << 1) | ((asc[pos >> 3] >> (7 - (pos & 7))) & 1); } return v; }
    var aot = rd(5); if (aot === 31) aot = 32 + rd(6);
    var sfi = rd(4), rate = sfi === 15 ? rd(24) : AAC_SAMPLE_RATE_TABLE[sfi];
    var ch = rd(4);
    if (aot !== 2) return { aot: aot, sampleRate: rate, channels: ch, sbr: false };
    if (rd(1) !== 0) return null;                       // frameLengthFlag: 960-сэмпловые кадры
    rd(1); rd(1);                                       // dependsOnCoreCoder, extensionFlag
    var sbr = false;
    if (asc.length * 8 - pos >= 16 && rd(11) === 0x2b7) {
        if (rd(5) === 5) sbr = rd(1) === 1;             // явная пометка: есть ли SBR
    }
    return { aot: aot, sampleRate: rate, channels: ch, sbr: sbr };
}
function _silentLeadPlan(demuxInfo, edit, opts) {
    var a = demuxInfo && demuxInfo.audio;
    if (!a || a.pcm || !edit || !edit.applied || !(edit.audioLeadUs > 0)) return null;
    if (!isAacPassthroughCodec(a.codec)) return null;
    if ((+(opts && opts.trimStart) > 0.001) || (+(opts && opts.trimEnd) > 0.001)) return null;
    var info = (a.ascBytes && a.ascBytes.length) ? _aacAscInfo(a.ascBytes)
        : { aot: 2, sampleRate: a.sampleRate, channels: a.channels, sbr: false };
    if (!info || info.aot !== 2 || info.sbr || !(info.channels === 1 || info.channels === 2) || !(info.sampleRate > 0)) return null;
    var frameUs = 1024e6 / info.sampleRate, residual = edit.audioResidualUs || 0;
    var n = Math.max(0, Math.ceil((edit.audioLeadUs - residual) / frameUs));
    return { frames: n, frameUs: frameUs, holdUs: Math.max(0, Math.round(n * frameUs + residual - edit.audioLeadUs)),
             frame: _silentAacFrame(info.channels) };
}

// What the decode → re-encode path will ACTUALLY hand the muxer. It renders
// through WebAudio, whose output rate is the AudioContext's (device) rate, and
// the AAC AudioEncoder takes only 44100/48000 Hz and ≤2 channels — so the
// muxer track, built before any of that runs, must be planned from here rather
// than copied from the source. The esds itself self-heals (the encoder's real
// decoderConfig rides the first chunk and Object.assign's over the guess), but
// `timescale` is fixed at construction: declaring the source rate left 48 kHz
// frames timed on a 22050 grid (measured: 576/940-unit frame durations).
// Keeping a 44.1 kHz source at 44.1 kHz also spares it a pointless resample to
// whatever the device happens to run at.
function plannedAudioOut(srcRate, srcChannels) {
    return {
        sampleRate: (srcRate === 44100 || srcRate === 48000) ? srcRate : 48000,
        numberOfChannels: Math.min(2, Math.max(1, srcChannels || 2))
    };
}

// ─────────────────────────────────────────────────────────────────────
// CAPABILITY CHECK + CODEC CASCADES
// ─────────────────────────────────────────────────────────────────────

// H.264 encoder profile cascade. A full codec string is avc1.PPCCLL where
// PP=profile_idc, CC=constraint flags, LL=level_idc. These are the
// profile PREFIXES (PP+CC); pickEncoderConfig appends the level byte (LL)
// computed per output resolution — see h264LevelHex.
//
// ROOT-CAUSE FIX: the cascade previously hardcoded Level 5.1 (0x33) on
// every profile ("5.1 covers ≤4K so it's sufficient"). But "sufficient
// capacity" is not "accepted by the encoder" — an encoder that does not
// advertise Level 5.1 then rejects ALL three profiles and there is no
// lower-level fallback → `encoder-rejects-all-h264-profiles` → the whole
// HW path is skipped, every export drops to slow FFmpeg WASM. Telemetry
// (14 d): 52 sessions hit this, 100 % Yandex Browser. A 720p export only
// needs Level 3.1; sending Level 5.1 for it is both needless and the bug.
// Fix: pick the minimum level the resolution requires, then cascade up.
//
// /pino telemetry 2026-05-08: `encoder-rejects-avc1.640028` — High profile
// alone was rejected; the Main/Baseline cascade rescues those devices.
var H264_ENCODER_PROFILES = [
    'avc1.6400',  // High        (profile_idc 0x64, constraints 0x00)
    'avc1.4d40',  // Main        (profile_idc 0x4d, constraints 0x40)
    'avc1.42e0',  // Constrained Baseline (profile_idc 0x42, constraints 0xe0)
];

// Minimum H.264 level_idc (as a 2-hex-digit string) for a frame size +
// rate, from the standard level table (MaxFS in macroblocks, MaxMBPS).
// Picking the minimum level maximises both encoder acceptance and decoder
// playback compatibility; pickEncoderConfig still cascades UP from here.
/* `bps` (optional) — the stream's bitrate must fit the level too (MaxBR, the
   Baseline/Main figure: the conservative one, High allows ×1.25). Without it
   the answer is the same as it always was; 4.1 is only ever reached by a
   bitrate that 4.0 cannot carry — the software encoder's budget for 1080p30
   (window.__swEncoderBudget, timbrica-core.js). */
function h264LevelHex(w, h, fps, bps) {
    var mb = Math.ceil(w / 16) * Math.ceil(h / 16);
    var mbps = mb * (fps || 30);
    var kbps = (+bps > 0) ? +bps / 1000 : 0;
    var T = [
        ['1e', 1620,   40500,   10000],    // 3.0
        ['1f', 3600,   108000,  14000],    // 3.1  — 1280×720@30
        ['20', 5120,   216000,  20000],    // 3.2
        ['28', 8192,   245760,  20000],    // 4.0  — 1920×1080@30
        ['29', 8192,   245760,  50000],    // 4.1
        ['2a', 8704,   522240,  50000],    // 4.2  — 1920×1080@60
        ['32', 22080,  589824,  135000],   // 5.0
        ['33', 36864,  983040,  240000],   // 5.1  — 3840×2160@30
        ['34', 36864,  2073600, 240000]    // 5.2  — 3840×2160@60
    ];
    for (var i = 0; i < T.length; i++)
        if (mb <= T[i][1] && mbps <= T[i][2] && kbps <= T[i][3]) return T[i][0];
    return '34';
}

/* ── The SOFTWARE H.264 rung gets its own bits ─────────────────────────────
   Chromium's software H.264 encoder (OpenH264 — what `prefer-software` gets,
   and what every Android export uses: ENCODER_HINTS below) spends its bits far
   worse than the hardware encoders and x264 the CRF table was calibrated on,
   and saturates near VMAF 96. The rule and its measurement live in ONE place,
   `window.__swEncoderBudget` (timbrica-core.js) — the same for this pipeline,
   the hybrid lane and CanvasVideoExport: ×2.2 up to 0.4 bits per pixel, never
   below the table's rate. On for every caller since 2026-10-08 (first only for
   /enhance-video, «как будто видео жестко ужали»); `softwareEncoderGain: false`
   opts out, and an EXPLICIT `videoBitrate` never gets it — /video-compressor's
   size and /car-usb's head-unit rate are promises about bytes. No core loaded
   (a page without the layout, a Node test) — the table's rate, as before. */
function softwareRungBps(bps, w, h, fps, opts) {
    if (opts && (opts.softwareEncoderGain === false || +opts.videoBitrate > 0)) return 0;
    var f = (typeof window !== 'undefined') && window.__swEncoderBudget;
    if (typeof f !== 'function') return 0;
    var b = f(bps, w, h, fps);
    // A caller's ceiling (opts.maxVideoBitrate) binds this rung too: the phone
    // proxies of /video-to-sound reached 4.8–7.4 Mbit/s through the gain alone.
    var cap = opts ? +opts.maxVideoBitrate : 0;
    if (isFinite(cap) && cap > 0 && b > cap) b = cap;
    return (b > bps) ? b : 0;
}

// H.264 / HEVC / VP9 / AV1 decoder candidates. mp4box gives us the full
// codec string (`hev1.X.X.X.X` etc.); we pass that through. For the
// generic "h264" / "hevc" / "vp9" / "av1" caller hints from FFmpeg probe,
// expand to known codec strings so isConfigSupported picks the right
// flavour. Order matters — first hit wins.
var DECODER_CODEC_EXPANSIONS = {
    h264:  ['avc1.640033', 'avc1.4d4033', 'avc1.42e033', 'avc1.640028'],
    avc:   ['avc1.640033', 'avc1.4d4033', 'avc1.42e033', 'avc1.640028'],
    hevc:  ['hev1.1.6.L93.B0', 'hvc1.1.6.L93.B0', 'hev1.2.4.L93.B0'],
    h265:  ['hev1.1.6.L93.B0', 'hvc1.1.6.L93.B0'],
    vp9:   ['vp09.00.31.08', 'vp09.02.31.10'],
    vp09:  ['vp09.00.31.08', 'vp09.02.31.10'],
    av1:   ['av01.0.04M.08', 'av01.0.05M.08'],
    av01:  ['av01.0.04M.08', 'av01.0.05M.08'],
};

// Hardware-acceleration hints in priority order. Some impls reject
// `prefer-hardware` even when they have it (Safari historically); some
// embedded encoders only accept the explicit `prefer-software` hint.
// Falling through `null` means "let the impl pick" — last resort.
//
// On Android (phones AND tablets), the platform's MediaCodec H.264
// encoder is empirically unreliable under WebCodecs. Telemetry (14 d):
// **91–94 % of Android HW-export attempts wedge or fail** (Chrome
// Android 23 used / 335 wedged-or-failed; Yandex Android 21 / 211 —
// the same Chromium core, same MediaCodec backend). All Android OS
// versions (10/13/14/15/16) hit it; not browser-specific. Successful
// exports use HW only because `isConfigSupported(prefer-hardware)`
// returns `supported:true` regardless of actual reliability, so the
// cascade commits to HW first and hits the wedge — then the #448
// stall-watchdog (30 s) falls users back to FFmpeg-WASM, which is
// catastrophically slow on Android's typical `sab:0` (main-thread,
// single-thread WASM).
//
// Fix: on Android, invert the cascade — try `prefer-software` first.
// Chromium honours the hint and uses openh264 — runs on its own thread,
// fully async, far faster than the FFmpeg-WASM fallback. iOS Safari
// (296 used / 0 stalls) and desktop platforms keep HW-first; their HW
// codecs are reliable. The cost: the ~6 % of Android users whose HW
// would have worked now get SW WebCodecs (slower than HW, but still
// massively faster than the watchdog → FFmpeg-WASM path).
// …all of the above is about the ENCODER.
var ENCODER_HINTS = (typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent || ''))
    ? ['prefer-software', 'prefer-hardware', null]
    : ['prefer-hardware', 'prefer-software', null];

// ── DECODER hints — HARDWARE FIRST ON EVERY PLATFORM ──────────────────────
// Root cause found 2026-07-10 (/video-crop, 7 d analytics): the encoder-only
// Android inversion above ALSO drove the decoder cascade, because both read one
// shared HW_HINTS array. Chromium on Android has no usable software H.264
// decoder — yet `isConfigSupported({hardwareAcceleration:'prefer-software'})`
// still answers `supported:true` (the flag is only a HINT). So configure()
// succeeded, chunk 0 was pushed, and decode died with the async
// `Unsupported configuration…` OperationError at decoded_so_far:0 —
// 114 of 118 such failures were mob=1, and on mobile the HW path failed
// (114 decode errors + 42 stalls) more often than it succeeded (92 used).
// The Wave-23 software-decode rescue was unreachable there too: it refuses to
// retry when the FAILING hint was already 'prefer-software'.
// Hardware decode is reliable everywhere (it is what every native player uses);
// software stays as the fallback, and the rescue now has somewhere to go.
var DECODER_HINTS = ['prefer-hardware', 'prefer-software', null];

// Back-compat alias for the encoder cascade (legacy name).
var HW_HINTS = ENCODER_HINTS;

// A decoder that never produces its first frame is a wedge, not a slow decode.
// `isConfigSupported` cannot detect it (see DECODER_HINTS). Bail typed and fast
// so the SW rescue / FFmpeg fallback runs in seconds instead of after the
// caller's 30 s no-progress watchdog (172 stalls/wk on /video-crop).
var FIRST_FRAME_DEADLINE_MS = 6000;

// ── Encoder WEDGE: the silent death the pipeline could not see (2026-09-12) ──
// A VideoEncoder that accepts frames and never emits a chunk — or never
// returns from flush() — raises no error, so nothing inside this file ever
// noticed it. The caller's 30 s no-progress watchdog did, and its only remedy
// was to tear the whole run down into the software lane: /enhance-video,
// 30 days, RU shard — 277 sessions stalled that way, 53 got a file (19 %),
// and 93 of the stalls hit at ≥95 % after a median 15 minutes of finished
// hardware work that was then redone in single-threaded WASM.
//
// The pipeline already owns a LOSSLESS encoder rebuild (retention + replay,
// see _sentFrames) — it just only ran on an error CALLBACK. A wedge is the
// same death without the callback. So: measure silence the way the caller
// does (visible time only — a hidden tab gets no compositor scheduling and a
// healthy encode goes quiet there), and when the encoder owes ≥1 frame and
// has emitted nothing for ENC_WEDGE_MS, synthesise the error the callback
// never sent. The rebuild then runs unchanged, with ONE difference: the
// wedged hint is not reused. A hardware encoder that just froze on this
// stream is rebuilt as the next rung of ENCODER_HINTS (software on desktop,
// hardware on Android where software goes first), so the retry is not the
// same bet twice.
//
// 12 s sits under the caller's 30 s so recovery lands before the outer
// watchdog fires: a healthy encoder emits within a frame period, and the
// slowest legitimate one measured (software 4 K on a weak laptop) still
// emits every ~0.7 s. A false positive costs one rebuild and a keyframe —
// never a frame, because retention replays everything unconfirmed.
var ENC_WEDGE_MS = 12000;
var _visClockSingleton = null;
// Milliseconds of VISIBLE page time since first use. One listener for the
// page lifetime; every run reads the same monotonic clock.
function _visNow() {
    if (!_visClockSingleton) {
        var acc = 0, since = (typeof document !== 'undefined' && document.hidden) ? -1 : performance.now();
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', function () {
                if (document.hidden) { if (since >= 0) { acc += performance.now() - since; since = -1; } }
                else if (since < 0) since = performance.now();
            });
        }
        _visClockSingleton = function () { return acc + (since >= 0 ? performance.now() - since : 0); };
    }
    return _visClockSingleton();
}
// ── Уступить поток так, чтобы скрытая вкладка не останавливала работу ───────
// (25.09.2026) Цикл подачи уступал поток таймером ПОСЛЕ КАЖДОГО образца —
// `setTimeout(0)`, «дать выдаче кадра сработать». В скрытой вкладке Chrome будит
// цепочки таймеров раз в секунду, а после 5 минут скрытия — раз в минуту: один
// образец в минуту. Замер настоящим Chrome по CDP (/video-retouch, 8-с ролик,
// 240 кадров): видимая вкладка — 140 с на весь экспорт, скрытая — проход анализа
// «~3 ч 53 мин осталось». У шестиминутного ролика 11 237 образцов на проход, то
// есть любой уход на другую вкладку фактически останавливал экспорт — у всех
// тулов конвейера. В видимой вкладке всё по-прежнему (таймер); в скрытой — задача
// через MessageChannel, которую браузер не троттлит. Дедлайны и сторожа остаются
// таймерами намеренно: их время — видимое (_visNow), а не стенное.
var _tickChan = null, _tickQ = [];
function _yieldTick(ms) {
    if (typeof document === 'undefined' || !document.hidden || typeof MessageChannel === 'undefined') {
        return new Promise(function (r) { setTimeout(r, ms || 0); });
    }
    if (!_tickChan) {
        _tickChan = new MessageChannel();
        _tickChan.port1.onmessage = function () { var f = _tickQ.shift(); if (f) f(); };
    }
    return new Promise(function (r) { _tickQ.push(r); _tickChan.port2.postMessage(0); });
}
// The hints that remain AFTER the one that just wedged, in cascade order.
// 'no-hint' / null is the last rung; a wedge there has nowhere to escalate
// and the caller keeps the validated config (the pre-existing reclaim path).
function _hintsAfter(chosenHint) {
    var idx = -1;
    for (var i = 0; i < ENCODER_HINTS.length; i++) {
        if ((ENCODER_HINTS[i] || 'no-hint') === (chosenHint || 'no-hint')) { idx = i; break; }
    }
    return idx >= 0 ? ENCODER_HINTS.slice(idx + 1) : [];
}

// Diagnostic probe fired ONLY when the whole H.264 cascade fails: do the
// royalty-free encoders work on this device? Answers (via prod telemetry)
// whether a VP9/VP8 output path would rescue the "no h264 profile supported"
// cohort (52/14d, mobile Chrome/Yandex) BEFORE we build it — measure, don't
// guess. One feature_use event per failed run; never throws.
async function _probeAltEncoders(width, height) {
    var out = { vp8: 0, vp9: 0, av1: 0 };
    var alts = { vp8: 'vp8', vp9: 'vp09.00.31.08', av1: 'av01.0.08M.08' };
    for (var k in alts) {
        try {
            var s = await VideoEncoder.isConfigSupported({ codec: alts[k], width: width, height: height, bitrate: 2e6, framerate: 30 });
            out[k] = (s && s.supported) ? 1 : 0;
        } catch (_) {}
    }
    try { window.__ae && window.__ae('feature_use', { action: 'hw_enc_alt_probe', vp8: out.vp8, vp9: out.vp9, av1: out.av1, w: width, h: height }); } catch (_) {}
    return out;
}

// ── verifyEncoderAt(w, h, fps) — can this device REALLY encode H.264 at these
// dimensions? Answered by DOING, not by asking: configure a real VideoEncoder
// and demand one chunk out of one frame. `isConfigSupported` lies in both
// directions (Android answers true for a software decoder it does not have;
// a wedged GPU process answers false for every profile it encoded yesterday
// — user 4126), so no prediction may rest on its word alone. The one-frame
// encode is the same authority `encoder.configure()` holds at run time,
// bought for ~100-300 ms instead of a doomed export.
//
// Born from comment #905 (Cecil, 2026-08-12): a phone that DECODES 3840×1728
// fine but cannot CREATE an encoder there was promised the hardware lane —
// «~1 min» on the button, 73.5 min in the WASM fallback, a rage-comment at
// «99%». The lane predictor consulted the decoder and the container, never
// the encoder. This probe is what it should have asked. ~550 sessions/30 d
// die on chunk-0 encoder creation — the class, not the man.
//
// Session-cached per w×h (the answer cannot change mid-session short of a
// GPU-process restart, and a stale `false` only costs pessimism the
// unverified run-time attempt corrects). Never throws; a probe that dies or
// times out reports { ok:false, uncertain:true } — callers may treat
// `uncertain` as "no verdict" rather than "verified no".
// ── verifyDecoderFor(file) — the OTHER half of the same question ──────────
// #905 fixed «the predictor asked the decoder and promised the encoder».
// the 2026-08-15 feedback-form report (Кольчицкий) is its mirror: enc_verify_probe said
// ok:1 hw:1 at 1096×2560, the button promised 20 s — and the DECODER refused
// the very first chunk («Decoding error», then «Unsupported configuration»
// on the retry), so the run fell into FFmpeg-WASM and took 1258 s. 63×.
//
// Same doctrine, same shape: earn the verdict by DOING — configure a real
// VideoDecoder with the file's own avcC/hvcC description and demand one
// decoded frame out of its first keyframe. isConfigSupported is not enough
// here for the reason already documented above (Android answers `true` for
// decoders it does not have — that is DECODER_HINTS' whole story).
//
// Cached per (codec, w×h). Returns { ok, uncertain }: a timeout is
// `uncertain` and keeps today's optimism, a clean refusal is a verified no.
// One-call entry for callers that hold a FILE, not a demuxInfo. Owns its own
// dependency: demuxMp4 throws `mp4box-missing` unless someone else happened to
// warm the global first, and a probe that only runs when another feature
// warmed it for you is a probe that silently never runs (measured while
// building this — the guard passed only because probeCodec had loaded mp4box
// a moment earlier). Exporting the footgun and documenting the order would be
// the костыль; owning the dependency is the fix.
var _decFileCache = new WeakMap();
async function verifyDecoderForFile(file) {
    if (!file) return { ok: false, uncertain: true };
    var hit = _decFileCache.get(file);
    if (hit) return hit;
    var p = (async function () {
        try { await ensureMp4Box(); } catch (_) { return { ok: false, uncertain: true }; }
        if (!window.MP4Box) return { ok: false, uncertain: true };
        var info;
        try { info = await demuxMp4(file, function () {}, { headOnly: true }); }
        catch (_) { return { ok: false, uncertain: true }; }   // unreadable ≠ "decoder can't"
        return await verifyDecoderFor(info);
    })();
    _decFileCache.set(file, p);
    return p;
}

var _decVerifyCache = {};
async function verifyDecoderFor(demuxInfo) {
    if (!demuxInfo || !demuxInfo.codec) return { ok: false, uncertain: true };
    var key = demuxInfo.codec + '@' + demuxInfo.width + 'x' + demuxInfo.height;
    if (_decVerifyCache[key]) return _decVerifyCache[key];
    _decVerifyCache[key] = (async function () {
        var t0 = Date.now();
        var out = { ok: false, uncertain: false, ms: 0, hint: '' };
        try {
            if (typeof VideoDecoder === 'undefined') { out.uncertain = true; return out; }
            // `is_sync`, not `isSync` — the demuxer's own field name (see
            // videoSamples.push in demuxMp4). Reading the camelCase spelling
            // would find no keyframe and silently report `uncertain` forever,
            // i.e. a probe that never says anything.
            var key0 = null, samples = demuxInfo.samples || [];
            for (var i = 0; i < samples.length; i++) { if (samples[i].is_sync) { key0 = samples[i]; break; } }
            if (!key0) { out.uncertain = true; return out; }      // no keyframe to try
            // ⚠️ CASCADE the same hints the real decode loop does. The first
            // draft of this probe tried DECODER_HINTS[0] only and reported a
            // verified NO in 14 ms for an ordinary 720×1280 H.264 file on a
            // perfectly healthy machine — headless/软-only boxes have no
            // hardware H.264 decoder, and the real path just falls through to
            // 'prefer-software'. A probe stricter than the path it vouches
            // for pushes healthy users onto the slow lane: exactly the
            // «проба отобрала HW» incident from /music-video-maker. Only when
            // EVERY hint fails is this a verified no.
            var baseCfg = {
                codec: sanitizeAvcLevel(demuxInfo.codec),
                codedWidth: demuxInfo.width, codedHeight: demuxInfo.height,
                description: demuxInfo.avcC || demuxInfo.hvcC || demuxInfo.av1C || demuxInfo.vpcC
            };
            if (!baseCfg.description) delete baseCfg.description;
            var timedOut = false;
            for (var hi = 0; hi < DECODER_HINTS.length && !out.ok; hi++) {
                var dec = null;
                try {
                    var frames = 0, settle, dead;
                    var verdict = new Promise(function (res, rej) { settle = res; dead = rej; });
                    // Same trap as verifyEncoderAt: a configure() refused
                    // inline closes the decoder, decode() throws past the race,
                    // and the queued error callback rejects an orphan.
                    verdict.catch(function () {});
                    dec = new VideoDecoder({
                        output: function (f) { frames++; try { f.close(); } catch (_) {} settle(); },
                        error: function (e) { dead(e || new Error('probe-decoder-error')); }
                    });
                    var cfg = Object.assign({}, baseCfg);
                    if (DECODER_HINTS[hi]) cfg.hardwareAcceleration = DECODER_HINTS[hi];
                    dec.configure(cfg);                           // sync throw → catch
                    dec.decode(new EncodedVideoChunk({
                        type: 'key', timestamp: key0.timestamp || 0,
                        duration: key0.duration || 0,
                        data: key0.data instanceof Uint8Array ? key0.data : new Uint8Array(key0.data)
                    }));
                    dec.flush().then(function () { settle(); }).catch(function () {});
                    await Promise.race([
                        verdict,
                        new Promise(function (_, rej) { setTimeout(function () {
                            var e = new Error('probe-timeout'); e.probeTimeout = true; rej(e);
                        }, 4000); })
                    ]);
                    if (frames > 0) { out.ok = true; out.hint = DECODER_HINTS[hi] || 'none'; }
                } catch (e) {
                    if (e && e.probeTimeout) timedOut = true;
                } finally {
                    try { if (dec && dec.state !== 'closed') dec.close(); } catch (_) {}
                }
            }
            // A timeout anywhere in the cascade is "no verdict", not "no":
            // same asymmetry as the encoder probe — attempting costs seconds,
            // a false negative costs the fast lane entirely.
            if (!out.ok && timedOut) out.uncertain = true;
        } catch (e) {
            out.ok = false;
            out.uncertain = !!(e && e.probeTimeout);
        }
        out.ms = Date.now() - t0;
        try { window.__ae && window.__ae('feature_use', {
            action: 'dec_verify_probe', codec: String(demuxInfo.codec).slice(0, 20),
            w: demuxInfo.width, h: demuxInfo.height,
            ok: out.ok ? 1 : 0, uncertain: out.uncertain ? 1 : 0, ms: out.ms,
            hint: out.hint || ''
        }); } catch (_) {}
        return out;
    })();
    return _decVerifyCache[key];
}

var _encVerifyCache = {};
/* One rung of the ladder: one config, one frame, one chunk. */
async function _encodeOneFrame(pick, width, height) {
    var r = { ok: false, uncertain: false, step: '', err: '' };
    var enc = null, frame = null, step = 'configure';
    try {
        var chunks = 0, settle, dead;
        var verdict = new Promise(function (res, rej) { settle = res; dead = rej; });
        verdict.catch(function () {});
        enc = new VideoEncoder({
            output: function () { chunks++; settle(); },
            error: function (e) { dead(e || new Error('probe-encoder-error')); }
        });
        enc.configure(pick.config);                       // sync throw → catch
        step = 'frame';
        var cv;
        if (typeof OffscreenCanvas !== 'undefined') cv = new OffscreenCanvas(width, height);
        else { cv = document.createElement('canvas'); cv.width = width; cv.height = height; }
        var c2d = cv.getContext('2d');
        if (c2d) c2d.fillRect(0, 0, 2, 2);                // realize the backing store
        frame = new VideoFrame(cv, { timestamp: 0, duration: 16666 });
        step = 'encode';
        enc.encode(frame, { keyFrame: true });
        step = 'flush';
        var flushed = enc.flush().then(function () { settle(); });
        flushed.catch(function () {});                     // verdict promise carries the error
        step = 'race';
        await Promise.race([
            verdict,
            new Promise(function (_, rej) { setTimeout(function () {
                var e = new Error('probe-timeout'); e.probeTimeout = true; rej(e);
            }, 4000); })
        ]);
        r.ok = chunks > 0;
    } catch (e) {
        r.ok = false;
        r.uncertain = !!(e && e.probeTimeout) || step === 'frame';
        r.step = step;
        r.err = String((e && (e.name || e.message)) || e || '').slice(0, 40);
    } finally {
        try { if (frame) frame.close(); } catch (_) {}
        try { if (enc && enc.state !== 'closed') enc.close(); } catch (_) {}
    }
    return r;
}

/* The verdict walks the run's OWN ladder (ENCODER_HINTS): the run picks the first rung
   the browser calls supported and, when that rung dies on the first chunk, rebuilds on the
   next (_hintsAfter). Until 28.10.2026 the probe tried the first rung only, so «no» meant
   «the first rung fails» — and /enhance-video read it as «no fast lane» and promised the
   FFmpeg-WASM time: RU 30 d, 357 of ~420 exports behind such a «no» ran the fast lane
   anyway, 5–6× faster than promised (step=encode InvalidStateError on phones — configure
   had already failed asynchronously; step=race OperationError on desktops). Now «no» = no
   rung produced a chunk; a rung that only timed out leaves the verdict uncertain.
   `rung` names the hint that worked (the estimate's speed depends on it: desktop p50 8 /
   16 / 41 ms a frame for hardware / software / no-hint). The first failing rung's
   step/err stay in the event, as before. */
async function verifyEncoderAt(width, height, fps) {
    width  = Math.max(2, Math.floor(width  / 2) * 2);
    height = Math.max(2, Math.floor(height / 2) * 2);
    var key = width + 'x' + height;
    if (_encVerifyCache[key]) return _encVerifyCache[key];
    _encVerifyCache[key] = (async function () {
        var t0 = Date.now();
        var out = { ok: false, hardware: false, uncertain: false, ms: 0, step: '', err: '', rung: '', tried: 0 };
        try {
            if (typeof VideoEncoder === 'undefined') { out.uncertain = true; return out; }
            var bps = Math.min(2e7, Math.round(width * height * 0.15)), first = null, anyUnsure = false;
            for (var hi = 0; hi < ENCODER_HINTS.length; hi++) {
                var pick = null;
                try { pick = await pickEncoderConfig(width, height, bps, fps || 30, undefined, [ENCODER_HINTS[hi]]); } catch (_) { pick = null; }
                if (!pick || !pick.config) continue;
                out.tried++;
                var r = await _encodeOneFrame(pick, width, height);
                if (r.ok) { out.ok = true; out.hardware = !!pick.hardware; out.rung = pick.hint || ENCODER_HINTS[hi] || 'no-hint'; break; }
                if (!first) first = r;
                if (r.uncertain) anyUnsure = true;
            }
            if (!out.ok) {
                out.uncertain = anyUnsure || !out.tried;
                if (first) { out.step = first.step; out.err = first.err; }
            } else if (first) { out.step = first.step; out.err = first.err; }
        } catch (e) {
            out.ok = false; out.uncertain = true;
            out.err = String((e && (e.name || e.message)) || e || '').slice(0, 40);
        }
        out.ms = Date.now() - t0;
        try { window.__ae && window.__ae('feature_use', {
            action: 'enc_verify_probe', w: width, h: height,
            ok: out.ok ? 1 : 0, hw: out.hardware ? 1 : 0,
            uncertain: out.uncertain ? 1 : 0, ms: out.ms,
            step: out.step, err: out.err, rung: out.rung, tried: out.tried
        }); } catch (_) {}
        return out;
    })();
    return _encVerifyCache[key];
}

// Try each profile × level × hint combo until one is supported. Returns
// { config, hardware: bool, codec: string } or null.
/* latency: 'realtime' (default — every existing caller) | 'quality'.
   'realtime' asks for no B-frames, which suits capture-shaped work and costs
   compression efficiency. Measured on a 1080p/60 s clip at a 10 MB target the
   two modes were indistinguishable (SSIM matched to six decimals), so this is
   a hint the encoders here appear to ignore — kept because it is the correct
   request to make for file compression and costs nothing. */
// ── The `framerate` field is a HINT with a hard ceiling behind it ─────────
// `VideoEncoderConfig.framerate` exists so the encoder can size a per-frame
// bit budget — the spec calls it "the expected frame rate", not a promise
// about the stream. But encoders DECLARE a maximum for it, and Chromium
// enforces that maximum in `isConfigSupported`, so quoting a genuinely high
// output rate throws away the whole hardware encoder.
//
// Measured 2026-09-06 in real Chrome with the codec string, resolution and
// bitrate all pinned and ONLY this field moving: `prefer-hardware` accepted
// 121 and refused 128 — the same cutoff at 1080×1440 and at 640×480, i.e. a
// property of the number, not of the workload. The same sweep showed the
// field does not change the bytes: 180 real frames at 1/180 s timestamps came
// out 44934 / 45350 / 46816 B when the encoder was told 30 / 60 / 120, and the
// effective bitrate stayed inside 359–375 kbps. So a lower value is nearly
// free, and asking for the true one is nearly worthless.
//
// This matters for interpolation, the one lane whose output rate is a
// multiple of the source: /video-fps ×3 on a 60 fps phone clip asks for 180.
// On prod (RU, 30 d) 329 sessions died at `webcodecs_encoder_error` and
// completion fell from 75.6 % (≤60 fps) to 57 % (>120 fps). The fallback they
// landed on — FFmpeg-WASM `minterpolate` — finished 18.8 % of mobile runs.
//
// So the cascade gets one more axis, tried LAST: the honest value first (no
// behaviour changes for anyone it already works for), then progressively
// clamped candidates as a rescue instead of surrender.
// The core's rule (window.__encoderRate, timbrica-core.js) when the page has it;
// without the core (a Node test, a page without the layout) — as given.
function encoderRateFor(bps, fps, told, hint) {
    var f = (typeof window !== 'undefined') && window.__encoderRate;
    if (typeof f === 'function') return f(bps, +fps, told, hint);
    return { bitrate: bps, framerate: told };
}

function frameRateCandidates(framerate, cap) {
    var f = +framerate;
    if (!isFinite(f) || f <= 0) f = 30;
    var out = [];
    var ceil = (cap > 0) ? Math.min(cap, f) : f;
    function push(v) {
        v = Math.round(v);
        // Never above the caller's ceiling: a rescue that is told «60 or less»
        // and then offers 120 is just the failing attempt again, one rung later.
        if (v >= 1 && v <= ceil && out.indexOf(v) < 0) out.push(v);
    }
    push(ceil);
    [120, 60, 30].forEach(function (v) { push(Math.min(v, ceil)); });
    if (!out.length) out.push(Math.max(1, Math.round(f)));
    return out;
}

/* Каскад профилей под КОНКРЕТНОГО потребителя.
 *
 * Порядок по умолчанию — High → Main → Baseline: он про то, что примет
 * ЭНКОДЕР, и для веба это верно. Но у выдачи бывает второй адресат, чьё
 * мнение важнее: автомагнитола и экран в подголовнике играют, как правило,
 * только Constrained Baseline, и файл в High они не откроют вовсе — притом
 * что энкодер его принял, экспорт «удался» и человек узнаёт правду уже в
 * машине. Поэтому потребитель может НАВЯЗАТЬ порядок; каскад внутри
 * навязанного порядка сохраняется, чтобы отказ энкодера всё так же имел куда
 * отступить. Заведено 2026-09-10 для /car-usb-video.
 */
var H264_PROFILE_ORDERS = {
    high:     ['avc1.6400', 'avc1.4d40', 'avc1.42e0'],   // = поведение по умолчанию
    main:     ['avc1.4d40', 'avc1.42e0', 'avc1.6400'],
    baseline: ['avc1.42e0', 'avc1.4d40', 'avc1.6400'],
};

/* `bitrateMode` — опционально (01.10.2026, /video-compressor «Целевой размер»): 'constant'
   держит обещанный размер там, где 'variable' перелетает. Замер на аппаратном H.264
   (RTX 4060, Chrome 154): лекция 640×360 при запросе 95 кбит/с — VBR 118, CBR 91
   (VMAF 75.3 / 76.1); настоящая съёмка 1080p при 1.84 Мбит/с — VBR +3 %, CBR ровно
   (VMAF 93.0 / 92.9), при 600 кбит/с — VBR +18 %, CBR ровно (77.9 / 76.3 на 15 % меньших
   битах), CBR ещё и быстрее (38 / 52 с, 22 / 61 с). Кодер, не принимающий режим, получает
   прежний подбор без него. */
/* `softwareBps` — опционально (08.10.2026): битрейт ПРОГРАММНОЙ ступени (`prefer-software`),
   по правилу `window.__swEncoderBudget` (ядро). Ступень проверяется с ним и с
   уровнем, который его вмещает; остальные ступени — с прежним `bitrate`. Ответ называет
   битрейт, с которым ступень проверена (`bitrate`), и множитель (`softwareGain`). */
async function pickEncoderConfig(width, height, bitrate, framerate, latency, hintsOverride, framerateCap, profileOrder, bitrateMode, softwareBps) {
    // `hintsOverride` — каскад подсказок, навязанный СНАРУЖИ (rescue в run()).
    // Нужен по той же причине, что `_decodeHints` у декодера: `configure()` —
    // единственная инстанция, которая знает правду, и её отказ обязан иметь
    // куда отступить, а не только куда упасть.
    var hints = hintsOverride || HW_HINTS;
    var PROFILES = (profileOrder && profileOrder.length) ? profileOrder : H264_ENCODER_PROFILES;
    var rates = frameRateCandidates(framerate, framerateCap);
    /* The HINT is the outer axis, the rate the inner one (2026-10-22). Until
       now the rate was outer: the honest rate was asked of every hint before a
       clamped one was asked of any — so on a desktop whose hardware encoder
       refuses 112 fps, the SOFTWARE encoder at 112 won over the hardware one
       told 60. /video-fps report «до 120 увеличил, а стало лишь 30» ran exactly
       that: OpenH264 at 33 ms a frame (13 min for 3½ minutes of video) and its
       ×2.2 bit budget (985 MB from 312); RU 30 d — 347 of 1550 fast runs left
       on a non-hardware encoder, 63 ms a frame against 19.8. The hint itself is
       nearly free to clamp: hardware H.264 told 60 instead of 113 for 112.77 fps
       frames wrote 9.2 Mbit/s against 9.6 (Chrome 155, Windows hardware
       encoder); told 30 it inflated 2.4×, which is why 60 comes before 30. */
    for (var hi = 0; hi < hints.length; hi++) {
    for (var ri = 0; ri < rates.length; ri++) {
        var rate = rates[ri];
        // Levels to try, low→high: the minimum the resolution requires first
        // (widest encoder + decoder compatibility — fixes the all-5.1 bug),
        // then 5.1 / 5.2 as fall-backs for encoders that prefer a higher level.
        // Derived from the TRUE output rate on the first pass and from the
        // clamped one afterwards: a level that no encoder accepts is exactly
        // what we are climbing down from.
        var minLvl = h264LevelHex(width, height, rate);
        var levels = [minLvl];
        ['33', '34'].forEach(function (L) {
            if (parseInt(L, 16) > parseInt(minLvl, 16) && levels.indexOf(L) < 0) levels.push(L);
        });
        for (var ci = 0; ci < PROFILES.length; ci++) {
            for (var li = 0; li < levels.length; li++) {
                {
                    var swRung = hints[hi] === 'prefer-software' && +softwareBps > bitrate;
                    var rungBps = swRung ? +softwareBps : bitrate;
                    var lvl = levels[li];
                    if (swRung) {
                        var needLvl = h264LevelHex(width, height, rate, rungBps);
                        if (parseInt(needLvl, 16) > parseInt(lvl, 16)) lvl = needLvl;
                    }
                    var codec = PROFILES[ci] + lvl;
                    try {
                        var cfg = {
                            codec: codec,
                            width: width, height: height,
                            bitrate: rungBps, framerate: rate,
                            avc: { format: 'avc' },
                            // latencyMode:'realtime' asks the encoder for no
                            // B-frames (lowest-latency rate control). It is only
                            // a HINT — if the encoder emits B-frames anyway the
                            // muxer handles them via compositionTimeOffset (see
                            // addVideoChunk). Kept as the cheap first line of
                            // defence; harmless when ignored.
                            latencyMode: (latency === 'quality') ? 'quality' : 'realtime'
                        };
                        if (hints[hi]) cfg.hardwareAcceleration = hints[hi];
                        if (bitrateMode) cfg.bitrateMode = bitrateMode;
                        // What the encoder WRITES is bitrate ÷ the told rate per frame, and
                        // on Windows bitrate × told must not reach 2³² (window.__encoderRate).
                        var rr = encoderRateFor(rungBps, framerate, rate, hints[hi]);
                        cfg.bitrate = rr.bitrate; cfg.framerate = rr.framerate;
                        var r = await VideoEncoder.isConfigSupported(cfg);
                        if (r && r.supported) {
                            return {
                                config: r.config || cfg,
                                hardware: hints[hi] === 'prefer-hardware',
                                hint: hints[hi] || 'no-hint',
                                bitrate: rungBps,
                                softwareGain: swRung ? +(rungBps / bitrate).toFixed(2) : 1,
                                codec: codec,
                                framerate: rr.framerate,
                                framerateClamped: rr.framerate !== Math.round(+framerate) ? Math.round(+framerate) : 0,
                                rateGuard: rr.wrapGuard || ''
                            };
                        }
                    } catch (_) {}
                }
            }
        }
    }
    }
    // No encoder took the asked rate mode: the ordinary pick (and its last resort).
    if (bitrateMode) return pickEncoderConfig(width, height, bitrate, framerate, latency, hintsOverride, framerateCap, profileOrder, null, softwareBps);
    var lastRate = rates[rates.length - 1];
    var minLvlLast = h264LevelHex(width, height, lastRate);
    // ── Last resort: attempt an UNVERIFIED config instead of surrendering ──
    // `isConfigSupported` answering `supported:false` for EVERY profile ×
    // level × hint is almost always a wedged GPU process, not a device that
    // truly cannot encode H.264 — and the probe result is only advisory.
    // Measured on prod (user 4126, AMD GCN-1 + Yandex 26): the SAME browser
    // encoded fine on 08-04 and rejected all profiles on 08-05; fleet-wide
    // every one of the 160 enc_preflight skips in 14 d carried this exact
    // reason. Trusting the probe cost those sessions the whole HW path —
    // a 90+ minute FFmpeg-WASM grind for a file the HW rig does in ~2 min
    // (and files over ~1 h cannot finish in WASM at all: 2 GiB heap).
    //
    // So: hand back a best-guess Main-profile config flagged `unverified`.
    // The caller's `encoder.configure()` is the authoritative check — a
    // genuinely broken encoder throws there within seconds and the normal
    // `encoder-configure-failed` → FFmpeg fallback contract applies. The
    // asymmetry is decisive: attempting costs seconds when truly broken,
    // trusting a lying probe costs the fast path entirely.
    //
    // ⚠️ The unverified guess uses the LOWEST framerate the cascade climbed
    // down to, not the caller's. Every honest answer above was "no", and the
    // one field we know encoders refuse outright is this one — guessing with
    // the value that was already rejected would only reproduce the failure.
    var lastResort = {
        /* При навязанном порядке «последняя попытка» обязана держаться ЕГО
           первой ступени: смысл навязывания в том, что чужой профиль выдачу
           не спасает, а обесценивает — файл примет энкодер и не примет
           магнитола. Без навязывания поведение прежнее: Main. */
        codec: (profileOrder && profileOrder.length ? profileOrder[0] : H264_ENCODER_PROFILES[1]) + minLvlLast,
        width: width, height: height,
        bitrate: bitrate, framerate: lastRate,
        avc: { format: 'avc' },
        latencyMode: (latency === 'quality') ? 'quality' : 'realtime'
    };
    // ⚠️ Досюда «последняя попытка» шла БЕЗ подсказки вовсе, и это делало
    // навязанный каскад фикцией: пересдача, пришпиленная к 'prefer-software',
    // на этой ветке снова отдавала выбор браузеру — то есть повторяла ровно
    // тот прогон, который только что упал. Если каскад навязан снаружи,
    // последняя попытка обязана держаться его первой ступени.
    if (hintsOverride && hintsOverride[0]) lastResort.hardwareAcceleration = hintsOverride[0];
    var lrSw = !!(hintsOverride && hintsOverride[0] === 'prefer-software' && +softwareBps > bitrate);
    if (lrSw) {
        lastResort.bitrate = +softwareBps;
        var lrLvl = h264LevelHex(width, height, lastRate, +softwareBps);
        if (parseInt(lrLvl, 16) > parseInt(minLvlLast, 16)) lastResort.codec = lastResort.codec.slice(0, -2) + lrLvl;
    }
    var lrBps = lastResort.bitrate;
    var lrRate = encoderRateFor(lrBps, framerate, lastRate, lastResort.hardwareAcceleration || null);
    lastResort.bitrate = lrRate.bitrate; lastResort.framerate = lrRate.framerate;
    return {
        config: lastResort, hardware: false,
        bitrate: lrBps, softwareGain: lrSw ? +(lrBps / bitrate).toFixed(2) : 1,
        hint: (hintsOverride && hintsOverride[0]) || 'no-hint',
        codec: lastResort.codec, unverified: true,
        framerate: lrRate.framerate,
        framerateClamped: lrRate.framerate !== Math.round(+framerate) ? Math.round(+framerate) : 0,
        rateGuard: lrRate.wrapGuard || ''
    };
}

// Sanitize the RFC-6381 codec string emitted by mp4box for AVC tracks.
// ───────────────────────────────────────────────────────────────────
// Some encoders (cheap phone cameras, surveillance DVRs, certain Yandex /
// VK / TikTok re-uploads) write an avcC box where `AVCLevelIndication` is
// below the H.264 spec floor of 0x0A (Level 1.0). mp4box surfaces that
// byte verbatim in the codec string → we end up passing
// `avc1.640005` / `avc1.640003` / `avc1.420002` etc. to
// `VideoDecoder.isConfigSupported`. EVERY browser (Chrome, Yandex, Edge,
// Firefox) returns `supported:false` on these strings even though the
// in-band SPS is decodable — the codec string is a hint, the real
// params are read from the bitstream. This silent rejection routed
// ~34 valid videos / 14d to FFmpeg-WASM fallback for no reason.
// Bug-report 2026-05-27 sid bf533dcf (Андрей Орлов, Yandex Android 10,
// avc1.640005) was the trigger.
//
// Fix: if the level byte is < 0x0A, substitute 0x33 (Level 5.1) — covers
// 4K@30fps headroom, accepted by every WebCodecs decoder. The decoder
// reads the real SPS from the bitstream, so the substitution is purely
// a hint-string repair and doesn't affect actual decoding behaviour.
// HEVC / AV1 / VP9 codec strings have entirely different formats; this
// function leaves them untouched.

// ─────────────────────────────────────────────────────────────────────
// _readSliceWithRetry — robust file.slice().arrayBuffer() wrapper
// ─────────────────────────────────────────────────────────────────────
//
// HISTORICAL CONTEXT
// ──────────────────
// Wave 2.B (2026-06-01) added on-demand retry-on-NotReadableError, waiting
// up to 60 s for `visibilitychange→visible` to recover backgrounded-tab
// URI revocations. Wave 13 (2026-06-03) added a module-local WeakMap
// pre-read cache (`_prereadCache`) populated at `run()` entry to make
// file-handle loss impossible for the ≤ 100 MB cohort. Wave 21
// (2026-06-19) moved that cache priming to file-pick time via a new
// public `WebCodecsPipeline.prefetchFileBytes` API plus a blade hook in
// `handleFile()` — to close feedback #759 (Android 10 Chrome 149,
// 1.2 MB mp4, sid 8b9ed46d-...) where the URI was revoked between pick
// and Submit because Wave 13 fired ~30 s too late.
//
// WAVE 22 (2026-06-19) — ARCHITECTURAL ETALON
// ───────────────────────────────────────────
// The codebase already ships a canonical pick-time file-bytes primitive:
// `window.FileBytes` (`public/js/file-buffer-cache.js`). It is
// auto-invoked by the shared `<x-tool-uploader>` component on every
// file pick for files ≤ 100 MB. ALL tools that use the uploader
// (including enhance-video) already hand FileBytes a copy of the bytes
// before the user even reaches the Submit button. FileBytes ALSO has
// richer logic than this module's WeakMap cache had:
//   - 2-attempt retry with 200/800 ms backoff on first prime failure
//   - Visibility-aware `whenVisible()` rescue for backgrounded tabs
//   - `_dead` Set so a permanently-revoked handle short-circuits
//     instead of dragging through arrayBuffer() N times per session
//   - LRU eviction with a shared memory budget
//   - Cache survives across modules (FFmpegProcessor reads the same
//     cache via `FileBytes.read`/`asBlob`)
//   - Stays correct for anonymous Blobs (per-instance WeakMap-keyed id),
//     closing the acx-mastering Stage-A/B cross-blob bleed
//
// Wave 13 + Wave 21's `_prereadCache` was a parallel universe that
// duplicated bytes, ignored FileBytes' retry intelligence, and split
// the source-of-truth across two caches. Wave 22 deletes the duplicate
// and routes through `FileBytes.read` for the size band where
// FileBytes is guaranteed to be populated. For > 100 MB files (where
// the shared uploader deliberately skips priming), the legacy
// retry-after-visibility path is preserved.
async function _readSliceWithRetry(file, start, end) {
    // Wave 22 etalon fast path: the shared <x-tool-uploader> already
    // primes FileBytes at file-pick time. Cache hit → slice from RAM,
    // immune to any later URI revocation by the OS.
    //
    // ⚠️ ONLY when the bytes are already in the RAM tier (hasCached — a
    // non-reading peek). Until 2026-09-17 this called FileBytes.read()
    // unconditionally, and for a file ABOVE the RAM cap read() answers the
    // WHOLE file, uncached — from the OPFS spill (`filebytes_spill_hit`) or a
    // live whole-file read. The eager demux calls this once per 4 MB chunk,
    // so a 400 MB pick cost 100 × 400 MB = 40 GB of reads and a hundred
    // 400 MB allocations before the first frame was decoded; a 1.6 GB pick
    // would cost 660 GB. RU /video-crop 30 d: filebytes_spill_hit 4034 events
    // over 87 sessions — 46 whole-file reads per session — and the ≥512 MB
    // exports averaging 388 s against 51 s below 256 MB. The lazy demux reads
    // through this same door in 8 MB windows, so the door must be O(window).
    if (window.FileBytes && typeof window.FileBytes.read === 'function'
        && typeof window.FileBytes.hasCached === 'function' && window.FileBytes.hasCached(file)) {
        try {
            var allBytes = await window.FileBytes.read(file);
            // `allBytes` is a fresh Uint8Array view over its own buffer
            // (FileBytes.read returns `.slice()`-copies so transfer-list
            // detachment by one consumer can't break others). Slice the
            // underlying ArrayBuffer at the requested range.
            return allBytes.buffer.slice(start, end);
        } catch (fbErr) {
            // A RAM-tier entry that fails to read (flagged _dead) falls
            // through to the live slice below, which has its own
            // visibility-aware recovery.
        }
    }
    // Direct-slice path with visibility-aware retry: the live File for
    // anything not in the RAM tier — a slice is O(range) whatever the file
    // size, and a durable copy (OPFS spill) exists only as the rescue below.
    try {
        return await file.slice(start, end).arrayBuffer();
    } catch (err) {
        if (!err || err.name !== 'NotReadableError') throw err;
        // The OS revoked the handle (iOS Photos-picker transcode, Android
        // after backgrounding). The uploader's pick-time tier — RAM prime or
        // OPFS spill — is the insurance for exactly this; settle() hands it
        // back without starting a read of its own.
        try {
            if (window.FileBytes && typeof window.FileBytes.settle === 'function') {
                var durable = await window.FileBytes.settle(file);
                if (durable && durable !== file) {
                    var rb = await durable.slice(start, end).arrayBuffer();
                    try { if (typeof window.__ae === 'function') window.__ae('feature_use', { slug: (window.__currentSlug || 'webcodecs'), action: 'hw_pipeline_slice_rescued_from_tier', range: end - start, offset: start }); } catch (_) {}
                    return rb;
                }
            }
        } catch (_) { /* no tier, or the tier failed too — the visibility wait below is the last resort */ }
        try {
            if (typeof window.__ae === 'function') {
                window.__ae('feature_use', {
                    slug: (window.__currentSlug || 'webcodecs'),
                    action: 'hw_pipeline_slice_retry_attempt',
                    range: end - start,
                    offset: start,
                    bg: document.visibilityState === 'hidden' ? 1 : 0
                });
            }
        } catch (_) {}
        if (document.visibilityState !== 'visible') {
            await new Promise(function(resolve) {
                var done = false, to = null;
                function settle() { if (done) return; done = true; clearTimeout(to); document.removeEventListener('visibilitychange', onVis); resolve(); }
                function onVis() { if (document.visibilityState === 'visible') settle(); }
                to = setTimeout(settle, 60000);
                document.addEventListener('visibilitychange', onVis);
            });
        }
        var buf = await file.slice(start, end).arrayBuffer();
        try {
            if (typeof window.__ae === 'function') {
                window.__ae('feature_use', {
                    slug: (window.__currentSlug || 'webcodecs'),
                    action: 'hw_pipeline_slice_retry_ok',
                    range: end - start
                });
            }
        } catch (_) {}
        return buf;
    }
}

// ─────────────────────────────────────────────────────────────────────
// AVCC SAMPLE NAL SANITIZER — Wave 23 (2026-07-08)
// ─────────────────────────────────────────────────────────────────────
// Strips NAL units that Android MediaCodec chokes on in AVCC mode:
//   - SEI (type 6): x264/re-encoder user-data (UUID dc45e9bd… dominates
//     prod chunk0 hex). Never needed for pixel decode.
//   - SPS (7) / PPS (8) — ONLY when byte-identical to a parameter set
//     already present in the avcC `description`. In-band duplicates are
//     redundant (decoder is configured from avcC) and are exactly what
//     the MediaCodec AVCC parser rejects. NON-identical in-band SPS/PPS
//     (legal mid-stream parameter switch, e.g. concatenated sources)
//     are KEPT — removing those would break decode.
//   - AUD (9): access-unit delimiters, pure structure, safe to drop.
// Returns the original buffer when nothing was stripped (zero-copy hot
// path) and never returns an empty sample (safety: if stripping would
// leave 0 bytes, the original is returned untouched).
//
// Root telemetry: 808 "Unsupported configuration" decoder errors with
// decoded_so_far=0 across 660 sessions / 30 d, 99.5 % Android
// (Chrome 419 + Yandex 385). Feedback #948 (2026-07-08) hit this: HW
// path died on chunk 0 → 16-minute FFmpeg-WASM fallback on a 30 MB
// phone clip that the HW path would have finished in ~2 min.
function _bytesIndexOf(haystack, needle) {
    if (!needle.length || needle.length > haystack.length) return -1;
    var first = needle[0];
    for (var i = 0, max = haystack.length - needle.length; i <= max; i++) {
        if (haystack[i] !== first) continue;
        var hit = true;
        for (var j = 1; j < needle.length; j++) {
            if (haystack[i + j] !== needle[j]) { hit = false; break; }
        }
        if (hit) return i;
    }
    return -1;
}
function _sanitizeAvccSample(data, avcC, stats) {
    if (!data || data.length < 5) return data;
    var d = data instanceof Uint8Array ? data : new Uint8Array(data);
    var keep = [];        // [start, end) byte ranges to keep
    var dropped = 0, pos = 0, len = d.length;
    while (pos + 4 <= len) {
        var nalLen = (d[pos] << 24) | (d[pos + 1] << 16) | (d[pos + 2] << 8) | d[pos + 3];
        if (nalLen <= 0 || pos + 4 + nalLen > len) {
            // Malformed length — bail out, return original untouched.
            return data;
        }
        var nalType = d[pos + 4] & 0x1f;
        var drop = false;
        if (nalType === 6 || nalType === 9) {
            drop = true;
            if (stats) stats.sei++;
        } else if (nalType === 7 || nalType === 8) {
            // Drop only when this exact NAL already lives inside avcC.
            var nal = d.subarray(pos + 4, pos + 4 + nalLen);
            if (avcC && _bytesIndexOf(avcC, nal) !== -1) {
                drop = true;
                if (stats) stats.ps++;
            }
        }
        if (!drop) keep.push(pos, pos + 4 + nalLen);
        else dropped++;
        pos += 4 + nalLen;
    }
    if (!dropped || !keep.length) return data;    // nothing stripped OR would empty the sample
    var outLen = 0;
    for (var k = 0; k < keep.length; k += 2) outLen += keep[k + 1] - keep[k];
    var out = new Uint8Array(outLen);
    var o = 0;
    for (var m = 0; m < keep.length; m += 2) {
        out.set(d.subarray(keep[m], keep[m + 1]), o);
        o += keep[m + 1] - keep[m];
    }
    if (stats) stats.samples++;
    return out;
}

function sanitizeAvcLevel(codec) {
    if (!codec || typeof codec !== 'string') return codec;
    // avc1 (in-band SPS/PPS) and avc3 (out-of-band) share the same
    // `<family>.<profile><constraint><level>` shape — six hex digits.
    var m = codec.match(/^(avc[13])\.([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
    if (!m) return codec;
    var levelByte = parseInt(m[4], 16);
    if (!(levelByte < 0x0A)) return codec;             // already valid
    return m[1] + '.' + m[2] + m[3] + '33';            // ←  0x33 = Level 5.1
}

// Probe decoder support for the source codec. Accepts a full RFC-6381
// codec string (e.g. "hev1.1.6.L93.B0") or a short alias (e.g. "hevc")
// which expands via DECODER_CODEC_EXPANSIONS. Returns { codec: string }
// for the first supported variant or null.
async function pickDecoderConfig(codecHint) {
    if (!codecHint) return null;
    // Repair non-spec AVC level bytes before probing (see sanitizeAvcLevel).
    var saneHint = sanitizeAvcLevel(codecHint);
    var candidates;
    var lower = String(saneHint).toLowerCase().trim();
    if (DECODER_CODEC_EXPANSIONS[lower]) {
        candidates = DECODER_CODEC_EXPANSIONS[lower];
    } else {
        // Already a full codec string — try it directly, then fall back to
        // its family alias if known.
        candidates = [saneHint];
        var prefix = lower.replace(/^([a-z0-9]+).*$/, '$1');
        if (DECODER_CODEC_EXPANSIONS[prefix] && prefix !== lower) {
            candidates = candidates.concat(DECODER_CODEC_EXPANSIONS[prefix]);
        }
    }
    for (var i = 0; i < candidates.length; i++) {
        try {
            var r = await VideoDecoder.isConfigSupported({ codec: candidates[i] });
            if (r && r.supported) return { codec: candidates[i] };
        } catch (_) {}
    }
    return null;
}

// Real WebGL2 probe with retry + granular failure reasons. Replaces the
// earlier one-shot `canvas.getContext('webgl2')` check which dropped users
// to FFmpeg WASM whenever the GPU process briefly refused (background tab,
// driver hiccup, hardware-accel temporarily disabled). Telemetry of the
// `no-webgl2` cluster (5 sessions/24h on Yandex 24/26 + Samsung Internet
// 29 — all browsers that DO support WebGL2) showed almost every "skip"
// was a transient context-creation failure, not a missing browser feature.
//
// Three failure classes now distinguished so we can act on them differently:
//   'no-webgl2'         — confirmed missing after 3 attempts (retry+wait)
//   'webgl2-disabled'   — WebGL1 works but WebGL2 doesn't (hardware-accel
//                          partially blocked, browser policy, or driver
//                          blacklist of WebGL2 specifically)
//   'webgl2-shader-fail' — context created, but ESSL 3.00 vertex/fragment
//                          shader compile rejected (rare driver bug)
//
// Wrapped in window.__capabilityGate so the answer is cached per page-load.
// Probe runs on a 4×4 canvas (cheapest possible context) and tries the
// exact shader prologue our filter rig uses — so a green probe means the
// real rig will compile too.
async function probeWebGL2() {
    var canvas = document.createElement('canvas');
    canvas.width = 4; canvas.height = 4;

    // Retry up to 3 attempts with 100 ms backoff. Closes the
    // GPU-process-race / background-tab class of false negatives.
    var gl = null;
    for (var attempt = 0; attempt < 3; attempt++) {
        try { gl = canvas.getContext('webgl2', { failIfMajorPerformanceCaveat: false }); } catch (_) {}
        if (gl) break;
        if (attempt < 2) await new Promise(function(r){ setTimeout(r, 100); });
    }
    if (!gl) {
        // Differentiate "WebGL2 specifically blocked" vs "no GPU at all".
        // If WebGL1 works, the GPU is alive — WebGL2 was singled out.
        var gl1 = null;
        try { gl1 = canvas.getContext('webgl'); } catch (_) {}
        return { ok: false, reason: gl1 ? 'webgl2-disabled' : 'no-webgl2' };
    }
    // Probe shader compile — the real rig uses #version 300 es with
    // highp + texture() + out vec4 fragColor. If any of that fails
    // here, the rig will fail later — surface it now as a typed reason.
    try {
        var vs = gl.createShader(gl.VERTEX_SHADER);
        gl.shaderSource(vs, '#version 300 es\nin vec2 a;void main(){gl_Position=vec4(a,0.0,1.0);}');
        gl.compileShader(vs);
        if (!gl.getShaderParameter(vs, gl.COMPILE_STATUS)) {
            return { ok: false, reason: 'webgl2-shader-fail:vertex' };
        }
        var fs = gl.createShader(gl.FRAGMENT_SHADER);
        gl.shaderSource(fs, '#version 300 es\nprecision highp float;out vec4 c;void main(){c=vec4(1.0);}');
        gl.compileShader(fs);
        if (!gl.getShaderParameter(fs, gl.COMPILE_STATUS)) {
            return { ok: false, reason: 'webgl2-shader-fail:fragment' };
        }
    } catch (e) {
        return { ok: false, reason: 'webgl2-probe-throw:' + ((e && e.message) || '').slice(0, 40) };
    }
    return { ok: true };
}

// Page-load-cached version of probeWebGL2 — runs the probe at most once
// even if isSupported() is called many times during a session. We use a
// custom cache instead of __capabilityGate because that primitive expects
// the probe to return a boolean (it does !!passed internally), and our
// probe needs to surface a *typed reason* on failure (no-webgl2 vs
// webgl2-disabled vs webgl2-shader-fail) so telemetry can categorise.
var _webgl2ProbeCache = null;
function gatedWebGL2() {
    if (_webgl2ProbeCache) return _webgl2ProbeCache;
    _webgl2ProbeCache = (async function () {
        try {
            return await probeWebGL2();
        } catch (e) {
            return { ok: false, reason: 'webgl2-probe-throw:' + ((e && e.message) || '').slice(0, 40) };
        }
    })();
    return _webgl2ProbeCache;
}

async function isSupported(opts) {
    opts = opts || {};
    var out = { ok: false, reason: '' };
    if (typeof VideoDecoder === 'undefined' || typeof VideoEncoder === 'undefined' ||
        typeof VideoFrame === 'undefined' || typeof EncodedVideoChunk === 'undefined') {
        out.reason = 'no-webcodecs';
        return out;
    }
    // geometry2d consumers (video-crop) render via 2D canvas, not the WebGL
    // rig — a blocked/blacklisted WebGL2 must not disqualify them.
    if (!opts.skipWebGL) {
        var glProbe = await gatedWebGL2();
        if (!glProbe.ok) { out.reason = glProbe.reason; return out; }
    }

    var inCodec = opts.inputCodec || 'avc1.640028';
    var dPick;
    try { dPick = await pickDecoderConfig(inCodec); }
    catch (e) { out.reason = 'decoder-check-error'; return out; }
    if (!dPick) { out.reason = 'decoder-rejects-' + inCodec; return out; }

    // Probe the encoder at the size the CALLER will actually ask for. The
    // hardcoded 1280x720 here answered a question nobody asked: an encoder's
    // verdict is resolution-dependent (measured on this machine — 5120x1440
    // accepted, 7680x2160 refused), and hardware limits differ wildly, with
    // plenty of mobile GPUs stopping at 4096 across. A caller whose output is
    // ultra-wide — /video-to-vr stacks two eyes side by side, up to 5430 px —
    // therefore passed a 720p preflight and then died in `configure()`, AFTER
    // mp4box had demuxed the whole file. Seen in production 2026-08-19:
    // `hw_lane_failed: encoder-failed: Encoder creation error`.
    // Defaults keep every existing caller on exactly the old probe.
    // ⚠️ `Math.max(2, opts.width|0) || 1280` is NOT this: it yields 2 when the
    // caller passes nothing (2 is truthy, so the || never fires), which probes
    // a 2x2 encode and comes back unverified. Every existing caller omits the
    // size, so that spelling would have quietly turned their preflight into
    // noise. Caught by measuring the default path, not by reading it.
    var pw = (opts.width  | 0) >= 2 ? (opts.width  | 0) : 1280;
    var ph = (opts.height | 0) >= 2 ? (opts.height | 0) : 720;
    var ePick = await pickEncoderConfig(pw, ph, 4_000_000, 30);
    if (!ePick) { out.reason = 'encoder-rejects-' + pw + 'x' + ph; return out; }

    out.ok = true;
    // pickEncoderConfig no longer surrenders on an all-profiles probe
    // rejection — it returns an `unverified` best-guess config and lets
    // encoder.configure() be the authoritative check (see its comment).
    // Surface that state so consumers can telemeter the attempt while
    // still going ahead with the run.
    out.encoderUnverified = !!ePick.unverified;
    if (ePick.unverified) out.reason = 'encoder-preflight-unverified';
    out.encoderHardware = ePick.hardware;
    out.encoderCodec = ePick.codec;
    out.decoderCodec = dPick.codec;
    return out;
}

// ─────────────────────────────────────────────────────────────────────
// WEBGL FILTER PIPELINE
// ─────────────────────────────────────────────────────────────────────

// Pass 1 — pixel-local: EQ + curves + warmth + vignette + grain. All operate
// on a single texel; one fragment shader, no neighbour reads.
//
// HDR sources reach the rig already converted to SDR: uploadFrameTexture
// takes every VideoFrame / <video> through the HDR → SDR door
// (hdr-to-sdr.js, docs/hdr-to-sdr.md). The Hable pass that used to live here
// (2026-05-08) assumed the texture held linear light past 1.0; the upload
// actually hands over the 8-bit CODED HLG/PQ signal, and Hable on it darkened
// every iPhone HDR export by ~40 % (comment #1968: «стало темнее»).
var FRAG_PIXEL_LOCAL = [
'#version 300 es',
'precision highp float;',
'in vec2 v_uv;',
'out vec4 fragColor;',
'uniform sampler2D u_tex;',
'uniform float u_brightness;   // -0.5..0.5',
'uniform float u_contrast;     // 0.5..2.0',
'uniform float u_saturation;   // 0.0..3.0',
'uniform float u_gamma;        // 0.1..3.0',
'uniform float u_highlights;   // -1..1',
'uniform float u_shadows;      // -1..1',
'uniform float u_warmth;       // -1..1',
'uniform float u_vignette;     // 0..1',
'uniform float u_grain;        // 0..1',
'uniform float u_seed;         // per-frame random for grain',
// Standard luma weights (Rec.709)
'const vec3 LUMA = vec3(0.2126, 0.7152, 0.0722);',

'float rand(vec2 co){ return fract(sin(dot(co, vec2(12.9898, 78.233)) + u_seed) * 43758.5453); }',

'void main() {',
'  vec3 c = texture(u_tex, v_uv).rgb;',
// brightness — additive shift
'  c += u_brightness;',
// contrast — pivot around 0.5
'  c = (c - 0.5) * u_contrast + 0.5;',
// saturation — mix toward grayscale
'  float l = dot(c, LUMA);',
'  c = mix(vec3(l), c, u_saturation);',
// gamma
'  c = pow(max(c, 0.0), vec3(1.0 / u_gamma));',
// highlights — gain in bright regions (smooth weight by luma)
'  float wHi = smoothstep(0.5, 1.0, l);',
'  c = c * (1.0 + u_highlights * wHi * 0.4);',
// shadows — lift dark regions
'  float wLo = 1.0 - smoothstep(0.0, 0.5, l);',
'  c = c + u_shadows * wLo * 0.3;',
// warmth — push red+, blue- when warm; reverse when cool
'  c.r += u_warmth * 0.12;',
'  c.b -= u_warmth * 0.12;',
// vignette — radial darkening
'  vec2 d = v_uv - 0.5;',
'  float r = dot(d, d) * 4.0;            // r=1 at corners',
'  c *= 1.0 - u_vignette * smoothstep(0.4, 1.4, r);',
// grain — additive luma noise
'  if (u_grain > 0.001) {',
'    float n = (rand(v_uv) - 0.5) * u_grain * 0.15;',
'    c += vec3(n);',
'  }',
'  fragColor = vec4(clamp(c, 0.0, 1.0), 1.0);',
'}'
].join('\n');

// Pass 1.5 — frame BLEND: linear interpolation between two textures.
// Output = mix(texA, texB, u_t). For frame interpolation Fast tier: t=0.5
// gives motion-blur-style midpoint, varying t produces sub-frame timesteps.
// This is the cheap interpolation path — quality is "FFmpeg minterpolate
// mi_mode=blend" equivalent. AI tier delegates to a JS callback instead.
var FRAG_BLEND = [
'#version 300 es',
'precision highp float;',
'in vec2 v_uv;',
'out vec4 fragColor;',
'uniform sampler2D u_texA;',
'uniform sampler2D u_texB;',
'uniform float u_t;            // 0..1',
'void main() {',
'  vec3 a = texture(u_texA, v_uv).rgb;',
'  vec3 b = texture(u_texB, v_uv).rgb;',
'  fragColor = vec4(mix(a, b, u_t), 1.0);',
'}'
].join('\n');

// Pass 2 — convolution: AMD CAS sharpening + bilateral denoise.
//
// Sharpening: AMD FidelityFX CAS — public-domain edge-aware filter used
// in many game engines (Borderlands 3, Death Stranding). Single 3×3 read,
// no halos (clamps to local min/max), stronger on detail than flat regions.
// Reference: https://gpuopen.com/fidelityfx-cas/
//
// Denoise: 3×3 bilateral instead of plain box-blur gaussian. Each sample
// weight = spatial × range, where range falls off with color distance from
// center. Pixels across a hard edge get ~0 weight, so denoise smooths
// noise inside flat patches without softening textures or blurring edges.
// On grainy / film-looking sources the difference is dramatic — gaussian
// melts skin / hair detail; bilateral keeps it.
var FRAG_CONVOLUTION = [
'#version 300 es',
'precision highp float;',
'in vec2 v_uv;',
'out vec4 fragColor;',
'uniform sampler2D u_tex;',
'uniform vec2 u_texelSize;     // (1/width, 1/height)',
'uniform float u_unsharpAmt;   // 0..0.6  (CAS sharpness)',
'uniform float u_denoiseAmt;   // 0..0.8  (spatial denoise mix — see mapParams)',
'uniform sampler2D u_prevTex;  // previous OUTPUT frame (temporal denoise)',
'uniform float u_temporalK;    // history cap; 0 = off (stills / frame 1 / no denoise)',

// BT.601 full-range YCbCr — the luma/chroma split below needs it.
'vec3 rgb2ycc(vec3 c) {',
'  return vec3(dot(c, vec3(0.299, 0.587, 0.114)),',
'              dot(c, vec3(-0.169, -0.331, 0.5)),',
'              dot(c, vec3(0.5, -0.419, -0.081)));',
'}',
'vec3 ycc2rgb(vec3 y) {',
'  return vec3(y.x + 1.403 * y.z,',
'              y.x - 0.344 * y.y - 0.714 * y.z,',
'              y.x + 1.773 * y.y);',
'}',

// (denoise moved to its own pass — see FRAG_DENOISE below; 2026-07-30)
//
// This shader now reads an ALREADY-DENOISED texture (drawFiltered routes
// pass1 → [denoise pass →] this pass), so every tap is one flat fetch.
// The previous single-pass design read each tap through an inline
// denoise chain; after the two-scale bilateral upgrade that ballooned to
// ~250 fetches/px (measured 3.3× slower on the SwiftShader proxy) — the
// split brings it back to ~33/px total across both passes.

// Gaussian-ish 3×3 for the USM boost.
'vec3 blur3(vec2 uv) {',
'  vec3 acc = texture(u_tex, uv).rgb * 0.25;',
'  acc += texture(u_tex, uv + vec2( u_texelSize.x, 0.0)).rgb * 0.125;',
'  acc += texture(u_tex, uv + vec2(-u_texelSize.x, 0.0)).rgb * 0.125;',
'  acc += texture(u_tex, uv + vec2(0.0,  u_texelSize.y)).rgb * 0.125;',
'  acc += texture(u_tex, uv + vec2(0.0, -u_texelSize.y)).rgb * 0.125;',
'  acc += texture(u_tex, uv + vec2( u_texelSize.x,  u_texelSize.y)).rgb * 0.0625;',
'  acc += texture(u_tex, uv + vec2(-u_texelSize.x,  u_texelSize.y)).rgb * 0.0625;',
'  acc += texture(u_tex, uv + vec2( u_texelSize.x, -u_texelSize.y)).rgb * 0.0625;',
'  acc += texture(u_tex, uv + vec2(-u_texelSize.x, -u_texelSize.y)).rgb * 0.0625;',
'  return acc;',
'}',

// CAS sharpening — operates on the cross-pattern (NSEW neighbours).
'vec3 cas(vec2 uv, float amt) {',
// Sample cross neighbours and the center
'  vec3 a = texture(u_tex, uv + vec2(0.0,            -u_texelSize.y)).rgb;',  // N
'  vec3 b = texture(u_tex, uv + vec2(-u_texelSize.x,  0.0)).rgb;',            // W
'  vec3 c = texture(u_tex, uv).rgb;',                                          // center
'  vec3 d = texture(u_tex, uv + vec2( u_texelSize.x,  0.0)).rgb;',            // E
'  vec3 e = texture(u_tex, uv + vec2(0.0,             u_texelSize.y)).rgb;',  // S
// Local min/max for clamping (no ringing)
'  vec3 mnRGB = min(c, min(min(a, b), min(d, e)));',
'  vec3 mxRGB = max(c, max(max(a, b), max(d, e)));',
// Compute "amplification" factor: in flat regions (small range) → small;
// near edges → larger sharpening. AMD\'s formula:
'  vec3 rcpMRGB = vec3(1.0) / mxRGB;',
'  vec3 ampRGB = clamp(min(mnRGB, vec3(1.0) - mxRGB) * rcpMRGB, 0.0, 1.0);',
'  ampRGB = sqrt(ampRGB);',
// Sharpening weight. Pre-2026-07-30 this was -mix(0.125, 0.20, amt): the
// whole slider only spanned peak -0.125..-0.17 (a 1.4× swing, never off) —
// one of the two reasons slider-100 measured a bare +2.4% acutance
// (comment #1231). Widened to a true 0→strong range; measured +3.7%
// acutance on top of the USM boost, no ringing on the clean-edge crop.
'  float peak = -mix(0.02, 0.22, amt);',
'  vec3 wRGB = ampRGB * peak;',
'  vec3 rcpWeightRGB = vec3(1.0) / (1.0 + 4.0 * wRGB);',
// Final: weighted sum, clamped to local min/max so no overshoot
'  vec3 outColor = (wRGB * (a + b + d + e) + c) * rcpWeightRGB;',
'  return clamp(outColor, mnRGB, mxRGB);',
'}',

'void main() {',
'  vec3 base = texture(u_tex, v_uv).rgb;',
// Sharpen: CAS base + USM boost (2026-07-30, comment #1231 «резкость
// практически не делает» — and he was right, MEASURED: slider 100 gave
// +2.4% acutance on the retro class, +8% on real textures, because CAS
// clamps to the local min/max and its old peak only spanned -0.125..-0.17
// over the whole slider). CAS stays as the halo-free base; an unsharp
// component on top restores a real dynamic range: slider 100 → +49%
// acutance on the reporter's class, +16% at the auto level (55), zero
// visible ringing on a clean synthetic edge (crop-verified; 0.9 gain
// stays below ring onset). The input texture is already denoised, so
// USM amplifies cleaned detail, not raw grain.
// amtEff un-compresses mapParams' 0..0.6 cap so the slider spans 0..1.
'  float amtEff = min(1.0, u_unsharpAmt / 0.6);',
'  vec3 final = base;',
'  if (u_unsharpAmt > 0.001) {',
'    final = cas(v_uv, amtEff);',
'    vec3 usm = final - blur3(v_uv);',
'    final += amtEff * 0.9 * usm;',
'  }',
// Temporal denoise — blend toward the previous OUTPUT frame, gated by pixel
// difference so motion opts out (same range-kernel form as the bilateral;
// sigma 0.05 measured best on moving content at every noise level). Noise is
// uncorrelated across frames, so this is the strongest lever a video denoiser
// has; it is also why the denoise gain in a single-frame preview is an honest
// LOWER bound of the export. u_temporalK is 0 for stills, for frame 1, and
// whenever denoise is off — in those cases this compiles to nothing.
'  if (u_temporalK > 0.001) {',
'    vec3 prev = texture(u_prevTex, v_uv).rgb;',
'    vec3 dv = final - prev;',
'    float wT = exp(-dot(dv, dv) / (2.0 * 0.05 * 0.05)) * u_temporalK;',
'    final = mix(final, prev, wT);',
'  }',
'  fragColor = vec4(clamp(final, 0.0, 1.0), 1.0);',
'}'
].join('\n');

// Pass 2a — DENOISE (own pass since 2026-07-30, comment #1231 «шумы не
// удаляет»): a single 3×3 bilateral ring cannot see grain coarser than a
// pixel pair — on the retro/VHS class (the reporter restored 1985 concert
// clips) the measured removal was σ −3.3% at slider 100, i.e.
// imperceptible. Two-scale à-trous (same 3×3 taps at dilation 2) + a
// stronger blend curve took it to σ −9.3% on the same fixture with
// flat-area grain visibly smoothed and textures kept (crops eyeballed).
// Temporal denoise in the conv pass adds on top — the still figure is the
// lower bound. Runs render-to-texture BEFORE the conv pass so sharpening
// taps read cleaned pixels with flat fetches (perf note above).
//
// The blend is split by channel in YCbCr: chroma noise is the visually
// ugly one and chroma detail the expendable one, so chroma gets a larger
// blend than luma (heritage of the measured mixY/mixC grid search).
var FRAG_DENOISE = [
'#version 300 es',
'precision highp float;',
'in vec2 v_uv;',
'out vec4 fragColor;',
'uniform sampler2D u_tex;',
'uniform vec2 u_texelSize;',
'uniform float u_denoiseAmt;   // 0..0.8 (see mapParams)',
'vec3 rgb2ycc(vec3 c) {',
'  return vec3(dot(c, vec3(0.299, 0.587, 0.114)),',
'              dot(c, vec3(-0.169, -0.331, 0.5)),',
'              dot(c, vec3(0.5, -0.419, -0.081)));',
'}',
'vec3 ycc2rgb(vec3 y) {',
'  return vec3(y.x + 1.403 * y.z,',
'              y.x - 0.344 * y.y - 0.714 * y.z,',
'              y.x + 1.773 * y.y);',
'}',
// 3×3 bilateral ring at dilation d (d=1 fine ring, d=2 à-trous ring).
// Spatial kernel = 1/0.6/0.4 gaussian-ish; range kernel falls off with
// color distance from center so hard edges keep ~0 weight.
'vec3 bilateralRing(vec2 uv, float amt, float d, float sigmaR) {',
'  vec3 c = texture(u_tex, uv).rgb;',
'  float invTwoSigmaR2 = 1.0 / (2.0 * sigmaR * sigmaR);',
'  vec3 sum = c;',
'  float wTotal = 1.0;',
'  for (int dy = -1; dy <= 1; dy++) {',
'    for (int dx = -1; dx <= 1; dx++) {',
'      if (dx == 0 && dy == 0) continue;',
'      vec3 s = texture(u_tex, uv + vec2(float(dx), float(dy)) * u_texelSize * d).rgb;',
'      vec3 diff = s - c;',
'      float wR = exp(-dot(diff, diff) * invTwoSigmaR2);',
'      float wS = (dx * dy == 0) ? 0.6 : 0.4;',
'      float w = wR * wS;',
'      sum += s * w;',
'      wTotal += w;',
'    }',
'  }',
'  return sum / wTotal;',
'}',
'void main() {',
'  vec3 c = texture(u_tex, v_uv).rgb;',
'  vec3 b  = bilateralRing(v_uv, u_denoiseAmt, 1.0, mix(0.05, 0.30, u_denoiseAmt));',
'  vec3 b2 = bilateralRing(v_uv, u_denoiseAmt, 2.0, mix(0.04, 0.14, u_denoiseAmt));',
'  b = mix(b, b2, 0.5);',
'  float mixY = min(1.0, 0.85 * u_denoiseAmt + 0.1);',
'  float mixC = min(0.95, 1.7 * u_denoiseAmt + 0.1);',
'  vec3 yc = rgb2ycc(c);',
'  vec3 yb = rgb2ycc(b);',
'  vec3 outc = ycc2rgb(vec3(mix(yc.x, yb.x, mixY), mix(yc.yz, yb.yz, vec2(mixC))));',
'  fragColor = vec4(clamp(outc, 0.0, 1.0), 1.0);',
'}'
].join('\n');

// Pass R — ENLARGE: separable Lanczos-3 with anti-ringing, run twice
// (u_dir (1,0) into texHalf, then (0,1) onto the canvas).
//
// Until 2026-10-20 a larger output frame was made by pass 1 sampling the
// source texture LINEARLY at the output size — bilinear, the softest
// resampler there is — and the CAS/USM sharpening then ran on the stretched
// picture, one OUTPUT pixel wide, where an enlarged frame has almost nothing
// left to sharpen. The /enhance-video bug report of 18.10 (720p → 1080p,
// «какое качество и разрешение было, таким и осталось») was that path doing
// exactly what it was built to do. Measured through this very rig on
// lossless references (Xiph: Sintel, Elephants Dream, crowd, pedestrians,
// old town, a screen recording; sources shrunk and x264-compressed the way
// people's files are — 0.045–0.08 bit/px), VMAF / VMAF NEG against the true
// high-resolution frame, default sharpness 30:
//                     bilinear+sharpen   sharpen at source + this pass
//    720 → 1080         62.9 / 61.0            68.6 / 65.2
//    480 → 1080         46.0 / 44.6            55.1 / 51.7
//    360 →  720         52.1 / 50.4            61.7 / 58.2
//    screen 480 → 1080  54.5 / 52.4            66.8 / 61.8
// The old path scored BELOW a plain Lanczos stretch with no sharpening at all
// (64.9 at 720 → 1080). Bicubic lands ~1 point under Lanczos-3; the clamp to
// the two nearest source texels costs ≤0.4 VMAF and keeps dark halos off text
// and hard CG edges (crops checked). Downscaling keeps the linear sample: at
// 1080 → 720 / 480 it scores 99.6 against 99.9 for a proper filter.
var FRAG_RESAMPLE = [
'#version 300 es',
'precision highp float;',
'in vec2 v_uv;',
'out vec4 fragColor;',
'uniform sampler2D u_tex;',
'uniform vec2 u_srcSize;       // u_tex in texels',
'uniform vec2 u_dir;           // (1,0) horizontal pass, (0,1) vertical pass',
'float lanczos3(float x) {',
'  x = abs(x);',
'  if (x < 1e-5) return 1.0;',
'  if (x >= 3.0) return 0.0;',
'  float px = 3.14159265 * x;',
'  return 3.0 * sin(px) * sin(px / 3.0) / (px * px);',
'}',
'void main() {',
'  ivec2 sz = ivec2(u_srcSize);',
// The other axis is 1:1 between input and output, so its texel is exact.
'  ivec2 q = ivec2(floor(v_uv * u_srcSize));',
'  float t = dot(v_uv * u_srcSize - 0.5, u_dir);',
'  float b = floor(t);',
'  float f = t - b;',
'  vec3 acc = vec3(0.0);',
'  float ws = 0.0;',
'  vec3 lo = vec3(1.0);',
'  vec3 hi = vec3(0.0);',
'  for (int i = -2; i <= 3; i++) {',
'    float w = lanczos3(float(i) - f);',
'    if (u_dir.x > 0.5) q.x = clamp(int(b) + i, 0, sz.x - 1);',
'    else               q.y = clamp(int(b) + i, 0, sz.y - 1);',
'    vec3 c = texelFetch(u_tex, q, 0).rgb;',
'    acc += c * w;',
'    ws += w;',
'    if (i == 0 || i == 1) { lo = min(lo, c); hi = max(hi, c); }',
'  }',
'  fragColor = vec4(clamp(acc / ws, lo, hi), 1.0);',
'}'
].join('\n');

// Pass S — STYLIZE: a self-contained single-pass artistic-filter shader for
// the /video-filter tool (B&W, sepia, vintage, film stocks, VHS/retro).
//
// Deliberately separate from FRAG_PIXEL_LOCAL: this path is selected INSTEAD
// of the pixel+convolution passes when run() is given `opts.stylize`. When
// `opts.stylize` is absent the program is never even compiled, so
// enhance-video / video-fps are byte-for-byte unaffected.
//
// Everything here is single-texel or small-offset (3-tap chroma split, line
// jitter) — no kernels — so one fragment shader covers the whole filter:
//   fine-tune EQ → 3×4 colour matrix → duotone → scanlines →
//   flicker → vignette → grain/noise.
// Filter INTENSITY is folded in by the caller (the colour matrix is lerped
// toward identity, retro amounts pre-scaled) — there is no global mix uniform.
var FRAG_STYLIZE = [
'#version 300 es',
'precision highp float;',
// ⚠️ У sampler3D в GLSL ES 3.00 НЕТ точности по умолчанию — в отличие от
// sampler2D. Без этой строки шейдер не компилируется ЦЕЛИКОМ, и
// createStylizeRenderer возвращает null, то есть тул выглядит как «нет WebGL2»
// на машине, где WebGL2 прекрасно есть.
'precision highp sampler3D;',
'in vec2 v_uv;',
'out vec4 fragColor;',
'uniform sampler2D u_tex;',
'uniform vec2  u_texelSize;    // (1/w, 1/h)',
'uniform mat4  u_colorMatrix;  // 3x4 affine colour transform (row 4 = identity)',
'uniform float u_duoMix;       // 0..1 duotone blend',
'uniform vec3  u_duoDark;      // duotone shadow colour',
'uniform vec3  u_duoLight;     // duotone highlight colour',
'uniform float u_brightness;   // -0.5..0.5  (fine-tune)',
'uniform float u_contrast;     // 0.5..2.0   (fine-tune)',
'uniform float u_saturation;   // 0.0..3.0   (fine-tune)',
'uniform float u_warmth;       // -1..1      (fine-tune)',
'uniform float u_shadows;      // -1..1      (fine-tune) lift / deepen the dark tones only',
'uniform float u_highlights;   // -1..1      (fine-tune) brighten / pull down the light tones only',
'uniform float u_vignette;     // 0..1',
'uniform float u_grain;        // 0..1  film grain',
'uniform float u_grainSize;    // 0..1  grain clump size: 540 → 180 cells per frame HEIGHT',
'uniform float u_scanline;     // 0..1  CRT scanlines',
'uniform float u_chroma;       // 0..N  RGB-split offset, in pixels',
'uniform float u_noise;        // 0..1  analogue noise',
'uniform float u_jitter;       // 0..1  horizontal line jitter',
'uniform float u_flicker;      // 0..1  brightness wobble',
'uniform float u_dust;         // 0..1  film damage: dust specks + vertical scratches',
'uniform float u_shake;        // 0..1  whole-frame camera shake / film gate weave',
'uniform float u_time;         // seconds — animates noise / jitter / flicker / dust / shake',
'uniform float u_seed;         // per-frame random',
'uniform float u_soften;       // 0..1  lens/sensor softness (blur radius)',
'uniform sampler3D u_lut;      // imported .cube colour grade',
'uniform float u_lutMix;       // 0..1  how much of it to apply',
'uniform float u_lutSize;      // cube edge in samples (33 is the industry norm)',
'uniform float u_rampMix;      // 0..1  4-stop palette blend (thermal, night vision)',
'uniform vec3  u_ramp0;        // palette stop at luma 0.00',
'uniform vec3  u_ramp1;        // palette stop at luma 0.33',
'uniform vec3  u_ramp2;        // palette stop at luma 0.67',
'uniform vec3  u_ramp3;        // palette stop at luma 1.00',
'uniform float u_keepMix;      // 0..1  selective colour ("b&w except this hue")',
'uniform float u_keepHue;      // 0..1  hue to keep (0 = red)',
'uniform float u_keepRange;    // 0..0.5  half-width of the kept hue band',
'uniform float u_quantMix;     // 0..1  hard quantise to a 4-colour palette (CGA)',
'uniform vec3  u_pal[16];      // palette entries, u_palCount of them are live',
'uniform int   u_palCount;     // 4 for the CGA-era sets, 16 for EGA',
'uniform float u_dither;       // ordered-dither amplitude, palette-scaled by the caller',
'uniform float u_pixelMix;     // 0..1  how far the raster coarsens toward u_pixelGrid',
'uniform vec2  u_pixelGrid;    // (cols, rows) in CELLS across the frame; cols<=0 = derive from aspect',
'uniform float u_pixelPar;     // pixel aspect (w/h) of the machine being imitated',
'uniform float u_autoGain;     // 0..1  image-intensifier gain: lifts a dark frame toward a mid-grey key',
'uniform vec3  u_hsl[8];       // per-colour (hue, saturation, lightness) shifts, -1..1 each',
'uniform float u_hslOn;        // 0/1   any of the eight is non-zero',
'const vec3 LUMA = vec3(0.2126, 0.7152, 0.0722);',
'float rand(vec2 co){ return fract(sin(dot(co, vec2(12.9898, 78.233)) + u_seed) * 43758.5453); }',
'float hash11(float p){ return fract(sin(p * 127.1) * 43758.5453); }',
'float hash21(vec2 p){ return fract(sin(dot(p, vec2(41.3, 289.1))) * 43758.5453); }',
'float vnoise(float x){ float i = floor(x), f = fract(x); f = f * f * (3.0 - 2.0 * f);',
'  return mix(hash11(i), hash11(i + 1.0), f); }',
// Sine-free hash (Hoskins) for the grain and tape-noise lattices: their cell
// coordinates run to ~1000 plus a per-frame offset, and sin() of an argument
// that large repeats in patterns on some mobile GPUs.
'float hash12(vec2 p){ vec3 p3 = fract(vec3(p.xyx) * 0.1031); p3 += dot(p3, p3.yzx + 33.33);',
'  return fract((p3.x + p3.y) * p3.z); }',
'float vnoise2(vec2 p){ vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);',
'  return mix(mix(hash12(i), hash12(i + vec2(1.0, 0.0)), f.x),',
'             mix(hash12(i + vec2(0.0, 1.0)), hash12(i + vec2(1.0, 1.0)), f.x), f.y); }',
// Softness has to be measured against the FRAME, not against texels: the
// preview renders at <=960px and the export at full resolution, and a
// radius in texels would blur the preview harder than the download. Tying
// it to frame height keeps the tool's one invariant — preview == export.
// The picture is always read from the BASE level (textureLod 0). The frame
// texture grows a mip chain while the night-vision gain is on (u_autoGain
// reads the frame's brightness from it, below), and plain texture() would then
// pick a blurrier level wherever the render is smaller than the source — the
// <=960 px preview would soften and the full-size export would not. With no
// mip chain (every other look) textureLod(…, 0.0) is exactly texture().
'vec3 tex0(vec2 p){ return textureLod(u_tex, p, 0.0).rgb; }',
'vec3 sampleSoft(vec2 uv){',
'  if (u_soften < 0.002) return tex0(uv);',
'  float rpx = u_soften * (1.0 / max(u_texelSize.y, 1e-6)) * 0.006;',
'  vec2 o = vec2(rpx * u_texelSize.x, rpx * u_texelSize.y);',
'  vec3 s = tex0(uv) * 4.0;',
'  s += (tex0(uv + vec2(o.x, 0.0)) + tex0(uv - vec2(o.x, 0.0))',
'      +  tex0(uv + vec2(0.0, o.y)) + tex0(uv - vec2(0.0, o.y))) * 2.0;',
'  s += tex0(uv + o) + tex0(uv - o)',
'     + tex0(uv + vec2(o.x, -o.y)) + tex0(uv + vec2(-o.x, o.y));',
'  return s / 16.0;',
'}',
// One picture-taking step, so the raster fade below can take it twice (sharp
// and on-cell) without duplicating the chroma split.
// Chroma aberration — R and B sampled at a horizontal offset.
'vec3 sampleRGB(vec2 uv){',
'  if (u_chroma > 0.001) {',
'    float off = u_chroma * u_texelSize.x;',
'    return vec3(sampleSoft(uv + vec2(off, 0.0)).r,',
'                sampleSoft(uv).g,',
'                sampleSoft(uv - vec2(off, 0.0)).b);',
'  }',
'  return sampleSoft(uv);',
'}',
// Hue/saturation of a colour, for the selective-colour mask. Standard
// branchless RGB->HSV; only .x (hue) and .y (saturation) are used.
// Ordered (Bayer) dither. Written as arithmetic rather than a 16-entry lookup
// on purpose: a const array indexed by a computed value is legal in GLSL ES
// 3.00 but is exactly the sort of thing individual drivers get wrong, and a
// shader that fails to compile takes the whole tool's preview with it.
// bayer2 yields the classic [[0,2],[3,1]]/4 cell; bayer4 nests it into 4x4.
'float bayer2(vec2 a){ a = floor(a); return fract(a.x / 2.0 + a.y * a.y * 0.75); }',
'float bayer4(vec2 a){ return bayer2(0.5 * a) * 0.25 + bayer2(a); }',
// Nearest palette entry by squared RGB distance. Four compares, no loop —
// the palette is fixed at four because that is what the hardware being
// imitated had.
// Was four hard-coded slots because that is what CGA had. EGA has sixteen,
// and the loop costs the GPU a handful of dot products per pixel — measured
// rather than assumed, see the palette bench in video-filter-palette.mjs.
'vec3 nearestPal(vec3 q){',
'  vec3 best = u_pal[0]; vec3 d = q - u_pal[0]; float bd = dot(d, d);',
'  for (int i = 1; i < 16; i++) {',
'    if (i >= u_palCount) break;',
'    vec3 dd = q - u_pal[i]; float t = dot(dd, dd);',
'    if (t < bd) { bd = t; best = u_pal[i]; }',
'  }',
'  return best;',
'}',
// Trilinear lookup into an imported cube.
//
// ⚠️ The half-texel inset is not cosmetic. A cube of n samples spans n texels,
// and 0.0/1.0 land on the OUTER EDGE of the first and last one, where LINEAR
// filtering blends against the clamped neighbour — black and white drift, which
// on a grade reads as "the LUT is wrong" rather than "the sampling is". Mapping
// to texel CENTRES ((v*(n-1)+0.5)/n) is what makes an identity cube actually
// identity, and the guard test asserts exactly that.
'vec3 sampleLut(vec3 c){',
'  float n = max(u_lutSize, 2.0);',
'  vec3 uvw = (clamp(c, 0.0, 1.0) * (n - 1.0) + 0.5) / n;',
'  return texture(u_lut, uvw).rgb;',
'}',
'vec3 rgb2hsv(vec3 c){',
'  vec4 K = vec4(0.0, -1.0 / 3.0, 2.0 / 3.0, -1.0);',
'  vec4 p = mix(vec4(c.bg, K.wz), vec4(c.gb, K.xy), step(c.b, c.g));',
'  vec4 q = mix(vec4(p.xyw, c.r), vec4(c.r, p.yzx), step(p.x, c.r));',
'  float d = q.x - min(q.w, q.y);',
'  return vec3(abs(q.z + (q.w - q.y) / (6.0 * d + 1e-10)), d / (q.x + 1e-10), q.x);',
'}',
'vec3 hsv2rgb(vec3 c){',
'  vec3 p = abs(fract(c.xxx + vec3(1.0, 2.0 / 3.0, 1.0 / 3.0)) * 6.0 - 3.0);',
'  return c.z * mix(vec3(1.0), clamp(p - 1.0, 0.0, 1.0), c.y);',
'}',
// Per-colour shift for a hue (0..1), blended between the two neighbouring
// colour centres — red 0°, orange 30°, yellow 60°, green 120°, aqua 180°,
// blue 240°, purple 270°, magenta 300°, the eight every phone editor's HSL
// panel has. Neighbours share the hue between them (weights sum to one), so
// moving «red» never leaves a hard edge where red turns orange. Written as a
// branch ladder with CONSTANT indices on purpose (see bayer2 above).
'vec3 hslShift(float h){',
'  float d = h * 360.0;',
'  vec3 a = u_hsl[7]; vec3 b = u_hsl[0]; float t = (d - 300.0) / 60.0;',
'  if      (d <  30.0) { a = u_hsl[0]; b = u_hsl[1]; t = d / 30.0; }',
'  else if (d <  60.0) { a = u_hsl[1]; b = u_hsl[2]; t = (d - 30.0) / 30.0; }',
'  else if (d < 120.0) { a = u_hsl[2]; b = u_hsl[3]; t = (d - 60.0) / 60.0; }',
'  else if (d < 180.0) { a = u_hsl[3]; b = u_hsl[4]; t = (d - 120.0) / 60.0; }',
'  else if (d < 240.0) { a = u_hsl[4]; b = u_hsl[5]; t = (d - 180.0) / 60.0; }',
'  else if (d < 270.0) { a = u_hsl[5]; b = u_hsl[6]; t = (d - 240.0) / 30.0; }',
'  else if (d < 300.0) { a = u_hsl[6]; b = u_hsl[7]; t = (d - 270.0) / 30.0; }',
'  return mix(a, b, smoothstep(0.0, 1.0, clamp(t, 0.0, 1.0)));',
'}',
// The frame's KEY — the log-average luminance of nine regional means, read
// from the mip chain the renderer builds while u_autoGain is on. Log-average,
// not the plain mean: on a night street a few lamps lift the plain mean and
// the gain would leave the dark majority dark — which is the complaint the
// gain exists to answer («сделать тёмные участки видимыми, как с эффектом
// ночного видения», #1965/#1966). A statistic of the SOURCE texture, so the
// <=960 px preview and the full-size export (same source frame) read the same
// number — the tool's one invariant holds without a second pass.
'float frameKey(){',
'  vec2 ts = vec2(textureSize(u_tex, 0));',
'  float lod = max(0.0, log2(max(ts.x, ts.y)) - 2.0);',
'  float s = 0.0;',
'  for (int j = 0; j < 3; j++) {',
'    for (int i = 0; i < 3; i++) {',
'      vec2 p = (vec2(float(i), float(j)) + 0.5) / 3.0;',
'      s += log(max(dot(textureLod(u_tex, p, lod).rgb, LUMA), 0.004));',
'    }',
'  }',
'  return exp(s / 9.0);',
'}',
'void main() {',
'  vec2 uv = v_uv;',
// Camera shake / film gate weave — the WHOLE frame moves, which is what makes
// it a different axis from u_jitter (that one slips single scanlines sideways
// and is a tape artefact). Asked for as «Тряска экрана»; the requester spent
// the session hammering the datamosh repeat slider, which is the nearest knob
// we had and is not this.
//
// The picture is zoomed in first so the displaced frame still has material at
// its edges: without it the shake would drag a smeared border in from the
// clamp, and a "shake" that frays the edges reads as a broken export. The zoom
// is sized to the displacement, so at small amounts (the 1970s preset's gate
// weave) it is not visible at all.
'  if (u_shake > 0.001) {',
'    float sp = u_time * 9.0;',
'    vec2 off = vec2(vnoise(sp) - 0.5, vnoise(sp + 37.0) - 0.5) * 2.0;',
'    off += vec2(vnoise(sp * 2.7 + 11.0) - 0.5, vnoise(sp * 2.7 + 53.0) - 0.5);',
'    float zoom = 1.0 + u_shake * 0.09;',
'    uv = (uv - 0.5) / zoom + 0.5 + off * (u_shake * 0.022);',
'    uv = clamp(uv, vec2(0.0), vec2(1.0));',
'  }',
// VHS tracking jitter — whole scanlines slip horizontally
'  if (u_jitter > 0.001) {',
'    float line = floor(uv.y / u_texelSize.y);',
'    uv.x += (rand(vec2(line * 0.013, u_time)) - 0.5) * u_jitter * 0.03;',
'  }',
// Coarse pixel grid (the CGA raster). Counted in CELLS ACROSS THE FRAME, not
// in texels, so the <=960px preview and the full-size export land on the
// identical grid — the same reason u_soften measures against the frame.
//
// Only the SCANLINE count arrives (u_pixelGrid.y); the columns are derived
// here from the frame's own aspect at the era's 5:6 pixel. Passing a fixed
// 320x200 CELL COUNT instead — which is what shipped — silently made the cell
// shape a function of the source: 1.11 on 16:9, 0.83 on 4:3, and 0.42 on a
// phone-shot 9:16 clip, i.e. horizontal streaks 2.4x taller than wide. Derived
// this way a 4:3 clip lands on exactly 320x200, the real mode 4 raster, and
// every other aspect keeps the same cell SHAPE.
'  vec2 res = 1.0 / max(u_texelSize, vec2(1e-6));',
'  float tRows = clamp(u_pixelGrid.y, 2.0, 2048.0);',
// ⚠️ Множитель здесь БЫЛ зашит как 1.2 — это 1/(5/6), пиксель CGA. Пока
// палитра была одна, он был верен; с EGA (par 35/48) и Game Boy (квадратный
// пиксель) он молча давал ячейку чужой формы, и «EGA» на ролике 4:3
// растрировался в 560x350 вместо 640x350. Форма пикселя — свойство машины и
// приезжает из реестра палитр.
'  float par = u_pixelPar > 0.01 ? u_pixelPar : 0.8333333;',
'  float tCols = u_pixelGrid.x > 0.5 ? u_pixelGrid.x : max(2.0, tRows * (res.x / res.y) / par);',
// Cells never go below one rendered pixel: past that the raster is invisible
// anyway and the ordered dither would alias into noise instead of drawing the
// crosshatch. A no-op at every raster the UI can reach on a normal clip.
'  vec2 pxGrid = min(vec2(tCols, tRows), res);',
'  vec2 uvCell = (floor(uv * pxGrid) + 0.5) / pxGrid;',
// u_pixelMix FADES the raster the way every other axis here fades: by blending
// toward the untouched picture. It does NOT make the cells finer.
//
// That distinction is the whole bug. The shipped version mixed the grid from
// the RENDER RESOLUTION toward the target, so the moment the intensity slider
// left 100% the preview and the export were rastered on different grids —
// measured 1.46x apart at 75% and 1.95x apart at 25%, on the one tool whose
// stated invariant is that the two agree. Anything that fades a raster by
// refining it has to name a "fine" resolution, and the only one in scope is
// the render target, which is exactly what differs between the two paths.
'  vec3 c;',
'  if (u_pixelMix > 0.999)      { c = sampleRGB(uvCell); }',
'  else if (u_pixelMix < 0.001) { c = sampleRGB(uv); }',
'  else                         { c = mix(sampleRGB(uv), sampleRGB(uvCell), u_pixelMix); }',
// Image-intensifier gain — a night-vision tube amplifies the light it gets
// until the scene sits at a usable level (automatic brightness control), and
// that, not the green, is what makes dark parts VISIBLE. As a power curve on
// the frame's key: black stays black, white stays white, the key lands near
// 0.40; a frame already that bright is left alone (exponent 1). Exponent
// floor 0.30 — below it the 8-bit steps of a dark source turn into bands.
'  if (u_autoGain > 0.001) {',
'    float key = clamp(frameKey(), 0.004, 0.95);',
'    float ex = clamp(log(0.40) / log(key), 0.30, 1.0);',
'    c = pow(max(c, vec3(0.0)), vec3(mix(1.0, ex, u_autoGain)));',
'  }',
// fine-tune EQ — user corrections applied to the source
'  c += u_brightness;',
'  c = (c - 0.5) * u_contrast + 0.5;',
'  float lpre = dot(c, LUMA);',
'  c = mix(vec3(lpre), c, u_saturation);',
'  c.r += u_warmth * 0.12;',
'  c.b -= u_warmth * 0.12;',
// Shadows / highlights — the two sliders every phone editor has (#1307 asked
// for «Тени» in a session that never found the curves). Each moves ONE half of
// the tones: the shadow weight is zero from mid-grey up and the highlight
// weight zero from mid-grey down, so «Тени» never touches the sky. Pure black
// and pure white are pinned (the 0.08 / 0.92 shoulders): lifting shadows must
// not turn black into grey fog, and that is what a flat offset would do.
'  if (abs(u_shadows) > 0.001 || abs(u_highlights) > 0.001) {',
'    float tl = clamp(dot(c, LUMA), 0.0, 1.0);',
'    float ws = (1.0 - smoothstep(0.0, 0.5, tl)) * smoothstep(0.0, 0.08, tl);',
'    float wh = smoothstep(0.5, 1.0, tl) * (1.0 - smoothstep(0.92, 1.0, tl));',
'    c += vec3(0.25 * (u_shadows * ws + u_highlights * wh));',
'  }',
'  c = clamp(c, 0.0, 1.0);',
// One colour at a time — «уменьши яркость всего красного» (#1946): hue,
// saturation and lightness per colour band, before the preset, so the look
// lands on the corrected colours. Greys and near-black have no colour to
// move (same two gates as the selective-colour mask). Lightness scales RGB
// down (hue and saturation stay — a darker red, not a muddier one) and up
// toward white.
'  if (u_hslOn > 0.5) {',
'    vec3 hv = rgb2hsv(c);',
'    vec3 a = hslShift(hv.x) * (smoothstep(0.03, 0.16, hv.y) * smoothstep(0.015, 0.06, hv.z));',
'    hv.x = fract(hv.x + a.x * 0.0833333);',
'    hv.y = clamp(hv.y * (1.0 + a.y), 0.0, 1.0);',
'    vec3 o = hsv2rgb(hv);',
'    o = a.z < 0.0 ? o * (1.0 + 0.75 * a.z) : o + (1.0 - o) * (0.6 * a.z);',
'    c = clamp(o, 0.0, 1.0);',
'  }',
// The un-stylised source is kept: the selective-colour mask below reads the
// hue the CAMERA saw, not the hue the preset produced. Deciding "is this
// pixel red" after a b&w matrix has run would find no red anywhere.
'  vec3 src = c;',
// preset colour matrix (already lerped toward identity by intensity)
'  vec3 m = (u_colorMatrix * vec4(c, 1.0)).rgb;',
// duotone — map luma onto a dark→light gradient
'  if (u_duoMix > 0.001) {',
'    float l = dot(clamp(m, 0.0, 1.0), LUMA);',
'    m = mix(m, mix(u_duoDark, u_duoLight, l), u_duoMix);',
'  }',
// Palette ramp — luma onto a 4-stop gradient. Duotone is the 2-stop case and
// stays as it is; thermal ironbow and the night-vision phosphor need the
// middle stops (black → purple → orange → white cannot be a lerp of two
// colours). Kept data-driven so a new palette is a row in the preset table,
// not another shader.
'  if (u_rampMix > 0.001) {',
'    float x = clamp(dot(clamp(m, 0.0, 1.0), LUMA), 0.0, 1.0) * 3.0;',
'    vec3 pal = x < 1.0 ? mix(u_ramp0, u_ramp1, x)',
'             : x < 2.0 ? mix(u_ramp1, u_ramp2, x - 1.0)',
'                       : mix(u_ramp2, u_ramp3, x - 2.0);',
'    m = mix(m, pal, u_rampMix);',
'  }',
'  c = clamp(m, 0.0, 1.0);',
// Imported grade goes HERE: after the preset has said what the look is, before
// selective colour and the palette quantiser stylise on top of it. A .cube is a
// grade, not an effect — putting it last would mean quantising to four colours
// and then grading the four.
'  if (u_lutMix > 0.001) {',
'    c = mix(c, sampleLut(c), u_lutMix);',
'  }',
// Selective colour — "make it b&w but keep the red bits". The kept band
// restores the ORIGINAL colour rather than the styled one, so it still works
// under a monochrome preset (where the styled pixel has no colour left to
// keep). Everything outside the band keeps the full look and loses only its
// saturation, so a noir's contrast and vignette survive.
'  if (u_keepMix > 0.001) {',
'    vec3 hsv = rgb2hsv(src);',
'    float dh = abs(hsv.x - u_keepHue);',
'    dh = min(dh, 1.0 - dh);',                  // hue is a circle — red wraps
'    float keep = 1.0 - smoothstep(u_keepRange, u_keepRange + 0.055, dh);',
// Greys and near-black have a meaningless hue: without these two gates a
// dark grey wall reads as "dark red" and half the frame refuses to go b&w.
'    keep *= smoothstep(0.10, 0.26, hsv.y);',
'    keep *= smoothstep(0.05, 0.14, hsv.z);',
'    vec3 sel = mix(vec3(dot(c, LUMA)), src, keep);',
'    c = mix(c, sel, u_keepMix);',
'  }',
// CRT scanlines — darken alternating rows
'  if (u_scanline > 0.001) {',
'    float s = 0.5 + 0.5 * sin((uv.y / u_texelSize.y) * 3.14159265);',
'    c *= 1.0 - u_scanline * 0.35 * (1.0 - s);',
'  }',
// flicker — global brightness wobble
'  if (u_flicker > 0.001) {',
'    c *= 1.0 + (rand(vec2(u_time, 0.0)) - 0.5) * u_flicker * 0.18;',
'  }',
// vignette
'  vec2 d = v_uv - 0.5;',
'  float r = dot(d, d) * 4.0;',
'  c *= 1.0 - u_vignette * smoothstep(0.4, 1.4, r);',
// Film grain and tape noise — TWO artefacts, both sized against the FRAME.
//
// Until 2026-10-15 this was one line: `max(u_grain, u_noise)` of per-pixel
// white noise. So «Зерно» 100 + «Шум плёнки» 100 equalled one of them at 100
// (#1834 set both, then filed «Зернистость и шум зернистости»), and one pixel
// of the RENDER target is not one size: the preview renders at <=960 px and a
// 1080p export at 1920, so the export carried half the grain the preview
// showed (measured on flat grey, preview vs export averaged 2x2: SD 13.27 →
// 6.64, ratio 0.50) before the encoder smeared what was left. Same rule as
// u_soften and the CGA raster: anything spatial is counted in cells of the
// frame height, never in texels. Guard: tests/playwright/video-filter-grain-scale.mjs.
//
// Grain: monochrome, clumped (value noise, plus a half-frequency octave for
// the mottle of real emulsion; never a FINER octave — finer than a preview
// pixel would come back as preview != export), strongest in the midtones as
// on film. Cell count 540 → 180 per frame height via u_grainSize (540 = one
// pixel of the 960x540 preview, the finest the preview can still show).
// Re-rolled per 24 fps frame of film time and per u_seed (per frame on video,
// per FILE on /photo-filters, so a still re-exports byte-identical).
//
// Tape noise: coloured, hard-edged cells three times wider than tall — the
// horizontal sparkle of a worn VHS — re-rolled per 30 fps frame.
'  float frameAspect = u_texelSize.y / max(u_texelSize.x, 1e-9);',
'  if (u_grain > 0.001) {',
'    float gfi = mod(floor(u_time * 24.0), 1009.0);',
'    float gRows = mix(540.0, 180.0, clamp(u_grainSize, 0.0, 1.0));',
'    vec2 gp = v_uv * vec2(gRows * frameAspect, gRows)',
'            + vec2(u_seed * 317.0 + gfi * 13.1, u_seed * 151.0 + gfi * 7.7);',
'    float g = (vnoise2(gp) - 0.5) + 0.5 * (vnoise2(gp * 0.5 + vec2(19.7, 7.3)) - 0.5);',
'    float gl = clamp(dot(c, LUMA), 0.0, 1.0);',
'    c += vec3(g * u_grain * 0.28 * (0.35 + 2.6 * gl * (1.0 - gl)));',
'  }',
'  if (u_noise > 0.001) {',
'    float nfi = mod(floor(u_time * 30.0), 1009.0);',
'    vec2 np = floor(v_uv * vec2(180.0 * frameAspect, 540.0))',
'            + vec2(u_seed * 211.0 + nfi * 17.3, nfi * 5.1);',
'    float nl = hash12(np) - 0.5;',
'    vec3 nc = vec3(hash12(np + vec2(31.7, 11.3)), hash12(np + vec2(67.1, 43.9)),',
'                   hash12(np + vec2(5.3, 91.1))) - 0.5;',
'    c += (vec3(nl) * 0.6 + nc * 0.4) * u_noise * 0.25;',
'  }',
// Film damage — dust specks and vertical scratches. NOT grain and not tape
// noise: the person who asked for «шум старой плёнки» had already dialled
// grain, noise, jitter, flicker and scanlines in that session and filed the
// request anyway, because what makes a print look old is physical damage to
// it, and we had none of it.
//
// Frame-space (v_uv), not uv: dirt sits on the gate and the print, so it must
// NOT move with the shake above. Time-quantised so a speck lives exactly one
// frame at 24 fps and a scratch survives a third of a second — a scratch that
// re-rolled every frame would read as coloured noise.
'  if (u_dust > 0.001) {',
'    float fi = floor(u_time * 24.0);',
'    vec2 dg = vec2(96.0, 54.0);',
'    vec2 cellD = floor(v_uv * dg);',
'    if (hash21(cellD + fi * 7.3) < u_dust * 0.10) {',
'      vec2 c0 = (cellD + 0.5) / dg;',
'      float dd = length((v_uv - c0) * dg);',
'      float sp = 1.0 - smoothstep(0.15, 0.55, dd);',
'      float lit = hash21(cellD + fi * 13.7 + 3.0) > 0.55 ? 1.0 : 0.0;',
'      c = mix(c, vec3(lit), sp * 0.9 * u_dust);',
'    }',
'    float ep = floor(u_time * 3.0);',
'    for (int i = 0; i < 3; i++) {',
'      float k = float(i);',
'      if (hash11(ep * 3.1 + k * 17.0) > 1.0 - u_dust * 0.8) {',
'        float xs = hash11(ep * 5.7 + k * 29.0);',
'        float wdt = 0.0006 + hash11(ep + k * 7.0) * 0.0016;',
'        float m = 1.0 - smoothstep(0.0, wdt, abs(v_uv.x - xs));',
'        float tone = hash11(ep * 2.3 + k) > 0.5 ? 0.12 : 0.96;',
'        c = mix(c, vec3(tone), m * 0.7 * u_dust);',
'      }',
'    }',
'  }',
// Palette quantisation — LAST, because it is a display step, not a look.
// Anything added after it (grain, scanlines, a vignette) reintroduces colours
// the palette does not contain, and the promise "four colours" breaks
// silently: the image still looks retro, it just is not CGA any more.
//
// The dither threshold is per CELL, not per fragment. Sampling it per fragment
// would draw a dither finer than the pixels it is dithering — the crosshatch
// that makes this read as a 1981 adapter would dissolve into noise. With the
// raster turned off the cell is still the palette's own raster (pxGrid falls
// back to `target`), because a dither measured in TEXELS would be one size in
// the preview and another in the download.
//
// u_dither is an absolute amplitude, already scaled by the caller to the
// palette's widest luma gap. It has to be: the shader cannot know how far
// apart these four colours sit, and an amplitude that cannot cross the gap
// leaves the picture in the flat blobs the dither exists to prevent.
'  if (u_quantMix > 0.001) {',
'    float d = (bayer4(floor(v_uv * pxGrid)) - 0.46875) * u_dither;',
'    c = mix(c, nearestPal(clamp(c + d, 0.0, 1.0)), u_quantMix);',
'  }',
'  fragColor = vec4(clamp(c, 0.0, 1.0), 1.0);',
'}'
].join('\n');

// Column-major identity mat4 — the "no colour change" matrix.
var STYLIZE_IDENTITY_M4 = [1,0,0,0, 0,1,0,0, 0,0,1,0, 0,0,0,1];
var STYLIZE_EMPTY_PAL = new Float32Array(48);
var STYLIZE_EMPTY_HSL = new Float32Array(24);
var STYLIZE_UNIFORM_KEYS = ['texelSize','colorMatrix','duoMix','duoDark','duoLight',
    'brightness','contrast','saturation','warmth','shadows','highlights','vignette','grain','grainSize','scanline',
    'chroma','noise','jitter','flicker','dust','shake','time','seed','tex',
    'soften','rampMix','ramp0','ramp1','ramp2','ramp3','lut','lutMix','lutSize',
    'keepMix','keepHue','keepRange',
    'quantMix','pal','palCount','dither','pixelMix','pixelGrid','pixelPar',
    'autoGain','hsl','hslOn'];

function collectStylizeUniforms(gl, prog) {
    var u = {};
    STYLIZE_UNIFORM_KEYS.forEach(function(k){
        u[k] = gl.getUniformLocation(prog, k === 'tex' ? 'u_tex' : 'u_' + k);
    });
    return u;
}

// Push a stylize-param object `su` (built by VideoFilterPresets) into the
// uniforms of the currently-bound progStylize program. `w`,`h` = render
// target size so u_texelSize is correct. Shared by run()'s export path AND
// createStylizeRenderer()'s live-preview path → preview is WYSIWYG.
function applyStylizeUniforms(gl, u, su, w, h) {
    su = su || {};
    gl.uniformMatrix4fv(u.colorMatrix, false, su.colorMatrix || STYLIZE_IDENTITY_M4);
    gl.uniform2f(u.texelSize, 1 / Math.max(1, w), 1 / Math.max(1, h));
    gl.uniform1f(u.duoMix,     +su.duoMix || 0);
    gl.uniform3fv(u.duoDark,   su.duoDark  || [0, 0, 0]);
    gl.uniform3fv(u.duoLight,  su.duoLight || [1, 1, 1]);
    gl.uniform1f(u.brightness, +su.brightness || 0);
    gl.uniform1f(u.contrast,   su.contrast   == null ? 1 : +su.contrast);
    gl.uniform1f(u.saturation, su.saturation == null ? 1 : +su.saturation);
    gl.uniform1f(u.warmth,     +su.warmth   || 0);
    gl.uniform1f(u.shadows,    +su.shadows    || 0);
    gl.uniform1f(u.highlights, +su.highlights || 0);
    gl.uniform1f(u.vignette,   +su.vignette || 0);
    gl.uniform1f(u.grain,      +su.grain    || 0);
    // Size of the grain clump, 0..1 (0 = 540 cells per frame height, 1 = 180).
    // A caller that predates the axis gets the catalogue's default (0.35).
    gl.uniform1f(u.grainSize,  su.grainSize == null ? 0.35 : +su.grainSize);
    gl.uniform1f(u.scanline,   +su.scanline || 0);
    gl.uniform1f(u.chroma,     +su.chroma   || 0);
    gl.uniform1f(u.noise,      +su.noise    || 0);
    gl.uniform1f(u.jitter,     +su.jitter   || 0);
    gl.uniform1f(u.flicker,    +su.flicker  || 0);
    gl.uniform1f(u.dust,       +su.dust     || 0);
    gl.uniform1f(u.shake,      +su.shake    || 0);
    gl.uniform1f(u.time,       +su.time     || 0);
    gl.uniform1f(u.seed,       su.seed != null ? +su.seed : Math.random());
    gl.uniform1f(u.soften,     +su.soften   || 0);
    gl.uniform1f(u.rampMix,    +su.rampMix  || 0);
    gl.uniform3fv(u.ramp0,     su.ramp0 || [0, 0, 0]);
    gl.uniform3fv(u.ramp1,     su.ramp1 || [0, 0, 0]);
    gl.uniform3fv(u.ramp2,     su.ramp2 || [1, 1, 1]);
    gl.uniform3fv(u.ramp3,     su.ramp3 || [1, 1, 1]);
    gl.uniform1f(u.keepMix,    +su.keepMix   || 0);
    gl.uniform1f(u.keepHue,    +su.keepHue   || 0);
    gl.uniform1f(u.keepRange,  su.keepRange == null ? 0.05 : +su.keepRange);
    gl.uniform1f(u.quantMix,   +su.quantMix || 0);
    // The cube lives on texture unit 1 — unit 0 is the frame itself.
    gl.uniform1f(u.lutMix,     +su.lutMix  || 0);
    gl.uniform1f(u.lutSize,    +su.lutSize || 2);
    if (u.lut) gl.uniform1i(u.lut, 1);
    // One flat Float32Array for the whole array uniform. Unused tail entries
    // are never read: the loop stops at u_palCount.
    gl.uniform3fv(u.pal,       su.pal || STYLIZE_EMPTY_PAL);
    gl.uniform1i(u.palCount,   Math.max(1, Math.min(16, +su.palCount || 4)));
    gl.uniform1f(u.dither,     +su.dither   || 0);
    gl.uniform1f(u.pixelMix,   +su.pixelMix || 0);
    gl.uniform2fv(u.pixelGrid, su.pixelGrid || [0, 0]);
    // Форма пикселя машины. Запасное значение — пиксель CGA: ровно то, что
    // стояло зашитой константой до 30.08, поэтому старый вызов без поля даёт
    // прежнюю картинку, а не чёрный кадр.
    gl.uniform1f(u.pixelPar, +su.pixelPar || (5 / 6));
    gl.uniform1f(u.autoGain,   +su.autoGain || 0);
    gl.uniform1f(u.hslOn,      su.hslOn ? 1 : 0);
    gl.uniform3fv(u.hsl,       su.hsl || STYLIZE_EMPTY_HSL);
}

// The night-vision gain reads the frame's brightness from a mip chain of the
// frame texture (frameKey() in FRAG_STYLIZE). Built only while the gain is on
// — one generateMipmap per frame — and the texture goes back to plain LINEAR
// when it is off, so every other look samples exactly as it always did.
// Call with the frame texture BOUND, right after the upload.
function stylizeMips(gl, su, state) {
    var want = !!(su && +su.autoGain > 0.001);
    if (want) {
        if (state.mips !== true) gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
        gl.generateMipmap(gl.TEXTURE_2D);
    } else if (state.mips === true) {
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    }
    state.mips = want;
}

var VERT_QUAD = [
'#version 300 es',
'precision highp float;',
'in vec2 a_pos;',
'out vec2 v_uv;',
'void main() {',
'  v_uv = a_pos * 0.5 + 0.5;',
'  gl_Position = vec4(a_pos, 0.0, 1.0);',
'}'
].join('\n');

function compileShader(gl, type, src) {
    var s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
        var log = gl.getShaderInfoLog(s) || '';
        gl.deleteShader(s);
        throw new Error('shader compile: ' + log);
    }
    return s;
}

function buildProgram(gl, vertSrc, fragSrc) {
    var vs = compileShader(gl, gl.VERTEX_SHADER, vertSrc);
    var fs = compileShader(gl, gl.FRAGMENT_SHADER, fragSrc);
    var p = gl.createProgram();
    gl.attachShader(p, vs);
    gl.attachShader(p, fs);
    gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
        throw new Error('link: ' + gl.getProgramInfoLog(p));
    }
    return p;
}

// ─────────────────────────────────────────────────────────────────────
// FRAME SOURCE → TEXTURE, THE SAME WAY UP FOR EVERY SOURCE TYPE
// ─────────────────────────────────────────────────────────────────────
// Both rigs here (makeFilterRig, createStylizeRenderer) set
// UNPACK_FLIP_Y_WEBGL, and the quad is calibrated for an upload the flag
// flips. The WebGL spec makes ImageBitmap the ONE source type that ignores
// the flag (its own creation options rule), so a bitmap went in upside-down
// while a <video>, a VideoFrame, a canvas and ImageData went in right. Every
// consumer that met this patched it its own way: renderStill flipped its
// readback (calibrated on the bitmap the paused preview passes), /photo-filters
// staged bitmaps through a canvas — and the enhance-video PLAYING preview,
// which hands renderStill a <video>, got that readback flip on top of a
// correct upload: the «After» pane stood on its head for every person who
// pressed Play, 2026-08-07 → 2026-10-15 (support chat 50). One owner now:
// every frame upload goes through here, a bitmap is drawn into a 2D canvas
// first (a source the flag applies to in every engine), and no consumer
// compensates. Flipping in the shader instead was rejected: FRAG_STYLIZE
// places grain, dust, scratches and dither in v_uv, so moving the flip there
// would mirror the look of every /video-filter export.
// Guards: GlFrameUploadsHaveOneOwnerTest, tests/playwright/gl-rig-orientation-every-source.mjs.
var _bitmapStages = [];
// A stage above this stays only for its own upload: a full-size photo export
// (24 MP ≈ 96 MB) must not stay parked here after the file is out.
var BITMAP_STAGE_KEEP_PX = 2560 * 1600;
function uploadFrameTexture(gl, source) {
    var stage = null;
    // A VideoFrame / <video> goes in through the HDR → SDR door: texImage2D
    // hands an HLG/PQ frame over as the 8-bit CODED signal (no transfer, no
    // gamut conversion — measured, docs/hdr-to-sdr.md). The door returns the
    // SDR picture on a canvas, or the source itself when it is SDR.
    if (window.HdrToSdr && ((typeof VideoFrame !== 'undefined' && source instanceof VideoFrame) ||
        (typeof HTMLVideoElement !== 'undefined' && source instanceof HTMLVideoElement))) {
        source = window.HdrToSdr.image(source, { texture: true });
    }
    if (typeof ImageBitmap !== 'undefined' && source instanceof ImageBitmap) {
        var w = source.width, h = source.height;
        for (var i = 0; i < _bitmapStages.length; i++) {
            if (_bitmapStages[i].width === w && _bitmapStages[i].height === h) { stage = _bitmapStages[i]; break; }
        }
        if (!stage) {
            // Two sizes stay resident (a preview and a contact-sheet crop
            // alternate on /photo-filters); a third evicts the oldest.
            stage = (typeof OffscreenCanvas !== 'undefined') ? new OffscreenCanvas(w, h)
                : Object.assign(document.createElement('canvas'), { width: w, height: h });
            stage.__ctx = stage.getContext('2d');
            _bitmapStages.push(stage);
            if (_bitmapStages.length > 2) _bitmapStages.shift();
        }
        stage.__ctx.clearRect(0, 0, w, h);
        stage.__ctx.drawImage(source, 0, 0);
    }
    var dims = sourceTexelSize(stage || source);
    try {
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, stage || source);
    } finally {
        if (stage && stage.width * stage.height > BITMAP_STAGE_KEEP_PX) {
            _bitmapStages.splice(_bitmapStages.indexOf(stage), 1);
            stage.width = stage.height = 1;
        }
    }
    return dims;
}
// The size texImage2D gives the texture for this source — what the rig needs
// to know whether the output frame is an ENLARGEMENT (WebGL2 cannot ask a
// texture its size). A VideoFrame uploads at its display size.
function sourceTexelSize(s) {
    var w = 0, h = 0;
    try {
        if (typeof VideoFrame !== 'undefined' && s instanceof VideoFrame) { w = s.displayWidth; h = s.displayHeight; }
        else if (typeof HTMLVideoElement !== 'undefined' && s instanceof HTMLVideoElement) { w = s.videoWidth; h = s.videoHeight; }
        else if (typeof HTMLImageElement !== 'undefined' && s instanceof HTMLImageElement) { w = s.naturalWidth; h = s.naturalHeight; }
        else if (s) { w = s.width; h = s.height; }
    } catch (_) {}
    return { w: w | 0, h: h | 0 };
}

function makeFilterRig(width, height, rigOpts) {
    rigOpts = rigOpts || {};
    var canvas = new OffscreenCanvas(width, height);
    var gl = canvas.getContext('webgl2', { premultipliedAlpha: false });
    if (!gl) throw new Error('webgl2 missing');

    // ───────────────────────────────────────────────────────────────────
    // VERTICAL-FLIP FIX (bug #427, #370 — 2026-05-16)
    // ───────────────────────────────────────────────────────────────────
    // texImage2D uploads a VideoFrame with its top row at texture t=0, but
    // a WebGL render targets a bottom-left-origin framebuffer and the canvas
    // is then read top-left-origin by `new VideoFrame(canvas)`. The net of
    // that chain is exactly ONE vertical flip — so every frame this rig
    // produced was upside-down. Proven by a 4-quadrant fixture run through
    // the prod HW pipeline: input TL=red → output TL=blue (a pure V-flip,
    // not a 180° rotation — the 2-colour fixture used earlier could not
    // tell those apart, which is how this hid behind the rotation reports).
    //
    // UNPACK_FLIP_Y_WEBGL is GL context state: it flips EVERY VideoFrame /
    // image upload (texImage2D / texSubImage2D with a TexImageSource) except
    // an ImageBitmap, which uploadFrameTexture() stages through a canvas. The
    // only other texImage2D here allocates texMid with `null` data, which
    // FLIP_Y does not touch. So one pixelStorei makes the whole rig —
    // run()'s 2-pass filter, video-fps GPU-blend AND CPU-interpolator
    // paths — geometrically neutral: coded pixels in === coded pixels out.
    //
    // With the rig neutral, the source tkhd rotation matrix is correctly
    // re-emitted unchanged onto the output (Stage 5, commit 2217fce59) —
    // that part was right; the flip was the actual bug behind #427.
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);

    var progPixel = buildProgram(gl, VERT_QUAD, FRAG_PIXEL_LOCAL);
    var progConv  = buildProgram(gl, VERT_QUAD, FRAG_CONVOLUTION);
    var progDn    = buildProgram(gl, VERT_QUAD, FRAG_DENOISE);
    var progBlend = buildProgram(gl, VERT_QUAD, FRAG_BLEND);
    var progResample = buildProgram(gl, VERT_QUAD, FRAG_RESAMPLE);
    // Stylize program — only compiled when a stylize consumer asked for it
    // (the /video-filter tool). enhance-video / video-fps never pay this cost.
    var progStylize = rigOpts.stylize ? buildProgram(gl, VERT_QUAD, FRAG_STYLIZE) : null;

    // Full-screen quad VAO
    var vao = gl.createVertexArray();
    gl.bindVertexArray(vao);
    var buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
        -1, -1,  1, -1,  -1, 1,
        -1,  1,  1, -1,   1, 1
    ]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    gl.bindVertexArray(null);
    // Re-bind attrib at index 0 for all programs
    gl.bindAttribLocation(progPixel, 0, 'a_pos');
    gl.bindAttribLocation(progConv,  0, 'a_pos');
    gl.bindAttribLocation(progBlend, 0, 'a_pos');
    gl.bindAttribLocation(progResample, 0, 'a_pos');
    if (progStylize) gl.bindAttribLocation(progStylize, 0, 'a_pos');

    // Source texture #1 — the standard slot used by single-frame paths
    // (filter-only enhance-video flow). Frame-interpolation uses both
    // texSrc + texSrcB as a frame-pair buffer (A=current, B=next).
    var texSrc = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texSrc);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

    // Source texture #2 — frame-interpolation pair buffer (B slot).
    // Allocated unconditionally; filter-only consumers ignore it.
    var texSrcB = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texSrcB);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

    // Intermediate texture between pass 1 and pass 2
    var texMid = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texMid);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);

    var fbMid = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbMid);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texMid, 0);
    if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) {
        throw new Error('fb-mid incomplete');
    }
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);

    // Denoise pass target (pass 2a, 2026-07-30). pixel-local → texMid →
    // [denoise → texDn] → conv → output. Allocated up front like texMid.
    var texDn = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texDn);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    var fbDn = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbDn);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texDn, 0);
    if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) {
        throw new Error('fb-dn incomplete');
    }
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);

    // Uniform locations
    var uPixel = {};
    var pixelKeys = ['brightness','contrast','saturation','gamma','highlights','shadows',
                     'warmth','vignette','grain','seed'];
    pixelKeys.forEach(function(k){ uPixel[k] = gl.getUniformLocation(progPixel, 'u_'+k); });
    var uPixelTex = gl.getUniformLocation(progPixel, 'u_tex');

    var uConv = {
        unsharp:  gl.getUniformLocation(progConv, 'u_unsharpAmt'),
        denoise:  gl.getUniformLocation(progConv, 'u_denoiseAmt'),
        texel:    gl.getUniformLocation(progConv, 'u_texelSize'),
        tex:      gl.getUniformLocation(progConv, 'u_tex'),
        prevTex:  gl.getUniformLocation(progConv, 'u_prevTex'),
        temporalK: gl.getUniformLocation(progConv, 'u_temporalK')
    };
    var uDn = {
        denoise: gl.getUniformLocation(progDn, 'u_denoiseAmt'),
        texel:   gl.getUniformLocation(progDn, 'u_texelSize'),
        tex:     gl.getUniformLocation(progDn, 'u_tex')
    };

    // Previous OUTPUT frame for the temporal denoise term. Allocated up front
    // (copyTexSubImage2D needs storage); content only ever read when the
    // caller passes temporalK > 0 AND a frame has already been copied in.
    var texPrev = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texPrev);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);

    var uBlend = {
        texA: gl.getUniformLocation(progBlend, 'u_texA'),
        texB: gl.getUniformLocation(progBlend, 'u_texB'),
        t:    gl.getUniformLocation(progBlend, 'u_t')
    };

    var uStylize = progStylize ? collectStylizeUniforms(gl, progStylize) : null;
    var uRs = {
        tex:  gl.getUniformLocation(progResample, 'u_tex'),
        size: gl.getUniformLocation(progResample, 'u_srcSize'),
        dir:  gl.getUniformLocation(progResample, 'u_dir')
    };

    return {
        gl: gl, canvas: canvas, width: width, height: height,
        progPixel: progPixel, progConv: progConv, progBlend: progBlend,
        progStylize: progStylize, uStylize: uStylize,
        texSrc: texSrc, texSrcB: texSrcB, texMid: texMid, fbMid: fbMid,
        texDn: texDn, fbDn: fbDn, progDn: progDn, uDn: uDn,
        texPrev: texPrev, _prevValid: false,
        // The size the filter passes run at (texMid/texDn/texPrev): the output,
        // or the SOURCE when the output enlarges it — see _rigWorkAt.
        workW: width, workH: height,
        progResample: progResample, uRs: uRs,
        texWork: null, fbWork: null, texHalf: null, fbHalf: null, _enlargeKey: '',
        vao: vao, uPixel: uPixel, uPixelTex: uPixelTex, uConv: uConv, uBlend: uBlend,
        // Whether convolution pass actually does anything
        needsConv: function(p){ return p.sharpness > 0 || p.denoise > 0; }
    };
}

// The output frame is an ENLARGEMENT of the source: no axis shrinks and at
// least one grows. A mixed aspect change (one axis down) keeps the linear
// sample, like every reduction (see FRAG_RESAMPLE).
function _isEnlargement(src, outW, outH) {
    return !!(src && src.w > 0 && src.h > 0 && src.w <= outW && src.h <= outH &&
              (src.w < outW || src.h < outH));
}

// Size the filter passes' textures to (w, h). Sizes are constant within a run,
// so this allocates once per run (and when a preview source changes size).
// The previous frame kept for temporal denoise is from another size — dropped.
function _rigWorkAt(rig, w, h) {
    if (rig.workW === w && rig.workH === h) return;
    var gl = rig.gl;
    [rig.texMid, rig.texDn, rig.texPrev].forEach(function (t) {
        gl.bindTexture(gl.TEXTURE_2D, t);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    });
    rig.workW = w; rig.workH = h;
    rig._prevValid = false;
}

function _rigTarget(gl, w, h, what) {
    var tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    var fb = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
    if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) {
        throw new Error(what + ' incomplete');
    }
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    return { tex: tex, fb: fb };
}

// Targets of an enlargement from (sw, sh): the filtered frame at source size
// and the horizontally-resampled half step (outW × sh).
function _rigEnlargeTargets(rig, sw, sh) {
    var key = sw + 'x' + sh;
    if (rig._enlargeKey === key) return;
    var gl = rig.gl;
    if (rig.texWork) { gl.deleteTexture(rig.texWork); gl.deleteFramebuffer(rig.fbWork); }
    if (rig.texHalf) { gl.deleteTexture(rig.texHalf); gl.deleteFramebuffer(rig.fbHalf); }
    var wk = _rigTarget(gl, sw, sh, 'fb-work');
    var hf = _rigTarget(gl, rig.width, sh, 'fb-half');
    rig.texWork = wk.tex; rig.fbWork = wk.fb;
    rig.texHalf = hf.tex; rig.fbHalf = hf.fb;
    rig._enlargeKey = key;
}

// srcTex (sw × sh) → the rig canvas (width × height), FRAG_RESAMPLE twice.
function _rigEnlarge(rig, srcTex, sw, sh) {
    var gl = rig.gl;
    gl.useProgram(rig.progResample);
    gl.activeTexture(gl.TEXTURE0);
    gl.uniform1i(rig.uRs.tex, 0);
    gl.bindFramebuffer(gl.FRAMEBUFFER, rig.fbHalf);
    gl.viewport(0, 0, rig.width, sh);
    gl.bindTexture(gl.TEXTURE_2D, srcTex);
    gl.uniform2f(rig.uRs.size, sw, sh);
    gl.uniform2f(rig.uRs.dir, 1, 0);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, rig.width, rig.height);
    gl.bindTexture(gl.TEXTURE_2D, rig.texHalf);
    gl.uniform2f(rig.uRs.size, rig.width, sh);
    gl.uniform2f(rig.uRs.dir, 0, 1);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
}

// ─────────────────────────────────────────────────────────────────────
// FILTER PARAMETER MAPPING (FFmpeg semantics → shader uniforms)
// ─────────────────────────────────────────────────────────────────────

function mapParams(p) {
    // Defensive defaults so missing keys don't NaN the shader.
    var s  = +p.sharpness   || 0;     // 0..200 → 0..0.6 amount
    var dn = +p.denoise     || 0;     // 0..100 → 0..0.7 weight
    var br = +p.brightness  || 0;     // -100..100 → -0.5..0.5
    var ct = +p.contrast    || 0;     // -100..100 → 0.5..2.0
    var sa = +p.saturation  || 0;     // -100..100 → 0..3.0
    var g  = +p.gamma; if (!isFinite(g) || g <= 0) g = 1.0;
    var hi = +p.highlights  || 0;     // -100..100 → -1..1
    var sh = +p.shadows     || 0;     // -100..100 → -1..1
    var wm = +p.warmth      || 0;     // -100..100 → -1..1
    var vg = +p.vignette    || 0;     // 0..100 → 0..1
    var gr = +p.grain       || 0;     // 0..100 → 0..1

    return {
        // Pass 1 — pixel-local
        brightness: br / 200,                                      // -0.5..0.5
        contrast:   ct >= 0 ? 1 + ct/100 : 1 + ct/200,             // 0.5..2.0
        saturation: sa <= 0 ? Math.max(0, 1 + sa/100) : 1 + sa/50, // 0..3
        gamma:      g,
        highlights: hi / 100,
        shadows:    sh / 100,
        warmth:     wm / 100,
        vignette:   vg / 100,
        grain:      gr / 100,
        // Pass 2 — convolution
        // Slider 0..200 -> shader amt 0..1.0, in two segments.
        // 0..100 keeps the shipped curve exactly (s/100*0.6), so every
        // existing preset and saved setting renders byte-identical.
        // 100..200 was DEAD: this used to be `Math.min(0.6, ...)`, so the
        // whole upper half of the control did nothing on either lane (the
        // FFmpeg chain mirrored the cap). The shader's own design allows
        // amt up to 1.0 — CAS peak mix(0.02,0.22,amt) and the USM boost both
        // still have headroom there. Measured on the reporter's real file
        // (Kolchicki, soft phone footage): amt 0.6 -> 1.2-equivalent lifted
        // fine-detail energy +36% while max ringing rose 18% — the trade
        // favours soft sources, which is exactly who drags the slider up.
        // On already-sharp content the trade inverts (ringing outruns
        // detail), and that is why the top is 1.0 rather than higher.
        unsharp:    (s <= 100 ? s / 100 * 0.6 : Math.min(1.0, 0.6 + (s - 100) / 100 * 0.4)),
        // Slider -> spatial mix. Line fitted through the measured optima
        // (dn=35 -> 0.40 at moderate noise, dn=61 -> 0.66 at heavy), capped at
        // 0.8 where clean-content damage would otherwise start. The shader
        // derives sigmaR, the luma/chroma split and the temporal gate from
        // this one number.
        denoise:    dn > 0 ? Math.min(0.8, 0.05 + dn / 100) : 0,
    };
}

// All parameters in this list are covered by our shaders. If the caller
// hands us an unknown key with non-trivial value, isSupported returns
// false and the caller should fallback.
var SUPPORTED_PARAMS = {
    sharpness: 1, denoise: 1, brightness: 1, contrast: 1, saturation: 1,
    gamma: 1, highlights: 1, shadows: 1, warmth: 1, vignette: 1, grain: 1,
    resolution: 1
};

function paramsCovered(params) {
    if (!params) return true;
    for (var k in params) {
        if (!params.hasOwnProperty(k)) continue;
        if (SUPPORTED_PARAMS[k]) continue;
        // Unknown key — only OK if it's a no-op value
        var v = params[k];
        if (v === 0 || v === null || v === undefined || v === '' || v === false) continue;
        return false;
    }
    return true;
}

// ─────────────────────────────────────────────────────────────────────
// SHARED PER-FRAME DRAW («ОДНА renderFrame»)
// ─────────────────────────────────────────────────────────────────────
// The ONE place a frame goes through the filter shaders. run() calls it for
// every export frame; renderStill() calls it for the live preview frame.
// Preview === export by construction — the enhance-video preview previously
// approximated the CAS+bilateral shaders with ffmpeg `unsharp` (a DIFFERENT
// algorithm) through the SHARED FFmpeg engine, which both lied about the HW
// look and raced other flows' reinit() (bug-report class «мутно, до/после
// одинаковые», 2026-07-06).
//
// o = { mapped        — mapParams() output
//       hasFilter     — computeHasFilter(mapped)
//       needsConv     — rig.needsConv(params)
//       stylizeUniforms — merged stylize uniform set, or null for standard }
function computeHasFilter(mapped) {
    return mapped.brightness !== 0 || mapped.contrast !== 1 || mapped.saturation !== 1 ||
        mapped.gamma !== 1 || mapped.highlights !== 0 || mapped.shadows !== 0 ||
        mapped.warmth !== 0 || mapped.vignette !== 0 || mapped.grain !== 0 ||
        mapped.unsharp !== 0 || mapped.denoise !== 0;
}

function drawFiltered(rig, source, o) {
    var gl = rig.gl;
    var outW = rig.width, outH = rig.height;
    // Upload frame/bitmap → texture (FLIP_Y is rig context state)
    gl.bindTexture(gl.TEXTURE_2D, rig.texSrc);
    var src = uploadFrameTexture(gl, source);
    // An enlargement filters at the SOURCE size and then resamples once with
    // FRAG_RESAMPLE (the measurement is there): sharpening a frame after a
    // stretch sharpens the stretch. The stylize pass keeps its own geometry —
    // its grain, scanlines and dust are laid out in output pixels.
    var enlarge = !o.stylizeUniforms && _isEnlargement(src, outW, outH);
    var workW = enlarge ? src.w : outW, workH = enlarge ? src.h : outH;
    if (enlarge) _rigEnlargeTargets(rig, workW, workH);
    if (!o.stylizeUniforms) _rigWorkAt(rig, workW, workH);
    // Where the last filter pass writes: the canvas, or the source-size
    // target that _rigEnlarge then resamples onto the canvas.
    var finalFb = enlarge ? rig.fbWork : null;

    gl.bindVertexArray(rig.vao);

    if (o.stylizeUniforms) {
        // Single artistic STYLIZE pass — texSrc → canvas directly.
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.viewport(0, 0, outW, outH);
        gl.useProgram(rig.progStylize);
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, rig.texSrc);
        stylizeMips(gl, o.stylizeUniforms, rig.__mipState || (rig.__mipState = {}));
        gl.uniform1i(rig.uStylize.tex, 0);
        applyStylizeUniforms(gl, rig.uStylize, o.stylizeUniforms, outW, outH);
        gl.drawArrays(gl.TRIANGLES, 0, 6);
    } else if (o.hasFilter) {
        var mapped = o.mapped;
        // Pass 1 → texMid (work size: the output, or the source of an
        // enlargement)
        gl.bindFramebuffer(gl.FRAMEBUFFER, rig.fbMid);
        gl.viewport(0, 0, workW, workH);
        gl.useProgram(rig.progPixel);
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, rig.texSrc);
        gl.uniform1i(rig.uPixelTex, 0);
        gl.uniform1f(rig.uPixel.brightness, mapped.brightness);
        gl.uniform1f(rig.uPixel.contrast,   mapped.contrast);
        gl.uniform1f(rig.uPixel.saturation, mapped.saturation);
        gl.uniform1f(rig.uPixel.gamma,      mapped.gamma);
        gl.uniform1f(rig.uPixel.highlights, mapped.highlights);
        gl.uniform1f(rig.uPixel.shadows,    mapped.shadows);
        gl.uniform1f(rig.uPixel.warmth,     mapped.warmth);
        gl.uniform1f(rig.uPixel.vignette,   mapped.vignette);
        gl.uniform1f(rig.uPixel.grain,      mapped.grain);
        gl.uniform1f(rig.uPixel.seed,       o.seed || 0);
        gl.drawArrays(gl.TRIANGLES, 0, 6);

        // Pass 2a (denoise → texDn) — only when the denoise slider is up.
        // Runs render-to-texture so the conv pass's CAS/USM taps read the
        // CLEANED image with flat fetches (the inline-chain design cost
        // ~250 fetches/px after the two-scale upgrade; split ≈ 33/px).
        var convSrcTex = rig.texMid;
        var dnAmt = o.needsConv ? mapped.denoise : 0;
        if (dnAmt > 0.001) {
            gl.bindFramebuffer(gl.FRAMEBUFFER, rig.fbDn);
            gl.viewport(0, 0, workW, workH);
            gl.useProgram(rig.progDn);
            gl.activeTexture(gl.TEXTURE0);
            gl.bindTexture(gl.TEXTURE_2D, rig.texMid);
            gl.uniform1i(rig.uDn.tex, 0);
            gl.uniform1f(rig.uDn.denoise, dnAmt);
            gl.uniform2f(rig.uDn.texel, 1.0/workW, 1.0/workH);
            gl.drawArrays(gl.TRIANGLES, 0, 6);
            convSrcTex = rig.texDn;
        }

        // Pass 2 (convolution) → the canvas, or the source-size target of an
        // enlargement; with zero params it degenerates into a plain copy, so
        // one code path covers both.
        gl.bindFramebuffer(gl.FRAMEBUFFER, finalFb);
        gl.viewport(0, 0, workW, workH);
        gl.useProgram(rig.progConv);
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, convSrcTex);
        gl.uniform1i(rig.uConv.tex, 0);
        // Temporal denoise: only when the caller opts in AND a previous frame
        // has actually been captured (frame 1 and every renderStill get 0).
        var tK = (o.temporalK && rig._prevValid) ? o.temporalK : 0;
        gl.activeTexture(gl.TEXTURE1);
        gl.bindTexture(gl.TEXTURE_2D, rig.texPrev);
        gl.uniform1i(rig.uConv.prevTex, 1);
        gl.uniform1f(rig.uConv.temporalK, tK);
        gl.activeTexture(gl.TEXTURE0);
        gl.uniform1f(rig.uConv.unsharp, o.needsConv ? mapped.unsharp : 0);
        gl.uniform1f(rig.uConv.denoise, o.needsConv ? mapped.denoise : 0);
        gl.uniform2f(rig.uConv.texel, 1.0/workW, 1.0/workH);
        gl.drawArrays(gl.TRIANGLES, 0, 6);
        if (o.temporalK) {
            // Keep this output as the next frame's history. copyTexSubImage2D
            // reads the currently-bound framebuffer (the canvas, or fbWork of
            // an enlargement — both at work size) with the same lower-left
            // origin the next draw's v_uv uses, so no flip issues.
            gl.bindTexture(gl.TEXTURE_2D, rig.texPrev);
            gl.copyTexSubImage2D(gl.TEXTURE_2D, 0, 0, 0, 0, 0, workW, workH);
            rig._prevValid = true;
        }
        if (enlarge) _rigEnlarge(rig, rig.texWork, workW, workH);
    } else if (enlarge) {
        // No filter, larger frame: the resample alone.
        _rigEnlarge(rig, rig.texSrc, workW, workH);
    } else {
        // No-filter path — direct copy via conv shader with zero params
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.viewport(0, 0, outW, outH);
        gl.useProgram(rig.progConv);
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, rig.texSrc);
        gl.uniform1i(rig.uConv.tex, 0);
        gl.uniform1f(rig.uConv.unsharp, 0);
        gl.uniform1f(rig.uConv.denoise, 0);
        gl.uniform1f(rig.uConv.temporalK, 0);
        gl.uniform2f(rig.uConv.texel, 1.0/outW, 1.0/outH);
        gl.drawArrays(gl.TRIANGLES, 0, 6);
    }
    gl.bindVertexArray(null);
}

// renderStill — one-frame filter render for live previews. Same rig, same
// shaders, same mapParams as the export loop. Returns the rig's OffscreenCanvas
// (contents valid until the next renderStill call). Throws when webgl2 is
// unavailable — callers fall back to their legacy path.
//
// opts.temporal (2026-08-15, #1396): opt-in for callers that feed CONSECUTIVE
// frames of a playing video through this rig — the playback preview. Without
// it the temporal denoise term is invisible EVERYWHERE in the preview (paused
// AND playing), while it is the term that removes most film grain: a user
// with a grainy B&W archive clip dragged the denoise slider, watched the
// preview do almost nothing, and wrote «шумы вааще не убирает» — his EXPORT
// then measured −70% residual noise. The condition mirrors run()'s export
// loop exactly (denoise > 0, no stylize); the rig keeps history between
// calls, and the shader's own pixel-difference gate rejects the blend across
// seeks/scene cuts — the same mechanism the export trusts. A paused still
// stays temporal-free by physics: one frame has no second frame to average
// noise against.
var _stillRig = null;
var _stillOut = null;
function renderStill(source, params, w, h, opts) {
    w = w | 0; h = h | 0;
    if (!(w > 0 && h > 0)) throw new Error('renderStill: bad dims');
    if (_stillRig && (_stillRig.width !== w || _stillRig.height !== h)) {
        _stillRig = null; _stillOut = null; // dims changed — rebuild
    }
    if (!_stillRig) _stillRig = makeFilterRig(w, h);
    var mapped = mapParams(params);
    drawFiltered(_stillRig, source, {
        mapped: mapped,
        hasFilter: computeHasFilter(mapped),
        needsConv: _stillRig.needsConv(params),
        seed: 0,
        stylizeUniforms: null,
        temporalK: (opts && opts.temporal && mapped.denoise > 0.001) ? 0.75 : 0
    });
    // Test hook. ⚠️ Was `__lastTemporalK` — a SNAPSHOT of the most recent
    // call, which races the end of playback: the moment the player pauses it
    // renders one still (temporal off by physics) and the snapshot reads 0,
    // so the guard passed or failed by luck depending on where sampling
    // landed. A COUNTER cannot be raced: it only grows, and a guard asks
    // whether it grew across the playback window.
    var _tkNow = (opts && opts.temporal && mapped.denoise > 0.001) ? 0.75 : 0;
    renderStill.__lastTemporalK = _tkNow;
    if (_tkNow > 0) renderStill.__temporalFrames = (renderStill.__temporalFrames || 0) + 1;
    // A 2D snapshot, NOT turned over: the rig canvas is already the right way
    // up for every source (uploadFrameTexture). The 2026-07-10 copy here also
    // flipped — «a still readback sees the canvas the OTHER way up» — but the
    // readback was never the difference: the bitmap the paused preview passes
    // ignores FLIP_Y, and the flip turned every <video> frame of the PLAYING
    // preview upside-down. The copy itself stays: callers read the result
    // later (convertToBlob), and a 2D snapshot of a WebGL canvas is the path
    // every engine has served since July — convertToBlob straight off a WebGL
    // OffscreenCanvas is not proven on Safari.
    if (!_stillOut) {
        _stillOut = new OffscreenCanvas(w, h);
        _stillOut.__ctx = _stillOut.getContext('2d');
    }
    _stillOut.__ctx.clearRect(0, 0, w, h);
    _stillOut.__ctx.drawImage(_stillRig.canvas, 0, 0);
    return _stillOut;
}

// ─────────────────────────────────────────────────────────────────────
// MAIN PIPELINE
// ─────────────────────────────────────────────────────────────────────

// Telemetry helper — fires `webcodecs_<which>_error` immediately from the
// async error callbacks. Without this, the only signal is the cascade
// `hw_pipeline_failed` event from the caller's catch handler, which carries
// the *secondary* "closed codec" message instead of the root cause.
// Slug defaults to 'enhance-video' since that's the only consumer at fix
// time; new consumers should pass opts.slug to attribute correctly.
function _aeWebcodecsErr(slug, which, err, extra) {
    if (typeof window === 'undefined' || typeof window.__ae !== 'function') return;
    try {
        var payload = {
            slug: slug || 'enhance-video',
            action: 'webcodecs_' + which + '_error',
            err: ((err && err.message) || String(err) || '').substring(0, 200)
        };
        if (extra) for (var k in extra) if (extra.hasOwnProperty(k)) payload[k] = extra[k];
        window.__ae('feature_use', payload);
    } catch (_) {}
}

/* Decode → edit → HW-encode AAC the audio track so it matches the edited
   video, then mux it. Throws on any gap so the caller's FFmpeg fallback
   yields a correct file rather than a silent/desynced one.

   `edit` (all optional, omitted === no-op → byte-identical to the original
   speed-only behaviour every existing caller relies on):
     gain      — linear multiplier (1 = untouched)
     fadeIn    — seconds of fade-from-silence at the START of the OUTPUT
     fadeOut   — seconds of fade-to-silence at the END of the OUTPUT
     trimStart / trimEnd — seconds; the window to keep, in SOURCE time

   Order matches the FFmpeg filter chain this mirrors (aselect → atempo →
   volume → afade): trim first, then stretch, then gain, then the fades —
   so fade timings live on the FINAL timeline, not the source one.

   ⚠️ trim was previously absent here: the video honoured trimStart/trimEnd
   while this decoded and encoded the WHOLE file, so a caller combining trim
   with speed shipped audio longer than its video. video-speed has no trim UI
   so nothing hit it, but reels-maker does. */
// _decodeWholeAudio — the ONE way this module turns a source file into an
// AudioBuffer. Shared by muxSpeedAudio (the edit/transcode path) and by
// gateAudioDecodable (the up-front can-we-decode-it probe) so the two can
// never drift apart — a probe that decodes differently from the path it
// vouches for is not a probe.
/* The decode result is CHECKED against the container's media duration
   (demuxInfo.audio.durationSec, mdhd). A decode that comes back the wrong
   length is a failed decode, not a result: Yandex Browser 26.8's
   decodeAudioData returns HE-AAC v2 at twice the length, an octave down,
   folded to mono — and never throws. That buffer became a 125 s soundtrack on
   a 62 s clip in this very pipeline (E2E replay of support 2026-09-01). 20 %
   is far above priming/padding and far below the 2× it exists to catch. */
function _audioLengthDisagrees(buf, demuxInfo, via, slug) {
    var oracle = demuxInfo && demuxInfo.audio && demuxInfo.audio.durationSec;
    if (!(oracle > 1) || !buf || !(buf.duration > 0)) return false;
    var ratio = buf.duration / oracle;
    if (ratio > 0.8 && ratio < 1.2) return false;
    try { window.__ae && window.__ae('feature_use', {
        slug: slug || 'enhance-video', f: 'decode_duration_mismatch', via: via,
        expected: +oracle.toFixed(2), got: +buf.duration.toFixed(2), sr: buf.sampleRate, ch: buf.numberOfChannels
    }); } catch (_) {}
    return true;
}

async function _decodeWholeAudio(file, isAborted, demuxInfo) {
    var AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) throw new Error('hw-audio-unavailable');
    if (demuxInfo && demuxInfo.audio && demuxInfo.audio.pcm) {
        var pb = await _decodePcmTable(demuxInfo, isAborted);
        if (isAborted && isAborted()) throw new DOMException('cancelled', 'AbortError');
        return pb;
    }
    // Matroska: decode from the sample table — never the whole file into the
    // heap (see _decodeTableAudio). No native fallback on purpose: a failure
    // throws, and the caller's own fallback (FFmpeg) keeps the audio.
    if (demuxInfo && demuxInfo.container === 'matroska' && demuxInfo.audio) {
        var tb = await _decodeTableAudio(demuxInfo, isAborted);
        if (isAborted && isAborted()) throw new DOMException('cancelled', 'AbortError');
        try { tb.__via = 'table'; } catch (_) {}
        return tb;
    }
    // WebCodecs first for AAC-in-MP4 (2026-09-02) — the tier that reads the
    // container's clock instead of a label (webcodecs-audio-decode.js). Native
    // decodeAudioData stays the fallback: it is what mislabels HE-AAC on
    // Yandex and refuses the core-rate-header flavour outright. Any result,
    // from either path, must agree with the container on length.
    var WC = window.WebCodecsAudioDecode;
    if (WC && typeof WC.decode === 'function' && WC.supported(file)) {
        var wcCtx = new AC();
        try {
            var wcBuf = await WC.decode(file, wcCtx);
            if (isAborted && isAborted()) throw new DOMException('cancelled', 'AbortError');
            if (wcBuf && wcBuf.length && !_audioLengthDisagrees(wcBuf, demuxInfo, 'webcodecs')) {
                try { wcBuf.__via = 'webcodecs'; } catch (_) {}   // muxSpeedAudio: who padded the lead, if anyone
                return wcBuf;
            }
        } catch (eW) {
            if (eW && eW.name === 'AbortError') throw eW;
            /* fall through to native */
        } finally {
            try { wcCtx.close(); } catch (_) {}
        }
    }
    // FileBytes-first read (same Wave-22 etalon as _readSliceWithRetry): the
    // shared uploader primes the bytes at pick time, so this survives the OS
    // revoking the file handle between pick and Process — the top hw_fallback
    // reason on prod ("The requested file could not be read", 69/14d, and the
    // WASM retry rescued only 5 of them because the raw handle was dead there
    // too). Direct arrayBuffer() stays as the >100 MB / no-FileBytes fallback.
    var bytes = null;
    if (window.FileBytes && typeof window.FileBytes.read === 'function') {
        try {
            var _fbA = await window.FileBytes.read(file);
            bytes = _fbA.buffer.slice(_fbA.byteOffset, _fbA.byteOffset + _fbA.byteLength);
        } catch (_) { /* not primed (>100 MB) or dead — try the raw handle */ }
    }
    if (!bytes) bytes = await file.arrayBuffer();
    var ctx = new AC();
    var srcBuf;
    // Suppress the global decodeAudioData→FFmpeg-wav fallback shim here: the
    // callers' OWN contract on failure is throw → the caller falls back to
    // the FFmpeg WASM pipeline wholesale. Letting the shim detour through a
    // second FFmpeg decode (input→decoded.wav) is pure harm — on audio-less
    // videos it always fails loudly, and its singleton churn raced the probe
    // hard enough to wedge the Process button on slow devices (#1006).
    var _prevSuppress = window.__suppressDecodeFallback;
    window.__suppressDecodeFallback = true;
    try { srcBuf = await ctx.decodeAudioData(bytes.slice(0)); }
    finally {
        window.__suppressDecodeFallback = _prevSuppress;
        try { ctx.close(); } catch (_) {}
    }
    if (isAborted && isAborted()) throw new DOMException('cancelled', 'AbortError');
    if (_audioLengthDisagrees(srcBuf, demuxInfo, 'native')) {
        // Both lanes disagree with the container → the caller's FFmpeg
        // fallback (libavcodec decodes SBR/PS correctly) takes the file.
        throw new Error('audio-decode-length-mismatch');
    }
    try { srcBuf.__via = 'native'; } catch (_) {}
    return srcBuf;
}

// gateAudioDecodable — the shared audio-codec gate for run() and
// runInterpolation(). AAC-LC samples are byte-copied; any other mp4a flavour
// goes through decode → AAC re-encode (the MP3-in-MP4 class). Non-mp4a codecs
// (Opus, FLAC, Vorbis, AC-3 …) used to be refused BY NAME right here — the
// comment guessed «decodeAudioData may not even accept them» — which sent
// every YouTube download (AV1 video + Opus audio in MP4) to an FFmpeg
// fallback that cannot decode AV1 at all: both lanes dead while the PREVIEW
// played fine. #1402 (German, 2026-08-14): bought Premium mid-session, hit
// the wall 8 times, looped through /repair-video (a remux that changes no
// codecs) and back, wrote «Выдаёт ошибку при скачивании». 244 sessions/30 d
// die on the fallback's «Error while decoding stream #0:0» class.
//
// The verdict is now earned by DOING (same doctrine as verifyEncoderAt): one
// up-front decodeAudioData over the real bytes — the exact decode the
// transcode path will run. Success → transcode lane, as mp4a flavours
// already do. Failure → the SAME typed throw as before (AC-3 lands here:
// Chromium has no licence for it; the FFmpeg lane decodes it and keeps
// serving those files). The probe buffer is discarded on purpose — holding
// PCM of an hour-long source through the whole video encode would cost
// ~1.4 GB; one extra audio decode is the price of not wasting the entire
// VIDEO encode on a file whose audio would fail at the very end (audio is
// muxed AFTER the video loop in both callers).
async function gateAudioDecodable(file, demuxInfo, slug, isAborted) {
    var codec = (demuxInfo.audio && demuxInfo.audio.codec) || '';
    if (!demuxInfo.audio || !codec) return { needsTranscode: false };
    // Uncompressed sound: its layout was read off the sample entry (__mp4Pcm) —
    // that IS the verdict; there is no decoder to ask. It always becomes AAC.
    if (demuxInfo.audio.pcm) return { needsTranscode: true, pcm: true };
    if (/^mp4a/i.test(codec)) {
        return { needsTranscode: !isAacPassthroughCodec(codec) };
    }
    /* A codec the WebCodecs AudioDecoder accepts (Opus, FLAC, Vorbis, MP3
       under its own tag …) is decided by isConfigSupported — a few ms, no
       I/O — instead of the whole-file decode probe below, which read the
       ENTIRE file into the heap just to answer "can we?" (the 1.6 GB class,
       17.09.2026). The lazy transcode (muxAudioLazy) then reads the track in
       8 MB windows. Anything the decoder declines still takes the probe. */
    if (demuxInfo.lazy && typeof AudioDecoder !== 'undefined') {
        var lazyCfg = null;
        try { lazyCfg = lazyAudioDecoderConfig(demuxInfo.audio); } catch (_) { lazyCfg = null; }
        if (lazyCfg) {
            // Judge by DOING, at window cost: isConfigSupported says the codec
            // is known; the first access units through a real decoder say the
            // stream is. A NO here is the same verdict the whole-file probe
            // gave (audio-codec-unsupported → FFmpeg), reached in ms.
            var tL = Date.now(), okL = false, outs = 0, whyL = '';
            try {
                var supL = await AudioDecoder.isConfigSupported(lazyCfg);
                if (!supL || !supL.supported) throw new Error('config-unsupported');
                var rdP = _lazySampleReader(demuxInfo);
                var decErrP = null;
                var decP = new AudioDecoder({ output: function (d) { outs++; try { d.close(); } catch (_) {} }, error: function (e) { decErrP = e; } });
                decP.configure(lazyCfg);
                var unitsP = demuxInfo.audio.samples;
                for (var pi = 0; pi < unitsP.length && pi < 6; pi++) {
                    var uP = unitsP[pi];
                    decP.decode(new EncodedAudioChunk({ type: 'key', timestamp: uP.timestamp, duration: uP.duration || 0, data: uP.data || await rdP.bytes(uP, false) }));
                }
                await decP.flush();
                try { decP.close(); } catch (_) {}
                if (decErrP) throw decErrP;
                okL = outs > 0;
                if (!okL) whyL = 'no-output';
            } catch (eP) { okL = false; whyL = String((eP && eP.message) || eP).slice(0, 60); }
            try { window.__ae && window.__ae('feature_use', {
                slug: slug || 'enhance-video', action: 'audio_decode_probe', lane: 'lazy',
                codec: String(codec).slice(0, 20), ok: okL ? 1 : 0, units: outs, ms: Date.now() - tL, why: whyL
            }); } catch (_) {}
            if (isAborted && isAborted()) throw new DOMException('cancelled', 'AbortError');
            if (okL) return { needsTranscode: true, lazyOk: true };
            throw new Error('audio-codec-unsupported-' + codec);
        }
    }
    var t0 = Date.now();
    var ok = false;
    try {
        await _decodeWholeAudio(file, isAborted, demuxInfo);
        ok = true;
    } catch (e) {
        if (e && e.name === 'AbortError') throw e;
    }
    try { window.__ae && window.__ae('feature_use', {
        slug: slug || 'enhance-video', action: 'audio_decode_probe',
        codec: String(codec).slice(0, 20), ok: ok ? 1 : 0, ms: Date.now() - t0
    }); } catch (_) {}
    if (!ok) throw new Error('audio-codec-unsupported-' + codec);
    return { needsTranscode: true };
}

// ─────────────────────────────────────────────────────────────────────
// LAZY AUDIO TRANSCODE — AudioDecoder → AudioEncoder relay over the sample
// table (2026-09-17)
// ─────────────────────────────────────────────────────────────────────
// muxSpeedAudio below decodes the WHOLE file's audio into one AudioBuffer
// (decodeAudioData, or WebCodecsAudioDecode which itself reads the whole
// file) — the right shape for a speed change, which has to see the entire
// track, and the wrong one for the plain case that is 30× more common: same
// speed, no gain, no fade, just an audio codec the muxer will not carry
// (MP3 / Opus / Vorbis / FLAC / HE-AAC inside an mp4). That path put a
// second whole copy of the input on the heap, so /video-crop routed every
// non-AAC file to the single-thread x264 lane instead (hw_crop_skipped
// audio_mp3 / audio_opus: 13 sessions/30 d on RU).
//
// This relay keeps the pipeline's memory shape: access units come off the
// disk in 8 MB windows through the same _lazySampleReader the video feed
// uses, each decoded AudioData is re-timed for the trim window and handed
// straight to the AAC AudioEncoder (through AacAccessUnit — the door every
// AAC-in-MP4 must take), and nothing outlives its window. It applies only
// when the decoded format already matches the planned track (44.1/48 kHz,
// ≤2 ch — no resampler here) and there is no leading silence to synthesise;
// every other case keeps muxSpeedAudio. Preflight returns null BEFORE any
// chunk reaches the muxer, so the caller can still take the old path; once
// a chunk is muxed, a failure throws — two audio tracks would be worse.
function lazyAudioDecoderConfig(a) {
    if (!a || !a.codec) return null;
    var c = String(a.codec).toLowerCase();
    var cfg = { sampleRate: a.sampleRate, numberOfChannels: a.channels };
    if (!(cfg.sampleRate > 0) || !(cfg.numberOfChannels > 0)) return null;
    if (c === 'mp4a.6b' || c === 'mp3' || c === '.mp3') cfg.codec = 'mp3';
    else if (c.indexOf('opus') === 0) cfg.codec = 'opus';
    else if (c.indexOf('flac') === 0) cfg.codec = 'flac';
    else if (c.indexOf('vorbis') === 0) cfg.codec = 'vorbis';
    else if (/^mp4a\.40\.(5|29)$/.test(c)) { cfg.codec = c; if (a.ascBytes && a.ascBytes.length) cfg.description = a.ascBytes; else return null; }
    else return null;
    // A Matroska track brings the decoder its own setup: Vorbis cannot decode
    // without its three headers, Opus needs OpusHead for >2 channels / pre-skip,
    // FLAC its STREAMINFO. mp4 sources carry no `description` here (unchanged).
    if (!cfg.description && a.description && a.description.length && /^(opus|vorbis|flac)$/.test(cfg.codec)) cfg.description = a.description;
    if (cfg.codec === 'vorbis' && !cfg.description) return null;
    return cfg;
}

async function muxAudioLazy(demuxInfo, muxer, isAborted, edit) {
    edit = edit || {};
    var a = demuxInfo.audio;
    if (!demuxInfo.lazy || !a || !a.samples || !a.samples.length) return null;
    if (a.pcm) return await _muxPcmLazy(demuxInfo, muxer, isAborted, edit);
    if (typeof AudioDecoder === 'undefined' || typeof AudioData === 'undefined' || typeof EncodedAudioChunk === 'undefined') return null;
    if (typeof AudioEncoder === 'undefined') return null;   // muxSpeedAudio makes the AAC without one (_muxAacViaFfmpeg)
    var cfg = lazyAudioDecoderConfig(a);
    if (!cfg) return null;
    var outRate = +edit.outRate > 0 ? +edit.outRate : cfg.sampleRate;
    var outCh = +edit.outChannels > 0 ? Math.min(2, +edit.outChannels) : Math.min(2, cfg.numberOfChannels);
    /* What the decoder REALLY emits, by deed (WebCodecsAudioDecode.relayFormat):
       the sample entry is not the stream. An HE-AAC v2 track the header calls
       stereo 44.1 kHz comes out MONO in Chrome/Edge and labelled 88.2 kHz in
       Yandex, and an encoder planned from the header threw «Input audio buffer
       is incompatible», hidden by the next unit's «closed codec» — the
       pipeline's top audio failure (2026-10-08, reproduced in both browsers).
       No resampler in this relay: a stream at another RATE than the planned
       track stays on muxSpeedAudio; the label and the channel count are
       conformed unit by unit (conformAudioData). */
    var WAD = window.WebCodecsAudioDecode;
    if (!WAD || typeof WAD.relayFormat !== 'function' || typeof WAD.conformAudioData !== 'function') return null;
    var rd = _lazySampleReader(demuxInfo);
    var fmt = await WAD.relayFormat(cfg, a.samples, function (sm) { return rd.bytes(sm, false); });
    if (!fmt || fmt.sampleRate !== outRate) return null;
    cfg = fmt.config;
    await ensureAacAccessUnit();
    var tStart = +edit.trimStart > 0 ? +edit.trimStart : 0;
    var tEnd = +edit.trimEnd > 0 ? +edit.trimEnd : 0;
    var doTrim = tEnd > tStart;
    var startUs = Math.round(tStart * 1e6), endUs = doTrim ? Math.round(tEnd * 1e6) : Infinity;

    /* The FIRST error is the one that happened: a codec closes itself on its
       own error and queues the callback, and the next encode() throws
       «Cannot call 'encode' on a closed codec» before that callback lands —
       which is what the field saw (59 of them in 30 days). */
    var encErr = null, decErr = null, closedCall = false, first = true, muxed = 0, encInFlight = 0;
    var aenc = window.AacAccessUnit.encoder({
        output: function (chunk, meta) {
            try { muxer.addAudioChunk(chunk, (first && meta) ? meta : undefined); muxed++; first = false; }
            catch (e) { encErr = encErr || e; }
        },
        error: function (e) { encErr = encErr || e; }
    }, { slug: window.__TOOL_SLUG });
    aenc.configure({ codec: 'mp4a.40.2', sampleRate: outRate, numberOfChannels: outCh, bitrate: 192000 });

    // Re-time a decoded unit into output time (0 = the cut) and drop what is
    // outside the window. Units straddling the cut are kept whole — at most
    // one AAC-frame-sized slack, the same tolerance the passthrough has.
    var fedUnits = 0;
    function relay(raw) {
        var data = raw;
        try {
            data = WAD.conformAudioData(raw, outRate, outCh);
            if (data !== raw) raw.close();
            var ts = data.timestamp, durUs = Math.round(data.numberOfFrames * 1e6 / data.sampleRate);
            if (ts + durUs <= startUs || ts >= endUs) { data.close(); return; }
            var outTs = Math.max(0, ts - startUs);
            if (outTs === data.timestamp) { aenc.encode(data); data.close(); return; }
            // Copy the planes into a fresh AudioData at the new timestamp.
            var planar = /planar$/.test(data.format || '');
            var planes = planar ? data.numberOfChannels : 1;
            var bytesPerSample = /^(u8)/.test(data.format || '') ? 1 : /^(s16)/.test(data.format || '') ? 2 : 4;
            var perPlane = data.numberOfFrames * (planar ? 1 : data.numberOfChannels) * bytesPerSample;
            var buf = new Uint8Array(perPlane * planes);
            for (var pi = 0; pi < planes; pi++) data.copyTo(buf.subarray(pi * perPlane, (pi + 1) * perPlane), { planeIndex: pi });
            var re = new AudioData({ format: data.format, sampleRate: data.sampleRate, numberOfFrames: data.numberOfFrames,
                                     numberOfChannels: data.numberOfChannels, timestamp: outTs, data: buf });
            data.close();
            aenc.encode(re); re.close();
        } catch (e) {
            if (aenc.state === 'closed') closedCall = true; else encErr = encErr || e;
            try { data.close(); } catch (_) {}
            try { raw.close(); } catch (_) {}
        }
    }
    var dec = new AudioDecoder({ output: relay, error: function (e) { decErr = decErr || e; } });
    try { dec.configure(cfg); } catch (e) { try { aenc.close(); } catch (_) {} return null; }
    async function closedError() {
        await _yieldTick(0);                                   // let the queued error callback land
        if (encErr) return encErr;
        if (decErr) return new Error('audio-decode: ' + ((decErr && decErr.message) || decErr));
        return new Error(closedCall || aenc.state === 'closed' ? 'audio-encode: encoder closed' : 'audio-decode: decoder closed');
    }

    var samples = a.samples;
    try {
        for (var i = 0; i < samples.length; i++) {
            if (isAborted && isAborted()) throw new DOMException('cancelled', 'AbortError');
            if (encErr) throw encErr;
            if (decErr) throw new Error('audio-decode: ' + ((decErr && decErr.message) || decErr));
            if (closedCall || aenc.state === 'closed' || dec.state === 'closed') throw await closedError();
            var sm = samples[i];
            // Skip units wholly before the cut — minus one for the decoder's
            // warm-up (an MP3/AAC frame needs its predecessor's overlap).
            if (doTrim && i + 1 < samples.length && (samples[i + 1].timestamp + (samples[i + 1].duration || 0)) <= startUs) continue;
            if (sm.timestamp >= endUs + 200000) break;
            var bytes = sm.data || await rd.bytes(sm, false);
            if (dec.state === 'closed') throw await closedError();
            dec.decode(new EncodedAudioChunk({ type: 'key', timestamp: sm.timestamp, duration: sm.duration || 0, data: bytes }));
            fedUnits++;
            if (typeof edit.onProgress === 'function' && (i & 511) === 0) { try { edit.onProgress(i / samples.length); } catch (_) {} }
            while (dec.decodeQueueSize > 32) { await _yieldTick(0); if (decErr) break; }
        }
        if (dec.state === 'closed') throw await closedError();
        await dec.flush();
        if (decErr) throw new Error('audio-decode: ' + ((decErr && decErr.message) || decErr));
        if (closedCall || aenc.state === 'closed') throw await closedError();
        await aenc.flush();
    } catch (e) {
        if (e && e.name === 'AbortError') throw e;
        // A flush/encode that threw because its codec had closed on an error
        // of its own: the error is the codec's, not the call's.
        if (!encErr && !decErr && (closedCall || aenc.state === 'closed' || dec.state === 'closed')) throw await closedError();
        throw encErr || e;
    } finally {
        try { dec.close(); } catch (_) {}
        try { aenc.close(); } catch (_) {}
    }
    if (encErr) throw encErr;
    if (!muxed) throw new Error('audio-encode-no-output');
    try { if (typeof window.__ae === 'function') window.__ae('feature_use', { slug: edit.slug || (window.__currentSlug || 'webcodecs'), action: 'audio_transcode_lazy', codec: cfg.codec, units: fedUnits, muxed: muxed, reads: rd.stats().reads,
        sr: fmt.sampleRate, ch: fmt.channels, hdr: (a.sampleRate || 0) + '/' + (a.channels || 0), label: fmt.labelRate }); } catch (_) {}
    return muxed;
}

/* AAC without an AudioEncoder (2026-10-08).
   Safari before 26 — every iPhone and iPad we see — has VideoEncoder and
   AudioDecoder but no AudioEncoder, and muxSpeedAudio threw
   'hw-audio-unavailable' at its first line: any audio edit (speed, gain, fade,
   trim) sent the WHOLE run to FFmpeg-WASM on the phone. RU, 14 days, /video-
   speed alone: 46 sessions (Safari 29, Yandex iOS 17), and the HW-fallback
   class there leaves 39 % without a file. Everything before the encoder —
   decode, trim, stretch, gain, resample — is WebAudio and runs there; only the
   AAC is missing. So FFmpeg encodes JUST the prepared track (seconds of work,
   not the picture), as ADTS, and its access units go into the same muxer track:
   the video stays on the fast lane.
   ⚠️ FFmpeg's AAC encoder primes 1024 samples (initial_padding), ADTS carries
   no edit list, and mp4-muxer writes none — the first frame is dropped, so
   frame k (k ≥ 1) starts at (k−1)·1024 and the sound sits on the picture. */
function _pcm16Wav(buf) {
    var ch = buf.numberOfChannels, n = buf.length, sr = buf.sampleRate;
    var out = new ArrayBuffer(44 + n * ch * 2), v = new DataView(out);
    function str(o, s) { for (var i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i)); }
    str(0, 'RIFF'); v.setUint32(4, 36 + n * ch * 2, true); str(8, 'WAVE'); str(12, 'fmt ');
    v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, ch, true);
    v.setUint32(24, sr, true); v.setUint32(28, sr * ch * 2, true); v.setUint16(32, ch * 2, true);
    v.setUint16(34, 16, true); str(36, 'data'); v.setUint32(40, n * ch * 2, true);
    var planes = [];
    for (var c = 0; c < ch; c++) planes.push(buf.getChannelData(c));
    var p = 44;
    for (var i = 0; i < n; i++) {
        for (var c2 = 0; c2 < ch; c2++) {
            var x = planes[c2][i]; x = x < -1 ? -1 : (x > 1 ? 1 : x);
            v.setInt16(p, x < 0 ? x * 0x8000 : x * 0x7FFF, true); p += 2;
        }
    }
    return new Blob([out], { type: 'audio/wav' });
}
async function _muxAacViaFfmpeg(buf, muxer, isAborted) {
    var t0 = performance.now();
    await ensureAacAccessUnit();
    var AAU = window.AacAccessUnit;
    if (typeof window.__ensureFFmpeg === 'function') { try { await window.__ensureFFmpeg(); } catch (_) {} }
    var P = window.FFmpegProcessor;
    if (!AAU || typeof AAU.parseAdts !== 'function' || !P || typeof P.process !== 'function') throw new Error('hw-audio-unavailable');
    if (typeof P.isReady === 'function' && !P.isReady()) {
        var ready = await P.init();
        if (!ready) throw new Error('hw-audio-unavailable');
    }
    if (isAborted && isAborted()) throw new DOMException('cancelled', 'AbortError');
    var sampleRate = buf.sampleRate, channels = buf.numberOfChannels;
    var r = await P.process(new File([_pcm16Wav(buf)], 'hw_aud.wav', { type: 'audio/wav' }),
        ['-c:a', 'aac', '-b:a', '192k', '-f', 'adts'], 'hw_aud.aac', 'hw_aud.wav');
    if (isAborted && isAborted()) throw new DOMException('cancelled', 'AbortError');
    var out = r && (r.blob || r);
    if (!out || !out.size) throw new Error('audio-encode-no-output');
    var b = new Uint8Array(await out.arrayBuffer());
    var frames = [], off = 0;
    while (off < b.length) {
        var h = AAU.parseAdts(b, off);
        if (!h) break;
        frames.push(b.slice(off + h.headerLen, off + h.frameLen));
        off += h.frameLen;
    }
    if (frames.length < 2) throw new Error('audio-encode-no-output');
    var spf = 1024 * 1e6 / sampleRate;
    var meta = { decoderConfig: { codec: 'mp4a.40.2', sampleRate: sampleRate, numberOfChannels: channels,
        description: AAU.asc({ aot: 2, sampleRate: sampleRate, channels: channels }) } };
    for (var k = 1; k < frames.length; k++) {
        muxer.addAudioChunkRaw(frames[k], 'key', Math.round((k - 1) * spf), Math.round(spf), k === 1 ? meta : undefined);
    }
    try { if (typeof window.__ae === 'function') window.__ae('feature_use', { slug: window.__TOOL_SLUG || 'webcodecs', action: 'audio_aac_via_ffmpeg',
        frames: frames.length - 1, sec: Math.round(buf.duration), ms: Math.round(performance.now() - t0) }); } catch (_) {}
    return frames.length - 1;
}

/* Copy the source's AAC access units into the muxer as they are — no codec
   involved, so no codec API either (15.10.2026). Both passthroughs (run and
   runInterpolation) used to wrap every unit in an EncodedAudioChunk, which
   is WebCodecs AUDIO: Safari ships it only from 26. On Safari 17/18 the first
   `new EncodedAudioChunk` threw, the catch swallowed it, the file came out
   silent, and the caller threw the whole encoded picture away
   («hw-audio-dropped»): RU, 30 days to 15.10, desktop Safari 17/18 finished
   «Быстро» in 0 of 7 sessions, while Safari 26 did. mp4-muxer's
   addAudioChunkRaw is what addAudioChunk calls after copying the chunk out,
   so the file is byte-for-byte the same in every browser.
   o: { trimStartUs, trimEndUs (a window when trimEndUs > trimStartUs),
        onProgress(frac), slug, stage }.
   Never throws: returns { muxed, err } — the caller judges an output without
   its sound — and a failure is now REPORTED (it used to reach console.warn
   only, so the field could not say why sound was lost). */
async function muxAudioPassthrough(demuxInfo, muxer, o) {
    o = o || {};
    var a = demuxInfo.audio, muxed = 0, ai = 0;
    var n = (a && a.samples) ? a.samples.length : 0;
    if (!n) return { muxed: 0, err: null };
    var t0 = +o.trimStartUs || 0, t1 = +o.trimEndUs || 0, doTrim = t1 > t0;
    try {
        // The source's own esds when the demuxer found one — an AAC-LC config
        // fabricated from the header drops the SBR/PS extension of an HE-AAC
        // stream that MP4Box still spells `mp4a.40.2` (backward-compatible
        // signalling: GPAC, Nero, most phones). Copied bytes under a copied
        // config; the builder stays for esds-less .mov.
        var asc = (a.ascBytes && a.ascBytes.length) ? a.ascBytes : buildAacAsc(a.sampleRate, a.channels);
        var first = true;        // decoderConfig rides the first EMITTED unit
        var rd = demuxInfo.lazy ? _lazySampleReader(demuxInfo) : null;
        var cfg = { decoderConfig: { codec: a.codec || 'mp4a.40.2', sampleRate: a.sampleRate, numberOfChannels: a.channels, description: asc } };
        // A track that starts after the picture: silent AAC-LC frames first
        // (_silentLeadPlan), the source's own units after them.
        var lead = (o.lead && o.lead.frames > 0) ? o.lead : null, shift = 0;
        if (lead) {
            for (var li = 0; li < lead.frames; li++) {
                muxer.addAudioChunkRaw(lead.frame.slice(), 'key', Math.round(li * lead.frameUs), Math.round(lead.frameUs), first ? cfg : undefined);
                first = false;
            }
            shift = Math.round(lead.frames * lead.frameUs);
        }
        for (ai = 0; ai < n; ai++) {
            var s = a.samples[ai];
            var ts = s.timestamp, dur = s.duration || 0;
            if (doTrim) {
                // Drop audio outside the clip window and rebase — without this the
                // muxer keeps the full-length audio track and the container
                // duration ignores the (shorter) trimmed video.
                if (ts + dur <= t0 || ts >= t1) continue;
                ts = Math.max(0, ts - t0);
            }
            var b = s.data || (rd ? await rd.bytes(s, false) : null);
            if (!b) throw new Error('audio sample ' + ai + ' has no bytes');
            /* The tail is real work: audio interleaved with every video frame is
               read back through 8 MB windows — the whole file again (a 16-min 4K
               mp4: 22.4 s, 521 reads, 4.2 GB, measured 01.10.2026; smaller
               windows or exact runs are SLOWER, a read costs ~5 ms). Without a
               word from here the callers' «no progress for 30 s» watchdogs killed
               finished encodes at 95 % (/video-compressor, 24.7 s of silence on a
               4.4 GB file, then a stall). */
            if ((ai & 511) === 0 && typeof o.onProgress === 'function') { try { o.onProgress(ai / n); } catch (_) {} }
            /* A COPY: the reader hands out a view into its 8 MB window, and the
               muxer keeps what it is given until the chunk is written — a view
               would pin every window of the file. (The EncodedAudioChunk this
               replaces copied too.) */
            var bytes = (b instanceof Uint8Array) ? b.slice() : new Uint8Array(b);
            muxer.addAudioChunkRaw(bytes, 'key', ts + shift, dur, first ? cfg : undefined);
            first = false;
            muxed++;
        }
        if (lead) try { if (typeof window.__ae === 'function') window.__ae('feature_use', { slug: o.slug || 'webcodecs',
            action: 'audio_lead_silent', frames: lead.frames, lead_ms: Math.round(shift / 1000), stage: o.stage || 'main' }); } catch (_) {}
        return { muxed: muxed, err: null };
    } catch (e) {
        _aeWebcodecsErr(o.slug, 'audio_passthrough', e, {
            stage: o.stage || 'main', at: ai, of: n, muxed: muxed,
            codec: String((a && a.codec) || '').slice(0, 20), err_name: (e && e.name) || ''
        });
        return { muxed: muxed, err: e };
    }
}

async function muxSpeedAudio(file, muxer, speed, demuxInfo, isAborted, edit) {
    edit = edit || {};
    var _gain    = (typeof edit.gain === 'number' && isFinite(edit.gain)) ? edit.gain : 1;
    var _fadeIn  = +edit.fadeIn  > 0 ? +edit.fadeIn  : 0;
    var _fadeOut = +edit.fadeOut > 0 ? +edit.fadeOut : 0;
    var _tStart  = +edit.trimStart > 0 ? +edit.trimStart : 0;
    var _tEnd    = +edit.trimEnd   > 0 ? +edit.trimEnd   : 0;
    var _doTrim  = _tEnd > _tStart;
    /* No AudioEncoder (Safari before 26 on every iPhone and iPad we see):
       the PCM below is all WebAudio and works there; only the AAC is made
       elsewhere — see _muxAacViaFfmpeg. */
    var _noNativeAac = (typeof AudioData === 'undefined' || typeof AudioEncoder === 'undefined');
    var srcBuf = await _decodeWholeAudio(file, isAborted, demuxInfo);

    /* Leading empty edit: the audio track starts `leadSec` after the movie.
       The decoded buffer is the track's MEDIA (WebCodecsAudioDecode starts it
       at the edit's media_time and never pads the lead), so the lead becomes
       silence in front — on the presentation timeline, BEFORE any trim. The
       native decoder is the one reader that might already have padded it;
       a buffer that is long enough to contain the lead is left alone. */
    var _leadSec = +edit.leadSec > 0 ? +edit.leadSec : 0;
    if (_leadSec > 0) {
        var _mediaSec = (demuxInfo && demuxInfo.audio && demuxInfo.audio.durationSec) || 0;
        var _alreadyPadded = srcBuf.__via === 'native' && _mediaSec > 0 && srcBuf.duration >= _mediaSec + _leadSec - 0.05;
        if (!_alreadyPadded) {
            var _srL = srcBuf.sampleRate, _padN = Math.round(_leadSec * _srL);
            var _padded = new AudioBuffer({ length: srcBuf.length + _padN, numberOfChannels: srcBuf.numberOfChannels, sampleRate: _srL });
            for (var _cL = 0; _cL < srcBuf.numberOfChannels; _cL++) _padded.copyToChannel(srcBuf.getChannelData(_cL), _cL, _padN);
            srcBuf = _padded;
        }
    }

    /* Trim FIRST, in source time — matches FFmpeg's -ss/-to preceding the
       filter chain, and keeps the stretch working on only the kept window. */
    if (_doTrim) {
        var _sr0 = srcBuf.sampleRate;
        var _from = Math.max(0, Math.min(srcBuf.length, Math.round(_tStart * _sr0)));
        var _to   = Math.max(_from, Math.min(srcBuf.length, Math.round(_tEnd * _sr0)));
        var _len  = Math.max(1, _to - _from);
        var _cut  = new AudioBuffer({ length: _len, numberOfChannels: srcBuf.numberOfChannels, sampleRate: _sr0 });
        for (var _c = 0; _c < srcBuf.numberOfChannels; _c++) {
            _cut.copyToChannel(srcBuf.getChannelData(_c).subarray(_from, _to), _c);
        }
        srcBuf = _cut;
    }

    // rate=speed → output duration = inputDuration / speed (matches the video).
    var stretched;
    if (speed === 1) {
        stretched = srcBuf;                       // no time change requested
    } else if (window.AudioPipeline && AudioPipeline.stretch) {
        // No content word: a video's track can be a voice or a song — the door
        // asks the classifier (speech → WSOLA, music → vocoder). The slug puts
        // the engine telemetry on the tool, not on 'unknown'.
        stretched = await AudioPipeline.stretch(srcBuf, { rate: speed, semitones: 0, slug: window.__TOOL_SLUG || null });
    } else if (window.PitchEngine && PitchEngine.shift) {
        stretched = await PitchEngine.shift(srcBuf, { rate: speed, semitones: 0 });
    } else {
        throw new Error('no-audio-stretch-engine');
    }
    if (!stretched || !stretched.length) throw new Error('audio-stretch-empty');
    if (isAborted && isAborted()) throw new DOMException('cancelled', 'AbortError');

    /* Gain + fades, on the FINAL timeline (post-stretch), mirroring
       `volume` → `afade=t=in:st=0` → `afade=t=out` in the FFmpeg chain.
       Written in place on a private copy — `stretched` is either our own
       trimmed buffer or the stretch engine's fresh output, never the
       caller's. Skipped entirely when nothing was asked for. */
    if (_gain !== 1 || _fadeIn > 0 || _fadeOut > 0) {
        var _sr = stretched.sampleRate, _n = stretched.length;
        var _outStart = _fadeOut > 0 ? Math.max(0, _n - Math.round(_fadeOut * _sr)) : _n;
        var _inEnd    = _fadeIn  > 0 ? Math.min(_n, Math.round(_fadeIn * _sr))      : 0;
        var _edited = new AudioBuffer({ length: _n, numberOfChannels: stretched.numberOfChannels, sampleRate: _sr });
        for (var _ch = 0; _ch < stretched.numberOfChannels; _ch++) {
            var _src = stretched.getChannelData(_ch);
            var _dst = new Float32Array(_n);
            for (var _i = 0; _i < _n; _i++) {
                var _g = _gain;
                if (_i < _inEnd) _g *= _i / _inEnd;
                if (_i >= _outStart && _n > _outStart) _g *= (_n - _i) / (_n - _outStart);
                _dst[_i] = _src[_i] * _g;
            }
            _edited.copyToChannel(_dst, _ch);
        }
        stretched = _edited;
        if (isAborted && isAborted()) throw new DOMException('cancelled', 'AbortError');
    }
    // AAC AudioEncoder accepts only 44100/48000 Hz (and ≤2 channels). Some
    // devices hand back odd rates (Bluetooth-routed contexts at 8–16 kHz,
    // exotic sources) → "Unsupported sample rate" killed the whole HW run
    // (7/14d on prod). Resample via OfflineAudioContext — it also folds >2
    // channels down to stereo in the same render.
    // The caller passes the format it already built the muxer track with
    // (plannedAudioOut) and we land on it exactly — decodeAudioData renders at
    // the AudioContext's device rate (48 kHz on most Windows boxes), which is
    // otherwise neither the source's nor the one the track timescale states.
    var _tgtRate = +edit.outRate > 0 ? +edit.outRate
        : ((stretched.sampleRate === 44100 || stretched.sampleRate === 48000) ? stretched.sampleRate : 48000);
    var _tgtCh = +edit.outChannels > 0 ? Math.min(2, +edit.outChannels)
        : Math.min(2, stretched.numberOfChannels);
    if (stretched.sampleRate !== _tgtRate || stretched.numberOfChannels !== _tgtCh) {
        var _tgtLen = Math.max(1, Math.ceil(stretched.duration * _tgtRate));
        var _oac = new OfflineAudioContext(_tgtCh, _tgtLen, _tgtRate);
        var _osrc = _oac.createBufferSource();
        _osrc.buffer = stretched; _osrc.connect(_oac.destination); _osrc.start();
        stretched = await _oac.startRendering();
        if (isAborted && isAborted()) throw new DOMException('cancelled', 'AbortError');
    }
    var sampleRate = stretched.sampleRate, channels = stretched.numberOfChannels;
    if (_noNativeAac) return await _muxAacViaFfmpeg(stretched, muxer, isAborted);
    var encErr = null, first = true, muxed = 0;
    // Through the AAC door (raw access units + guaranteed description), never
    // a bare AudioEncoder — see aac-access-unit.js, report #6990.
    await ensureAacAccessUnit();
    var aenc = window.AacAccessUnit.encoder({
        output: function (chunk, meta) {
            try { muxer.addAudioChunk(chunk, (first && meta) ? meta : undefined); muxed++; first = false; }
            catch (e) { encErr = encErr || e; }
        },
        error: function (e) { encErr = encErr || e; }
    }, { slug: window.__TOOL_SLUG });
    aenc.configure({ codec: 'mp4a.40.2', sampleRate: sampleRate, numberOfChannels: channels, bitrate: 192000 });
    var BLOCK = sampleRate;            // ~1 s of audio per AudioData
    var total = stretched.length;
    var chans = [];
    for (var c = 0; c < channels; c++) chans.push(stretched.getChannelData(c));
    for (var off = 0; off < total; off += BLOCK) {
        if (encErr) throw encErr;
        if (isAborted && isAborted()) { try { aenc.close(); } catch (_) {} throw new DOMException('cancelled', 'AbortError'); }
        var n = Math.min(BLOCK, total - off);
        var inter = new Float32Array(n * channels);   // f32 interleaved
        for (var ch = 0; ch < channels; ch++) {
            var cd = chans[ch];
            for (var i = 0; i < n; i++) inter[i * channels + ch] = cd[off + i];
        }
        var adata = new AudioData({
            format: 'f32', sampleRate: sampleRate, numberOfFrames: n,
            numberOfChannels: channels,
            timestamp: Math.round((off / sampleRate) * 1e6), data: inter
        });
        aenc.encode(adata);
        adata.close();
    }
    await aenc.flush();
    try { aenc.close(); } catch (_) {}
    if (encErr) throw encErr;
    if (!muxed) throw new Error('audio-encode-no-output');
    return muxed;
}

// ─────────────────────────────────────────────────────────────────────
// VIDEO COLOUR LABEL — what `colr` says about the pixels we encoded
// ─────────────────────────────────────────────────────────────────────

/* The rig draws the decoded video into a canvas and hands the encoder CANVAS
   frames, which browsers label sRGB (transfer iec61966-2-1); the encoder copies
   the label into its decoderConfig and mp4-muxer into the `colr` box. The pixels
   are not sRGB-coded: they are the source's own BT.709 gamma-coded values (no
   transfer conversion happens on the way — an HDR source was mapped to BT.709
   SDR by the door), so the label must say bt709. Players that colour-manage by
   the label (Apple's Photos, QuickTime, Safari) otherwise show the file with
   another tone curve than its source: an iPhone 1-1-1 clip came out 1-13-1
   (bug report 25.10.2026, «качество стало еще хуже»). Matrix and range stay the
   encoder's — they describe the RGB→YUV it actually did. Transfers that ARE the
   pixels' (pq, hlg, smpte170m…) are left alone. */
function muxColorSpace(cs) {
    if (!cs || typeof cs !== 'object') return cs;
    if (cs.transfer !== 'iec61966-2-1' && cs.transfer) return cs;
    return Object.assign({}, cs, { transfer: 'bt709', primaries: cs.primaries || 'bt709' });
}

// ─────────────────────────────────────────────────────────────────────
// VIDEO BITRATE — one owner for run() and runInterpolation()
// ─────────────────────────────────────────────────────────────────────

/* The source's own bitrate, re-expressed for a smaller output frame — in the
   bits OUR H.264 needs for it.

   Both lanes bound the encoder by what the SOURCE spends: a floor at 85 %
   (an enhance filter must never compress below its source, #494) and, at the
   same size, a ceiling at 1.3× (dfb0fd7d). Those are «this many bits for this
   picture» — right while the output IS that picture, wrong the moment it is
   smaller. Until 2026-09-30 a downscale kept the full-size number: «720p» of a
   12.6 Mbit/s 1080p clip was floored at 10.7 Mbit/s (29.1 MB out of a 31.5 MB
   source), 720p of a 40 Mbit/s 4K clip at 34 Mbit/s (38.9 MB out of 50.1 MB).
   And a downscale is the common case, not the odd one: the guest plan ceiling
   is 720p, so every 1080p/4K file a guest brings to /video-speed,
   /enhance-video or /video-filter IS one.

   A smaller frame of the same scene needs fewer bits, but not proportionally
   fewer — each output pixel carries more of the scene's detail — so the bits
   follow the pixel count to the 0.75 power (1080p → 720p: 0.54 of the bits for
   0.44 of the pixels; 4K → 1080p: 0.35 for 0.25). And a source in HEVC / VP9 /
   AV1 carries its picture in fewer bits than H.264 can: re-sized alone, a
   4K HEVC phone clip at 720p fell to VMAF 80 (H.264 sources of the same scene:
   95), so its number counts ×1.7. Quality first (decision 2026-09-30): ×1.4,
   the converter's earlier factor, still left that clip at VMAF 89; ×1.7 is
   the smallest step of the measured ladder (1.0 / 1.4 / 1.7 / 2.0) that puts
   it over 92 while the file stays a third of what it was. Measured in
   Chrome's hardware H.264 on real footage, VMAF at the output size against
   the source scaled to it — the table and the ladder are in
   tests/js/webcodecs-pipeline-downscale-bitrate.test.mjs.

   Same size or larger → the source's number untouched (no exponent, no codec
   gain): every same-size run asks the encoder for the same bitrate as before,
   to the bit. ONE formula: /video-converter's explicit downscale bitrate
   (VideoConvertPlan.hwBitrate) asks this same function instead of keeping
   copies of the exponent and the gain. */
var DOWNSCALE_BITS_EXPONENT = 0.75;
var DENSE_SOURCE_GAIN = 1.7;
// srcCodec: an RFC 6381 string from the demux ('hvc1.1.6.L150.90',
// 'vp09.00.10.08', 'av01…', 'avc1…') or a probe's short name ('hevc', 'vp9').
function sourceCodecGain(srcCodec) {
    return /^(hvc1|hev1|hevc|h265|vp09|vp9|av01|av1)\b/i.test(String(srcCodec || '')) ? DENSE_SOURCE_GAIN : 1;
}
function sourceBpsForOutput(srcBps, srcPx, outPx, srcCodec) {
    srcBps = +srcBps;
    if (!(srcBps > 0)) return 0;
    if (!(srcPx > 0) || !(outPx > 0) || outPx >= srcPx) return srcBps;
    return srcBps * Math.pow(outPx / srcPx, DOWNSCALE_BITS_EXPONENT) * sourceCodecGain(srcCodec);
}

/* The 1.3× ceiling on an output that keeps the source's pixels grows for a
   STARVED source — up to 3× its bits, never past 0.10 bit per pixel per frame
   nor the picked quality's own table (2026-10-29). 1.3× is «this picture plus what the filters add» —
   true while the SOURCE spent enough. A download from a video site does not:
   x264 two-pass at ~0.03 bit/px, which our hardware H.264 cannot hold at 1.3×.
   Support chat 731 (/enhance-video): a 720p lecture at 729 kbit/s came back at
   948 kbit/s, «каким было, таким и осталось».
   Measured on three real recordings degraded the way a download is (720p25,
   x264 2-pass 600 kbit/s), sharpness 30, hardware H.264, VMAF NEG against the
   true 1080p, Δ to the input (lecture / board with text / archive film):
     1.3× (0.041 bit/px)  +0.7 / +0.5 / −2.2   ← the archive film WORSE than its input
     2×   (0.064)         +1.2 / +1.1 / −0.8
     3×   (0.095)         +1.4 / +1.3 /  0.0
     4.5× (0.143)         +1.5 / +1.4 / +0.3
   1080p25 at 0.03 bit/px (the lecture): 1.3× +0.1, 0.10 bit/px +0.7.
   The film's grain is what the encoder drops first; 0.10 (3× here) is where
   none of the three is below its input, and past 3× the gain is +0.1…+0.3 —
   bytes, not picture: a near-static screen recording at 0.008 bit/px is not
   inflated to the table (×18). «Среднее» (0.075) and «Низкое» stop at their
   table — smaller files by choice, but not a 1.3× that is worse still. A source
   that spent more keeps its 1.3×. RU 30 d: 17 % of /enhance-video exports start
   under 0.05 bit/px. Every tool on run() at the source's size gets it (crops
   too — per output pixel; interpolation — ×its frame-rate ratio). */
var KEEP_PIXELS_MIN_BPP = 0.10;
var STARVED_GROWTH_MAX = 3;

// Map CRF (18..30) → bitrate (12 Mbps .. 1.5 Mbps) for VideoEncoder, bounded by
// the source (sourceBitrateBps — already expressed for THIS output, see
// sourceBpsForOutput). One copy for both lanes: run() used to carry one and
// runInterpolation() a second, identical but for the frame-rate allowance of
// the ceiling (capOpts.outputFps / sourceFps).
function crfToBitrate(crf, w, h, fps, sourceBitrateBps, capOpts) {
    // ──────────────────────────────────────────────────────────────
    // ROOT-CAUSE FIX (comment #494 Diggezz, 2026-05-22): the previous
    // formula `pixels × 2^((28-crf)/4) × 0.04` produced 0.5 Mbps for
    // 1080p at CRF 18, capped at a 500 kbps floor. Industry target for
    // visually-lossless 1080p (CRF 18) is 8-12 Mbps. We were under-
    // shooting by 15-25×, which is why users uploading 40+ Mbps
    // source files reported the re-encode "compressed instead of
    // enhanced" (their 245 MB clip came back at ~3 MB with massive
    // detail loss).
    //
    // FOLLOW-UP — bug-report 2026-06-01 (Opera, sid dfb0fd7d):
    // "Два часа промурыжил, а качество видео как было 360 так и
    // осталось. Лишь объём вырос в два раза." User picked Sharpen on
    // a 308 MB / 360p source (~0.55 Mbps actual source bitrate); HW
    // pipeline computed CRF-18 target = 1.24 Mbps for 360p; output
    // came back 651 MB = 2.1× source with no perceptible quality
    // change (a CRF-18 reencode CAN'T restore detail that was
    // already lost in source compression — it just spends more bits
    // on the same blocks). Audit found this is a 31 % / 21 % cluster
    // (295/958 exports with size > 1.5× src; 204/958 doubled).
    //
    // Design — four-stage:
    //   (1) CRF-driven target: pixels × fps × qFactor.
    //   (2) Source-aware FLOOR: output ≥ 85 % of source (Diggezz).
    //   (3) Source-aware CEILING (NEW, Wave 4 2026-06-02): when the
    //       caller didn't request a resolution change (capOpts.noUpscale),
    //       cap output bitrate at max(min(FLOOR, source), source × 1.3).
    //       30 % growth allowance covers the filter chain's added
    //       complexity; absolute lower bound caps the FLOOR (1 Mbps)
    //       at the source's own bitrate so a 0.5 Mbps source doesn't
    //       force a 2× re-encode. If the user opts INTO an upscale
    //       (outW > inW or outH > inH), the cap is suppressed — they
    //       knowingly accept growth.
    //   (4) Absolute CAP at 60 Mbps — covers 4K, protects mobile HW
    //       encoders that wedge above their platform limit.
    // «Source» in (2) and (3) is the source's bitrate FOR THIS OUTPUT
    // SIZE (2026-09-30, sourceBpsForOutput) — the full-size number
    // floored every downscale at the bytes of the picture it shrank.
    // FFmpeg-fallback path is unaffected — it uses libx264's `-crf`
    // rate-control directly, which already does perceptual rate-
    // control and doesn't have this bug.
    var pixels = w * h;
    var fpsFactor = (fps && fps > 0) ? fps : 30;

    // qFactor calibrated to match x264 default-preset measurements:
    //   1080p @ 30fps CRF 18 ≈ 11.2 Mbps (visually lossless)
    //   1080p @ 30fps CRF 23 ≈  4.7 Mbps (default x264 high quality)
    //   1080p @ 30fps CRF 28 ≈  2.8 Mbps (medium)
    //    720p @ 30fps CRF 23 ≈  2.1 Mbps
    var qFactor;
    if (crf <= 18)      qFactor = 0.180;   // visually lossless
    else if (crf <= 21) qFactor = 0.120;   // high
    else if (crf <= 24) qFactor = 0.075;   // balanced (CRF 23 default)
    else if (crf <= 27) qFactor = 0.045;   // medium
    else                qFactor = 0.028;   // low / small file

    var computed = Math.floor(pixels * fpsFactor * qFactor);
    var FLOOR = 1_000_000;                              // 1 Mbps absolute floor
    var keepsPixels = !!(capOpts && capOpts.noUpscale && sourceBitrateBps && sourceBitrateBps > 0);
    // runInterpolation(): the frame-rate multiplier (60 fps interpolated
    // from a 30 fps source = 2× per-second density) legitimately needs
    // proportionally more bits, so the 1.3× growth allowance is
    // multiplied by (output_fps / source_fps) when both are known.
    // Without this, a 60-fps interpolation of a 0.5 Mbps source would cap
    // at 0.65 Mbps even though it needs ~1 Mbps for double the frames.
    // run() passes neither: the ratio is 1 and the ceiling is 1.3×.
    var fpsRatio = (keepsPixels && capOpts.outputFps && capOpts.sourceFps)
        ? Math.max(1, capOpts.outputFps / capOpts.sourceFps)
        : 1;
    // «Высокое» (CRF ≤ 18) on an output that keeps the source's pixels asks for
    // the ceiling itself (2026-10-26). A phone clip is 13–20 Mbit/s, the table's
    // CRF 18 for it 11 Mbit/s, so the 85 % floor decided every such export —
    // «Высокое», «Среднее» and «Низкое» gave one file, smaller than the source:
    // the bug report of 25.10 (iPhone 1080×1920, 15.5 Mbit/s → 13.2). Measured
    // through run() on six scenes × three kinds of phone footage (VMAF NEG
    // against the clean original, Δ to the input; Chrome hardware H.264): the
    // re-encode alone costs −3.2 / −3.5 / −2.5 NEG at 0.85×, −2.6 / −2.7 / −2.2
    // at 1×, −1.3 / −1.6 / −1.1 at 1.3× (detailed / dark-noisy / soft).
    var keepHigh = keepsPixels && crf <= 18;
    var sourceFloor = (sourceBitrateBps && sourceBitrateBps > 0)
        ? Math.floor(sourceBitrateBps * (keepHigh ? 1.3 * fpsRatio : 0.85))
        : 0;
    var CAP = 60_000_000;                               // 60 Mbps absolute cap
    var result = Math.min(CAP, Math.max(FLOOR, computed, sourceFloor));
    // No-upscale source-aware ceiling.
    if (keepsPixels) {
        var growthMax = Math.floor(sourceBitrateBps * 1.3 * fpsRatio);
        // Allow the cap to dip BELOW FLOOR (1 Mbps) only down to the
        // source's own bitrate — never force more bytes than the
        // source had if the user didn't ask for upscale. For a
        // 0.5 Mbps source, ceiling = max(0.5, 0.65) = 0.65 Mbps.
        var lowerBound = Math.min(FLOOR, sourceBitrateBps);
        var ceiling = Math.max(lowerBound, growthMax);
        // A starved source: up to 3× its bits — never past KEEP_PIXELS_MIN_BPP
        // nor the picked quality's own table (see KEEP_PIXELS_MIN_BPP).
        ceiling = Math.max(ceiling, Math.min(computed,
            Math.floor(pixels * fpsFactor * KEEP_PIXELS_MIN_BPP),
            Math.floor(sourceBitrateBps * STARVED_GROWTH_MAX * fpsRatio)));
        if (result > ceiling) {
            try {
                if (typeof window.__ae === 'function') {
                    var ev = {
                        slug: (window.__currentSlug || capOpts.slug || 'enhance-video'),
                        action: 'bitrate_cap_applied',
                        crf: crf, w: w, h: h
                    };
                    if (capOpts.outputFps) ev.fpsRatio = +fpsRatio.toFixed(2);
                    ev.src_kbps = Math.round(sourceBitrateBps / 1000);
                    ev.computed_kbps = Math.round(result / 1000);
                    ev.capped_kbps = Math.round(ceiling / 1000);
                    window.__ae('feature_use', ev);
                }
            } catch (_) {}
            result = ceiling;
        }
    }
    return result;
}

/* The video bitrate run() asks the encoder for (bits/s) — the whole decision
   as one pure function, so a test holds it without a browser.
     o.srcBps       the source file's bits/s (file.size × 8 ÷ duration). It
                    includes audio + container overhead, but the over-count is
                    small (≤ 5 % for video clips) and erring slightly UP is
                    exactly what the floor wants (#494).
     o.inW, o.inH   source coded size;  o.outW, o.outH  output coded size
     o.srcCodec     the source video codec string (demuxInfo.codec)
     o.geo          a geometry2d run (the draw composes the output frame)
     o.explicitBps  the caller's own average-bitrate target (opts.videoBitrate)
     o.maxBps       the caller's ceiling (opts.maxVideoBitrate) on what the
                    heuristic picks — opt-in, for pictures only a model sees;
                    the resize decision of 2026-09-30 (no source ceiling on a
                    downscale) stands for everything a person watches */
function runVideoBitrate(o) {
    var r = runVideoBitrateUncapped(o);
    var cap = +o.maxBps;
    return (isFinite(cap) && cap > 0 && !(+o.explicitBps > 0) && r > cap) ? Math.round(cap) : r;
}
function runVideoBitrateUncapped(o) {
    /* An EXPLICIT average-bitrate target wins outright. video-compressor
       derives it from the size the user demanded, so neither the CRF heuristic
       nor its source-aware ceiling (which exists to stop unrequested inflation)
       may override it — landing under "8 MB for Discord" is the entire job
       there. /video-converter sizes its downscales itself: its quality pick on
       top of the same sourceBpsForOutput. Absent by default. */
    var explicit = +o.explicitBps;
    if (isFinite(explicit) && explicit > 0) return Math.round(explicit);
    var inPx = o.inW * o.inH, outPx = o.outW * o.outH;
    var src = (o.srcBps > 0) ? o.srcBps : 0;
    if (src > 0) {
        if (o.geo) {
            // geometry2d: the output covers only a fraction of the source
            // pixels, so the source-bitrate floor/ceiling must scale with the
            // area ratio — a 25 % crop floored at 85 % of FULL source bitrate
            // would bloat the file ~3×. Linear, not sourceBpsForOutput: a crop
            // keeps the scene's detail per pixel and drops the rest of it.
            var area = outPx / Math.max(1, inPx);
            if (area < 1) src = Math.floor(src * Math.max(0.15, area));
        } else {
            // A resize (params.resolution, the plan ceiling, the H.264 area
            // cap on > 4K sources): the source's bits for THIS frame size.
            src = Math.floor(sourceBpsForOutput(src, inPx, outPx, o.srcCodec));
        }
    }
    // Wave 4 (2026-06-02): the source-aware ceiling guards the outputs that
    // keep the source's pixels — same size (sid dfb0fd7d: 360p game footage
    // 308 MB → 651 MB with no quality gain) and a geometry2d crop ("same
    // content, fewer pixels", its source already scaled by area above). A
    // resize has none, a downscale included — and stays without one (decided
    // 2026-09-30 on this measurement: the ceiling costs too much quality): a
    // 2.6 Mbit/s 1080p clip at «720p, high quality» asks for the CRF-18
    // table's 5.0 Mbit/s — 14.1 MB from a 6.8 MB source, VMAF 92.8; the
    // ceiling at 1.3× its 720p bits would make it 5.4 MB at VMAF 85.9
    // (default quality, CRF 23: 6.0 MB / 87.1 → 5.4 MB / 85.9).
    var noUpscale = o.geo ? true : (o.outW === o.inW && o.outH === o.inH);
    return crfToBitrate(o.crf, o.outW, o.outH, o.fps, src, { noUpscale: noUpscale });
}

// Wave 23 (2026-07-08): public run() is now a thin rescue wrapper around
// _runPipeline. When the decode phase dies with the async MediaCodec
// rejection `decode: Unsupported configuration…` AND the failing decoder
// was NOT already software, we retry the WHOLE pipeline once with the
// decoder cascade pinned to ['prefer-software']. Chromium's SW decoder
// (libavcodec-backed) accepts bitstreams MediaCodec rejects and still
// runs 5-10× faster than the FFmpeg-WASM fallback the caller would
// otherwise take (#948: 16-minute WASM encode on a 30 MB clip whose HW
// path died on chunk 0). The rescue is in the MODULE, not the blade, so
// every consumer (enhance-video, video-crop, video-filter,
// video-compressor, video-speed) inherits it.
async function run(opts) {
    // Created here so a rescue re-run's copy of opts shares it (see dropRunLanes).
    try { if (opts && !opts.__muxLanes) opts.__muxLanes = []; } catch (_) {}
    try {
        return await _runPipeline(opts);
    } catch (e) {
        dropRunLanes(opts);   // before any rescue re-run reserves its own room
        /* The browser's storage refused the result (diskWriteError — the run
           stopped at that frame) and the whole file fits in memory: once more,
           in memory, from the start. Every tool on the pipeline then gets its
           file, instead of falling to a slower lane that writes to the same
           storage (report #9939: «10 часов», then the same wall at 256 MB).
           Past the memory ceiling it stays the caller's question — /enhance-
           video asks where to save (opts.outputHandle). */
        if (e && /^opfs-write-failed:/.test(e.message || '') && e.memFits &&
            !(opts && (opts.noDiskTarget || opts.outputHandle || opts.__diskRetried))) {
            try { window.__ae && window.__ae('feature_use', { slug: (opts && opts.slug) || 'webcodecs', action: 'disk_refused_to_memory',
                mb: Math.round((e.needBytes || 0) / 1048576), quota: e.diskFull ? 1 : 0 }); } catch (_) {}
            return await run(Object.assign({}, opts, { noDiskTarget: true, __diskRetried: true }));
        }
        var _m = (e && e.message) || '';
        var _hint = e && e.decHint;
        // `decode: Decoding error` joined the rescue class 2026-07-31
        // (#1234): a 1922×1440@60 High@5.1 stream with 70 mid-stream
        // SPS/PPS sets killed the HW decoder MID-STREAM (decoded_so_far
        // 1863 then 2262 on the retry) — not at config time, so the
        // `Unsupported configuration` guard never matched, the rescue
        // never ran, and the user fell into the slow FFmpeg-WASM lane
        // twice and abandoned both times. Chromium's SW decoder
        // (libavcodec) tolerates mid-stream parameter-set switches that
        // D3D/MediaCodec reject; one pinned-software retry is bounded by
        // _swDecodeRetried exactly like the original class.
        // `decode: Decoder failure` joined 2026-08-07 (#video-fps bug report):
        // 41 events / 34 sessions in 14 d carried this Chromium wording and
        // matched none of the three patterns — no rescue ever ran for them.
        if ((/^decode: Unsupported configuration/i.test(_m)
                || /^decode: no-first-frame/i.test(_m)
                || /^decode: Decoding error/i.test(_m)
                || /^decode: Decoder failure/i.test(_m))
            && !(opts && opts._swDecodeRetried)
            && _hint !== 'prefer-software') {
            try { window.__ae && window.__ae('feature_use', { slug: (opts && opts.slug) || 'webcodecs', action: 'sw_decode_rescue_attempt', hint: _hint || 'unknown' }); } catch (_) {}
            /* The rescue re-enters the pipeline FROM SCRATCH — re-demux, then
               software decode, which is slower than hardware by design. Tell
               the caller so it can re-arm its stall watchdog to the same
               generous window a fresh run gets; without this the run is back
               in the tight steady-state gap with a silent phase ahead of it,
               and the watchdog kills the rescue (78 provable kills / 30 d).
               `onProgress(0)` covers callers that have no `onRestart`: every
               one of them resets its timer inside the progress handler. */
            try { if (typeof opts.onRestart === 'function') opts.onRestart({ reason: 'sw_decode_rescue' }); } catch (_) {}
            try { if (typeof opts.onProgress === 'function') opts.onProgress(0); } catch (_) {}
            var o2 = Object.assign({}, opts, { _swDecodeRetried: true, _decodeHints: ['prefer-software'] });
            var res = await _runPipeline(o2);
            try { window.__ae && window.__ae('feature_use', { slug: (opts && opts.slug) || 'webcodecs', action: 'sw_decode_rescue_ok' }); } catch (_) {}
            return res;
        }
        /* ── Программная пересдача КОДИРОВЩИКА ──────────────────────────────
           Зеркало SW-rescue декодера, которого у кодировщика не было.
           `hardwareAcceleration:'prefer-hardware'` в Chromium — не пожелание,
           а ТРЕБОВАНИЕ: если аппаратного кодировщика под эту конфигурацию нет,
           `configure()` отказывает `OperationError: Encoder creation error.`
           и НЕ отступает на программный сам. Проверено настоящим Chrome:
           7680×480 и 5760×1080 под 'prefer-hardware' → отказ, под
           'prefer-software' → сконфигурировался И закодировал кадр.
           То же и для «непроверенной» последней попытки: она шла без подсказки
           вовсе, то есть выбор оставался за браузером.

           Почему это стоит пересдачи, а не падения в FFmpeg-WASM — замер
           /enhance-video за 9 дней, доля дошедших до результата:
             аппаратный лейн сработал      2751 сессия → 99.3 %
             лейн пропущен по пробе         269       → 69.5 %
             лейн упал (любая причина)      284       → 43.7 %
             лейн завис                      99       → 37.4 %
             «Encoder creation error.»      254       → 27.2 %  ← худшее
           То есть запасной путь спасает меньше трети этой когорты, а
           программный WebCodecs, который здесь ни разу не пробовали, быстрее
           WASM в разы (см. комментарий у ENCODER_HINTS).

           Ограничения те же, что у декодерной пересдачи: РОВНО одна попытка
           (`_swEncodeRetried`) и только если упавшая подсказка ещё не была
           программной. */
        var _eHint = e && e.encHint;
        if (/^encoder-failed: .*(Encoder creation error|Unsupported configuration|Encoder initialization error|encoder wedged)/i.test(_m)
            && !(opts && opts._swEncodeRetried)
            && _eHint !== 'prefer-software') {
            try { window.__ae && window.__ae('feature_use', { slug: (opts && opts.slug) || 'webcodecs', action: 'sw_encode_rescue_attempt', hint: _eHint || 'unknown' }); } catch (_) {}
            try { if (typeof opts.onRestart === 'function') opts.onRestart({ reason: 'sw_encode_rescue' }); } catch (_) {}
            try { if (typeof opts.onProgress === 'function') opts.onProgress(0); } catch (_) {}
            var oE = Object.assign({}, opts, { _swEncodeRetried: true, _encodeHints: ['prefer-software'] });
            var resE = await _runPipeline(oE);
            try { window.__ae && window.__ae('feature_use', { slug: (opts && opts.slug) || 'webcodecs', action: 'sw_encode_rescue_ok' }); } catch (_) {}
            return resE;
        }
        throw e;
    }
}

async function _runPipeline(opts) {
    if (!opts || !opts.file) throw new Error('webcodecs-pipeline: no file');
    if (typeof OffscreenCanvas === 'undefined') throw new Error('no-offscreen-canvas');
    if (!paramsCovered(opts.params)) throw new Error('params-not-covered');
    var aeSlug = opts.slug || 'enhance-video';
    /* Where the run is, readable from outside (opts.state, 15.10.2026). A
       caller's own «no progress for 30 s» watchdog stops a run that the
       pipeline never sees fail, and until now said only «hw-stalled» — 54 of
       ~100 fast-lane failures on /video-compressor, Safari 17 among them,
       with no word on whether the decoder, the encoder or the audio tail had
       gone quiet. `state.read()` answers that at the moment of giving up. */
    var _stObj = (opts.state && typeof opts.state === 'object') ? opts.state : null;
    function _st(phase) { if (_stObj) _stObj.phase = phase; }
    if (_stObj) {
        _stObj.phase = 'demux';
        _stObj.read = function () {
            var r = { phase: _stObj.phase || '' };
            try {
                r.fed = _lastSentChunkIdx + 1; r.of = (samples && samples.length) || 0;
                r.dec = diagDecoded; r.enc = frameCount; r.out = _muxChunkIdx;
                r.dq = decoder ? decoder.decodeQueueSize : -1; r.eq = encoder ? encoder.encodeQueueSize : -1;
                r.held = decodedFrames ? decodedFrames.length : 0; r.owed = _sentFrames ? _sentFrames.size : 0;
                r.dr = _decRebuilds; r.er = _encRebuilds; r.dw = _decWedges;
            } catch (_) {}
            return r;
        };
    }

    var _mkv = isMatroskaFile(opts.file);
    /* Дверь тегов и ключа «как играть» — заранее: по требованию в конце прогона
       с потолком 10 с на медленной связи файл уходил без ключа (стенд /video-fps
       04.11.2026 — тот же код дал ключ в одном прогоне и не дал в другом). */
    ensureMp4Tags();
    await Promise.all([ ensureInputDemuxer(opts.file), ensureMp4Muxer() ]);
    // Clean-fail if a vendored lib genuinely didn't load (local + CDN both
    // failed) — otherwise `new Mp4MuxerNs.Muxer()` throws the cryptic
    // "Cannot read properties of undefined (reading 'Muxer')" that the caller
    // can't classify (1 event/day 2026-07-02). A typed error routes straight
    // to the FFmpeg fallback.
    if (!_mkv && !window.MP4Box) throw new Error('mp4box-unavailable');
    if (!window.Mp4Muxer) throw new Error('mp4-muxer-unavailable');

    var file = opts.file;
    // Wave 22 (2026-06-19): pre-read happens at file-pick time via the
    // shared <x-tool-uploader>'s FileBytes.prime call. _readSliceWithRetry
    // below reads through the canonical FileBytes cache, so we no longer
    // need a run()-entry preread here. See _readSliceWithRetry header.
    var params = opts.params || {};
    // video-speed: playback-speed remap. Defaults to 1 (no-op) so enhance-video,
    // upscale, video-filter, video-compressor (which never pass `speed`) keep a
    // byte-for-byte identical path. >1 speeds up (frames dropped to ~source fps);
    // <1 slows down (every frame kept, lower output fps). opts.audio: 'preserve'
    // (pitch-correct time-stretch) | 'mute'. Other audio modes never reach here.
    var speed = +opts.speed; if (!(speed > 0)) speed = 1;
    var speedAudioWanted = opts.audio || 'preserve';
    var crf = +opts.crf; if (!isFinite(crf)) crf = 23;
    // geometry2d (video-crop): pure 2D-canvas geometry transform — crop / pad /
    // circle mask — instead of the WebGL filter rig. Contract:
    //   opts.geometry2d = {
    //     width, height,             // output size, DISPLAY space (pre-evenized)
    //     draw: (ctx, video, W, H, frameIndex, timeSec) => void
    //   }
    // timeSec is the frame's presentation time in seconds — callbacks that
    // vary over time (video-blur's tracked/windowed masks) key off it; purely
    // geometric callbacks (video-crop) ignore the trailing args.
    // `video(sx,sy,sw,sh, dx,dy,dw,dh)` draws the current source frame with the
    // SOURCE rect given in DISPLAY space (what the user saw in the tool preview);
    // tkhd rotation is baked into pixels here, so the output carries NO rotation
    // matrix. W/H are the ACTUAL canvas dims (differ from width/height only when
    // the >4K coded-area cap engaged) — callbacks must scale their math by W/H.
    // When absent, the flow below is byte-for-byte the existing filter path.
    var geo = (opts.geometry2d && typeof opts.geometry2d.draw === 'function') ? opts.geometry2d : null;
    // geometry2d.analyzeOnly — a decode-and-look pass: every frame still reaches
    // draw() (display-oriented, trimmed, in order) but nothing is encoded or
    // muxed, and run() resolves with blob:null. /video-retouch reads the face
    // geometry of the whole clip this way BEFORE its render pass, so the render
    // can smooth with look-ahead and bridge short detector gaps. Without the
    // flag the flow below is byte-for-byte unchanged.
    // draw() of this pass MAY return a Promise: the pass awaits it before the
    // next frame (_anaHold below) — the backpressure a worker-pool consumer
    // needs (/video-stabilizer). A rejection fails the run like a decode error.
    var analyzeOnly = !!(geo && opts.geometry2d.analyzeOnly);
    // geometry2d.concurrency — несколько кадров «в работе» одновременно
    // (26.09.2026, /video-retouch). draw() может вернуть Promise: кадр
    // рисуется на СВОЙ холст, декодер идёт дальше, а кодируются кадры строго
    // в порядке показа, как только готов первый в очереди. Тул, считающий кадр
    // в воркерах, так получает N кадров в работе вместо одного. Без опции —
    // byte-for-byte прежний синхронный путь. ⚠️ Кадр (VideoFrame) закрывается
    // сразу после draw(): всё, что нужно из него, draw берёт синхронно
    // (drawVideo), а в обещании живут уже только пиксели холста.
    var geoConc = (geo && !analyzeOnly && +opts.geometry2d.concurrency > 1)
        ? Math.min(16, Math.floor(+opts.geometry2d.concurrency)) : 1;
    var onProgress = typeof opts.onProgress === 'function' ? opts.onProgress : function(){};
    var aborted = false;
    if (opts.abortSignal) {
        if (opts.abortSignal.aborted) throw new DOMException('cancelled', 'AbortError');
        opts.abortSignal.addEventListener('abort', function(){ aborted = true; });
    }

    // Demux: we need codec, width, height, timescale, avcC (description).
    // The liveness callback keeps the caller's stall watchdog honest while the
    // container is read (see demuxMp4's header).
    // Lazy demux unless a datamosh plan is coming: VideoMosh edits the
    // compressed sample LIST (duplicates, drops) and reads sample bytes while
    // planning, which needs them in hand.
    var demuxInfo = await demuxInput(file, function(){ onProgress(0); }, { lazy: !(opts && opts.mosh), mosh: opts && opts.mosh, slug: aeSlug, audioTrackIndex: opts && opts.audioTrackIndex });
    if (aborted) throw new DOMException('cancelled', 'AbortError');
    _aeDemuxLane(aeSlug, demuxInfo, file);

    /* opts.maxFps — an output frame-rate CEILING, downward only (60 → 30).
       Until 26.09.2026 a caller that needed one had no way to say so, and
       /video-compressor answered every fps reduction by skipping this lane —
       silently, while the user's «Быстро» stayed ticked: `fps_change` 133 runs
       in 30 days (Quick caps a 60 fps source at 30), each one handed to
       single-threaded x264. Decimation is the speed-up grid below at speed 1:
       keep the frame nearest each slot of the output rate, give it one slot of
       duration. Raising the rate is interpolation and belongs to
       runInterpolation(); a source within 5 % of the cap (29.97 under 30) is
       left alone. */
    var _capFps = (+opts.maxFps > 0 && speed === 1) ? +opts.maxFps : 0;
    var _srcFpsX = demuxInfo.fpsExact || demuxInfo.fps || 30;
    var _decimate = _capFps > 0 && _srcFpsX > _capFps * 1.05;
    var _encFps = _decimate ? _capFps : (demuxInfo.fps || 30);

    // Audio codec gate — see gateAudioDecodable: AAC-LC byte-copies, every
    // other decodable codec (mp4a flavours, Opus, FLAC …) transcodes to AAC,
    // and only a codec the probe FAILED to decode bails to FFmpeg. A sound
    // track the demux cannot see at all bails the same way (_refuseUnreadSound)
    // unless nothing is written (analyzeOnly) or the caller does not want the
    // sound (`_dropAudio` — the condition of _wantAudioTrack).
    /* «No sound» is ONE decision for the whole run (01.10.2026): `dropAudio`
       and `audio:'mute'` both mean the output carries no sound track, at any
       speed. Until then _wantAudioTrack honoured only a muted SPEED CHANGE,
       and the passthrough below copied the source track whenever there was
       one — /video-compressor «Удалить аудио» on «Быстро» and
       /video-converter «без звука» handed back the original sound (measured:
       5-min lecture, audio:'mute' → the source's 127 kbit/s AAC in the file).
       Sound nobody keeps is not probed either: an undecodable track used to
       bail a muted run to FFmpeg. */
    var _dropAudio = !!opts.dropAudio || speedAudioWanted === 'mute';
    _refuseUnreadSound(demuxInfo, opts.slug || 'enhance-video', !analyzeOnly && !_dropAudio);
    var _audioCodecIn = (demuxInfo.audio && demuxInfo.audio.codec) || '';
    var _audioNeedsTranscode = _dropAudio ? false : (await gateAudioDecodable(
        file, demuxInfo, opts.slug || 'enhance-video', function () { return aborted; }
    )).needsTranscode;
    /* Edit-list outcome (demuxMp4 → mp4-edit-list.js). Passthrough audio may
       start up to one access unit EARLY (the residual — the first video frame
       is held by that much) but can never start LATE: a track whose edit list
       opens with an empty edit longer than one unit is decoded and re-encoded
       with that much silence in front (muxSpeedAudio `leadSec`). */
    var _edit = demuxInfo.edit || { applied: false };
    var _audioLeadSec = 0, _leadPlan = null;
    if (!_dropAudio && _edit.applied && _edit.audioLeadUs > (_edit.audioUnitUs || 21333)) {
        _audioLeadSec = _edit.audioLeadUs / 1e6;
        // AAC-LC at speed 1 carries the lead as silent frames (_silentLeadPlan);
        // the decode path keeps `leadSec` for everything else (and for gain /
        // fades, decided below — they take muxSpeedAudio anyway).
        _leadPlan = (!_audioNeedsTranscode && speed === 1) ? _silentLeadPlan(demuxInfo, _edit, opts) : null;
        if (!_leadPlan) _audioNeedsTranscode = true;
    }
    _aeEditList(opts.slug || 'enhance-video', _edit);
    if (_audioNeedsTranscode) {
        try {
            if (typeof window !== 'undefined' && typeof window.__ae === 'function') {
                window.__ae('feature_use', {
                    slug: opts.slug || 'enhance-video',
                    action: 'audio_transcode_non_aac',
                    codec: String(_audioCodecIn).slice(0, 20)
                });
            }
        } catch (_) {}
    }

    var inW = demuxInfo.width, inH = demuxInfo.height;
    // For rotated sources (typically phone-shot portrait, stored as landscape
    // pixels with rotation=90/270 metadata), the user's mental model uses
    // DISPLAY dimensions: a "1080p" iPhone portrait is 1080 tall on screen,
    // even though its coded frames are 1920×1080 landscape. Compute resolution
    // change in display-space, then map back to coded dims so the encoder
    // operates on raw pixels and the muxer's matrix metadata makes the player
    // rotate on display. Closes comment #370 cluster (~299 sessions/7d HW path,
    // estimated 30-50% phone portrait sources affected).
    var rotation = demuxInfo.rotation || 0;
    var rotatedAxes = (rotation === 90 || rotation === 270);
    var inDispW = rotatedAxes ? inH : inW;
    var inDispH = rotatedAxes ? inW : inH;
    var outDispW = inDispW, outDispH = inDispH;
    if (params.resolution && params.resolution !== 'original') {
        var targetN = params.resolution === '4k' ? 2160 : parseInt(params.resolution, 10);
        // «1080p» means the SHORTER side — that is ResolutionModule's contract
        // (its dropdown labels and its buildScaleFilter, i.e. the FFmpeg lane,
        // both say so: portrait 1080p = 1080 WIDE). This mapping used to read
        // the pick as display HEIGHT — day-one landscape thinking (2026-05-02,
        // before the module existed), harmless for landscape where the shorter
        // side IS the height, but a portrait 1728×3840 at «1080p» came out
        // 486×1080 on this lane and 1080×1920 on the FFmpeg lane: one pick,
        // two different files depending on which lane the run happened to take
        // (found by the #905 probe-parity audit, 2026-08-13). One scale factor
        // off the shorter side keeps landscape byte-identical and gives
        // portrait what the label promises.
        //
        // Downscale to anything; UPSCALE only up to 1080p (shorter-side terms).
        // A plain resize up to 1080p is fine — the HW H.264 encoder handles it
        // on every device. Upscaling to 1440p/4K here would (a) force the
        // encoder to a resolution many devices reject → `encoder-configure-
        // failed` → the slow FFmpeg-WASM fallback (bug 2026-05-17 sid
        // 36b032d0: 640×360 → 4K hung 21 min), and (b) add zero real detail.
        // Genuine 1440p+ enlargement is the AI Upscale feature.
        var shortIn = Math.min(inDispW, inDispH);
        if (targetN && targetN > shortIn) targetN = Math.min(targetN, Math.max(shortIn, 1080));
        if (targetN && targetN !== shortIn) {
            // Keep aspect, even pixels — display-space, both axes from the
            // same factor so orientation cannot change the semantics.
            var _rf = targetN / shortIn;
            outDispW = Math.round(inDispW * _rf / 2) * 2;
            outDispH = Math.round(inDispH * _rf / 2) * 2;
        }
    }
    // Map back to coded dims for the encoder (swap if rotation 90/270).
    var outW = rotatedAxes ? outDispH : outDispW;
    var outH = rotatedAxes ? outDispW : outDispH;

    if (geo) {
        // geometry2d output: the 2D draw bakes rotation into pixels, so the
        // output is already display-oriented — coded dims = display dims, no
        // axis swap, and no rotation matrix on the muxed track (see below).
        outDispW = Math.max(2, Math.floor((+geo.width || inDispW) / 2) * 2);
        outDispH = Math.max(2, Math.floor((+geo.height || inDispH) / 2) * 2);
        outW = outDispW;
        outH = outDispH;
    }

    // ───────────────────────────────────────────────────────────────────
    // ROOT-CAUSE FIX: H.264 hardware encoder coded-area cap
    // ───────────────────────────────────────────────────────────────────
    // /pino telemetry 2026-05-08: 8 events / 8 sessions hit
    //   "The provided resolution (3840x2160 / 4166x2160 / etc) has a
    //    coded area which exceeds the encoder limit"
    // Cause: source files at >4K (8K phone exports, drone footage, etc)
    // exceed H.264 Level 5.1 max coded area (8,294,400 pixels = 4K).
    // Fix: downscale to fit ≤4K BEFORE encoder.configure(). 16:9 cap
    // = 3840×2160; for portrait/odd-aspect we recompute proportionally.
    var H264_MAX_CODED_AREA = 8294400; // 3840*2160 — H.264 Level 5.1 cap
    if (outW * outH > H264_MAX_CODED_AREA) {
        var scale = Math.sqrt(H264_MAX_CODED_AREA / (outW * outH));
        var capW = Math.floor(outW * scale / 2) * 2;
        var capH = Math.floor(outH * scale / 2) * 2;
        try {
            if (typeof window !== 'undefined' && typeof window.__ae === 'function') {
                window.__ae('feature_use', {
                    slug: opts.slug || 'enhance-video',
                    action: 'webcodecs_resolution_capped',
                    src_w: outW, src_h: outH, out_w: capW, out_h: capH
                });
            }
        } catch (_) {}
        outW = capW;
        outH = capH;
    }

    // Build encoder
    var Mp4MuxerNs = window.Mp4Muxer;

    // ── Кодек выхода: H.264, а если его на этом устройстве НЕТ — VP9 ──
    //
    // Зачем. Когда вся H.264-каскада отказывала, прогон уходил в
    // FFmpeg-WASM — однопоточный (SharedArrayBuffer на публичных
    // страницах недоступен: замер device_cap 24.08 — sab у 0.0–4.3 %). Цена
    // такого отступления измерена: сессии с отказом кодировщика
    // доходят до файла в 32.6 % случаев на мобильных против 72.2 % на
    // десктопе (24.08, 64 сессии за 4 дня).
    //
    // Зачем именно VP9 и почему это НЕ догадка. `_probeAltEncoders`
    // был заведён раньше ровно как замер ПЕРЕД постройкой этого пути
    // («measure, don't guess» в его собственном комментарии). Ответ
    // прочитан 2026-08-24: **1121 проба, 727 сессий за 30 дней, и в 1118
    // из 1121 (99.7 %) VP9 БЫЛ доступен** там, где H.264 отказал; на
    // мобильных — 435 из 435 (100 %). Разбивка: enhance-video 573/575,
    // video-crop 229/229, video-fps 179/179.
    //
    // Контейнер НЕ меняется: mp4-muxer умеет vp09 в MP4 (его
    // собственный список — avc/hevc/vp9/av1), а значит цель OPFS, резерв
    // moov, AAC-дорожка и вся остальная механика остаются теми же.
    // ⚠️ Это НЕ «VP9 через FFmpeg-WASM», который запрещён в CLAUDE.md
    // (падает memory access out of bounds): здесь кодирует штатный
    // VideoEncoder браузера — другой механизм целиком.
    //
    // ⚠️ Цена решения платится ТОЛЬКО той когортой, где проба
    // отвергла ВСЕ профили H.264 (`unverified`). У остальных не добавляется
    // ни одного вызова — байты выхода и время прежние.
    //
    // ⚠️ И нельзя решать по одному `isConfigSupported`: он врёт в обе стороны
    // (см. verifyEncoderAt). Зависший GPU-процесс отвечает «нет» на всё, что
    // кодировал вчера, — и такая сессия обязана остаться на H.264. Поэтому
    // переключаемся только после НАСТОЯЩЕГО однокадрового отказа
    // (verifyEncoderAt), и никогда при `uncertain` — там вердикта нет.
    var VP9_RESCUE_CODEC = 'vp09.00.31.08';   // та ЖЕ строка, что в _probeAltEncoders —
                                              // иначе мы бы опирались на замер другого кодека
    var _vCodecKind = 'avc';
    var _altProbed  = false;
    if (typeof VideoEncoder !== 'undefined') {
        try {
            var _pre = await pickEncoderConfig(outW, outH, 4e6, _encFps, opts.encoderLatency,
                null, null, H264_PROFILE_ORDERS[opts.h264Profile] || null);
            if (_pre && _pre.unverified) {
                var _real = await verifyEncoderAt(outW, outH, _encFps);
                if (!_real.ok && !_real.uncertain) {
                    var _alt = await _probeAltEncoders(outW, outH);
                    _altProbed = true;
                    if (_alt && _alt.vp9) _vCodecKind = 'vp9';
                }
            }
        } catch (_) { /* любая ошибка решения — остаёмся на прежнем пути */ }
    }
    if (_vCodecKind === 'vp9') {
        try { window.__ae && window.__ae('feature_use', {
            slug: aeSlug, action: 'enc_vp9_rescue', w: outW, h: outH
        }); } catch (_) {}
    }

    var videoMuxOpts = { codec: _vCodecKind, width: outW, height: outH, frameRate: MUX_VIDEO_TIMEBASE };
    // geometry2d bakes rotation into pixels — re-emitting the source matrix
    // would rotate the already-upright output a second time.
    if (rotation && !geo) videoMuxOpts.rotation = rotation;
    /* Sample-level edits (gain / fades / speed) and a non-AAC source alike
       need the decode→re-encode path — passthrough copies frames and cannot
       touch their content. Decided HERE, above the muxer, because the track
       header is written at construction time and must state the format that
       path will actually produce (plannedAudioOut). Callers that pass none of
       these keep the byte-identical passthrough. */
    var _aGain    = (typeof opts.audioGain === 'number' && isFinite(opts.audioGain)) ? opts.audioGain : 1;
    var _aFadeIn  = +opts.audioFadeIn  > 0 ? +opts.audioFadeIn  : 0;
    var _aFadeOut = +opts.audioFadeOut > 0 ? +opts.audioFadeOut : 0;
    var _needsAudioEdit = (speed !== 1) || _aGain !== 1 || _aFadeIn > 0 || _aFadeOut > 0 || _audioNeedsTranscode;
    var _wantAudioTrack = !!demuxInfo.audio && !_dropAudio;
    /* How much the video is held behind the audio (µs). Passthrough audio
       keeps up to one access unit from before the cut (the AAC decoder's
       warm-up), so its content plays that much late; the picture follows by
       holding the first frame (see drainOne). The decode → re-encode path
       starts exactly at the cut and needs no hold. */
    var _videoDelayUs = (_edit.applied && _wantAudioTrack && !_needsAudioEdit)
        ? (_leadPlan ? _leadPlan.holdUs : (_edit.audioResidualUs || 0)) : 0;
    var _audioOut = demuxInfo.audio
        ? (_needsAudioEdit
            ? plannedAudioOut(demuxInfo.audio.sampleRate, demuxInfo.audio.channels)
            : { sampleRate: demuxInfo.audio.sampleRate, numberOfChannels: demuxInfo.audio.channels })
        : null;
    // The video bitrate — the whole decision is runVideoBitrate() (module
    // level): the CRF table, bounded from both sides by the source's bits for
    // THIS output size (source-bitrate floor, #494 Diggezz; source-aware
    // ceiling, Wave 4); an explicit opts.videoBitrate wins.
    var _srcBps = (file && file.size && demuxInfo.durationSec > 0)
        ? Math.floor((file.size * 8) / demuxInfo.durationSec)
        : 0;
    var bitrate = runVideoBitrate({
        crf: crf, fps: demuxInfo.fps, srcBps: _srcBps, srcCodec: demuxInfo.codec,
        inW: inW, inH: inH, outW: outW, outH: outH,
        geo: !!geo, explicitBps: opts.videoBitrate, maxBps: opts.maxVideoBitrate
    });
    var _swBps = softwareRungBps(bitrate, outW, outH, _encFps, opts);
    var _swFirst = ((opts._encodeHints || HW_HINTS)[0] === 'prefer-software');
    // (Computed before the output target: the disk lane reserves its room from it.)

    /* Output target. Big results stream to OPFS instead of piling up on the
       JS heap — see the openMuxTarget block for the measurements and for why
       the reservation (moov at head) beats screen-recorder's moov-at-end here.
       Both proxies for "the output will be big" are known at this point;
       reserving generously is safe, under-reserving is not, so both counts
       carry a wide margin and anything unknown degrades to moov-at-end. */
    var _durS = demuxInfo.durationSec || 0;
    var _streamWanted = !analyzeOnly && ((file && file.size >= MUX_STREAM_MIN_BYTES) || _durS >= 600);
    var _expVideoChunks = Math.ceil((demuxInfo.totalFrames || (_durS * (demuxInfo.fps || 30))) * 1.35) + 300;
    var _expAudioChunks = _wantAudioTrack
        ? Math.ceil(_durS * ((_audioOut && _audioOut.sampleRate) || 48000) / 1024 * 1.35) + 300
        : 0;
    /* The room the disk lane reserves before frame 0 (see openMuxTarget): the
       bitrate over the longest output this run can make (a slow-down stretches
       it), plus the same 15 % + 8 MB margin as CanvasVideoExport. */
    // Where the ladder starts on the software rung (Android), the room is for ITS budget.
    var _reserveBps = (_swBps > bitrate && _swFirst) ? _swBps : bitrate;
    var _reserveBytes = _streamWanted
        ? Math.round(((_reserveBps + (_wantAudioTrack ? 192000 : 0)) / 8) * (_durS / speed) * 1.15) + 8 * 1048576
        : 0;
    // The person's file must be big enough for any result: reserve for it even
    // where the in-memory threshold would not stream (a short clip).
    var _needBytes = _reserveBytes || (opts.outputHandle
        ? Math.round(((_reserveBps + (_wantAudioTrack ? 192000 : 0)) / 8) * (_durS / speed) * 1.15) + 8 * 1048576 : 0);
    var muxTarget = await openMuxTarget(
        Mp4MuxerNs,
        (_streamWanted || opts.outputHandle) ? MUX_STREAM_MIN_BYTES : 0,
        _expVideoChunks, _expAudioChunks, _wantAudioTrack, _needBytes,
        { outputHandle: opts.outputHandle || null, noDiskTarget: opts.noDiskTarget === true }
    );
    trackRunLane(opts, muxTarget);
    if (muxTarget.opfs) { try { sweepStaleMuxTemps(); } catch (_) {} }
    try { window.__ae && window.__ae('feature_use', { slug: aeSlug, action: 'mux_target', mode: muxTarget.mode, blocks: muxTarget.blocks ? 1 : 0, unres: muxTarget.unreserved || '', durS: Math.round(_durS), inMB: Math.round(((file && file.size) || 0) / 1048576) }); } catch (_) {}
    var muxer = new Mp4MuxerNs.Muxer({
        target: muxTarget.target,
        video: videoMuxOpts,
        audio: _wantAudioTrack ? {
            codec: 'aac',
            numberOfChannels: _audioOut.numberOfChannels,
            sampleRate: _audioOut.sampleRate
        } : undefined,
        fastStart: muxTarget.fastStart
    });
    // Parameter sets that change mid-stream (an encoder rebuilt, OpenH264 in
    // Chromium < 130) travel in-band — see __muxVideoTrack in timbrica-core.js.
    var muxVideo = window.__muxVideoTrack ? window.__muxVideoTrack(muxer) : function (c, m, t, o) { return muxer.addVideoChunk(c, m, t, o); };

    // mp4-muxer needs `meta.decoderConfig.colorSpace` (4-tuple primaries/
    // transfer/matrix/fullRange) to write the `colr` box. VideoEncoder may
    // emit a chunk where decoderConfig is null OR colorSpace is null —
    // happens on certain input configurations (sid c913580e-... was a
    // 480p h264 source on Opera GX 146; the muxer crashed at
    // "Cannot read properties of null (reading 'colorSpace')" for both
    // export attempts). Patch defaults here so the muxer always has a
    // valid colr-compatible config.
    var DEFAULT_COLOR_SPACE = { primaries: 'bt709', transfer: 'bt709', matrix: 'bt709', fullRange: false };
    var firstChunkSeen = false;
    // B-frame-safe muxing — running decode-order DTS (see addVideoChunk below).
    var _videoDtsAccumUs = 0;
    var _frameDurFallbackUs = 1e6 / (_encFps);
    var _muxChunkIdx = 0;          // for diagnostic telemetry on muxer/encoder errors
    // Chunk-level offset normalisation. Phase ④ normalises sample timestamps
    // at the demux boundary (vOffset = min CTS of all video samples), which
    // closes the common case of edit-list / CTS-offset source files. But for
    // edge cases the FIRST encoded chunk's timestamp can still be non-zero:
    //   - mp4box's `rapAlignement` mode starts delivery at a keyframe past
    //     the absolute zero, so the smallest CTS we ever see is N>0
    //   - VFR sources where the first delivered sample has CTS != min CTS
    //     and the min-CTS sample appears later (rare but real)
    // Telemetry 2026-05-09–13: 31 sessions/5d hit "first chunk for your media
    // track must have a timestamp of 0 (received DTS=0.xxx)" — Phase ④'s
    // sample-side normalisation didn't catch them. Defensive fix at the
    // encoder-output boundary: capture the first chunk's timestamp as a
    // late-stage offset, subtract it from every subsequent chunk before
    // muxing. Cheap (one subtraction per chunk via EncodedVideoChunk's
    // copyTo + reconstruct), preserves A/V sync because both tracks are
    // shifted by their own per-track offset, and works regardless of which
    // upstream layer introduced the offset.
    var videoChunkOffset = null;
    var audioChunkOffset = null;
    /* WHICH frame an output chunk is (window.__encoderClock, timbrica-core.js):
       Firefox's H.264 encoder with B-frames stamps a chunk with the END of its frame.
       The offset normalisation below subtracted that first shift — right only at one
       frame rate; a variable-rate source's frames went out at their neighbours' times
       (#9567 timing, Firefox: 106 of 581 moments showed another picture) and the
       retention map released the NEXT frame's copy. The clock maps every chunk back. */
    var _encClock = (typeof window !== 'undefined' && window.__encoderClock) ? window.__encoderClock({ onEnd: function () {
        _aeWebcodecsErr(aeSlug, 'chunk_end_stamped', new Error('encoder stamps frame ends'), { stage: 'main' });
    } }) : null;
    // Encoder/muxer error capture. Pre-fix the error callback only console.warn'd —
    // when encoder hit an internal error (codec failure, GPU context lost, memory
    // pressure) the next encode() call threw "Cannot call 'encode' on a closed
    // codec" which the catch handler mis-attributed as a real hardware crash.
    // 14+ silent-fail events / 24h on prod (2026-05-08) hit this cascade.
    // Now: capture root cause; encode loop throws `encoder-failed: <root>`
    // instead of the cascade closed-codec error.
    var encoderError = null;
    var muxerError = null;
    // Encoder-reclaim recovery (Android MediaCodec eviction). 187 events / 3d
    // surfaced QuotaExceededError "Codec reclaimed due to inactivity" — 65%
    // of them had `tab_bg_during_encode` set in the events just before. When
    // the user switches apps mid-export, Android may evict our MediaCodec
    // instance; the encoder fires `error`, then every subsequent encode()
    // call throws on the closed codec. Recovery: on QuotaExceededError, wait
    // for the page to return to foreground, build a new encoder with the
    // SAME config and the SAME hoisted callbacks, force the next emitted
    // chunk to be a keyframe so the muxer treats the bitstream restart as a
    // clean GOP boundary. Only ONE attempt per export — a second reclaim
    // means the device is in a state we can't pull back from; fall through
    // to the existing FFmpeg-WASM fallback honestly.
    /* A small BUDGET of rebuilds, not one (2026-09-26). The single-rebuild rule
       assumed a second death means a device beyond recovery; on iOS the media
       session can die more than once in a long 4K export, and the decoder dies
       with it (support #649: decoder frame 716 / encoder chunk 715, the same
       instant). Every rebuild is lossless (retention + replay) and the
       completeness check below still refuses a file with any frame missing. */
    var _encRebuilds = 0, ENC_REBUILD_MAX = 3;
    function _encCanRebuild() { return _encRebuilds < ENC_REBUILD_MAX; }
    var _encForceKeyframeNext = false;   // honored by the encode() call site
    var _pendingEncRecovery = false;     // set by renderFrame, drained by outer loop
    // Wedge bookkeeping (see ENC_WEDGE_MS). Both stamps are on the visible
    // clock: `_encOwedSinceVis` is set the moment the encoder goes from owing
    // nothing to owing one frame, `_encLastOutVis` every time a chunk lands.
    // Silence = visible time since the LATER of the two.
    var _encOwedSinceVis = -1;
    var _encLastOutVis = -1;
    var _encWedges = 0;                  // detected this run (diagnostics)
    function _encSilentMs() {
        if (!_sentFrames.size) return 0;
        var base = Math.max(_encOwedSinceVis, _encLastOutVis);
        return base < 0 ? 0 : (_visNow() - base);
    }
    // The wedge check every waiting loop calls. Synthesises the error the
    // encoder never reported, so the existing recovery machinery (drainOne →
    // _pendingEncRecovery → _rebuildEncoderAfterReclaim) runs unchanged.
    // Returns true when a wedge was just declared.
    function _encWedgeCheck(phase) {
        if (encoderError || aborted) return false;
        var silent = _encSilentMs();
        if (silent < ENC_WEDGE_MS) return false;
        _encWedges++;
        var e = new Error('encoder wedged: no output for ' + Math.round(silent / 1000) + 's with ' + _sentFrames.size + ' frame(s) owed (' + phase + ')');
        e.name = 'EncoderWedged';
        encoderError = e;
        _aeWebcodecsErr(aeSlug, 'encoder_wedge_detected', e, {
            stage: 'main', phase: phase, owed: _sentFrames.size, silent_ms: Math.round(silent),
            hint: encoderChosenHint, chunk_idx: _muxChunkIdx, queue: encoder.encodeQueueSize,
            recovered_before: _encRebuilds
        });
        return true;
    }
    // Is THIS encoder death worth a rebuild? (2026-08-09)
    //
    // The gate used to be the error NAME — QuotaExceededError only. That let
    // one runtime failure recover and left every other one to kill the whole
    // fast lane. Measured on 30 d of prod (/enhance-video, sessions that
    // clicked export; baseline completion 66.2 %):
    //
    //     QuotaExceededError (has this rebuild)   575 sess → 78.4 % complete
    //     OperationError     (had none)           684 sess → 46.5 % complete
    //
    // Same subsystem, same shape: 76 % of the OperationErrors arrive
    // MID-STREAM (median chunk 83, p90 1306), i.e. the hardware had already
    // accepted the config and encoded dozens of frames before dying. Their
    // messages name runtime events, not bad configs: "Encoding error." ×619,
    // "Mojo is disconnected" (GPU process restarted), "Can't readback frame
    // textures", "Encoder can't encode now."
    //
    // So the honest discriminator is not the name — it is whether the encoder
    // ever produced output:
    //   · emitted ≥1 chunk → the hardware validated this config; the death is
    //     a runtime event → rebuild the SAME config once and force a keyframe.
    //   · emitted nothing  → the config itself was refused ("Encoder
    //     initialization error." / "Failed to create OpenH264 enc", the other
    //     24 %). Rebuilding it would fail identically — so the rebuild takes the
    //     NEXT rung of the hint cascade instead (a different config), the way a
    //     wedge does; with no rung left the run fails as before.
    // Quota keeps recovering at any point: an eviction before the first chunk
    // still leaves a good config, and that is the pre-existing behaviour.
    //
    // (2026-11-04) The «nothing emitted» case used to END the run: the rung
    // ladder lived only in the probe (verifyEncoderAt) and in the outer run()
    // rescue, and that rescue matched the error TEXT («Encoder creation error»
    // …) — «Encoding error.», the way Chrome 156's Media Foundation encoder dies
    // on some Windows boxes (Intel gen-9 in the report), was not in the list.
    // Review 04.11 («скачанное видео дёргается»): the probe walked hardware →
    // «EncodingError», software → ok; the run started on hardware anyway, died
    // at chunk 0, and five exports in a row went to FFmpeg-WASM (10–21 min each
    // for an 82-s 768×576 clip, 21 s promised). RU 02.11–04.11: «encoder-failed:
    // Encoding error.» is the biggest fast-lane failure class (7 of 289 exporting
    // sessions), 4 of them with exactly that probe verdict.
    function _encRecoverable() {
        if (!encoderError) return false;
        if (encoderError.name === 'QuotaExceededError') return true;
        // A wedge is recoverable at any point: the config was accepted (the
        // codec took frames), and the rebuild moves to the next hint anyway.
        if (encoderError.name === 'EncoderWedged') return true;
        if (_muxChunkIdx > 0) return true;
        return _encRungRefused();
    }
    // The rung never produced a chunk, and a different rung is left to try.
    function _encRungRefused() {
        return !!encoderError && _muxChunkIdx === 0 && encoderError.name !== 'QuotaExceededError'
            && encoderError.name !== 'EncoderWedged' && _vCodecKind !== 'vp9'
            && _hintsAfter(encoderChosenHint).length > 0;
    }
    // Callbacks hoisted to top-level inside run() so the same closures bind
    // to both the initial encoder and the post-reclaim rebuild.
    function _onEncOutput(chunk, meta) {
        try {
            var safeMeta = meta || {};
            // Confirmation: this frame is encoded, we no longer need to keep it
            // for a possible replay. MUST run before the offset normalisation
            // below rewrites chunk.timestamp — the map is keyed by the ORIGINAL
            // frame timestamp, and matching after the rewrite would silently
            // never hit, turning the retention buffer into a memory leak.
            var _tsIn = _encClock ? _encClock.ts(chunk) : chunk.timestamp;
            _releaseSent(_tsIn);
            _encLastOutVis = _visNow();
            // Late-stage offset normalisation (see comment on videoChunkOffset).
            if (videoChunkOffset === null) {
                videoChunkOffset = _tsIn;
                // Telemetry on detection — surfaces root-cause data we can
                // mine later (file types, devices, sample-table patterns)
                // to find the upstream bug that bypassed Phase ④. Fire ONCE
                // per run to avoid /pino noise.
                if (videoChunkOffset !== 0) {
                    _aeWebcodecsErr(aeSlug, 'video_chunk_offset_detected', new Error('first chunk ts=' + videoChunkOffset), { stage: 'main', offset_us: videoChunkOffset });
                }
            }
            if (videoChunkOffset !== 0 || _tsIn !== chunk.timestamp) {
                var ab = new ArrayBuffer(chunk.byteLength);
                chunk.copyTo(ab);
                chunk = new EncodedVideoChunk({
                    type: chunk.type,
                    timestamp: _tsIn - videoChunkOffset,
                    duration: chunk.duration,
                    data: ab,
                });
            }
            // Ensure the first chunk ALWAYS carries a full decoderConfig — mp4-muxer
            // sets `track.info.decoderConfig` from this on the first call and reads
            // `.colorSpace` later when it writes the `colr` box. If the encoder
            // emits the first chunk without `meta.decoderConfig` (Opera/Chromium
            // 146 was observed doing this on a 480p source mid-stream — sid
            // c913580e-...), the muxer crashes with "Cannot read properties of
            // null (reading 'colorSpace')". Reconstruct from the encoder config.
            if (!firstChunkSeen) {
                firstChunkSeen = true;
                if (!safeMeta.decoderConfig) {
                    safeMeta = {
                        decoderConfig: {
                            codec: 'avc1.640028',
                            codedWidth: outW,
                            codedHeight: outH,
                            colorSpace: DEFAULT_COLOR_SPACE
                        }
                    };
                } else if (!safeMeta.decoderConfig.colorSpace) {
                    safeMeta = Object.assign({}, safeMeta, {
                        decoderConfig: Object.assign({}, safeMeta.decoderConfig,
                            { colorSpace: DEFAULT_COLOR_SPACE })
                    });
                }
            } else if (safeMeta.decoderConfig && !safeMeta.decoderConfig.colorSpace) {
                // Mid-stream config update without colorSpace — patch in place
                safeMeta = Object.assign({}, safeMeta, {
                    decoderConfig: Object.assign({}, safeMeta.decoderConfig,
                        { colorSpace: DEFAULT_COLOR_SPACE })
                });
            }
            // The canvas frames' sRGB label must not reach `colr` — see muxColorSpace.
            if (safeMeta.decoderConfig && safeMeta.decoderConfig.colorSpace) {
                safeMeta = Object.assign({}, safeMeta, {
                    decoderConfig: Object.assign({}, safeMeta.decoderConfig,
                        { colorSpace: muxColorSpace(safeMeta.decoderConfig.colorSpace) })
                });
            }
            // B-frame-safe muxing (cluster: 120 enhance-video sessions
            // /14d failed "muxer-failed: Timestamps must be monotonically
            // increasing (DTS went from 66666 to 33333)"). The encoder
            // emits chunks in DECODE order; with B-frames their .timestamp
            // (presentation/CTS) is non-monotonic. mp4-muxer derives
            // DTS = timestamp − compositionTimeOffset and rejects a
            // non-monotonic DTS. The encoder config sets latencyMode:
            // 'realtime' to ask for no B-frames, but that is only a HINT —
            // telemetry proves HW encoders (mobile especially) emit
            // B-frames regardless. Fix: chunks ALWAYS arrive in decode
            // order, so DTS is the running sum of chunk durations
            // (strictly monotonic — duration is always > 0); hand the
            // muxer compositionTimeOffset = CTS − DTS so it writes a ctts
            // box. «For a no-B-frame stream DTS === CTS» was said here and
            // was false (04.11.2026): the sum of ROUNDED durations ran ahead
            // of the frames' times, every offset went a few ticks below zero
            // into an unsigned ctts, and our byte-range cut read them as
            // hours. The decode time is the clock's — the k-th smallest frame
            // time fed (window.__encoderClock … dts), on the same rebased
            // timeline as the chunk; the sum stays only for a core without it.
            var _dtsUs;
            if (_encClock) _dtsUs = _encClock.dts(_tsIn) - videoChunkOffset;
            else { _dtsUs = _videoDtsAccumUs; _videoDtsAccumUs += (chunk.duration > 0 ? chunk.duration : _frameDurFallbackUs); }
            _muxChunkIdx++;
            safeMeta = sanitizeChunkMeta(safeMeta);   // fix Firefox's malformed avcC (#458)
            muxVideo(chunk, safeMeta, chunk.timestamp, chunk.timestamp - _dtsUs);
        } catch (e) {
            // Capture so the encode loop bails on next iteration with the
            // root cause + stack trace. Pre-fix this was console.warn only,
            // and the muxer error became invisible until cascade.
            // Telemetry fires ONCE — a bad fixture (DTS!=0, etc.) would
            // throw on every chunk; we only want the first one in /pino.
            var firstMuxerErr = !muxerError;
            if (!muxerError) muxerError = e;
            console.warn('[webcodecs-pipeline] muxer.addVideoChunk:', e.message, e.stack);
            // Rich diagnostic on muxer failure — closes the telemetry blind
            // spot that left 16 events / 7d of "DTS went from X to Y" with
            // no source info to debug from. With these fields we can
            // (a) bucket by src codec/fps/dims, (b) verify the DTS values
            // match my running-sum model, (c) detect chunks that arrived
            // with non-monotonic .timestamp (would imply encoder bug, not
            // muxer fix bug).
            if (firstMuxerErr) _aeWebcodecsErr(aeSlug, 'muxer', e, {
                stage: 'main',
                src_codec: (demuxInfo && demuxInfo.codec || '').substring(0, 20),
                src_w: demuxInfo && demuxInfo.codedWidth || 0,
                src_h: demuxInfo && demuxInfo.codedHeight || 0,
                src_fps: Math.round((demuxInfo && demuxInfo.fps) || 0),
                out_w: outW, out_h: outH,
                chunk_idx: _muxChunkIdx,
                chunk_ts_us: chunk && chunk.timestamp || 0,
                chunk_dur_us: chunk && chunk.duration || 0,
                dts_accum_us: _videoDtsAccumUs,
                cur_dts_us: _dtsUs,
                vco_us: videoChunkOffset || 0
            });
        }
    }
    function _onEncError(e) {
        // Проходу анализа кодер не нужен (он и не настраивается) — его ошибка
        // не ошибка прохода.
        if (analyzeOnly) return;
        var firstEncErr = !encoderError;
        if (!encoderError) encoderError = e;
        console.warn('[webcodecs-pipeline] encoder error:', e && e.message);
        if (firstEncErr) _aeWebcodecsErr(aeSlug, 'encoder', e, {
            stage: 'main',
            err_name: (e && e.name) || '',
            src_codec: (demuxInfo && demuxInfo.codec || '').substring(0, 20),
            src_w: demuxInfo && demuxInfo.codedWidth || 0,
            src_h: demuxInfo && demuxInfo.codedHeight || 0,
            out_w: outW, out_h: outH,
            /* ⚠️ The rate we ASKED the encoder for was missing from this
               payload until 2026-09-06, and it is the field that decides the
               single biggest class of encoder refusals (see
               frameRateCandidates). Without it the whole fleet's 2297 encoder
               errors over 60 days could not be split by frame rate at all —
               the diagnosis had to be reconstructed from one bug report. */
            src_fps: Math.round((demuxInfo && demuxInfo.fps) || 0),
            enc_fps: (encPick && encPick.framerate) || 0,
            enc_clamped: (encPick && encPick.framerateClamped) || 0,
            chunk_idx: _muxChunkIdx
        });
    }
    var encoder = new VideoEncoder({ output: _onEncOutput, error: _onEncError });
    // Try hw → fall through to sw. The browser may also accept omitting the
    // hint entirely (default = no-preference), which we use as a final
    // fallback because some impls (Safari) reject 'prefer-hardware' even
    // when they have it.
    // Cascade through profile/hint combos. pickEncoderConfig returns the
    // first combo where isConfigSupported reports `supported:true`. Used
    // here so encoder.configure can't reject — we pre-validated.
    var encPick;
    if (_vCodecKind === 'vp9') {
        // Решение уже принято выше на НАСТОЯЩЕМ отказе H.264 и на пробе
        // VP9. Здесь только собираем конфиг с УЖЕ посчитанным битрейтом.
        // ⛔ Без `avc:{format:'avc'}` — это поле только для H.264.
        encPick = {
            config: {
                codec: VP9_RESCUE_CODEC,
                width: outW, height: outH,
                bitrate: bitrate, framerate: _encFps,
                latencyMode: (opts.encoderLatency === 'quality') ? 'quality' : 'realtime'
            },
            hardware: false,
            codec: VP9_RESCUE_CODEC
        };
    } else {
        encPick = await pickEncoderConfig(outW, outH, bitrate, _encFps, opts.encoderLatency, opts._encodeHints,
            null, H264_PROFILE_ORDERS[opts.h264Profile] || null, opts.bitrateMode || null, _swBps || null);
    }
    if (!encPick) {
        try { await _probeAltEncoders(outW, outH); } catch (_) {}
        throw new Error('encoder-configure-failed: no h264 profile supported by any hint at ' + outW + 'x' + outH);
    }
    if (encPick.unverified) {
        // The probe rejected every profile — keep the alt-encoder measurement
        // that used to fire on surrender (fire-and-forget), then let
        // configure() below give the authoritative verdict.
        // `_altProbed` — чтобы не слать тот же замер дважды за прогон:
        // решение о VP9 выше уже его вызвало, и двойной hw_enc_alt_probe
        // исказил бы знаменатель той самой статистики, на которой это стоит.
        if (!_altProbed) { try { _probeAltEncoders(outW, outH); } catch (_) {} }
        try { window.__ae && window.__ae('feature_use', { action: 'hw_enc_unverified_attempt', w: outW, h: outH }); } catch (_) {}
    }
    // ⚠️ Проход «только смотрим» (geometry2d.analyzeOnly) ничего не кодирует,
    // и кодер в нём НЕ настраивается. Настроенный и простаивающий весь проход,
    // он держал аппаратный слот (на телефонах их считаные) и в скрытой вкладке
    // изымался браузером — «Codec reclaimed due to inactivity». Восстановление
    // ждало возврата вкладки до 60 с и роняло проход, которому кодер был не
    // нужен вовсе, а /video-retouch уходил на покадровый лейн: замер 25.09.2026
    // на 8-с ролике — 477–731 с против 140 с; в поле за 30 дней это самая
    // частая поломка быстрого лейна тула. Ненастроенный кодер изымать нечего,
    // а flush() на нём отказывает сразу и тихо.
    if (!analyzeOnly) encoder.configure(encPick.config);
    var encoderProfileUsed = encPick.codec;
    var encoderChosenHint = encPick.hint || 'no-hint';

    // Encoder-reclaim recovery helpers. Defined HERE (after encPick is in
    // scope) so the rebuild can re-use the validated config. See the long
    // comment at `_encRecoveryDone` declaration above for cluster context.
    function _waitForForeground(timeoutMs) {
        if (document.visibilityState === 'visible') return Promise.resolve(true);
        return new Promise(function(resolve) {
            var done = false;
            function settle(ok) {
                if (done) return;
                done = true;
                clearTimeout(to);
                document.removeEventListener('visibilitychange', onVis);
                resolve(ok);
            }
            function onVis() {
                if (document.visibilityState === 'visible') settle(true);
            }
            var to = setTimeout(function() { settle(false); }, timeoutMs);
            document.addEventListener('visibilitychange', onVis);
        });
    }
    async function _rebuildEncoderAfterReclaim() {
        // Caller guarantees encoderError && _encCanRebuild(). Count FIRST so a
        // death during this same rebuild cannot re-enter past the budget.
        _encRebuilds++;
        // Asked BEFORE the close below: a rung that never produced a chunk was
        // refused by doing, and is never configured again (see _encRecoverable).
        var _refused = _encRungRefused();
        _aeWebcodecsErr(aeSlug, 'encoder_reclaim_recovery_attempted', new Error(encoderError && encoderError.message || 'reclaim'), {
            stage: 'main',
            err_name: (encoderError && encoderError.name) || '',
            chunk_idx: _muxChunkIdx,
            vis_state: document.visibilityState || '',
            refused: _refused ? 1 : 0, hint: encoderChosenHint
        });
        try { encoder.close(); } catch (_) {}
        var ok = await _waitForForeground(60000);
        if (!ok) {
            _aeWebcodecsErr(aeSlug, 'encoder_reclaim_recovery_failed', new Error('foreground-timeout'), { stage: 'main', chunk_idx: _muxChunkIdx });
            return false;
        }
        // A WEDGE is not rebuilt on the hint that just froze. The next rung of
        // the cascade is re-validated the same way the first pick was
        // (pickEncoderConfig → isConfigSupported, unverified last resort); if
        // no rung remains — the wedge was already on the last one, or the
        // rescue codec is VP9 — the validated config is kept, which is the
        // pre-existing reclaim behaviour.
        // A REFUSED rung climbs the same way, but never falls back to the
        // config it was refused on: no next rung picked → no rebuild.
        var _wedged = !!(encoderError && encoderError.name === 'EncoderWedged');
        var _escalatedTo = '';
        if ((_wedged || _refused) && _vCodecKind !== 'vp9') {
            var _next = _hintsAfter(encoderChosenHint);
            if (_next.length) {
                try {
                    var _pick2 = await pickEncoderConfig(outW, outH, bitrate, _encFps, opts.encoderLatency, _next,
                        null, H264_PROFILE_ORDERS[opts.h264Profile] || null, opts.bitrateMode || null, _swBps || null);
                    if (_pick2 && _pick2.config) {
                        encPick = _pick2;
                        encoderProfileUsed = _pick2.codec;
                        encoderChosenHint = _pick2.hint || 'no-hint';
                        _escalatedTo = encoderChosenHint;
                    }
                } catch (_) { /* keep the validated config */ }
            }
        }
        if (_refused && !_escalatedTo) {
            _aeWebcodecsErr(aeSlug, 'encoder_reclaim_recovery_failed', new Error('refused-rung-no-next'), { stage: 'main', chunk_idx: _muxChunkIdx, hint: encoderChosenHint });
            return false;
        }
        try {
            encoder = new VideoEncoder({ output: _onEncOutput, error: _onEncError });
            encoder.configure(encPick.config);
        } catch (e) {
            _aeWebcodecsErr(aeSlug, 'encoder_reclaim_recovery_failed', e, { stage: 'main', chunk_idx: _muxChunkIdx, escalated_to: _escalatedTo });
            return false;
        }
        if (_encClock) _encClock.restart();   // the new instance states its own clock
        // The new instance owes nothing yet; its silence starts when the replay
        // below hands it the first frame.
        _encOwedSinceVis = -1; _encLastOutVis = -1;
        // Reset error state so the encode loop continues; preserve recoveryDone
        // gate so a second reclaim doesn't loop forever.
        encoderError = null;
        _encForceKeyframeNext = true;
        // Re-feed everything the dead instance swallowed. Without this the
        // stream resumes past a hole and the file is short — the defect the
        // gap accounting used to merely REPORT.
        var _replayed = _replayUnconfirmed();
        if (_replayed) _encOwedSinceVis = _visNow();   // the rebuilt encoder now owes the replay
        _aeWebcodecsErr(aeSlug, 'encoder_reclaim_recovery_ok', new Error('rebuilt'), {
            stage: 'main', chunk_idx: _muxChunkIdx, replayed: _replayed, retain_peak: _retainPeak,
            wedge: _wedged ? 1 : 0, refused: _refused ? 1 : 0, escalated_to: _escalatedTo
        });
        return true;
    }

    // Stylize mode (/video-filter tool) — when opts.stylize is given, the
    // pixel+convolution passes are bypassed in favour of a single artistic
    // FRAG_STYLIZE pass. opts.stylize is a param object from
    // VideoFilterPresets.resolveParams() (colour matrix, duotone, retro).
    var stylizeActive = !!(opts.stylize && typeof opts.stylize === 'object');
    var stylizeBase = stylizeActive ? opts.stylize : null;

    // ── Optional post-shader 2D compose (the /video-filter camcorder OSD) ──
    // Burnt-in graphics and lens softness cannot live in FRAG_STYLIZE: text is
    // not a shader's job, and a second overlay implementation on the export
    // side is exactly the divergence this pipeline exists to prevent (see
    // /video-watermark, which grew four copies of its overlay code). So the
    // pipeline offers ONE compose step between the filter pass and the
    // encoder, and the tool hands it the same draw function it uses for the
    // live preview — preview and download cannot disagree.
    //
    //   opts.overlay(ctx2d, w, h, timeSec) — draw ON TOP of the filtered frame
    //   opts.overlayBlur = px | (h) => px  — lens softness UNDER the overlay
    //
    // overlayBlur takes a function as well as a number because softness has to
    // scale with the OUTPUT height, which only the pipeline knows: the caller
    // picked its slider against a 480p preview and must not get a visibly
    // sharper 1080p download out of the same setting.
    //
    // Blur is applied to the picture only, never to the overlay: a real
    // camcorder's OSD is sharp while the tape image is soft. With neither
    // option set nothing is allocated and renderFrame is unchanged, so every
    // other consumer of run() is byte-for-byte as before.
    var overlayFn   = typeof opts.overlay === 'function' ? opts.overlay : null;
    var overlayBlur = 0;
    try {
        overlayBlur = typeof opts.overlayBlur === 'function'
            ? Math.max(0, +opts.overlayBlur(outH) || 0)
            : Math.max(0, +opts.overlayBlur || 0);
    } catch (_) { overlayBlur = 0; }
    var overlayOn   = !!(overlayFn || overlayBlur > 0);
    var osdCanvas = null, osdCtx = null;
    // Overlay time is measured from the FIRST composed frame, not from the
    // container clock. With a trim the pipeline already rebases ts to 0
    // (see `ts -= trimStartUs`), but an untrimmed file may carry a non-zero
    // base_time, and a camcorder timecode that opens at 00:00:07 because of
    // the source container is a bug the user cannot explain.
    var overlayT0 = -1;

    function composeOverlay(srcCanvas, tsMicros) {
        if (!overlayOn) return srcCanvas;
        if (overlayT0 < 0) overlayT0 = tsMicros || 0;
        if (!osdCanvas) {
            osdCanvas = (typeof OffscreenCanvas !== 'undefined')
                ? new OffscreenCanvas(outW, outH)
                : Object.assign(document.createElement('canvas'), { width: outW, height: outH });
            osdCtx = osdCanvas.getContext('2d', { alpha: false });
        }
        osdCtx.filter = overlayBlur > 0 ? 'blur(' + overlayBlur + 'px)' : 'none';
        osdCtx.drawImage(srcCanvas, 0, 0, outW, outH);
        osdCtx.filter = 'none';
        // A throwing overlay must not kill the export: the frame simply ships
        // without its graphics rather than the whole encode dying at frame 900.
        if (overlayFn) {
            var tSec = Math.max(0, ((tsMicros || 0) - overlayT0) / 1e6);
            try { overlayFn(osdCtx, outW, outH, tSec); } catch (_) {}
        }
        return osdCanvas;
    }

    // Build filter rig — skipped for geometry2d (pure 2D-canvas path needs no
    // WebGL context; also keeps the path alive on webgl2-disabled devices).
    // The HDR → SDR door before the first frame (requested when this file ran;
    // usually already here). Fail-open: without it the browser's picture.
    try { await ensureHdrToSdr(); } catch (_) {}
    var rig = geo ? null : makeFilterRig(outW, outH, { stylize: stylizeActive });
    var geoCanvas = null, geoCtx = null;
    if (geo) {
        geoCanvas = new OffscreenCanvas(outW, outH);
        // alpha:false — output is opaque yuv420 anyway; opaque canvases blit
        // faster and VideoFrame(canvas) skips the alpha-premultiply pass.
        geoCtx = geoCanvas.getContext('2d', { alpha: false });
        if (!geoCtx) throw new Error('no-2d-context');
    }
    // geometry2d.concurrency: у каждого кадра в работе свой холст; очередь —
    // в порядке показа, кодируется всегда ГОЛОВА (renderEncode ниже).
    var _geoSlotsFree = [], _geoInflight = [], _geoDrawIdx = 0, _geoFlushing = false, _geoInflightMax = 0;
    // geometry2d.analyzeOnly: draw() может вернуть Promise — противодавление
    // потребителя, за draw которого стоит пул воркеров (/video-stabilizer,
    // 30.09.2026). Проход ждёт его, прежде чем взять следующий кадр; без этого
    // аппаратный декодер (200–400 кадр/с) обгонял пул, и очередь кадров на
    // разбор росла без предела — у десятиминутного ролика сотни мегабайт.
    // draw, который ничего не возвращает (/video-retouch, /eye-contact), идёт
    // прежним путём байт в байт.
    var _anaHold = null;
    function _takeGeoSlot() {
        var slot = _geoSlotsFree.pop();
        if (slot) return slot;
        var c = new OffscreenCanvas(outW, outH);
        var x = c.getContext('2d', { alpha: false });
        if (!x) throw new Error('no-2d-context');
        return { canvas: c, ctx: x };
    }
    function _geoQueueDraw(slot, ret, ts, dur, isKey) {
        var item = { slot: slot, ts: ts, dur: dur, isKey: isKey, done: false, err: null, p: null };
        item.p = Promise.resolve(ret).then(
            function () { item.done = true; },
            function (e) { item.done = true; item.err = e || new Error('geometry2d-draw-failed'); });
        _geoInflight.push(item);
        if (_geoInflight.length > _geoInflightMax) _geoInflightMax = _geoInflight.length;
    }
    var mapped = mapParams(params);
    // HDR auto-detect — set lazily on first decoded frame. If source is HDR
    // (bt2020 primaries / PQ or HLG transfer), we force the filter rig ON
    // so the tone-mapping pass runs. Without it, encoder gets HDR values
    // it can't represent in 8-bit H.264 → silent highlight clipping.
    var hdrDetected = false;
    var hdrSignaled = false;        // telemetry fires once per run
    var hasFilter = computeHasFilter(mapped);

    var t0 = performance.now();
    var frameTimes = [];
    var frameCount = 0;
    var totalFrames = demuxInfo.totalFrames || 0;
    // Progress is how far through the SOURCE window the run is, not how many
    // frames were encoded: a speed-up drops frames before they are drawn, and
    // frameCount / totalFrames stopped at 1/speed — a 16× run showed 6 % until
    // the file appeared (/video-speed «no percentage shown», 2026-09-21).
    // At speed 1 every window frame is drawn, so the two counts are equal.
    var _winSeen = 0;

    // ── Clip trim (frame-accurate) ──────────────────────────────────────
    // opts.trimStart / opts.trimEnd are seconds into the clip. Because this
    // pipeline RE-ENCODES every frame, trimming is just a decode-time filter:
    // decode everything (the decoder still needs the keyframe before the cut),
    // but only encode frames whose presentation time falls in the window, and
    // rebase their timestamps to 0. Exact — unlike a lossless `-c copy`
    // pre-pass, which can only cut on keyframe boundaries.
    // Trim points are <video> times, i.e. PRESENTATION time — 0 is the movie
    // start once the edit lists are applied. The decode-order first sample was
    // the old base; on a B-frame stream that is the I-frame's composition time
    // (2 frames in), which put every cut 2 frames late.
    var _editApplied = !!(demuxInfo.edit && demuxInfo.edit.applied);
    var _presStartUs = _editApplied ? (demuxInfo.edit.presStartUs || 0) : -Infinity;
    var _presEndUs   = _editApplied ? (demuxInfo.edit.presEndUs > 0 ? demuxInfo.edit.presEndUs : Infinity) : Infinity;
    var _baseTs = _editApplied ? 0 : ((demuxInfo.samples && demuxInfo.samples.length)
        ? (demuxInfo.samples[0].timestamp || 0) : 0);
    var trimStartSec = +opts.trimStart || 0;
    var trimEndSec   = +opts.trimEnd   || 0;
    var doTrim = (trimStartSec > 0.001 || trimEndSec > 0.001) && trimEndSec > trimStartSec;
    var trimStartUs = _baseTs + trimStartSec * 1e6;
    var trimEndUs   = _baseTs + trimEndSec   * 1e6;
    if (doTrim) {
        totalFrames = Math.max(1, Math.round((trimEndSec - trimStartSec) * (demuxInfo.fps || 30)));
    }

    // Diagnostics — counts let us understand truncation in prod telemetry.
    var diagSamples = demuxInfo.samples ? demuxInfo.samples.length : 0;
    var diagDecoded = 0, diagDecodeQueueMax = 0, diagFramesQueueMax = 0, diagEncodeQueueMax = 0;
    // Track the last chunk index + sync flag + size we handed to decoder
    // BEFORE the async decoder.error fires. Combined with decoded_so_far in
    // the error telemetry, the gap (`last_sent - decoded_so_far`) sizes
    // the in-flight queue at fault time, and chunk type (sync/delta) lets
    // us see if mid-stream errors concentrate on broken keyframes vs
    // broken delta frames. Covers the 369 events / 7d "Decoding error"
    // mid-stream cluster (41 % session abandonment after FFmpeg fallback
    // since the user gives up waiting for the slow path).
    var _lastSentChunkIdx = -1, _lastSentChunkSync = false, _lastSentChunkSize = 0;

    // Decode loop
    var decodedFrames = [];
    var decodeError = null;
    /* A decoder that died is CLOSED: its error callback has set decodeError,
       and the next decode() throws «Cannot call 'decode' on a closed codec».
       Until 29.10.2026 the feed looked at decodeError only when the queue was
       full, so a decoder that died with a short queue was fed again — and that
       secondary TypeError replaced the real cause. RU 14 d: 50 sessions on
       /enhance-video ended in «closed codec» (32 without a file); the real
       errors were «Decoding error» / «Unsupported configuration», and since
       the text no longer read «decode: …», run()'s software-decode rescue
       never ran for them. The feed asks before EVERY chunk now. */
    function _decoderDied() {
        if (decodeError) return true;
        var st = ''; try { st = decoder ? decoder.state : ''; } catch (_) {}
        if (st !== 'closed') return false;
        decodeError = new Error('decoder closed');
        return true;
    }
    /* After a decoder rebuild the new instance is fed again from a keyframe
       BEFORE the first frame still owed, so it re-emits frames that were
       already delivered; those are dropped here by timestamp. Output is in
       presentation order, so "at or before the last delivered" is exact. */
    var _decDropUpTo = -Infinity, _decLastOutTs = -Infinity;
    // samples the current decoder must not be fed: its cold-start keyframe's leading pictures (_leadingSkip)
    var _leadSkip = {};
    /* The last frame the renderer actually USED (drew, or dropped on purpose),
       in the decoder's own time. A rebuild owes everything after it — not after
       the last frame the decoder DELIVERED: on iOS the frames still waiting in
       the queue die together with the media session (support #649 — «Buffer
       has no frame» two to sixteen frames in), so they are decoded again. */
    var _decLastUsedTs = -Infinity;
    /* Set when rendering proves the GPU side of the session gone (context lost,
       a frame with no buffer). Until the rebuild, delivered frames are closed on
       arrival — they would be owed again anyway. */
    var _gpuLost = false, _gpuRevivals = 0;
    /* Callbacks answer only for the CURRENT decoder: a retired instance's error
       can land after its replacement exists (a flush rejects before the error
       callback fires) and would be taken for a second death. */
    /* ── A decoder that goes QUIET (15.10.2026) ────────────────────────────
       The encoder has had a wedge detector since 09.09 (_encWedgeCheck); the
       decoder had none in run(), only a first-frame deadline — and a hardware
       decoder whose output pool runs dry stops emitting WITHOUT an error
       (measured 24.08 on VP9: decOut frozen at decQ 25, state 'configured';
       the ENC_QUEUE_HI note below). The feed then spun in its backpressure
       loop and the flush loop waited forever, until the CALLER's watchdog
       threw the whole run away: «hw-stalled» on /video-compressor — Safari 17
       on an iPhone .mov stalled near the end twice in a row (14.10, support
       chat 689) and ~40 sessions in other browsers in 30 days.
       Same budget and the same recovery the decoder's death already has:
       ENC_WEDGE_MS of VISIBLE time with nothing to draw and nothing coming
       out → a synthetic error → _rebuildDecoder() feeds a fresh decoder from
       the keyframe before the first frame still owed. A flush that does not
       return although every fed frame is out is simply left behind — like
       the encoder's (_flushBounded): there is nothing it could still give. */
    var _decOuts = 0, _decWedges = 0, _decWaitVis = -1, _decWaitMark = -1, _maxFedTs = -Infinity;
    // Visible ms the decoder has been silent while we WAIT on it (nothing
    // decoded is waiting to be drawn). Counts from the last frame seen while
    // waiting — an idle spell spent on the encoder is not the decoder's.
    function _decSilentMs() {
        if (decodedFrames.length) { _decWaitVis = -1; return 0; }
        var now = _visNow();
        if (_decWaitVis < 0 || _decWaitMark !== _decOuts) { _decWaitVis = now; _decWaitMark = _decOuts; return 0; }
        return now - _decWaitVis;
    }
    function _decWedgeCheck(where) {
        if (decodeError || aborted) { _decWaitVis = -1; return false; }
        var silent = _decSilentMs();
        if (silent < ENC_WEDGE_MS) return false;
        _decWedges++;
        var e = new Error('decoder wedged: no frame for ' + Math.round(silent / 1000) + 's (' + where + ')');
        e.name = 'DecoderWedged';
        decodeError = e;
        _decWaitVis = -1;
        var dq = -1; try { dq = decoder.decodeQueueSize; } catch (_) {}
        _aeWebcodecsErr(aeSlug, 'decoder_wedge_detected', e, {
            stage: 'main', where: where, silent_ms: Math.round(silent), dec_q: dq,
            decoded_so_far: diagDecoded, last_sent: _lastSentChunkIdx, of: samples.length,
            enc_q: (function () { try { return encoder.encodeQueueSize; } catch (_) { return -1; } })(),
            retained: _sentFrames.size, hint: decoderChosenHint, codec: demuxInfo.codec, src_w: inW, src_h: inH
        });
        return true;
    }
    function _makeDecoder() { var self = new VideoDecoder({
        output: function(frame) {
            if (self !== decoder) { try { frame.close(); } catch(_){} return; }
            _decOuts++;
            if (aborted) { try { frame.close(); } catch(_){} return; }
            if (frame.timestamp <= _decDropUpTo) { try { frame.close(); } catch(_){} return; }
            if (_gpuLost) { try { frame.close(); } catch(_){} return; }
            if (frame.timestamp > _decLastOutTs) _decLastOutTs = frame.timestamp;
            decodedFrames.push(frame);
            diagDecoded++;
            if (decodedFrames.length > diagFramesQueueMax) diagFramesQueueMax = decodedFrames.length;
        },
        error: function(e) {
            if (self !== decoder) return;
            decodeError = e;
            // Enriched telemetry — pre-fix payload was just {err}; we now also
            // carry source codec, source resolution, decoded-so-far count, and
            // the WebCodecs error.name (e.g., NotSupportedError vs OperationError)
            // so the 90 generic "Decoding error" / "Decoder failure" events
            // become actionable classes in /pino.
            //
            // 2026-05-28 — added avcC/first-chunk fingerprint for the
            // "Unsupported configuration" cluster (992 events/7d). The two
            // first-principles hypotheses (multi-SPS/PPS and codedWidth/Height
            // mismatch with SPS coded dims) were both DISPROVED by direct
            // Chromium probing on /enhance-video. The actual root cause must
            // be specific to the bitstream chunk content we cannot synthesise
            // — these fields give /pino enough to triangulate from real
            // user files. avcC_hex32 covers the avcDecoderConfigurationRecord
            // header + first SPS bytes (profile/constraints/level); chunk_nal_b0
            // is the NAL header byte after the 4-byte length prefix on the
            // first sample we tried to decode (key/delta + nal_unit_type).
            var diag = {
                stage: 'main',
                codec: demuxInfo.codec,
                src_w: inW, src_h: inH,
                decoded_so_far: diagDecoded,
                err_name: e && e.name,
                // Mid-stream cluster fields — populated when the error fires
                // after some frames already decoded. `last_sent_idx` is the
                // sample index of the last chunk we handed to decoder.decode
                // before this error; `in_flight` = how many were queued but
                // not yet output (queue depth at fault). If errors concentrate
                // on `last_sync=false` with high `in_flight`, the failure
                // pattern is decoder dropping references; if `last_sync=true`
                // the source's keyframes are themselves broken.
                last_sent_idx: _lastSentChunkIdx,
                last_sync: _lastSentChunkSync,
                last_size: _lastSentChunkSize,
                in_flight: (_lastSentChunkIdx >= 0 && diagDecoded >= 0)
                    ? Math.max(0, _lastSentChunkIdx - diagDecoded)
                    : 0
            };
            try {
                if (demuxInfo.avcC && demuxInfo.avcC.length) {
                    var n = Math.min(32, demuxInfo.avcC.length);
                    var h = '';
                    for (var i = 0; i < n; i++) {
                        h += (demuxInfo.avcC[i] < 16 ? '0' : '') + demuxInfo.avcC[i].toString(16);
                    }
                    diag.avcC_hex32 = h;
                    diag.avcC_len = demuxInfo.avcC.length;
                }
                if (demuxInfo.samples && demuxInfo.samples.length) {
                    var s0 = demuxInfo.samples[0];
                    diag.chunk0_is_sync = !!s0.is_sync;
                    diag.chunk0_size = s0.data ? s0.data.length : 0;
                    // length-prefixed NAL: first 4 bytes are length (big-endian)
                    // then NAL header byte. Capture both to spot lengthSize
                    // mismatches.
                    if (s0.data && s0.data.length >= 5) {
                        diag.chunk0_lenpfx = (s0.data[0]<<24 | s0.data[1]<<16 | s0.data[2]<<8 | s0.data[3]) >>> 0;
                        diag.chunk0_nal_b0 = s0.data[4];
                    }
                    // First 256 hex chars (= 128 bytes) of chunk0 so we can
                    // reconstruct the full NAL layout offline. avcC_hex32
                    // showed only the first SPS — invisible to us was whether
                    // the chunk had SEI / multiple SPS-PPS / inline param-set
                    // updates / etc. before the IDR slice. 128 bytes is enough
                    // to cover length-prefix + first NAL + length-prefix +
                    // second NAL header. Captured ONLY on first-chunk
                    // decoder errors where decoded_so_far=0 (i.e. the
                    // unresolved 1016-event "Unsupported configuration"
                    // cluster) so the telemetry payload stays small for
                    // mid-stream errors.
                    if (s0.data && s0.data.length >= 8 && diagDecoded === 0) {
                        var lim = Math.min(128, s0.data.length);
                        var hh = '';
                        for (var ci = 0; ci < lim; ci++) {
                            var bv = s0.data[ci];
                            hh += (bv < 16 ? '0' : '') + bv.toString(16);
                        }
                        diag.chunk0_hex128 = hh;
                    }
                    // ALSO capture full avcC hex (typically 30-50 bytes) — was
                    // truncated to 32 chars (16 bytes), missing the second
                    // NAL set (PPS) entirely. Multi-NAL avcCs need full visibility.
                    if (demuxInfo.avcC && demuxInfo.avcC.length && diagDecoded === 0) {
                        var avLim = Math.min(128, demuxInfo.avcC.length);
                        var ah = '';
                        for (var aci = 0; aci < avLim; aci++) {
                            var av = demuxInfo.avcC[aci];
                            ah += (av < 16 ? '0' : '') + av.toString(16);
                        }
                        diag.avcC_hex_full = ah;
                    }
                }
            } catch (_) {}
            _aeWebcodecsErr(aeSlug, 'decoder', e, diag);
        }
    }); return self; }
    var decoder = null;
    decoder = _makeDecoder();
    // Same hw → sw → no-hint cascade as encoder. Pre-check via
    // VideoDecoder.isConfigSupported so a synchronous configure() can't
    // throw "Unsupported configuration" at runtime — that class accounted
    // for 18 events / day on /pino 2026-05-08.
    //
    // codec is sanitized via sanitizeAvcLevel — closes the 34 events/14d
    // cluster of `decoder-configure-failed: no hint accepted avc1.6400NN`
    // where NN was a non-spec sub-1.0 level byte (Andrey #bf533dcf
    // 2026-05-27 and others on Yandex Android, where some camera /
    // re-encoder writes bogus AVCLevelIndication).
    //
    // The OTHER 992-event cluster `decoder.error: "Unsupported
    // configuration. Check isConfigSupported() prior to calling
    // configure()"` (fires async with decoded_so_far=0 — i.e. on FIRST
    // chunk after a successful configure) is UNRESOLVED. Direct Chromium
    // probing in 2026-05-28 ruled out: multi-SPS/PPS (1, 2, even zero
    // SPS — all `supported:true`), codedWidth/codedHeight mismatch with
    // SPS coded dims (720×402, 1080×1084 all `supported:true`), and
    // omitting dims entirely. The reproduction is something specific to
    // the chunk-level bitstream content that we cannot synthesise from
    // first principles. Strategy: capture the avcC bytes (first 32 hex)
    // + first chunk's NAL header + chunk_idx in the error telemetry,
    // wait one week of prod data, then triangulate. See the enriched
    // _aeWebcodecsErr payload in the decoder.error callback above.
    var decConfigBase = {
        codec: sanitizeAvcLevel(demuxInfo.codec),
        codedWidth: inW, codedHeight: inH,
        description: demuxInfo.avcC || demuxInfo.hvcC || demuxInfo.av1C || demuxInfo.vpcC  // Wave 18+19 — HEVC/AV1/VP9 sources now carry their config bytes too
    };
    var decConfigured = false;
    var decConfigureError = null;
    var _decConfigChosen = null;          // what the decoder accepted — a rebuild reuses it
    // Wave 23 — cascade is caller-overridable (`opts._decodeHints`) so the
    // run() rescue wrapper can pin ['prefer-software'] on retry. The chosen
    // hint is recorded and attached to decode-loop errors so the wrapper
    // can skip a pointless SW-retry when SW was already the failing hint.
    var _decHints = (opts && opts._decodeHints) || DECODER_HINTS;
    var decoderChosenHint = null;
    for (var dh = 0; dh < _decHints.length; dh++) {
        try {
            var dcfg = Object.assign({}, decConfigBase);
            if (_decHints[dh]) dcfg.hardwareAcceleration = _decHints[dh];
            var dProbe = await VideoDecoder.isConfigSupported(dcfg);
            if (!dProbe || !dProbe.supported) continue;
            decoder.configure(dProbe.config || dcfg);
            decConfigured = true;
            decoderChosenHint = _decHints[dh] || 'no-hint';
            _decConfigChosen = dProbe.config || dcfg;
            break;
        } catch (e) { decConfigureError = e; }
    }
    if (!decConfigured) {
        var dMsg = (decConfigureError && decConfigureError.message)
            ? ('decoder-configure-failed: ' + decConfigureError.message)
            : ('decoder-configure-failed: no hint accepted ' + demuxInfo.codec);
        _aeWebcodecsErr(aeSlug, 'decoder', new Error(dMsg), { stage: 'main', codec: demuxInfo.codec });
        throw new Error(dMsg);
    }

    // Lazy HDR detection — VideoFrame.colorSpace is populated by the decoder
    // from the source's colour metadata (mp4 `colr` / VUI). Hoisted OUT of the
    // WebGL branch: it used to live after the `if (geo) { … return }` early
    // exit, so the geometry2d path (video-crop, video-blur) could never detect
    // HDR, never signalled it, and its HDR sources were invisible in telemetry.
    // The conversion itself is the HDR → SDR door, on both paths: the rig's
    // upload and drawVideo below. `door` says whether it was there.
    function noteHdr(frame) {
        if (hdrDetected || !frame || !frame.colorSpace) return;
        var cs = frame.colorSpace;
        if (cs.primaries !== 'bt2020' && cs.transfer !== 'pq' && cs.transfer !== 'hlg' &&
            cs.transfer !== 'smpte2084' && cs.transfer !== 'arib-std-b67') return;
        hdrDetected = true;
        if (!hdrSignaled) {
            hdrSignaled = true;
            _aeWebcodecsErr(aeSlug, 'hdr_source_detected', new Error('hdr-to-sdr'), {
                stage: 'main', primaries: cs.primaries, transfer: cs.transfer,
                path: geo ? 'geometry2d' : 'webgl', door: window.HdrToSdr ? 1 : 0
            });
        }
    }

    // Helper: render one VideoFrame → encoded chunk via filter rig + encoder.encode
    function renderFrame(frame, ts, frameDur, isKey) {
        noteHdr(frame);
        if (geo) {
            // ── geometry2d path (video-crop) — 2D canvas, no WebGL ─────────
            // `drawVideo(sx,sy,sw,sh, dx,dy,dw,dh)`: drawImage semantics with
            // the SOURCE rect in DISPLAY space. For rotated sources the coded
            // frame is drawn through a rotation transform so the pixels land
            // exactly as the user saw them in the tool's <video> preview.
            // HDR sources: the HDR → SDR door's picture, made once per frame on
            // the first draw (the browser's own 2D picture of an HLG/PQ frame is
            // the dim one — docs/hdr-to-sdr.md). Same size and orientation as
            // the frame, so every rect below holds.
            var gSlot = geoConc > 1 ? _takeGeoSlot() : null;
            var gctx = gSlot ? gSlot.ctx : geoCtx;
            var pic = null;
            var picOf = function() {
                if (!pic) pic = (hdrDetected && window.HdrToSdr) ? window.HdrToSdr.image(frame) : frame;
                return pic;
            };
            var drawVideo = function(sx, sy, sw, sh, dx, dy, dw, dh) {
                if (!rotation) { gctx.drawImage(picOf(), sx, sy, sw, sh, dx, dy, dw, dh); return; }
                gctx.save();
                gctx.beginPath(); gctx.rect(dx, dy, dw, dh); gctx.clip();
                var kx = dw / sw, ky = dh / sh;
                gctx.translate(dx - sx * kx, dy - sy * ky);
                gctx.scale(kx, ky);
                // Now in display space (1 unit = 1 display px, origin at the
                // display frame's top-left). Rotate so the coded frame lands
                // display-oriented: tkhd rotation = degrees the PLAYER turns
                // the coded frame clockwise on display.
                if (rotation === 90)       { gctx.translate(inDispW, 0); gctx.rotate(Math.PI / 2); }
                else if (rotation === 180) { gctx.translate(inDispW, inDispH); gctx.rotate(Math.PI); }
                else                       { gctx.translate(0, inDispH); gctx.rotate(-Math.PI / 2); }
                gctx.drawImage(picOf(), 0, 0);
                gctx.restore();
            };
            // 6th arg timeSec = this frame's presentation time in SECONDS
            // (ts is µs). video-crop ignores it; video-blur uses it to place
            // time-varying (tracked / windowed) blur masks per frame. Backward
            // compatible — extra positional args are invisible to old callbacks.
            //
            // 7th arg = the raw decoded VideoFrame, 8th = what the callback
            // needs to interpret it. `drawVideo` can only put pixels into the
            // 2D canvas, which is enough for geometry (crop / pad / stack) but
            // not for a per-pixel WARP: /video-to-vr's lens pre-distortion is a
            // radial resample a 2D canvas cannot do without a mesh. Handing the
            // frame over lets a consumer upload it straight to its own WebGL
            // texture and blit the result back — one extra GPU copy instead of
            // a second full pipeline.
            // ⚠️ The frame is CODED, not displayed: drawVideo bakes the tkhd
            // rotation in for you, a raw consumer must do it itself, so the
            // angle and the display size travel with it. Every phone writes
            // that matrix on portrait clips, and without it the hardware lane
            // would silently deliver a sideways file where the preview showed
            // an upright one. The frame is closed right after this call — a
            // callback must not retain it beyond the draw. `info.sdr()` is the
            // frame as a texture source in SDR (the HDR → SDR door; the frame
            // itself when it is SDR): the coded frame of an HDR source uploads
            // as its raw HLG/PQ signal.
            var drawRet;
            try { drawRet = geo.draw(gctx, drawVideo, outW, outH, gSlot ? _geoDrawIdx++ : frameCount, ts / 1e6, frame,
                           { rotation: rotation || 0, dispW: inDispW, dispH: inDispH,
                             sdr: function () { return window.HdrToSdr ? window.HdrToSdr.image(frame, { texture: true }) : frame; } }); }
            finally { try { frame.close(); } catch(_){} }
            if (analyzeOnly) {
                frameCount++;
                if (drawRet && typeof drawRet.then === 'function') _anaHold = drawRet;
                return;
            }
            if (gSlot) { _geoQueueDraw(gSlot, drawRet, ts, frameDur, isKey); return; }
            return renderEncode(composeOverlay(geoCanvas, ts), ts, frameDur, isKey);
        }
        var gl = rig.gl;
        // (HDR detection + rig force-on happened in noteHdr() above — one
        // implementation for both the WebGL and geometry2d paths.)
        // Frame → shared drawFiltered (the ONE filter-draw implementation,
        // also powering renderStill / the enhance-video live preview).
        var _fseed = (frameCount * 131 % 9973) / 9973;
        drawFiltered(rig, frame, {
            mapped: mapped,
            hasFilter: hasFilter,
            needsConv: rig.needsConv(params),
            // Temporal denoise across frames — export loop only (renderStill
            // has no frame history, so previews show the spatial part alone:
            // an honest lower bound of what the export removes). K=0.75 is
            // the measured optimum on moving content at every noise level.
            temporalK: (mapped.denoise > 0.001 && !stylizeActive) ? 0.75 : 0,
            seed: _fseed,
            stylizeUniforms: stylizeActive ? Object.assign({}, stylizeBase, {
                time:    frameCount / (_encFps),
                seed:    _fseed
            }) : null
        });
        try { frame.close(); } catch(_){}
        return renderEncode(composeOverlay(rig.canvas, ts), ts, frameDur, isKey);
    }

    // ── Frames handed to the encoder but not yet confirmed as output ─────────
    // A codec discards its whole input queue when it dies, and until now those
    // frames were simply gone: we closed each VideoFrame right after encode(),
    // so a rebuilt encoder resumed from wherever the stream happened to be and
    // the output came back SHORT (12-39 frames on the bench). That was patched
    // over with a "gap under half a second is acceptable, tell the user" rule —
    // an arbitrary threshold plus an apology in the UI.
    // Keeping the frame alive until its chunk arrives removes the gap instead
    // of forgiving it: on rebuild we simply re-encode what was never confirmed.
    // Memory is bounded because the encoder queue is bounded (ENC_QUEUE_HI);
    // RETAIN_CAP is the belt-and-braces ceiling, measured — see the cap check.
    var _sentFrames = new Map();          // ts → VideoFrame, insertion order = ts order
    var _retainPeak = 0;                  // diagnostics: how deep it really gets
    var _retainDropped = 0;               // frames the cap forced us to release
    // Ceiling in FRAMES. It is derived from the thing that actually drives the
    // depth — the encoder queue — not from a memory budget: a budget in bytes
    // reads well but does not bind, because retaining FEWER frames than the
    // queue holds makes retention pointless (it would clip on every rebuild).
    // A first attempt did exactly that and had to be floored back up, at which
    // point the "budget" was decoration.
    // Measured natural peak: 14-26 frames at 720p/1080p, 30 at 3840×2160 — it
    // tracks the queue, because each frame is released the moment its chunk
    // lands. Queue + 8 covers that with headroom and caps the pathological case
    // (a codec that accepts frames and emits nothing).
    // Cost, stated plainly (NV12 ≈ 1.5 bytes/px): ~100 MB at 1080p, ~400 MB at
    // 3840×2160. That is the price of a lossless recovery on the fast lane; the
    // alternative measured 3.2× slower AND still lost frames.
    // ⚠️ Assigned next to ENC_QUEUE_HI below, not here. Written here as
    // `ENC_QUEUE_HI + 8` it evaluated BEFORE that `var` was assigned — NaN —
    // and `size > NaN` is never true, so the ceiling this comment describes
    // never held (found 2026-09-12 while making the wedge rebuild lossless).
    var RETAIN_CAP;
    function _releaseSent(ts) {
        var f = _sentFrames.get(ts);
        if (f) { try { f.close(); } catch (_) {} _sentFrames.delete(ts); }
    }
    function _releaseAllSent() {
        _sentFrames.forEach(function (f) { try { f.close(); } catch (_) {} });
        _sentFrames.clear();
    }
    // Re-encode everything the dead encoder never confirmed, oldest first, so
    // the rebuilt encoder continues the stream instead of skipping a hole. The
    // first replayed frame carries the forced keyframe the muxer needs at a
    // bitstream restart.
    function _replayUnconfirmed() {
        if (!_sentFrames.size) return 0;
        var items = Array.from(_sentFrames.entries()).sort(function (a, b) { return a[0] - b[0]; });
        _sentFrames.clear();
        var first = true, n = 0;
        for (var i = 0; i < items.length; i++) {
            var ts = items[i][0], fr = items[i][1];
            try {
                if (_encClock) _encClock.fed(ts, fr.duration || 0);
                encoder.encode(fr, { keyFrame: first || false });
                _sentFrames.set(ts, fr);      // still unconfirmed until its chunk lands
                first = false; n++;
            } catch (e) {
                try { fr.close(); } catch (_) {}
            }
        }
        _encForceKeyframeNext = false;         // the replay already forced one
        return n;
    }

    // Shared encode tail for both render paths (WebGL rig / geometry2d canvas).
    function renderEncode(srcCanvas, ts, frameDur, isKey) {
        // Take canvas → VideoFrame for encode
        var outFrame = new VideoFrame(srcCanvas, {
            timestamp: ts,
            duration: frameDur
        });
        // Surface root-cause errors BEFORE calling encode(). Pre-fix: encoder
        // entered closed state silently → next encode() threw cryptic
        // "Cannot call 'encode' on a closed codec" → cascade-misattribution.
        if (encoderError) {
            // A recoverable death (see _encRecoverable) — signal the outer
            // loop to await a rebuild between drain passes. This frame was
            // rendered but never reached the dead codec: it joins the frames the
            // rebuild replays, so the stream has no hole where the death was
            // noticed. (2026-11-04: it used to be CLOSED here and frameCount
            // never counted it, so the lost-frame check could not see the hole —
            // one frame short at every rebuild, silently.)
            if (_encRecoverable() && _encCanRebuild()) {
                if (!_sentFrames.size) _encOwedSinceVis = _visNow();
                _sentFrames.set(ts, outFrame);
                frameCount++;
                _pendingEncRecovery = true;
                return;
            }
            try { outFrame.close(); } catch(_){}
            _releaseAllSent();
            try { decoder.close(); encoder.close(); } catch(_){}
            { var _ef1 = new Error('encoder-failed: ' + (encoderError.message || encoderError)); _ef1.encHint = encoderChosenHint; throw _ef1; }
        }
        if (muxerError) {
            try { outFrame.close(); } catch(_){}
            _releaseAllSent();
            try { decoder.close(); encoder.close(); } catch(_){}
            throw new Error('muxer-failed: ' + (muxerError.message || muxerError));
        }
        if (diskWriteFailed(muxTarget)) {
            try { outFrame.close(); } catch(_){}
            _releaseAllSent();
            try { decoder.close(); encoder.close(); } catch(_){}
            throw diskWriteError(muxTarget.opfs.lane, null, _needBytes);
        }
        var _forcedKey = _encForceKeyframeNext;
        if (_forcedKey) _encForceKeyframeNext = false;
        if (_encClock) _encClock.fed(ts, frameDur);
        encoder.encode(outFrame, { keyFrame: isKey || _forcedKey });
        // Hold it until its chunk comes back (see _sentFrames) instead of
        // closing here — that is what makes a rebuild lossless.
        if (!_sentFrames.size) _encOwedSinceVis = _visNow();   // silence is measured from here
        _sentFrames.set(ts, outFrame);
        if (_sentFrames.size > _retainPeak) _retainPeak = _sentFrames.size;
        // Hard ceiling so a codec that accepts frames and emits nothing cannot
        // grow this without bound. Releasing the OLDEST is the right end: those
        // are the least likely to still be replayable, and the gap accounting
        // after the flush still reports anything actually lost.
        while (_sentFrames.size > RETAIN_CAP) {
            var oldest = _sentFrames.keys().next();
            if (oldest.done) break;
            _releaseSent(oldest.value);
            _retainDropped++;
        }
        frameCount++;
    }

    // Drive decoder by feeding chunks from demuxInfo.samples. We do NOT
    // call decoder.flush() per chunk — that drains the pipeline AND requires
    // the next decode() to start on a keyframe (documented WebCodecs
    // behaviour). Instead: pump chunks, opportunistically drain decodedFrames
    // queue with a microtask yield to let async output fire, apply
    // backpressure when the queue grows.
    var samples = demuxInfo.samples;
    var _lazyRd = demuxInfo.lazy ? _lazySampleReader(demuxInfo) : null;
    /* ── Decoder rebuild (2026-09-26, support #649) ─────────────────────────
       A decoder that DIES mid-stream took the whole fast lane with it, and the
       caller's software fallback is what kills the tab on a phone. Measured on
       iOS, 30 d: 4K sources finished in 60 % of sessions (1080p: 90 %), the
       page died in 21 % (1080p: 2.5 %), "Decoder failure" / "Decoding task did
       not complete" at every point of the clip, often together with the
       encoder — the media session goes, not the file (the same 4K clip
       decodes on the next attempt). The chance of a death grows with length
       (≤20 s 67 %, >60 s 37 %): a per-frame hazard, which a restart survives.

       So a decoder that has already WORKED (emitted frames) is rebuilt with
       the config it accepted, fed again from the keyframe before the first
       frame still owed (one GOP further when frames reorder, for open-GOP
       leading pictures), and its re-emitted frames are dropped by timestamp.
       Needs the lazy demux — the fed samples' bytes were released and are
       re-read from the file. A budget of DEC_REBUILD_MAX; past it the error
       surfaces exactly as before. Returns the sample index to feed from, or -1. */
    var _decRebuilds = 0, DEC_REBUILD_MAX = 3, _decReorders = null;
    function _samplesReorder() {
        if (_decReorders !== null) return _decReorders;
        _decReorders = false;
        for (var ro = 1; ro < samples.length && ro < 4000; ro++) {
            if (samples[ro].timestamp < samples[ro - 1].timestamp) { _decReorders = true; break; }
        }
        return _decReorders;
    }
    /* Can a fresh decoder start on this sample? H.264: it carries an IDR slice
       (NAL 5). HEVC: an IRAP picture (16–23). Other codecs: their sync samples
       are real keyframes. Reads only the sample's own bytes (lazy demux). */
    async function _isDecoderEntry(s) { return _sampleStartsADecoder(demuxInfo.codec, s, _lazyRd); }
    function _glLost() {
        try { return !!(rig && rig.gl && typeof rig.gl.isContextLost === 'function' && rig.gl.isContextLost()); }
        catch (_) { return true; }
    }
    /* A render that failed because the session's GPU side is gone — as opposed
       to a bug, or a codec failure the typed paths already own. WebKit's
       new VideoFrame(OffscreenCanvas) reads the pixels back (getPixelBuffer) and
       throws «Buffer has no frame» when there are none. */
    function _isSessionLoss(e) {
        if (_glLost()) return true;
        var n = (e && e.name) || '', m = String((e && e.message) || e || '');
        if (n === 'AbortError' || /^(encoder-failed|muxer-failed|demux-read):/.test(m)) return false;
        return n === 'InvalidStateError' || n === 'OperationError'
            || /Buffer has no frame|Canvas has no frame|has no frame|closed|detached|context lost/i.test(m);
    }
    /* After the loss the rig's context is dead for good; a fresh canvas gets a
       fresh context once the GPU process is back. Everything per frame reaches
       drawFiltered as arguments, so a new rig loses no settings — only the
       temporal-denoise history, i.e. one frame of it. */
    async function _reviveCanvases() {
        if (!_gpuLost && !_glLost()) return true;
        for (var a = 0; a < 4; a++) {
            try {
                if (rig) rig = makeFilterRig(outW, outH, { stylize: stylizeActive });
                if (geo) {
                    geoCanvas = new OffscreenCanvas(outW, outH);
                    geoCtx = geoCanvas.getContext('2d', { alpha: false });
                    if (!geoCtx) throw new Error('no-2d-context');
                }
                osdCanvas = null; osdCtx = null;      // composeOverlay builds a new one on demand
                _gpuLost = false; _gpuRevivals++;
                return true;
            } catch (_) { await new Promise(function (r) { setTimeout(r, 500); }); }
        }
        return false;
    }
    async function _rebuildDecoder(where) {
        if (!_lazyRd || !_decConfigChosen || diagDecoded <= 0 || aborted) return -1;
        if (_decRebuilds >= DEC_REBUILD_MAX) return -1;
        _decRebuilds++;
        var deadErr = decodeError;
        var _dqAtDeath = -1, _dfAtDeath = decodedFrames.length, _eqAtDeath = -1;
        try { _dqAtDeath = decoder.decodeQueueSize; } catch (_) {}
        try { _eqAtDeath = encoder.encodeQueueSize; } catch (_) {}
        try { decoder.close(); } catch (_) {}
        if (!(await _waitForForeground(60000))) return -1;
        /* Frames the dead decoder delivered but nobody has drawn yet are owed
           again — whether or not they survived the death. */
        while (decodedFrames.length) { var _dfq = decodedFrames.shift(); try { _dfq.close(); } catch (_) {} }
        var _revived = _gpuLost || _glLost();
        if (!(await _reviveCanvases())) return -1;
        var owedTs = _decLastUsedTs;
        var upTo = Math.min(Math.max(_lastSentChunkIdx, _feedFrom), samples.length - 1);
        var first = -1;
        for (var j = _feedFrom; j <= upTo; j++) { if (samples[j].timestamp > owedTs) { first = j; break; } }
        if (first < 0) first = Math.min(upTo + 1, samples.length - 1);
        var k = first;
        while (k > _feedFrom && !samples[k].is_sync) k--;
        if (_samplesReorder() && k > _feedFrom) {
            var k2 = k - 1;
            while (k2 > _feedFrom && !samples[k2].is_sync) k2--;
            if (samples[k2].is_sync) k = k2;
        }
        /* A "sync" sample is not always a frame a FRESH decoder can start on:
           an open-GOP stream marks its recovery-point I-frames as sync, and
           Chrome refuses them ("marked as type `key` but wasn't a keyframe" —
           measured on an x264 open-GOP fixture). Walk back to a true IDR. */
        for (var guard = 0; guard < 400; guard++) {
            if (samples[k].is_sync && await _isDecoderEntry(samples[k])) break;
            if (k <= _feedFrom) return -1;
            k--;
            while (k > _feedFrom && !samples[k].is_sync) k--;
        }
        if (!samples[k].is_sync) return -1;
        decodeError = null;
        _decDropUpTo = owedTs;
        // the fresh decoder starts cold at k: its leading pictures stay out (_leadingSkip)
        _leadSkip = _leadingSkip(samples, k, samples.length);
        try {
            decoder = _makeDecoder();
            decoder.configure(_decConfigChosen);
        } catch (e) { decodeError = e; return -1; }
        _firstDecodeAtMs = 0;
        _aeWebcodecsErr(aeSlug, 'decoder_rebuilt', new Error((deadErr && deadErr.message) || 'decoder died'), {
            stage: 'main', where: where, n: _decRebuilds, decoded_so_far: diagDecoded,
            resume_from: k, first_owed: first, last_sent: _lastSentChunkIdx, lead: _leadSkip.n || 0,
            src_w: inW, src_h: inH, vis_state: document.visibilityState || '',
            enc_dead: encoderError ? 1 : 0, gpu_revived: _revived ? 1 : 0,
            /* Depths at the moment of death. A shallower pipeline for big frames
               on phones was tried and NOT shipped (2026-09-26): the gain on iOS
               cannot be measured from a desktop, and the cost can — an encoder
               queue of 12 made a 4K export 3.2x slower (see ENC_QUEUE_HI). If
               deaths gather at full queues on iOS, that is the evidence to run
               it as a field experiment. */
            dec_q: _dqAtDeath, dec_held: _dfAtDeath, enc_q: _eqAtDeath,
            retained: _sentFrames.size, retain_peak: _retainPeak,
            out_w: outW, out_h: outH
        });
        return k;
    }
    // ── Datamosh ────────────────────────────────────────────────────────
    // The ONE look in this tool that is not a shader: it edits the compressed
    // sample list before the decoder sees it, so the decoder produces the
    // effect by doing its ordinary job on a stream that lies to it. Injected
    // here because this is the last point where the samples are still a plain
    // list — everything below feeds them to the decoder one by one.
    // ⚠️ window.VideoMosh, not `root`: this file has no module-level `root`
    // (the name is a local in two unrelated OPFS helpers), so `root.VideoMosh`
    // is a ReferenceError — and one that only fires on a mosh export, i.e.
    // never during ordinary use or a normal smoke run.
    if (opts && opts.mosh && typeof window !== 'undefined' && window.VideoMosh) {
        try {
            var _moshed = window.VideoMosh.plan(samples, opts.mosh);
            if (_moshed && _moshed.samples && _moshed.samples.length) {
                samples = _moshed.samples;
                if (typeof opts.onMoshPlan === 'function') { try { opts.onMoshPlan(_moshed.notes); } catch (_) {} }
            }
        } catch (_) { /* fail-open: a broken plan must not cost the export */ }
    }
    var _firstEncoded = true;
    // video-speed: the output frame grid on a speed-up (see the drain below).
    // The same grid decimates to opts.maxFps at speed 1 (see _decimate).
    var _slotUs = 1_000_000 / (_decimate ? _capFps : (demuxInfo.fpsExact || demuxInfo.fps || 30));
    var _nextSlotUs = null;
    // ── Encoder-queue backpressure (ported from the interp path, 2026-08-09) ──
    // The interpolation copy of this pipeline已 solved this in July: the decode
    // gate (decodeQueueSize / decodedFrames) cannot see the ENCODER's input
    // queue, so on any machine where the encoder is slower than decode+render
    // the queue grows without bound. It measured 7360 queued frames (~20+ GB
    // outside the JS heap) on a 235 MB mov and fixed it with a 24-frame high
    // water mark. run() never got the same gate, and the field says it has the
    // same disease: `enc_q_max` over 7743 real HW exports is median 15, p75 51,
    // p90 229, p99 1458, **max 6521**.
    // 24 sits well above HW encoder pipelining depth (8-16), so throughput is
    // unaffected — verified on real footage, see the bench notes — and it also
    // caps what a dying encoder can swallow.
    //
    // ⛔ Do NOT scale this down for big frames. Tried `>2.5 MPx ? 12 : 24` to
    // halve the memory held at 4K; measured on a real 3840×2160 clip it made
    // the clean export 31.4 s → 101.3 s (**3.2×**) and still lost a frame on
    // the sabotaged run. "12 is above hardware pipelining depth (8-16) so it
    // is free" was wrong — the same wrong guess as the first gate attempt.
    // Memory is bounded at the RETENTION end instead (RETAIN_CAP, byte-aware).
    // ⚠️ Потолок ЗАВИСИТ ОТ КОДЕКА, и это не вкусовщина, а измеренная причина.
    // 24 подобраны под H.264: он кодирует быстрее, чем цикл успевает подавать,
    // и очередь до потолка почти не доходит. У VP9 (программный libvpx) она
    // упирается в потолок и держит два десятка кадров 1080p; вместе с
    // ожидающими декодированными это исчерпывает пул, и ДЕКОДЕР встаёт МОЛЧА:
    // decOut перестаёт расти при decQ 25 и state 'configured', без ошибки.
    // Кодировщик и мультиплексор при этом чисты (muxed == encOut, удержанных
    // кадров ноль) — то есть лечить надо подачу, а не кодек.
    //
    // Замер 24.08, одна фикстура 1080p, серии по 6:
    //   VP9 при потолке 24 — 0 из 6 (все зависли)
    //   VP9 при потолке  8 — 6 из 6
    //
    // ⚠️ H.264 НЕ ТРОНУТ намеренно, хотя та же гонка есть и у него
    // (на непропатченном проде 2 зависания из 4): потолок 8 чинит и его
    // (4 из 4), но стоит 8–12 с → 11–17 с на том же стенде. Это отдельное
    // решение с отдельной проверкой, а не побочный эффект правки про VP9.
    var ENC_QUEUE_HI = (_vCodecKind === 'vp9') ? 8 : 24;
    RETAIN_CAP = ENC_QUEUE_HI + 8;
    async function drainEncoderQueue() {
        /* ⛔ Gate on `encodeQueueSize` ONLY — never on how many frames are
           still unconfirmed. Retention is a MEMORY ceiling, not a pacing
           device, and holding the feed at it deadlocks a deep-buffering
           encoder: measured in real Firefox on this very page, it accepts 186
           frames before returning 118, so a feed that stops at 32 unconfirmed
           starves the encoder, the encoder then emits nothing, and the wedge
           detector below "finds" a wedge that the gate itself created. (It
           did: the Firefox end-to-end guard went from green to
           «encoder wedged: no output for 12s with 32 frame(s) owed».)
           Healthy pacing, measured the same way: Chromium first chunk 472 ms,
           worst gap 211 ms; Firefox 1645 ms and 1003 ms. Both sit far under
           ENC_WEDGE_MS, which is what makes that budget safe — as long as
           nothing here throttles the encoder into silence. */
        while (encoder.encodeQueueSize > ENC_QUEUE_HI) {
            if (aborted) throw new DOMException('cancelled', 'AbortError');
            if (encoderError) return;    // renderEncode's own handling surfaces it
            // A full queue that never drains is the wedge's usual shape — this
            // loop used to spin here until the caller's watchdog tore the run
            // down. Declaring it makes drainOne's recovery path run instead.
            if (_encWedgeCheck('encode')) return;
            await _yieldTick(8);
        }
    }
    async function drainOne() {
        // Instrumentation only — track the peak encoder backlog. NO behavior
        // change (no cap). Open question: are Android wedges (~80 % of
        // Android-mobile exports stall mid-stream, decoded_so_far ≥100 frames)
        // caused by MediaCodec input-buffer overflow when decoder out-paces
        // encoder? Desktop test (forced SW encoder) showed queue stayed at 0
        // because per-frame WebGL work paced the loop ≈ encoder speed. On
        // Android mobile decoder/encoder speeds differ more — queue may grow.
        // Reported via hw_pipeline_used `enc_q_max` for the telemetry watch
        // (T+7d). If Android shows growth correlated with wedges → encoder
        // backpressure becomes the root fix; if queue stays low → wedge is
        // elsewhere (driver-level MediaCodec bug, no JS fix) and the
        // platform-default SW-first cascade was the right call.
        if (encoder.encodeQueueSize > diagEncodeQueueMax) diagEncodeQueueMax = encoder.encodeQueueSize;
        // ── Hold the queue while the encoder is down (2026-08-09) ───────────
        // The encoder dies asynchronously; the outer loop only rebuilds it
        // between drain passes. Without this guard the drain keeps dequeuing
        // decoded frames in the meantime and renderEncode silently DROPS each
        // one — measured on the bench: a rebuild after frame 60 delivered
        // 160 of 180 frames, i.e. 0.67 s of video gone, while the container
        // duration still read a truthful-looking 6.02 s. A file that is fast,
        // downloads fine and quietly misses frames is worse than the slow
        // fallback it replaced.
        // `decodedFrames` IS the holding queue — leaving frames in it costs no
        // extra memory and lets the existing backpressure stall the decoder
        // until the rebuild lands.
        if (_pendingEncRecovery) return false;
        // The other wedge shape: a codec that keeps ACCEPTING frames (queue
        // never fills) and emits nothing. Without this check retention would
        // hit RETAIN_CAP and start releasing frames — the lossless rebuild
        // would then have holes to replay around. Checked before every frame.
        _encWedgeCheck('encode');
        if (encoderError && _encRecoverable() && _encCanRebuild()) {
            _pendingEncRecovery = true;
            return false;
        }
        // Wait for encoder headroom BEFORE taking a frame off the queue, so at
        // most one un-queued frame is alive during the wait — the same shape
        // the interp path uses.
        // ⚠️ An earlier attempt gated here with `if (encodeQueueSize >= 4)
        // return false;` — a SYNCHRONOUS refusal with a cap BELOW hardware
        // pipelining depth. It collapsed throughput 10× (clean 6 s fixture
        // 2.3 s → 46-52 s) because returning false ends the drain batch and the
        // caller moves on to the next decode instead of waiting. Awaiting at 24
        // is the difference between backpressure and starvation.
        // ── geometry2d.concurrency: голова очереди кадров в работе ──────────
        // Кодируется только она и только когда готова: первой по готовности,
        // при полной очереди (ждём её) и на финальном сбросе. Пока голова не
        // готова и место есть, берём следующий кадр — так в работе N кадров.
        if (_geoInflight.length) {
            var gHead = _geoInflight[0];
            if (gHead.done || _geoFlushing || _geoInflight.length >= geoConc) {
                if (!gHead.done) await gHead.p;
                if (aborted || gHead.err) {
                    _releaseAllSent();
                    try { decoder.close(); } catch (_) {}
                    try { encoder.close(); } catch (_) {}
                    if (aborted) throw new DOMException('cancelled', 'AbortError');
                    throw gHead.err;
                }
                // Кодер мог умереть, пока ждали: те же проверки, что выше, —
                // и голова остаётся в очереди до восстановления.
                if (_pendingEncRecovery) return false;
                _encWedgeCheck('encode');
                if (encoderError && _encRecoverable() && _encCanRebuild()) {
                    _pendingEncRecovery = true;
                    return false;
                }
                await drainEncoderQueue();
                _geoInflight.shift();
                renderEncode(composeOverlay(gHead.slot.canvas, gHead.ts), gHead.ts, gHead.dur, gHead.isKey);
                _geoSlotsFree.push(gHead.slot);
                return true;
            }
        }
        await drainEncoderQueue();
        if (_gpuLost) return false;
        var fr = decodedFrames.shift();
        if (!fr) return false;
        var ts = fr.timestamp != null ? fr.timestamp : 0;
        var _srcTs = ts;
        if (_editApplied && (ts < _presStartUs || ts >= _presEndUs)) {
            // Outside the edit list's presentation window — the camera's
            // video lead, an editor's trimmed tail. Decoded because later
            // frames reference it, shown by no player, never encoded here.
            try { fr.close(); } catch (_) {}
            if (_srcTs > _decLastUsedTs) _decLastUsedTs = _srcTs;
            return true;
        }
        if (doTrim) {
            // Outside the trim window — the frame was decoded only so the
            // window's frames can be reconstructed; it is never encoded.
            if (ts < trimStartUs - 1 || ts >= trimEndUs) {
                try { fr.close(); } catch (_) {}
                if (_srcTs > _decLastUsedTs) _decLastUsedTs = _srcTs;
                return true;
            }
            ts -= trimStartUs;            // rebase so the output starts at 0
        }
        _winSeen++;
        // How long this frame is shown. Prefer the frame's OWN duration — it comes
        // from the source sample table and is right even for variable-rate footage.
        // Fall back to the exact (unrounded) rate, and only then to the rounded one.
        //
        // ⚠️ This used to be `1_000_000 / (demuxInfo.fps || 30)`, and demuxInfo.fps is
        // Math.round()ed at the demuxer — so every 29.97 fps source was written with
        // 33333 µs frames instead of 33366.7. Measured on a 120 s 29.97 clip: the
        // chunk TIMESTAMPS were already correct (mean delta 33366.67 µs = 29.9700 fps
        // exactly), but mp4-muxer takes the sample duration from this field, so the
        // stts came out as 289 runs averaging 30.0003 fps — the picture ran ~0.1 %
        // fast against its own soundtrack, and the run-length table fragmented.
        // 25 / 30 / 50 / 60 fps sources were exact, which is why it went unseen.
        var dur = (fr.duration && fr.duration > 0)
            ? fr.duration
            : (1_000_000 / (demuxInfo.fpsExact || demuxInfo.fps || 30));
        if (speed !== 1 || _decimate) {
            var _outTs = ts / speed;
            if (speed > 1 || _decimate) {
                // Speed-up: keep the frame nearest each slot of an output grid
                // at the SOURCE frame rate — what FFmpeg's constant-rate output
                // does — so a 16× of a 30 fps clip stays 30 fps instead of a
                // 480 fps stream the HW encoder rejects. Each kept frame lasts
                // one grid step: mp4-muxer writes the sample table from this
                // duration, so it must be the OUTPUT spacing, not the source's.
                // Until 2026-09-25 it was the source duration, and the gap test
                // allowed 0.2 ms of slack: on a timeline quantised to 1 ms (every
                // WebM, many phone MP4s) or at a fractional speed, extra frames
                // fell out (a 29.97 fps WebM at 2× kept 661 frames of 900) and
                // the video track came out shorter than its own timestamps —
                // 21.8 s declared against 30.0 s of pictures. A quarter-slot of
                // tolerance absorbs quantisation without admitting a second
                // frame into one slot.
                if (_nextSlotUs !== null && _outTs < _nextSlotUs - _slotUs / 4) {
                    try { fr.close(); } catch (_) {}
                    if (_srcTs > _decLastUsedTs) _decLastUsedTs = _srcTs;
                    if (totalFrames > 0) onProgress(Math.min(0.95, _winSeen / totalFrames));
                    return true;
                }
                // Step along the grid; restart it only across a real gap in the
                // source (the next slot already behind this frame). Re-anchoring
                // on every late frame shifted the grid forward each time and
                // dropped every other frame at 1.5× (0.75 of the source rate).
                _nextSlotUs = (_nextSlotUs === null ? _outTs : _nextSlotUs) + _slotUs;
                if (_nextSlotUs <= _outTs) _nextSlotUs = _outTs + _slotUs;
                dur = _slotUs;
            } else {
                dur = dur / speed;   // slow-mo: hold each kept frame longer
            }
            ts = _outTs;
        }
        // The first shown frame is pinned at 0 — every track of an MP4 without
        // an edit list starts there — and holds until the second one arrives:
        // that hold is how a leading empty edit (the picture joins late) and
        // the audio residual (passthrough audio still carries up to one access
        // unit from before the cut) are expressed without an edit list. Every
        // later frame sits at its presentation time plus that residual.
        if (_firstEncoded) ts = 0;
        else if (_videoDelayUs > 0) ts += _videoDelayUs;
        /* A render that fails because the media session's GPU side died (iOS:
           the decoder, the encoder and the WebGL context go together) is a
           death like the decoder's, not the end of the run: the frame is owed
           again and the rebuild revives the canvases. Anything else still
           throws, as before. */
        try {
            if (_glLost()) throw new Error('context lost');
            renderFrame(fr, ts, dur, doTrim && _firstEncoded);
        } catch (_rErr) {
            if (!_isSessionLoss(_rErr)) throw _rErr;
            try { fr.close(); } catch (_) {}
            _gpuLost = true;
            while (decodedFrames.length) { var _dq2 = decodedFrames.shift(); try { _dq2.close(); } catch (_) {} }
            if (!decodeError) decodeError = new Error('gpu-lost: ' + String((_rErr && _rErr.message) || _rErr).slice(0, 80));
            return false;
        }
        if (_srcTs > _decLastUsedTs) _decLastUsedTs = _srcTs;
        _firstEncoded = false;
        if (totalFrames > 0) onProgress(Math.min(0.95, _winSeen / totalFrames));
        if (_anaHold) {
            // Та же форма выхода, что у головы очереди concurrency выше:
            // отмена или отказ потребителя закрывает кодеки до броска —
            // брошенный VideoDecoder переживает прогон (#345).
            var _ah = _anaHold, _ahErr = null; _anaHold = null;
            try { await _ah; } catch (e) { _ahErr = e || new Error('geometry2d-analyze-failed'); }
            if (aborted || _ahErr) {
                _releaseAllSent();
                try { decoder.close(); } catch (_) {}
                try { encoder.close(); } catch (_) {}
                if (aborted) throw new DOMException('cancelled', 'AbortError');
                throw _ahErr;
            }
        }
        return true;
    }
    // First-frame deadline. A decoder that configures but never emits frame 0 is
    // a wedge, not a slow decode — isConfigSupported cannot see it. Bail typed
    // and fast so run()'s SW rescue (or the caller's FFmpeg fallback) happens in
    // seconds rather than after a 30 s no-progress watchdog.
    var _firstDecodeAtMs = 0;
    function _checkFirstFrameDeadline() {
        if (diagDecoded > 0 || !_firstDecodeAtMs) return;
        if ((performance.now() - _firstDecodeAtMs) < FIRST_FRAME_DEADLINE_MS) return;
        var _ff = new Error('decode: no-first-frame');
        _ff.decHint = decoderChosenHint;
        _aeWebcodecsErr(aeSlug, 'decoder', _ff, {
            stage: 'main', codec: demuxInfo.codec, src_w: inW, src_h: inH,
            hint: decoderChosenHint, sent: _lastSentChunkIdx + 1,
            ms: Math.round(performance.now() - _firstDecodeAtMs)
        });
        try { decoder.close(); } catch (_) {}
        try { encoder.close(); } catch (_) {}
        throw _ff;
    }
    // A trim window needs the samples from the last keyframe at or before its
    // start up to the first keyframe past its end — nothing else can change a
    // frame inside it. Until 2026-09-17 the loop fed EVERY sample and let
    // drainOne() discard the frames outside the window after decoding them,
    // so a 60 s cut out of a 10 min source decoded (and, lazily, read) the
    // whole 10 min. Sync samples' timestamps are monotonic in decode order, so
    // the scan is a single pass; the +0.5 s slack past the end covers open-GOP
    // leading pictures.
    var _feedFrom = 0, _feedTo = samples.length;
    if (doTrim && samples.length) {
        for (var _fk = 0; _fk < samples.length; _fk++) {
            if (samples[_fk].is_sync && samples[_fk].timestamp <= trimStartUs) _feedFrom = _fk;
        }
        for (var _tk = _feedFrom + 1; _tk < samples.length; _tk++) {
            if (samples[_tk].is_sync && samples[_tk].timestamp >= trimEndUs + 500000) { _feedTo = _tk; break; }
        }
    }
    // the first keyframe is a cold start: its leading pictures are not fed (_leadingSkip)
    _leadSkip = _leadingSkip(samples, _feedFrom, _feedTo);
    if (doTrim && samples.length) {
        try { if (typeof window.__ae === 'function') window.__ae('feature_use', { slug: aeSlug, action: 'trim_feed_window', fed: _feedTo - _feedFrom, of: samples.length, lead: _leadSkip.n || 0 }); } catch (_) {}
    }
    var _feedResume = _feedFrom, _nalReported = false;
    for (;;) {   // re-entered only when the decoder died during the final flush
    _st('feed');
    for (var i = _feedResume; i < _feedTo; i++) {
        if (aborted) {
            // Close codecs before throwing — a leaked VideoEncoder/Decoder
            // outlives the page render and the next encode will fail with
            // "Cannot call 'encode' on a closed codec" (sid 77.90.61.176 #345).
            _releaseAllSent();
            try { decoder.close(); } catch(_){}
            try { encoder.close(); } catch(_){}
            throw new DOMException('cancelled', 'AbortError');
        }
        if (_leadSkip[i]) continue;
        var s = samples[i];
        if (decoder.decodeQueueSize > diagDecodeQueueMax) diagDecodeQueueMax = decoder.decodeQueueSize;
        // Lazy demux: the bytes come off the disk now, in 8 MB windows —
        // s.data is set only for an eager sample (or sample 0, kept for the
        // chunk0 diagnostic). A read that fails is a typed pipeline error the
        // caller falls back from, never a silent short video.
        var _sBytes = s.data;
        if (!_sBytes && _lazyRd) {
            try { _sBytes = await _lazyRd.bytes(s, true); }
            catch (rdE) { var _rdErr = new Error('demux-read: ' + String((rdE && rdE.message) || rdE).slice(0, 120)); _releaseAllSent(); try { decoder.close(); } catch(_){} try { encoder.close(); } catch(_){} throw _rdErr; }
            if (aborted) { _releaseAllSent(); try { decoder.close(); } catch(_){} try { encoder.close(); } catch(_){} throw new DOMException('cancelled', 'AbortError'); }
        }
        // Backpressure: don't queue more than ~30 in-flight frames.
        // A dead decoder enters the same loop: its own error has to be
        // handled BEFORE the next decode() — see _decoderDied.
        var _restartAt = -1;
        while (_decoderDied() || decoder.decodeQueueSize > 30 || decodedFrames.length > 20) {
            await _yieldTick(0);
            while (await drainOne()) {}
            _decWedgeCheck('feed');               // silent decoder → decodeError → the rebuild below
            if (decodeError) {
                _restartAt = await _rebuildDecoder('feed');
                if (_restartAt < 0) { var _de1 = new Error('decode: ' + decodeError.message); _de1.decHint = decoderChosenHint; throw _de1; }
                break;
            }
            _checkFirstFrameDeadline();
            if (_pendingEncRecovery) {
                _pendingEncRecovery = false;
                var ok = await _rebuildEncoderAfterReclaim();
                if (!ok) {
                    try { decoder.close(); } catch(_){}
                    { var _ef2 = new Error('encoder-failed: ' + (encoderError && (encoderError.message || encoderError) || 'reclaim-recovery-failed')); _ef2.encHint = encoderChosenHint; throw _ef2; }
                }
            }
        }
        if (_restartAt >= 0) { i = _restartAt - 1; continue; }
        _lastSentChunkIdx = i;
        _lastSentChunkSync = !!s.is_sync;
        _lastSentChunkSize = (_sBytes && _sBytes.length) || 0;
        if (s.timestamp > _maxFedTs) _maxFedTs = s.timestamp;
        decoder.decode(new EncodedVideoChunk({
            type: s.is_sync ? 'key' : 'delta',
            timestamp: s.timestamp,
            duration: s.duration,
            data: _sBytes
        }));
        /* Release the compressed bytes as soon as they are handed over.
           `demuxMp4` collects EVERY sample before the first frame is decoded,
           so a long file parks its whole compressed stream on the JS heap for
           the entire run — MEASURED on a 2 h / 776 MB export: steady 1.66 GB
           with a 3.3 GB peak, which is why big exports died on memory-tight
           machines. Each sample is fed exactly once, and the
           EncodedVideoChunk constructor COPIES the bytes, so nothing reads
           `s.data` again.
           ⚠️ Sample 0 is kept: the decoder-error diagnostic reports its first
           bytes (`chunk0_hex`), and that readout is what identified the SEI
           cluster. One frame of bytes is a cheap price for keeping it.
           ⚠️ The software-decode rescue does NOT re-read this array — it
           re-enters the pipeline and re-demuxes from the file — so releasing
           here cannot break it. (An earlier note claimed the opposite; it was
           written before the rescue path was read closely.) */
        if (i > 0) s.data = null;
        if (!_firstDecodeAtMs) _firstDecodeAtMs = performance.now();
        // Yield to allow output callback to fire, then drain
        await _yieldTick(0);
        while (await drainOne()) {}
        if (decodeError) {
            var _rk2 = await _rebuildDecoder('feed');
            if (_rk2 < 0) { var _de2 = new Error('decode: ' + decodeError.message); _de2.decHint = decoderChosenHint; throw _de2; }
            i = _rk2 - 1;
            continue;
        }
        _checkFirstFrameDeadline();
        if (_pendingEncRecovery) {
            _pendingEncRecovery = false;
            var okR = await _rebuildEncoderAfterReclaim();
            if (!okR) {
                try { decoder.close(); } catch(_){}
                { var _ef3 = new Error('encoder-failed: ' + (encoderError && (encoderError.message || encoderError) || 'reclaim-recovery-failed')); _ef3.encHint = encoderChosenHint; throw _ef3; }
            }
        }
    }

    // Lazy demux sanitises NAL units as the samples are read, so the
    // nal_sanitized row the eager demux emits at collection time is emitted
    // here, once the feed is done — same shape, same consumers (the Android
    // SEI cluster of Wave 23 is sized from it).
    if (!_nalReported && demuxInfo.lazy && demuxInfo._nalStats && demuxInfo._nalStats.samples > 0) {
        _nalReported = true;
        try {
            if (typeof window.__ae === 'function') {
                window.__ae('feature_use', {
                    slug: aeSlug, action: 'nal_sanitized',
                    sei: demuxInfo._nalStats.sei, ps: demuxInfo._nalStats.ps,
                    samples: demuxInfo._nalStats.samples, total: samples.length, lazy: 1
                });
            }
        } catch (_) {}
    }

    // Final flush — drains anything still in the decoder pipeline. drainOne()
    // returns false while a recovery is pending, so a single drain pass could
    // stop with frames still queued: yield and re-enter (rebuilding once if
    // that is what is pending) until the queue is genuinely empty.
    // ⚠️ Кадры разбираются и ВО ВРЕМЯ сброса (25.09.2026). Голый
    // `await decoder.flush()` ничего не разбирал, пока ждал: всё, что было в
    // полёте (до 30 в очереди декодера + до 20 готовых), копилось незакрытыми
    // VideoFrame, а пул выходных кадров у аппаратного декодера конечен — когда
    // в полёте больше, чем в пуле, flush() не завершается никогда (тот же
    // класс, от которого стоит сторож в runInterpolation, 2026-08-08). В скрытой
    // вкладке уступка потока быстрая (_yieldTick), очередь доходит до предела
    // обратного давления, и зависание стало воспроизводимым: стенд
    // webcodecs-analyze-pass-survives-a-hidden-tab.mjs стоял на кадре 147 из 180.
    var _decFlushed = false, _decFlushErr = null;
    // Кадры в работе (geometry2d.concurrency) — дожидаемся и кодируем по порядку.
    _geoFlushing = true;
    /* A decoder that dies while flushing REJECTS the flush; its error callback
       may land a task later. Keep the rejection: it is the death, and the check
       after the drain below must not depend on which of the two came first
       (measured with a flush-time death: 598 of 600 frames shipped as done). */
    /* The flush settles for ITS decoder only: a wedge below rebuilds, the
       rebuild closes this decoder, and its flush then rejects late — into the
       NEXT pass's variables if it were not tied to the decoder it belongs to. */
    _st('dec_flush');
    var _decFlushP = (function (d) {
        return d.flush().then(function () { if (d === decoder) _decFlushed = true; },
            function (fe) { if (d === decoder) { _decFlushed = true; _decFlushErr = fe || new Error('flush rejected'); } });
    })(decoder);
    while (!_decFlushed) {
        while (await drainOne()) {}
        if (muxerError || aborted) break;
        if (_pendingEncRecovery) {
            _pendingEncRecovery = false;
            if (!await _rebuildEncoderAfterReclaim()) break;
        } else if (encoderError) {
            break;                                  // dead and unrecoverable — the loop below surfaces it
        }
        if (!_decFlushed && !decodeError) {
            if (_decLastOutTs >= _maxFedTs) {
                // Every fed frame is out and the flush still has not returned:
                // nothing is owed — leave it, as _flushBounded leaves the encoder's.
                if (_decSilentMs() >= ENC_WEDGE_MS) {
                    _aeWebcodecsErr(aeSlug, 'decoder_flush_abandoned', new Error('flush never returned, nothing owed'), {
                        stage: 'main', decoded_so_far: diagDecoded, last_sent: _lastSentChunkIdx, of: samples.length, codec: demuxInfo.codec
                    });
                    break;
                }
            } else if (_decWedgeCheck('flush')) {
                break;                              // owed frames never came — the rebuild below re-feeds the tail
            }
        }
        if (!_decFlushed) await Promise.race([_decFlushP, _yieldTick(4)]);
    }
    for (var _fdGuard = 0; _fdGuard < 200000; _fdGuard++) {
        while (await drainOne()) {}
        if (!decodedFrames.length && !_geoInflight.length) break;
        if (decodeError) break;                    // the rebuild below owns the rest
        if (muxerError || aborted) break;
        if (_pendingEncRecovery) {
            _pendingEncRecovery = false;
            if (!await _rebuildEncoderAfterReclaim()) break;
        } else if (encoderError) {
            break;                                  // dead and unrecoverable — stop pumping
        }
        await _yieldTick(0);
    }
    /* A decoder that died DURING the flush used to end the stream right here:
       flush() rejects, the loop above sees an empty queue, and the file came
       out short with nothing said. Rebuild and feed the tail again. */
    if (!decodeError && _decFlushErr && !aborted && _decFlushErr.name !== 'AbortError') decodeError = _decFlushErr;
    /* The same short file through the other door (15.10.2026): a flush that
       RETURNED although frames are still owed — a decoder that stopped handing
       out frames without an error. Measured on the stand
       (video-compressor-safari17-fast-lane.mjs S3, Chromium, a decoder that
       goes quiet after 300 of 900 frames): the run «succeeded» with 300 frames
       in a 30-second container, and nothing anywhere said so. For H.264/HEVC
       every fed sample comes out as a frame (the edit list and the trim drop
       theirs AFTER the decoder), so the newest frame fed must be the newest
       frame out. Other codecs (VP9's hidden frames, AV1's temporal units) are
       not held to it. */
    if (!decodeError && !aborted && _decFlushed && !_decFlushErr && _decLastOutTs < _maxFedTs
        && /^(avc1|avc3|hvc1|hev1)/.test(String(demuxInfo.codec || ''))) {
        var _owedN = 0;
        for (var _oi = _feedFrom; _oi <= _lastSentChunkIdx && _oi < samples.length; _oi++) if (samples[_oi].timestamp > _decLastOutTs) _owedN++;
        var _lostE = new Error('decoder lost frames: flushed with ' + _owedN + ' owed');
        _lostE.name = 'DecoderLostFrames';
        decodeError = _lostE;
        _aeWebcodecsErr(aeSlug, 'decoder_lost_frames', _lostE, {
            stage: 'main', owed: _owedN, decoded_so_far: diagDecoded, last_sent: _lastSentChunkIdx, of: samples.length,
            hint: decoderChosenHint, codec: demuxInfo.codec, src_w: inW, src_h: inH
        });
    }
    if (decodeError && !aborted) {
        var _rkF = await _rebuildDecoder('flush');
        if (_rkF < 0) { var _deF = new Error('decode: ' + decodeError.message); _deF.decHint = decoderChosenHint; try { encoder.close(); } catch (_) {} _releaseAllSent(); throw _deF; }
        _geoFlushing = false;               // the tail is fed again: frames in flight may overlap again
        _feedResume = _rkF;
        continue;
    }
    break;
    }   // re-entry loop
    try { decoder.close(); } catch(_) {}
    // ── Bounded flush ──────────────────────────────────────────────────────
    // `await encoder.flush()` is the second place a wedge hides: 93 of the 294
    // outer-watchdog stalls in 30 days fired at ≥95 % — the frames were all
    // encoded, the codec never returned from flush, and the finished work was
    // thrown away. Wait on the same visible-time budget; a wedge while frames
    // are still owed is recovered like any other (rebuild on the next hint,
    // replay, flush again); a flush that owes NOTHING and still does not return
    // is simply left behind — there is nothing it could still deliver.
    async function _flushBounded() {
        var settled = false;
        try { encoder.flush().then(function () { settled = true; }, function () { settled = true; }); }
        catch (_) { settled = true; }
        var idleSinceVis = _visNow();
        while (!settled) {
            if (aborted || encoderError) return;   // a dead codec's flush may never settle
            if (_sentFrames.size) {
                if (_encWedgeCheck('flush')) return;
                idleSinceVis = _visNow();
            } else if (_visNow() - idleSinceVis > ENC_WEDGE_MS) {
                _aeWebcodecsErr(aeSlug, 'encoder_flush_abandoned', new Error('flush never returned, nothing owed'), {
                    stage: 'main', chunk_idx: _muxChunkIdx, hint: encoderChosenHint
                });
                return;
            }
            await _yieldTick(25);
        }
    }
    _st('enc_flush');
    await _flushBounded();
    while (encoderError && _encRecoverable() && _encCanRebuild()) {
        if (!(await _rebuildEncoderAfterReclaim())) break;
        await _flushBounded();
    }
    try { encoder.close(); } catch(_) {}
    // flush() has delivered every chunk the encoder still owed, so anything
    // left here was never going to be confirmed. Release it — a retained
    // VideoFrame pins a GPU surface, and leaking them would trade a frame gap
    // for a memory leak.
    _releaseAllSent();

    // ── Completeness check after a rebuild ──────────────────────────────────
    // Frames handed to encode() must equal the chunks the muxer received.
    //
    // Retention (see _sentFrames) is what makes a rebuild lossless, so this is
    // now a GUARD, not a policy: measured on real footage the shortfall is
    // zero at 720p, 1080p and 3840×2160. There used to be a tolerance here —
    // "a gap under half a second is disclosed, anything longer is refused" —
    // and that half-second was a guess about perception dressed up as a rule.
    // With the frames actually replayed there is nothing to forgive: any
    // shortfall at all now means retention failed (cap clipped, replay
    // rejected), and the honest answer is to refuse this artifact and let the
    // caller fall back to the exact software lane.
    var _framesLost = (_encRebuilds > 0 || _decRebuilds > 0) ? Math.max(0, frameCount - _muxChunkIdx) : 0;
    if (_framesLost > 0) {
        _aeWebcodecsErr(aeSlug, 'encoder_recovery_gap', new Error('gap survived replay'), {
            stage: 'main', sent: frameCount, emitted: _muxChunkIdx,
            lost: _framesLost, retain_peak: _retainPeak, retain_dropped: _retainDropped,
            chunk_idx: _muxChunkIdx
        });
        throw new Error('encoder-failed: recovered stream is missing ' + _framesLost + ' frame(s)');
    }

    // Surface encoder/muxer errors that happened during the loop or flush —
    // these would otherwise produce a confusing "colorSpace null" crash at
    // muxer.finalize() because no chunks reached the muxer (track.info.
    // decoderConfig stays null). Throw the root cause early.
    if (analyzeOnly) {
        // Nothing was encoded on purpose: no chunks, no audio, no file. The
        // muxer is left unfinalised — it never received a frame.
        onProgress(1.0);
        return {
            blob: null, file: null, frames: frameCount, analyzeOnly: true, framesLost: 0,
            encode_sec: (performance.now() - t0) / 1000,
            ms_per_frame: frameCount > 0 ? (performance.now() - t0) / frameCount : 0,
            diagSamples: diagSamples, diagDecoded: diagDecoded, audioMuxed: 0,
            audioExpected: _wantAudioTrack && demuxInfo.audio.samples ? demuxInfo.audio.samples.length : 0
        };
    }
    if (encoderError) { var _ef4 = new Error('encoder-failed: ' + (encoderError.message || encoderError)); _ef4.encHint = encoderChosenHint; throw _ef4; }
    if (muxerError) throw new Error('muxer-failed: ' + (muxerError.message || muxerError));

    // Defensive: if NO chunks ever reached the muxer's first-chunk patch,
    // the muxer's track.info.decoderConfig is still null and finalize() will
    // throw "Cannot read properties of null (reading 'colorSpace')" at the
    // colr-box build step. Surface a typed error so the catch handler routes
    // it correctly instead of leaving users with the cryptic muxer crash.
    if (!firstChunkSeen) {
        throw new Error('encoder-no-output: produced 0 video chunks (source may be invalid or decoder failed silently)');
    }

    // Audio. speed===1: copy original AAC samples (existing callers untouched).
    // speed≠1: pitch-correct time-stretch to match the new duration; mute → no
    // track. Any failure throws → caller's FFmpeg fallback produces the result.
    var audioMuxed = 0;
    /* _needsAudioEdit / _aGain / _aFadeIn / _aFadeOut are decided above the
       muxer — the track header depends on them. */
    _st('audio');
    if (_needsAudioEdit) {
        if (_wantAudioTrack) {
            // The plain transcode case (same speed, no gain / fade, no
            // leading silence) relays units off the disk — see muxAudioLazy;
            // null = preflight declined, nothing muxed, the whole-buffer path
            // serves as before.
            var _lazyMuxed = null;
            if (demuxInfo.lazy && speed === 1 && _aGain === 1 && !_aFadeIn && !_aFadeOut && !(_audioLeadSec > 0) && _audioNeedsTranscode) {
                _lazyMuxed = await muxAudioLazy(demuxInfo, muxer, function(){ return aborted; }, {
                    trimStart: doTrim ? trimStartSec : 0,
                    trimEnd:   doTrim ? trimEndSec   : 0,
                    outRate: _audioOut.sampleRate, outChannels: _audioOut.numberOfChannels,
                    slug: aeSlug,
                    onProgress: function (f) { onProgress(0.95 + 0.04 * f); }   // the same tail as the passthrough below
                });
            }
            audioMuxed = (_lazyMuxed != null) ? _lazyMuxed : await muxSpeedAudio(file, muxer, speed, demuxInfo, function(){ return aborted; }, {
                gain: _aGain, fadeIn: _aFadeIn, fadeOut: _aFadeOut,
                trimStart: doTrim ? trimStartSec : 0,
                trimEnd:   doTrim ? trimEndSec   : 0,
                leadSec:   _audioLeadSec,
                outRate: _audioOut.sampleRate, outChannels: _audioOut.numberOfChannels
            });
        }
    } else if (_wantAudioTrack && demuxInfo.audio.samples && demuxInfo.audio.samples.length) {
        // Copied as bytes, no codec API — see muxAudioPassthrough (Safari < 26).
        audioMuxed = (await muxAudioPassthrough(demuxInfo, muxer, {
            trimStartUs: doTrim ? trimStartUs : 0, trimEndUs: doTrim ? trimEndUs : 0,
            lead: doTrim ? null : _leadPlan,
            onProgress: function (f) { onProgress(0.95 + 0.04 * f); },
            slug: aeSlug, stage: 'main'
        })).muxed;
    }

    _st('finalize');
    muxer.finalize();

    var encodeSec = (performance.now() - t0) / 1000;
    onProgress(1.0);

    var newName = (file.name || 'enhanced.mp4').replace(/(\.[^.]+)?$/, '_hw.mp4');
    // Disk-backed when streaming (no copy of the bytes anywhere), plain Blob
    // otherwise — same shape for every caller either way.
    _st('collect');
    muxTarget.needBytes = _needBytes;   // the room the whole file wanted — said with a refused write
    var _out = await collectMuxOutput(muxTarget, muxer, newName, demuxInfo.container === 'matroska' ? null : file);
    _st('done');
    return {
        blob: _out.blob,
        file: _out.file,
        opfsName: _out.opfsName,
        savedToPicked: !!_out.savedToPicked, pickedName: _out.pickedName || '',
        frames: frameCount,
        // Frames the codec swallowed when it died and was rebuilt (0 normally).
        // Non-zero means the artifact has a gap the caller MUST disclose — the
        // budget check above already refused anything longer than ~0.5 s.
        framesLost: _framesLost,
        // Retention diagnostics — how deep the unconfirmed-frame buffer really
        // got, and whether the hard cap ever had to release anything. Both feed
        // the bench that sets RETAIN_CAP from measurement rather than taste.
        retainPeak: _retainPeak,
        retainDropped: _retainDropped,
        encode_sec: encodeSec,
        ms_per_frame: frameCount > 0 ? (performance.now() - t0) / frameCount : 0,
        // The rung that encoded and the bits it was given (window.__swEncoderBudget):
        // 'prefer-software' + gain > 1 is a run that the measured budget reached.
        encHint: encoderChosenHint,
        encBps: (encPick && encPick.config && encPick.config.bitrate) || bitrate,
        encGain: (encPick && encPick.softwareGain) || 1,
        // Diagnostics for truncation debugging (Phase 4.A v1):
        // - diagSamples: how many samples mp4box demux exposed
        // - diagDecoded: how many VideoFrames the decoder produced
        // - frameCount:  how many we actually rendered+encoded
        // - diagEncodeQueueMax: peak encoder.encodeQueueSize observed. Watch
        //   T+7d on Android — if high (>30) and correlated with wedge sessions
        //   → MediaCodec input overflow is the wedge root → next iteration
        //   ships an encoder cap and reverts the SW-first Android default.
        // If diagSamples >> diagDecoded, decoder dropped chunks (backpressure
        // handled poorly). If diagDecoded > frameCount, render loop drained
        // late or aborted early.
        diagSamples: diagSamples,
        diagDecoded: diagDecoded,
        diagEncodeQueueMax: diagEncodeQueueMax,
        diagDecodeQueueMax: diagDecodeQueueMax,
        diagFramesQueueMax: diagFramesQueueMax,
        // geometry2d.concurrency: сколько кадров реально было в работе разом.
        geoConcurrency: geoConc,
        geoInflightMax: _geoInflightMax,
        audioMuxed: audioMuxed,
        audioExpected: _wantAudioTrack && demuxInfo.audio.samples ? demuxInfo.audio.samples.length : 0
    };
}

// ─────────────────────────────────────────────────────────────────────
// FRAME-INTERPOLATION PIPELINE
// ─────────────────────────────────────────────────────────────────────
// Sibling of `run()` for /video-fps and any future tool that needs to
// produce more frames than the source. Two engines:
//   - 'blend' (default): WebGL2 frame-blend shader — Fast tier, ~100 fps
//     on M1 desktop. Quality matches FFmpeg `minterpolate=mi_mode=blend`.
//   - 'callback': caller supplies async (imgDataA, imgDataB, t) → ImageData
//     fn. Pipeline reads pixels back from GPU per-frame, hands ImageData
//     pairs to the callback (RIFE etc.), uploads result to GPU, encodes.
//     CPU readback is ~3 ms per 1080p frame — same order as inference, so
//     not the bottleneck.
// Both branches buffer ONE pair of source frames (texSrc + texSrcB) and
// stream output frames directly into the muxer with no in-memory PNG
// accumulator (closing the OOM hole the FFmpeg-FS path had).

// 2026-07-31 — the same SW-decode rescue run() has had since Wave 23,
// now for the interpolation entry (video-fps): 189 `decode: Decoding
// error` events / 92 sessions / 14 d died here with NO retry — the
// decoder cascade picked hardware, a mid-stream quirk killed it, and
// the caller fell straight to its slow fallback. One pinned-software
// retry, bounded, mirroring run().
/* Can a fresh decoder start on this sample? The answer has one owner,
   window.__leadingPictures.startsADecoder (timbrica-core.js: an H.264 IDR, an HEVC
   IRAP, any sync sample of other codecs); this reads the sample's own bytes for it
   (lazy demux). Both decode lanes' rebuilds ask it (run() since 26.09,
   runInterpolation since 27.09): an open-GOP stream marks recovery-point I-frames
   as sync, and Chrome refuses them («marked as type `key` but wasn't a keyframe»). */
async function _sampleStartsADecoder(codec, s, lazyRd) {
    var c = (codec || '').slice(0, 4);
    if (c !== 'avc1' && c !== 'avc3' && c !== 'hvc1' && c !== 'hev1') return true;
    var L = window.__leadingPictures;
    if (!L || typeof L.startsADecoder !== 'function') return true;
    var b;
    try { b = s.data || (lazyRd ? await lazyRd.bytes(s, true) : null); } catch (_) { return false; }
    return L.startsADecoder(codec, b);
}

/* What a fresh decoder that starts at sync sample k must NOT be fed: that
   keyframe's leading pictures (after it in decode order, shown before it). An
   iPhone's HEVC is an open GOP — every sync sample after the first is a CRA with
   RASL pictures that reference the group before it; WebKit turns the first one
   it cannot build into «Decoder failure» and closes the decoder (Chromium drops
   it silently, so «fed − out» never settles). The rule has one owner,
   window.__leadingPictures (timbrica-core.js); this is the set the feed loops
   step over at a trim's first keyframe and at every rebuild. */
function _leadingSkip(samples, k, end) {
    var set = {};
    if (typeof window.__leadingPictures !== 'function' || !samples || !(k >= 0)) return set;
    var L = window.__leadingPictures(Math.min(end, samples.length), k,
        function (i) { return samples[i].timestamp; }, function (i) { return !!samples[i].is_sync; });
    for (var i = 0; i < L.length; i++) set[L[i]] = 1;
    set.n = L.length;
    return set;
}

async function runInterpolation(opts) {
    // Created here so a rescue re-run's copy of opts shares it (see dropRunLanes).
    try { if (opts && !opts.__muxLanes) opts.__muxLanes = []; } catch (_) {}
    try {
        return await _runInterpolation(opts);
    } catch (e) {
        dropRunLanes(opts);   // before any rescue re-run reserves its own room
        /* The browser's storage refused the result (diskWriteError — the run
           stopped at that frame) and the whole file fits in memory: once more,
           in memory, from the start. Every tool on the pipeline then gets its
           file, instead of falling to a slower lane that writes to the same
           storage (report #9939: «10 часов», then the same wall at 256 MB).
           Past the memory ceiling it stays the caller's question — /enhance-
           video asks where to save (opts.outputHandle). */
        if (e && /^opfs-write-failed:/.test(e.message || '') && e.memFits &&
            !(opts && (opts.noDiskTarget || opts.outputHandle || opts.__diskRetried))) {
            try { window.__ae && window.__ae('feature_use', { slug: (opts && opts.slug) || 'webcodecs', action: 'disk_refused_to_memory',
                mb: Math.round((e.needBytes || 0) / 1048576), quota: e.diskFull ? 1 : 0 }); } catch (_) {}
            return await runInterpolation(Object.assign({}, opts, { noDiskTarget: true, __diskRetried: true }));
        }
        var _m = (e && e.message) || '';
        var _hint = e && e.decHint;
        if ((/^decode: Unsupported configuration/i.test(_m)
                || /^decode: no-first-frame/i.test(_m)
                || /^decode: Decoding error/i.test(_m)
                || /^decode: Decoder failure/i.test(_m))
            && !(opts && opts._swDecodeRetried)
            && _hint !== 'prefer-software') {
            try { window.__ae && window.__ae('feature_use', { slug: (opts && opts.slug) || 'video-fps', action: 'sw_decode_rescue_attempt', hint: _hint || 'unknown', stage: 'interp' }); } catch (_) {}
            try { if (typeof opts.onRestart === 'function') opts.onRestart({ reason: 'sw_decode_rescue' }); } catch (_) {}
            try { if (typeof opts.onProgress === 'function') opts.onProgress(0); } catch (_) {}
            var o2 = Object.assign({}, opts, { _swDecodeRetried: true, _decodeHints: ['prefer-software'] });
            var res = await _runInterpolation(o2);
            try { window.__ae && window.__ae('feature_use', { slug: (opts && opts.slug) || 'video-fps', action: 'sw_decode_rescue_ok', stage: 'interp' }); } catch (_) {}
            return res;
        }
        /* Rung 1 — the probe LIED about the framerate ceiling.
           `pickEncoderConfig` climbs down that ladder on its own when
           `isConfigSupported` is honest, so reaching here means the probe said
           yes and `configure()`/the first encode said no. The desktop-Yandex
           signature is exactly this shape: "Encoder initialization error.
           (Failed to create OpenH264 encoder.)" — 223 events / 96 sessions in
           30 d, the single biggest encoder-failure bucket on /video-fps.
           Retrying the SAME rate is what rung 2 has been doing, and it has
           never once worked; this rung changes the field the encoders are
           actually refusing. Only armed when there is headroom to give up. */
        var _askedFps = Math.round(+((opts && (opts.outputPlaybackFps || opts.outputFps)) || 0));
        if (/^encoder-failed: .*(Encoder creation error|Unsupported configuration|Encoder initialization error|encoder wedged)/i.test(_m)
            && !(opts && opts._encFpsRetried)
            && _askedFps > 60) {
            try { window.__ae && window.__ae('feature_use', { slug: (opts && opts.slug) || 'video-fps', action: 'enc_framerate_rescue_attempt', asked: _askedFps, stage: 'interp' }); } catch (_) {}
            try { if (typeof opts.onRestart === 'function') opts.onRestart({ reason: 'enc_framerate_rescue' }); } catch (_) {}
            try { if (typeof opts.onProgress === 'function') opts.onProgress(0); } catch (_) {}
            var oF = Object.assign({}, opts, { _encFpsRetried: true, _encodeFramerateCap: 60 });
            var resF = await _runInterpolation(oF);
            try { window.__ae && window.__ae('feature_use', { slug: (opts && opts.slug) || 'video-fps', action: 'enc_framerate_rescue_ok', stage: 'interp' }); } catch (_) {}
            return resF;
        }
        /* Rung 2 — та же программная пересдача кодировщика, что в run(): вход
           интерполяции пользуется тем же pickEncoderConfig и тем же отказом.
           ⚠️ Замерено: 76 попыток / 43 сессии за 30 дней и НОЛЬ успехов —
           Chrome на Android программного H.264-кодировщика не несёт вовсе, а
           у настольного Яндекса ломается именно он. Оставлена как последняя
           ступень, но чинит здесь rung 1. */
        var _eHint = e && e.encHint;
        if (/^encoder-failed: .*(Encoder creation error|Unsupported configuration|Encoder initialization error|encoder wedged)/i.test(_m)
            && !(opts && opts._swEncodeRetried)
            && _eHint !== 'prefer-software') {
            try { window.__ae && window.__ae('feature_use', { slug: (opts && opts.slug) || 'video-fps', action: 'sw_encode_rescue_attempt', hint: _eHint || 'unknown', stage: 'interp' }); } catch (_) {}
            try { if (typeof opts.onRestart === 'function') opts.onRestart({ reason: 'sw_encode_rescue' }); } catch (_) {}
            try { if (typeof opts.onProgress === 'function') opts.onProgress(0); } catch (_) {}
            var oE = Object.assign({}, opts, { _swEncodeRetried: true, _encodeHints: ['prefer-software'] });
            var resE = await _runInterpolation(oE);
            try { window.__ae && window.__ae('feature_use', { slug: (opts && opts.slug) || 'video-fps', action: 'sw_encode_rescue_ok', stage: 'interp' }); } catch (_) {}
            return resE;
        }
        throw e;
    }
}

/* Every exit of an interpolation attempt closes what the attempt opened.
   (2026-09-26) The body's error exits — a decode error, the stall watchdog,
   a failed flush — threw with the hardware VideoEncoder and VideoDecoder still
   configured and the decoded frames still held. The rescue in runInterpolation
   then opened a SECOND encoder, and a phone that has one: «The system ran out
   of platform encoders» right after every stall (prod: 14 fallbacks since
   13.09 carried it, all mobile), the rescue dead before its first frame.
   Registered closers run in `finally`, so success (codecs already closed —
   the closers are no-ops) and every throw share one path. */
async function _runInterpolation(opts) {
    var exits = [];
    try {
        return await _runInterpolationAttempt(opts, exits);
    } finally {
        for (var i = exits.length - 1; i >= 0; i--) { try { exits[i](); } catch (_) {} }
    }
}

async function _runInterpolationAttempt(opts, exits) {
    if (!opts || !opts.file) throw new Error('webcodecs-pipeline: no file');
    if (typeof OffscreenCanvas === 'undefined') throw new Error('no-offscreen-canvas');

    /* Дверь тегов и ключа «как играть» — заранее: по требованию в конце прогона
       с потолком 10 с на медленной связи файл уходил без ключа (стенд /video-fps
       04.11.2026 — тот же код дал ключ в одном прогоне и не дал в другом). */
    ensureMp4Tags();
    await Promise.all([ ensureInputDemuxer(opts.file), ensureMp4Muxer() ]);

    var aeSlug = opts.slug || 'video-fps';
    var file = opts.file;
    // Wave 22 — pre-read happens at pick time via FileBytes; see run() above.
    var crf = +opts.crf; if (!isFinite(crf)) crf = 23;
    var onProgress = typeof opts.onProgress === 'function' ? opts.onProgress : function(){};
    var aborted = false;
    if (opts.abortSignal) {
        if (opts.abortSignal.aborted) throw new DOMException('cancelled', 'AbortError');
        opts.abortSignal.addEventListener('abort', function(){ aborted = true; });
    }
    var interpolator = typeof opts.frameInterpolator === 'function' ? opts.frameInterpolator : null;
    var engineLabel = interpolator ? 'callback' : 'blend';

    var demuxInfo = await demuxInput(file, function(){ onProgress(0); }, { lazy: true, slug: aeSlug, audioTrackIndex: opts && opts.audioTrackIndex });
    _aeDemuxLane(aeSlug, demuxInfo, file);
    if (aborted) throw new DOMException('cancelled', 'AbortError');

    // Same audio gate as run() — ONE implementation (gateAudioDecodable):
    // AAC-LC byte-copies, every decodable codec transcodes, only a
    // probe-verified failure bails to the FFmpeg fallback.
    var _audioCodecIn = (demuxInfo.audio && demuxInfo.audio.codec) || '';
    var _audioNeedsTranscode = (await gateAudioDecodable(
        file, demuxInfo, opts.slug || 'video-fps', function () { return aborted; }
    )).needsTranscode;
    /* Edit-list outcome — same rule as _runPipeline: passthrough audio may
       start up to one access unit early (the video holds), never late. */
    var _editI = demuxInfo.edit || { applied: false };
    var _audioLeadSecI = 0, _leadPlanI = null;
    if (_editI.applied && _editI.audioLeadUs > (_editI.audioUnitUs || 21333)) {
        _audioLeadSecI = _editI.audioLeadUs / 1e6;
        _leadPlanI = !_audioNeedsTranscode ? _silentLeadPlan(demuxInfo, _editI, opts) : null;   // see run()
        if (!_leadPlanI) _audioNeedsTranscode = true;
    }
    _aeEditList(opts.slug || 'video-fps', _editI);
    if (_audioNeedsTranscode) {
        try {
            if (typeof window !== 'undefined' && typeof window.__ae === 'function') {
                window.__ae('feature_use', {
                    slug: opts.slug || 'video-fps',
                    action: 'audio_transcode_non_aac',
                    codec: String(_audioCodecIn).slice(0, 20)
                });
            }
        } catch (_) {}
    }

    var srcFps = demuxInfo.fps || 30;
    var outputFps = +opts.outputFps || srcFps;
    if (outputFps <= srcFps) {
        throw new Error('outputFps-must-exceed-source (' + outputFps + ' <= ' + srcFps + ')');
    }
    var ratio = Math.max(2, Math.round(outputFps / srcFps));
    var actualOutputFps = srcFps * ratio;
    // outputPlaybackFps: file's stated FPS (affects timestamps + container metadata
    // but NOT interpolation density). Smooth mode = actualOutputFps; slow-mo
    // mode = srcFps so the same N×source frames play over ratio× source duration.
    var playbackFps = +opts.outputPlaybackFps || actualOutputFps;
    if (playbackFps < 1) playbackFps = actualOutputFps;
    var isSlowMo = playbackFps < actualOutputFps - 0.01;     // tolerance for fp jitter
    /* The rate the OUTPUT IS TIMED AT, as opposed to the one it is labelled with.
     *
     * Everything above is built on demuxInfo.fps, which is Math.round()ed at the
     * demuxer — so a 29.97 source enters this function as 30, ×2 becomes 60, and
     * the timestamps below stride at 1/60. Measured on /video-fps before this:
     * a genuine 29.97 clip came out at 59.9988 fps instead of 59.94, i.e. 0.1 %
     * fast against its own soundtrack (the same rounding that made every re-encode
     * NTSC-fast — see MUX_VIDEO_TIMEBASE and the demuxer's fpsExact).
     *
     * One factor rather than an exact twin per variable: it carries the correction
     * through BOTH modes without re-deriving them. Smooth passes playbackFps =
     * actualOutputFps (60 → 59.94); slow-mo passes the source rate (30 → 29.97).
     * The rounded values stay in charge of everything a human reads or counts —
     * the ×N chip, the `_60fps.mp4` filename, GOP length, chunk estimates — because
     * «59.94» in a filename is a worse answer than the 0.1 % it buys.
     *
     * Guarded by tests/playwright/webcodecs-ntsc-timing.mjs (the /video-fps case).
     */
    var _fpsExactFactor = (demuxInfo.fpsExact > 0 && srcFps > 0)
        ? (demuxInfo.fpsExact / srcFps) : 1;
    var playbackFpsExact = playbackFps * _fpsExactFactor;
    // Audio in slow-mo must be dropped — passthrough at original timing would
    // desync the moment video stretches. Honest UI tells the user audio is
    // dropped before they hit Export (handled in the blade).
    var dropAudio = isSlowMo || !!opts.dropAudio;
    // A sound track this lane cannot see — refuse before any work unless the
    // sound is dropped anyway (see _refuseUnreadSound).
    _refuseUnreadSound(demuxInfo, opts.slug || 'video-fps', !dropAudio);
    /* Presentation window + the hold behind passthrough audio — the same
       contract as _runPipeline (see the drainOne comment there). */
    var _editAppliedI = !!_editI.applied;
    var _presStartUsI = _editAppliedI ? (_editI.presStartUs || 0) : -Infinity;
    var _presEndUsI   = _editAppliedI ? (_editI.presEndUs > 0 ? _editI.presEndUs : Infinity) : Infinity;
    var _videoDelayUsI = (_editAppliedI && demuxInfo.audio && !dropAudio && !_audioNeedsTranscode)
        ? (_leadPlanI ? _leadPlanI.holdUs : (_editI.audioResidualUs || 0)) : 0;

    var inW = demuxInfo.width, inH = demuxInfo.height;
    // Even-pixel guarantee for libx264 yuv420p downstream.
    var outW = inW & ~1;
    var outH = inH & ~1;

    // Same H.264 coded-area cap as primary pipeline — see comment there.
    var H264_MAX_CODED_AREA = 8294400;
    if (outW * outH > H264_MAX_CODED_AREA) {
        var capScale = Math.sqrt(H264_MAX_CODED_AREA / (outW * outH));
        var capW2 = Math.floor(outW * capScale / 2) * 2;
        var capH2 = Math.floor(outH * capScale / 2) * 2;
        try {
            if (typeof window !== 'undefined' && typeof window.__ae === 'function') {
                window.__ae('feature_use', {
                    slug: opts.slug || 'video-fps',
                    action: 'webcodecs_resolution_capped',
                    src_w: outW, src_h: outH, out_w: capW2, out_h: capH2,
                    stage: 'interp'
                });
            }
        } catch (_) {}
        outW = capW2;
        outH = capH2;
    }

    // Build the encoder at the OUTPUT FPS; the muxer gets the fixed timebase.
    // ⚠️ The comment here used to say mp4-muxer needs `frameRate` «for the trex /
    // mvex setup» and that getting it wrong shows up as wrong playback speed in
    // QuickTime / VLC. Neither is true of mp4-muxer 5.2.2: the option is the track
    // timescale and nothing else, and every timescale yields correct wall-clock
    // timing because timestamps are converted INTO it. See MUX_VIDEO_TIMEBASE.
    var Mp4MuxerNs = window.Mp4Muxer;
    // Preserve source rotation matrix on output (same metadata-passthrough
    // pattern as run() — see comment_#370 fix above). runInterpolation does
    // not change resolution so coded dims = source coded dims; rotation is
    // simply re-emitted into the new tkhd matrix.
    var interpRotation = demuxInfo.rotation || 0;
    var interpVideoOpts = { codec: 'avc', width: outW, height: outH, frameRate: MUX_VIDEO_TIMEBASE };
    if (interpRotation) interpVideoOpts.rotation = interpRotation;
    var _audioOutI = demuxInfo.audio
        ? (_audioNeedsTranscode
            ? plannedAudioOut(demuxInfo.audio.sampleRate, demuxInfo.audio.channels)
            : { sampleRate: demuxInfo.audio.sampleRate, numberOfChannels: demuxInfo.audio.channels })
        : null;
    /* Same disk-streaming policy as run() — interpolation MULTIPLIES the frame
       count (60→120 fps doubles it), so its output is the most likely of all
       to blow the heap. The reservation is computed from the OUTPUT rate. */
    var _durSI = demuxInfo.durationSec || 0;
    var _streamWantedI = (file && file.size >= MUX_STREAM_MIN_BYTES) || _durSI >= 600;
    var _expVideoChunksI = Math.ceil(_durSI * (actualOutputFps || playbackFps || 60) * 1.35) + 300;
    var _wantAudioI = !!(demuxInfo.audio && !dropAudio);
    var _expAudioChunksI = _wantAudioI
        ? Math.ceil(_durSI * ((_audioOutI && _audioOutI.sampleRate) || 48000) / 1024 * 1.35) + 300
        : 0;
    // Source bitrate floor/ceiling — the same crfToBitrate() as run(). The
    // source's bits are expressed for THIS output size (sourceBpsForOutput):
    // a > 4K source leaves the H.264 area cap above smaller, and its full
    // bitrate would floor the capped output at the bytes of the bigger picture.
    // Measured against the EVEN-rounded source: dropping an odd row/column
    // above is no resize, and must not move the bitrate by a bit.
    var _srcBpsI = (file && file.size && demuxInfo.durationSec > 0)
        ? Math.floor(sourceBpsForOutput((file.size * 8) / demuxInfo.durationSec, (inW & ~1) * (inH & ~1), outW * outH, demuxInfo.codec))
        : 0;
    // For interpolation we encode at the OUTPUT fps (playbackFps) so
    // bitrate scales accordingly — going 30→60 fps doubles the per-second
    // bitrate, which is correct: 2× more frames need 2× more bits.
    // Wave 4 (2026-06-02): runInterpolation never grows the picture (outW ≤
    // inW, outH ≤ inH above), so the noUpscale gate is always true here. The
    // fps multiplier passed in capOpts scales the ceiling so a legitimate
    // 30→60 fps interpolation is not capped at source bitrate.
    var bitrate = crfToBitrate(crf, outW, outH, playbackFps, _srcBpsI, {
        noUpscale: true, outputFps: playbackFps, sourceFps: demuxInfo.fps || 30,
        slug: 'video-fps'
    });
    // (Computed before the output target: the disk lane reserves its room from it.)
    /* Slow motion plays the interpolated frames at playbackFps, so the output
       lasts actualOutputFps/playbackFps times the source — reserve for that. */
    var _outDurSI = _durSI * Math.max(1, (actualOutputFps || playbackFps || 30) / (playbackFps || 30));
    /* The software rung's own bits (window.__swEncoderBudget) — at the PLAYBACK rate,
       the rate the encoder is told; the room is for them where the ladder starts there. */
    var _swBpsI = softwareRungBps(bitrate, outW, outH, playbackFps, opts);
    var _reserveBpsI = (_swBpsI > bitrate && ((opts && opts._encodeHints) || HW_HINTS)[0] === 'prefer-software') ? _swBpsI : bitrate;
    var _reserveBytesI = _streamWantedI
        ? Math.round(((_reserveBpsI + (_wantAudioI ? 192000 : 0)) / 8) * _outDurSI * 1.15) + 8 * 1048576
        : 0;
    var muxTargetI = await openMuxTarget(
        Mp4MuxerNs,
        _streamWantedI ? MUX_STREAM_MIN_BYTES : 0,
        _expVideoChunksI, _expAudioChunksI, _wantAudioI, _reserveBytesI,
        { noDiskTarget: opts.noDiskTarget === true }
    );
    trackRunLane(opts, muxTargetI);
    if (muxTargetI.opfs) { try { sweepStaleMuxTemps(); } catch (_) {} }
    try { window.__ae && window.__ae('feature_use', { slug: aeSlug, action: 'mux_target', mode: muxTargetI.mode, blocks: muxTargetI.blocks ? 1 : 0, unres: muxTargetI.unreserved || '', durS: Math.round(_durSI), inMB: Math.round(((file && file.size) || 0) / 1048576) }); } catch (_) {}
    var muxer = new Mp4MuxerNs.Muxer({
        target: muxTargetI.target,
        // ⚠️ mp4-muxer writes NO playback-rate metadata — its `frameRate` is the
        // track timescale (MUX_VIDEO_TIMEBASE). The interpolated rate reaches the
        // file through the sample TIMESTAMPS, which is what slow-mo / smoothing
        // actually change; the encoder below still gets playbackFps.
        video: interpVideoOpts,
        // Header states the format the audio will REALLY be in — the source
        // track's own for the byte-copy, the re-encode target when the source
        // is not AAC-LC (see plannedAudioOut).
        audio: (demuxInfo.audio && !dropAudio) ? {
            codec: 'aac',
            numberOfChannels: _audioOutI.numberOfChannels,
            sampleRate: _audioOutI.sampleRate
        } : undefined,
        fastStart: muxTargetI.fastStart
    });
    var muxVideo = window.__muxVideoTrack ? window.__muxVideoTrack(muxer) : function (c, m, t, o) { return muxer.addVideoChunk(c, m, t, o); };

    var DEFAULT_COLOR_SPACE = { primaries: 'bt709', transfer: 'bt709', matrix: 'bt709', fullRange: false };
    var firstChunkSeen = false;
    // B-frame-safe muxing — running decode-order DTS (see addVideoChunk below).
    var _videoDtsAccumUs = 0;
    var _frameDurFallbackUs = 1e6 / (playbackFpsExact || playbackFps || demuxInfo.fps || 30);
    var _muxChunkIdx = 0;
    // Mirror of run()'s chunk-offset normalisation — see comment there for
    // full context. interpolation has its own encoder so we need separate
    // offset capture; same cluster of bugs applies (B-frame reorder + edit
    // list offsets bypassing Phase ④).
    var videoChunkOffset = null;
    // Which frame a chunk is — the same clock as run() (window.__encoderClock).
    var _encClockI = (typeof window !== 'undefined' && window.__encoderClock) ? window.__encoderClock({ onEnd: function () {
        _aeWebcodecsErr(aeSlug, 'chunk_end_stamped', new Error('encoder stamps frame ends'), { stage: 'interp' });
    } }) : null;
    // Same encoder/muxer error capture as the primary pipeline. See run() comment.
    var encoderError = null;
    var muxerError = null;
    // Encoder-reclaim recovery state — mirror of run()'s flow. See the long
    // comment in run() for cluster context. The interpolation encoder is
    // equally vulnerable to MediaCodec eviction since it shares the same
    // hardware codec slot.
    var _encRecoveryDone = false;
    var _encForceKeyframeNext = false;
    var _pendingEncRecovery = false;
    /* (2026-09-27) This lane now survives a media-session death the way run()
       does since 26.09 (support #649). Frames handed to encode() are HELD until
       their chunk comes back, and a rebuilt encoder replays every unconfirmed
       one, first as a keyframe — so a rebuild loses nothing and the gap check at
       the end becomes a guard, not a policy. Before this the lane held nothing,
       rebuilt only for QuotaExceededError and only once, and every other death —
       «Encoding task did not complete» with the decoder dying beside it, the iOS
       shape — threw the whole run into runInterpolation's rescue: from zero, on
       prefer-software, which phones mostly do not have (prod, RU, 30 d: 23 of
       123 mobile rescues finished). */
    var _sentFramesI = new Map();        // output ts → VideoFrame awaiting its chunk
    var _encRebuildsI = 0, ENC_REBUILD_MAX_I = 3, _retainDroppedI = 0, _retainPeakI = 0;
    function _releaseSentI(ts) {
        var f = _sentFramesI.get(ts);
        if (f) { try { f.close(); } catch (_) {} _sentFramesI.delete(ts); }
    }
    function _releaseAllSentI() {
        _sentFramesI.forEach(function (f) { try { f.close(); } catch (_) {} });
        _sentFramesI.clear();
    }
    /* Worth a rebuild: an eviction at any point; any other death once the
       encoder has produced output — the config is proven, the death is a runtime
       event (run()'s _encRecoverable, same rule). A death before the first chunk
       is a refused RUNG: rebuilt on the next rung of the hint cascade (a
       different config, never the refused one); with none left it goes down the
       rescue ladder as before (2026-11-04, see run()'s _encRecoverable). */
    function _encRecoverableI() {
        if (!encoderError) return false;
        if (encoderError.name === 'QuotaExceededError') return true;
        if (_muxChunkIdx > 0) return true;
        return _encRungRefusedI();
    }
    function _encRungRefusedI() {
        return !!encoderError && _muxChunkIdx === 0 && encoderError.name !== 'QuotaExceededError'
            && _hintsAfter(encoderChosenHintI).length > 0;
    }
    function _onEncOutput(chunk, meta) {
        try {
            // Confirmed: no longer needed for a replay. Keyed by the ORIGINAL
            // frame timestamp, so this runs before the offset rewrite below.
            var _tsInI = _encClockI ? _encClockI.ts(chunk) : chunk.timestamp;
            _releaseSentI(_tsInI);
            var safeMeta = meta || {};
            if (videoChunkOffset === null) {
                videoChunkOffset = _tsInI;
                if (videoChunkOffset !== 0) {
                    _aeWebcodecsErr(aeSlug, 'video_chunk_offset_detected', new Error('first chunk ts=' + videoChunkOffset), { stage: 'interp', offset_us: videoChunkOffset });
                }
            }
            if (videoChunkOffset !== 0 || _tsInI !== chunk.timestamp) {
                var ab = new ArrayBuffer(chunk.byteLength);
                chunk.copyTo(ab);
                chunk = new EncodedVideoChunk({
                    type: chunk.type,
                    timestamp: _tsInI - videoChunkOffset,
                    duration: chunk.duration,
                    data: ab,
                });
            }
            if (!firstChunkSeen) {
                firstChunkSeen = true;
                if (!safeMeta.decoderConfig) {
                    safeMeta = { decoderConfig: { codec: 'avc1.640028', codedWidth: outW, codedHeight: outH, colorSpace: DEFAULT_COLOR_SPACE } };
                } else if (!safeMeta.decoderConfig.colorSpace) {
                    safeMeta = Object.assign({}, safeMeta, {
                        decoderConfig: Object.assign({}, safeMeta.decoderConfig, { colorSpace: DEFAULT_COLOR_SPACE })
                    });
                }
            } else if (safeMeta.decoderConfig && !safeMeta.decoderConfig.colorSpace) {
                safeMeta = Object.assign({}, safeMeta, {
                    decoderConfig: Object.assign({}, safeMeta.decoderConfig, { colorSpace: DEFAULT_COLOR_SPACE })
                });
            }
            if (safeMeta.decoderConfig && safeMeta.decoderConfig.colorSpace) {
                safeMeta = Object.assign({}, safeMeta, {
                    decoderConfig: Object.assign({}, safeMeta.decoderConfig, { colorSpace: muxColorSpace(safeMeta.decoderConfig.colorSpace) })
                });
            }
            // B-frame-safe muxing — monotonic decode-order DTS +
            // compositionTimeOffset. See run()'s addVideoChunk comment;
            // the interpolation encoder can emit B-frames too. Decode time —
            // the clock's, as in run() (04.11.2026).
            var _dtsUs;
            if (_encClockI) _dtsUs = _encClockI.dts(_tsInI) - videoChunkOffset;
            else { _dtsUs = _videoDtsAccumUs; _videoDtsAccumUs += (chunk.duration > 0 ? chunk.duration : _frameDurFallbackUs); }
            _muxChunkIdx++;
            safeMeta = sanitizeChunkMeta(safeMeta);   // fix Firefox's malformed avcC (#458)
            muxVideo(chunk, safeMeta, chunk.timestamp, chunk.timestamp - _dtsUs);
        } catch (e) {
            var firstMuxerErr = !muxerError;
            if (!muxerError) muxerError = e;
            console.warn('[webcodecs-pipeline:interp] muxer.addVideoChunk:', e.message, e.stack);
            if (firstMuxerErr) _aeWebcodecsErr(aeSlug, 'muxer', e, {
                stage: 'interp',
                src_codec: (demuxInfo && demuxInfo.codec || '').substring(0, 20),
                src_w: demuxInfo && demuxInfo.codedWidth || 0,
                src_h: demuxInfo && demuxInfo.codedHeight || 0,
                src_fps: Math.round((demuxInfo && demuxInfo.fps) || 0),
                out_w: outW, out_h: outH,
                chunk_idx: _muxChunkIdx,
                chunk_ts_us: chunk && chunk.timestamp || 0,
                chunk_dur_us: chunk && chunk.duration || 0,
                dts_accum_us: _videoDtsAccumUs,
                cur_dts_us: _dtsUs,
                vco_us: videoChunkOffset || 0
            });
        }
    }
    function _onEncError(e) {
        var firstEncErr = !encoderError;
        if (!encoderError) encoderError = e;
        console.warn('[webcodecs-pipeline:interp] encoder error:', e && e.message);
        if (firstEncErr) _aeWebcodecsErr(aeSlug, 'encoder', e, {
            stage: 'interp',
            err_name: (e && e.name) || '',
            src_codec: (demuxInfo && demuxInfo.codec || '').substring(0, 20),
            src_w: demuxInfo && demuxInfo.codedWidth || 0,
            src_h: demuxInfo && demuxInfo.codedHeight || 0,
            out_w: outW, out_h: outH,
            // Same reason as run()'s copy: this lane's output rate is a
            // MULTIPLE of the source, so it is the one that walks into the
            // encoder's framerate ceiling — and it was the one field the
            // failure payload did not carry.
            src_fps: Math.round((demuxInfo && demuxInfo.fps) || 0),
            out_fps: Math.round(playbackFps || 0),
            enc_fps: (encPickI && encPickI.framerate) || 0,
            enc_clamped: (encPickI && encPickI.framerateClamped) || 0,
            chunk_idx: _muxChunkIdx
        });
    }
    var encoder = new VideoEncoder({ output: _onEncOutput, error: _onEncError });
    // `decoder` / `decodedFrames` are declared further down (var hoisting): the
    // closer reads whatever this attempt got as far as creating.
    exits.push(function () {
        try { _releaseHeldFrames(); } catch (_) {}
        try { _releaseAllSentI(); } catch (_) {}
        try { if (decoder && decoder.state !== 'closed') decoder.close(); } catch (_) {}
        try { if (encoder && encoder.state !== 'closed') encoder.close(); } catch (_) {}
    });

    // Same encoder profile cascade as primary pipeline.
    // Interpolation is the one lane whose output rate is a MULTIPLE of the
    // source, so it is the one that walks into the encoder's `framerate`
    // ceiling (see frameRateCandidates). The cap is the rescue ladder's knob;
    // the honest value still goes first when no cap is imposed.
    var encPickI = await pickEncoderConfig(outW, outH, bitrate, playbackFps, null,
                                           opts && opts._encodeHints, opts && opts._encodeFramerateCap,
                                           null, null, _swBpsI || null);
    if (!encPickI) {
        try { await _probeAltEncoders(outW, outH); } catch (_) {}
        throw new Error('encoder-configure-failed: no h264 profile supported by any hint at ' + outW + 'x' + outH);
    }
    if (encPickI.framerateClamped) {
        try { window.__ae && window.__ae('feature_use', {
            slug: aeSlug, action: 'encoder_framerate_clamped',
            asked: encPickI.framerateClamped, used: encPickI.framerate,
            w: outW, h: outH, hint: encPickI.hint,
            unverified: encPickI.unverified ? 1 : 0,
            guard: encPickI.rateGuard || '', bps: encPickI.config && encPickI.config.bitrate || 0
        }); } catch (_) {}
    }
    encoder.configure(encPickI.config);
    var encoderHwTag = encPickI.hardware ? 'prefer-hardware' : 'no-hint';
    var encoderChosenHintI = encPickI.hint || 'no-hint';

    // Encoder-reclaim recovery helpers — see run()'s copy for the long
    // comment. Same shape; uses interp's own encPickI + _onEnc* closures.
    function _waitForForeground(timeoutMs) {
        if (document.visibilityState === 'visible') return Promise.resolve(true);
        return new Promise(function(resolve) {
            var done = false;
            function settle(ok) {
                if (done) return;
                done = true;
                clearTimeout(to);
                document.removeEventListener('visibilitychange', onVis);
                resolve(ok);
            }
            function onVis() {
                if (document.visibilityState === 'visible') settle(true);
            }
            var to = setTimeout(function() { settle(false); }, timeoutMs);
            document.addEventListener('visibilitychange', onVis);
        });
    }
    async function _rebuildEncoderAfterReclaim() {
        _encRecoveryDone = true;
        _encRebuildsI++;
        var _refusedI = _encRungRefusedI();
        _aeWebcodecsErr(aeSlug, 'encoder_reclaim_recovery_attempted', new Error(encoderError && encoderError.message || 'reclaim'), {
            stage: 'interp',
            err_name: (encoderError && encoderError.name) || '',
            chunk_idx: _muxChunkIdx,
            owed: _sentFramesI.size,
            n: _encRebuildsI,
            vis_state: document.visibilityState || '',
            refused: _refusedI ? 1 : 0, hint: encoderChosenHintI
        });
        try { encoder.close(); } catch (_) {}
        var ok = await _waitForForeground(60000);
        if (!ok) {
            _aeWebcodecsErr(aeSlug, 'encoder_reclaim_recovery_failed', new Error('foreground-timeout'), { stage: 'interp', chunk_idx: _muxChunkIdx });
            return false;
        }
        if (_refusedI) {
            var _pickI2 = null;
            try {
                _pickI2 = await pickEncoderConfig(outW, outH, bitrate, playbackFps, null, _hintsAfter(encoderChosenHintI),
                    opts && opts._encodeFramerateCap, null, null, _swBpsI || null);
            } catch (_) { _pickI2 = null; }
            if (!_pickI2 || !_pickI2.config) {
                _aeWebcodecsErr(aeSlug, 'encoder_reclaim_recovery_failed', new Error('refused-rung-no-next'), { stage: 'interp', chunk_idx: _muxChunkIdx, hint: encoderChosenHintI });
                return false;
            }
            encPickI = _pickI2;
            encoderChosenHintI = _pickI2.hint || 'no-hint';
        }
        try {
            encoder = new VideoEncoder({ output: _onEncOutput, error: _onEncError });
            encoder.configure(encPickI.config);
        } catch (e) {
            _aeWebcodecsErr(aeSlug, 'encoder_reclaim_recovery_failed', e, { stage: 'interp', chunk_idx: _muxChunkIdx });
            return false;
        }
        if (_encClockI) _encClockI.restart();
        encoderError = null;
        var _replayedI = _replayUnconfirmedI();
        if (!_replayedI) _encForceKeyframeNext = true;
        _aeWebcodecsErr(aeSlug, 'encoder_reclaim_recovery_ok', new Error('rebuilt'), { stage: 'interp', chunk_idx: _muxChunkIdx, replayed: _replayedI, n: _encRebuildsI });
        return true;
    }
    // Re-encode everything the dead encoder never confirmed, oldest first; the
    // first replayed frame carries the keyframe a bitstream restart needs.
    function _replayUnconfirmedI() {
        if (!_sentFramesI.size) return 0;
        var items = Array.from(_sentFramesI.entries()).sort(function (a, b) { return a[0] - b[0]; });
        _sentFramesI.clear();
        var first = true, n = 0;
        for (var ri = 0; ri < items.length; ri++) {
            try {
                if (_encClockI) _encClockI.fed(items[ri][0], items[ri][1].duration || 0);
                encoder.encode(items[ri][1], { keyFrame: first });
                _sentFramesI.set(items[ri][0], items[ri][1]);
                first = false; n++;
            } catch (_) { try { items[ri][1].close(); } catch (_e) {} }
        }
        _encForceKeyframeNext = false;
        return n;
    }

    // Filter rig — re-used for blend shader. Filter passes are NOT applied
    // on the interpolation path: video-fps doesn't have brightness/contrast
    // controls, and applying them through the same shader pipeline would
    // require a third texture + reorder. Keep it focused.
    try { await ensureHdrToSdr(); } catch (_) {}
    var rig = makeFilterRig(outW, outH);
    var gl = rig.gl;

    /* Motion-compensated in-betweens (2026-10-22). Without a callback this lane
       CROSS-FADED A into B: the file came out 112 fps by count and 30 by motion —
       every made-up frame showed A's positions with a ghost of B (measured
       against a 120 fps truth: 30 distinct motion positions per second at ×4).
       Report «до 120 фпс увеличил, а стало лишь 30»; ~80 % of /video-fps files
       came from this lane. `opts.motion` asks for window.MotionInterpGL (the
       caller loads it); a device without float render targets, or a driver that
       refuses the shaders, keeps the cross-fade — and the result says so
       (`interp`, `interpWhyNot`). It lives on the rig's context, so a revived
       rig gets a fresh one (_reviveRigI). */
    var _motionI = null, _motionWhyNotI = interpolator ? 'callback' : (opts.motion ? '' : 'not_asked');
    var _motionPairsI = 0, _motionSampledI = 0, _motionUnreadableI = 0, _motionAgreeI = 0;
    // The source frame whose pyramid the module holds as B — slot A may reuse it
    // only when A IS that frame (two textures alternate; a pair without
    // in-betweens rolls the slots without a prepare).
    var _motionBTsI = -Infinity;
    function _makeMotionI() {
        _motionI = null;
        _motionBTsI = -Infinity;
        if (interpolator || !opts.motion) return;
        var M = (typeof window !== 'undefined') ? window.MotionInterpGL : null;
        if (!M || typeof M.create !== 'function') { _motionWhyNotI = 'no_module'; return; }
        try {
            _motionI = M.create(gl, { width: outW, height: outH });
            _motionWhyNotI = '';
        } catch (e) {
            _motionWhyNotI = (e && e.code) || 'create_failed';
            _aeWebcodecsErr(aeSlug, 'motion_interp_unavailable', e, { stage: 'interp', why: _motionWhyNotI, out_w: outW, out_h: outH });
        }
    }
    _makeMotionI();
    exits.push(function () { try { if (_motionI) _motionI.dispose(); } catch (_) {} });

    // Helpers — uploadFrame puts a VideoFrame into a specific texture slot.
    function uploadFrameTo(tex, frame) {
        gl.bindTexture(gl.TEXTURE_2D, tex);
        // Pre-define storage shape so subsequent texSubImage2D calls match.
        // First call here uses texImage2D to allocate; conventional WebGL.
        uploadFrameTexture(gl, frame);
    }

    // Render a single texture directly to canvas via blend shader (degenerate
    // case t=0 → output = texA exactly). Avoids needing a separate copy shader.
    function renderTexToCanvas(tex) {
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.viewport(0, 0, outW, outH);
        gl.useProgram(rig.progBlend);
        gl.bindVertexArray(rig.vao);
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, tex);
        gl.uniform1i(rig.uBlend.texA, 0);
        // Bind same tex to second slot — t=0 makes texB unused but binding
        // SOMETHING is required to keep the sampler valid.
        gl.activeTexture(gl.TEXTURE1);
        gl.bindTexture(gl.TEXTURE_2D, tex);
        gl.uniform1i(rig.uBlend.texB, 1);
        gl.uniform1f(rig.uBlend.t, 0.0);
        gl.drawArrays(gl.TRIANGLES, 0, 6);
        gl.bindVertexArray(null);
    }

    function renderBlendToCanvas(texA, texB, t) {
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.viewport(0, 0, outW, outH);
        gl.useProgram(rig.progBlend);
        gl.bindVertexArray(rig.vao);
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, texA);
        gl.uniform1i(rig.uBlend.texA, 0);
        gl.activeTexture(gl.TEXTURE1);
        gl.bindTexture(gl.TEXTURE_2D, texB);
        gl.uniform1i(rig.uBlend.texB, 1);
        gl.uniform1f(rig.uBlend.t, t);
        gl.drawArrays(gl.TRIANGLES, 0, 6);
        gl.bindVertexArray(null);
    }

    // Pixel readback for AI callback path. Reuses an OffscreenCanvas we own
    // (one per slot) so the callback gets a clean ImageData per call.
    var readCanvasA = null, readCanvasB = null, readCanvasOut = null;
    function ensureReadCanvases() {
        if (!readCanvasA) {
            readCanvasA = new OffscreenCanvas(outW, outH);
            readCanvasB = new OffscreenCanvas(outW, outH);
            readCanvasOut = new OffscreenCanvas(outW, outH);
        }
    }
    function texToImageData(tex, dstCanvas) {
        ensureReadCanvases();
        // Render the texture to canvas via blend shader (t=0), then read pixels
        // back via gl.readPixels into a buffer, then putImageData to dstCanvas.
        // OffscreenCanvas2D.transferFromImageBitmap could be faster but needs
        // ImageBitmap from the WebGL canvas — adds a copy. Direct readPixels
        // is the standard zero-copy* path (* one buffer alloc).
        renderTexToCanvas(tex);
        var pixels = new Uint8Array(outW * outH * 4);
        gl.readPixels(0, 0, outW, outH, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
        // WebGL framebuffer is bottom-up; flip to top-down for ImageData.
        var flipped = new Uint8ClampedArray(outW * outH * 4);
        var rowBytes = outW * 4;
        for (var y = 0; y < outH; y++) {
            var srcRow = (outH - 1 - y) * rowBytes;
            var dstRow = y * rowBytes;
            flipped.set(pixels.subarray(srcRow, srcRow + rowBytes), dstRow);
        }
        var imgData = new ImageData(flipped, outW, outH);
        dstCanvas.getContext('2d').putImageData(imgData, 0, 0);
        return imgData;
    }

    // Encode a frame from rig.canvas at output index k.
    var outFrameCount = 0;
    var t0 = performance.now();
    var totalSrcSamples = demuxInfo.samples ? demuxInfo.samples.length : 0;
    var totalOutFrames = totalSrcSamples > 0 ? (totalSrcSamples - 1) * ratio + 1 : 0;
    var firstFrameOfStream = true;
    // Encoder-queue backpressure. Interpolation feeds the encoder ratio× (2-4×)
    // the decode rate, and the feed loop's decode-side gate (decodeQueueSize/
    // decodedFrames) cannot see the ENCODER's input queue — so on any machine
    // where the encoder is slower than decode+blend the queue grew without
    // bound. MEASURED on a 235 MB / 1080p / 150 s mov: encodeQueueSize peaked
    // at 7360 frames (~20+ GB of frame memory outside the JS heap). On big-RAM
    // boxes that starves the decoder's output pool and wedges the run at ~95%;
    // on ordinary machines the renderer is OOM-killed mid-run — the prod
    // "синий экран каждые ~20%, страница перезапускается" report class
    // (83 fast-starts ≥150 MB / 30 d → only 9 exports). 24 frames ≈ well
    // above HW encoder pipelining depth (8-16), so throughput is unaffected;
    // the cost is only the over-buffering. Polling mirrors the decode gate's
    // setTimeout pattern above — 'dequeue' events are Chrome-only-recent.
    var ENC_QUEUE_HI = 24;
    // Retention ceiling: the queue plus a margin (run()'s rule — fewer than the
    // queue holds would clip every replay). Declared HERE, after ENC_QUEUE_HI:
    // written before it, the sum is NaN and `size > NaN` never holds.
    var RETAIN_CAP_I = ENC_QUEUE_HI + 8;
    async function drainEncoderQueue() {
        while (encoder.encodeQueueSize > ENC_QUEUE_HI) {
            if (aborted) throw new DOMException('cancelled', 'AbortError');
            if (encoderError) return;    // emit's own error handling surfaces it
            await _yieldTick(8);
        }
    }
    async function emitFrameFromCanvas(srcCanvas /* OffscreenCanvas or VideoFrame source */) {
        // Wait BEFORE grabbing the canvas into a VideoFrame — the canvas is
        // only re-rendered after this call returns, so waiting here is safe
        // and keeps at most one un-queued frame alive during the drain.
        await drainEncoderQueue();
        // Timestamps stride at 1/playbackFps so slow-mo stretches duration
        // while smooth keeps source duration. Same N×source frames either way.
        // ⚠️ ts/dur take the EXACT rate; keyEvery takes the rounded one. A GOP is a
        // count of frames, so it neither needs nor wants the correction.
        // Output 0 is pinned at 0 and holds by the audio residual (see
        // _videoDelayUsI): passthrough audio plays up to one access unit
        // from before the cut first, the picture waits for it.
        var ts = outFrameCount === 0 ? 0 : (Math.round(outFrameCount * 1_000_000 / playbackFpsExact) + _videoDelayUsI);
        var dur = Math.round(1_000_000 / playbackFpsExact);
        var keyEvery = Math.max(1, Math.round(playbackFps * 2));   // GOP ~2s of playback
        var isKey = firstFrameOfStream || (outFrameCount % keyEvery === 0);
        // A context lost between the render and this grab would hand the encoder
        // an empty picture; a loss found here, mid-frame, cannot be replayed
        // without doubling the outputs already made from this source frame, so
        // it ends the attempt loudly (the loss found BEFORE a frame — the iOS
        // shape, everything dying at once — is rebuilt in processNewDecodedFrame).
        if (srcCanvas === rig.canvas && _glLostI()) throw _glLostErrI();
        var outFrame = new VideoFrame(srcCanvas, { timestamp: ts, duration: dur });
        if (encoderError) {
            if (_encRecoverableI() && _encRebuildsI < ENC_REBUILD_MAX_I) {
                // Held unencoded: the rebuilt encoder replays it with the rest.
                _sentFramesI.set(ts, outFrame);
                firstFrameOfStream = false;
                outFrameCount++;
                _pendingEncRecovery = true;
                return;
            }
            try { outFrame.close(); } catch(_){}
            _releaseHeldFrames();
            try { decoder.close(); encoder.close(); } catch(_){}
            { var _efI1 = new Error('encoder-failed: ' + (encoderError.message || encoderError)); _efI1.encHint = encoderChosenHintI; throw _efI1; }
        }
        if (muxerError) {
            try { outFrame.close(); } catch(_){}
            _releaseHeldFrames();
            try { decoder.close(); encoder.close(); } catch(_){}
            throw new Error('muxer-failed: ' + (muxerError.message || muxerError));
        }
        if (diskWriteFailed(muxTargetI)) {
            try { outFrame.close(); } catch(_){}
            _releaseHeldFrames();
            try { decoder.close(); encoder.close(); } catch(_){}
            throw diskWriteError(muxTargetI.opfs.lane, null, _reserveBytesI);
        }
        var _forcedKey = _encForceKeyframeNext;
        if (_forcedKey) _encForceKeyframeNext = false;
        if (_encClockI) _encClockI.fed(ts, dur);
        encoder.encode(outFrame, { keyFrame: isKey || _forcedKey });
        // Held until its chunk comes back (see _sentFramesI) — that is what
        // makes an encoder rebuild lossless.
        _sentFramesI.set(ts, outFrame);
        if (_sentFramesI.size > _retainPeakI) _retainPeakI = _sentFramesI.size;
        while (_sentFramesI.size > RETAIN_CAP_I) {
            var _oldI = _sentFramesI.keys().next();
            if (_oldI.done) break;
            _releaseSentI(_oldI.value);
            _retainDroppedI++;
        }
        firstFrameOfStream = false;
        outFrameCount++;
        // The second argument carries the COUNT the fraction was made of: on a
        // slow interpolator one output frame is a sliver of a per cent, and a
        // page that can only print «0 %» for ten minutes looks dead (bug report
        // 2026-09-25, «Не загружает»). Callers that take one argument ignore it.
        if (totalOutFrames > 0) onProgress(Math.min(0.95, outFrameCount / totalOutFrames), { frames: outFrameCount, total: totalOutFrames });
    }

    // Pair-buffer state. slotA/slotB are texture handles that swap roles per
    // pair; lastImgDataA/B mirror the textures as ImageData when callback
    // path is active (avoids re-readback per intermediate).
    var slotA = rig.texSrc;
    var slotB = rig.texSrcB;
    var hasA = false;
    var lastImgDataA = null, lastImgDataB = null;

    /* WHERE the in-betweens of a pair fall (2026-10-22). Output k is the moment
       pts0 + k ÷ (source rate × ratio) on the SOURCE's own clock; a pair (A, B)
       makes the outputs whose moments lie in (A, B], each at t = its share of
       the pair's REAL interval, and B itself when a moment lands on it (within
       a tenth of an output frame). For an even source that is exactly the old
       «ratio − 1 in-betweens, then B». A recording that dropped frames — a game
       that lagged; 28 % of this tool's sources (RU, 30 d) have an average rate
       no camera writes, the report's Roblox file 28.19 — had its frames spread
       evenly by COUNT: measured on a 3D clip with one 2-second lag in 8 seconds,
       the picture ran up to 0.6 s off its own (copied) sound. Now a lag gets as
       many in-betweens as its length asks for and the picture keeps the source's
       time. Output timestamps still stride by count (emitFrameFromCanvas) — on
       this grid count and time are the same thing. */
    var _gridStepUsI = 1e6 / ((demuxInfo.fpsExact > 0 ? demuxInfo.fpsExact : srcFps) * ratio);
    var _gridSnapUsI = _gridStepUsI * 0.1;
    var _pts0I = null;
    function _pairTimesI(ptsA, ptsB) {
        var out = [];
        if (_pts0I === null || !(ptsB > ptsA)) return out;
        var kLo = Math.floor((ptsA + _gridSnapUsI - _pts0I) / _gridStepUsI) + 1;
        var kHi = Math.floor((ptsB + _gridSnapUsI - _pts0I) / _gridStepUsI);
        for (var k = kLo; k <= kHi; k++) {
            var at = _pts0I + k * _gridStepUsI;
            out.push(at >= ptsB - _gridSnapUsI ? 1 : (at - ptsA) / (ptsB - ptsA));
        }
        return out;
    }

    async function processNewDecodedFrame(frame) {
        var fts = frame.timestamp;
        // Readback canvases must exist BEFORE they are passed to texToImageData:
        // the argument (readCanvasA/B) is evaluated before texToImageData's own
        // ensureReadCanvases() runs, so on the very first frame it would be null
        // → "Cannot read properties of null (reading 'getContext')". This is the
        // only reason the interpolator (RIFE/AI) path ever reached the encoder.
        if (interpolator) ensureReadCanvases();
        // After a lost WebGL context the rebuilt decoder delivers slot A's frame
        // again first: its texture died with the context, its outputs did not.
        // Upload it, emit nothing.
        if (_reprimeA) {
            if (fts !== _slotATsI) {
                try { frame.close(); } catch(_){}
                throw new Error('decode: rebuilt decoder skipped the held frame (' + fts + ' after ' + _slotATsI + ')');
            }
            uploadFrameTo(slotA, frame);
            try { frame.close(); } catch(_){}
            if (interpolator) lastImgDataA = texToImageData(slotA, readCanvasA);
            if (_glLostI()) { _gpuLostI = true; _releaseHeldFrames(); if (!decodeError) decodeError = new Error('gpu-lost: context lost'); return; }
            _reprimeA = false;
            return;
        }
        // The media session's GPU side is gone (iOS: decoder, encoder and the
        // WebGL context go together). Rendering now would encode black frames
        // without a word — this frame is owed again after the rebuild.
        if (_glLostI()) {
            try { frame.close(); } catch(_){}
            _gpuLostI = true;
            _releaseHeldFrames();
            if (!decodeError) decodeError = new Error('gpu-lost: context lost');
            return;
        }
        // A context lost part-way through this frame: nothing more is made from
        // it now; the rebuild owes the frame again and resumes after the
        // outputs already emitted (_partialDoneI).
        function _lostMidFrame(e) {
            if (!(e && e.__glLost)) return false;
            _gpuLostI = true;
            _releaseHeldFrames();
            if (!decodeError) decodeError = new Error('gpu-lost: context lost mid-frame');
            return true;
        }
        if (!hasA) {
            // First decoded frame — uploads to slotA and emits as output 0.
            try {
                uploadFrameTo(slotA, frame);
                try { frame.close(); } catch(_){}
                renderTexToCanvas(slotA);
                await emitFrameFromCanvas(rig.canvas);
                if (interpolator) {
                    lastImgDataA = texToImageData(slotA, readCanvasA);
                    if (_glLostI()) throw _glLostErrI();
                }
            } catch (e0) {
                try { frame.close(); } catch(_){}
                if (_lostMidFrame(e0)) {
                    // Output 0 is out (or never made): either way it is not made twice.
                    if (outFrameCount > 0) { hasA = true; _slotATsI = fts; _pts0I = fts; _reprimeA = false; }
                    return;
                }
                throw e0;
            }
            hasA = true;
            _slotATsI = fts;
            _pts0I = fts;
            if (fts > _decLastUsedTsI) _decLastUsedTsI = fts;
            return;
        }
        // A frame stamped at or before A's moment has no interval to fill: it is
        // dropped and A stays (rolling to it would walk the clock backwards).
        if (!(fts > _slotATsI)) {
            try { frame.close(); } catch(_){}
            if (fts > _decLastUsedTsI) _decLastUsedTsI = fts;
            return;
        }
        var _pairT = _pairTimesI(_slotATsI, fts);
        var _skipI = (fts === _partialTsI) ? _partialDoneI : 0;
        var _nthI = 0;
        _partialTsI = fts;
        _partialDoneI = _skipI;
        var emitPart = async function (cv) {
            if (_nthI++ < _skipI) return;
            await emitFrameFromCanvas(cv);
            _partialDoneI = _nthI;
        };
        try {
        // Upload to slotB (next frame).
        uploadFrameTo(slotB, frame);
        try { frame.close(); } catch(_){}

        if (interpolator) {
            // CPU-readback path. Get ImageData of B, hand pair to callback.
            lastImgDataB = texToImageData(slotB, readCanvasB);
            if (_glLostI()) throw _glLostErrI();          // a lost context reads back zeros
            for (var k = 0; k < _pairT.length; k++) {
                if (aborted) throw new DOMException('cancelled', 'AbortError');
                if (_nthI < _skipI) { _nthI++; continue; }  // already in the encoder
                var t = _pairT[k];
                if (t >= 1) { renderTexToCanvas(slotB); await emitPart(rig.canvas); continue; }
                var interpImgData;
                try {
                    interpImgData = await interpolator(lastImgDataA, lastImgDataB, t);
                } catch (e) {
                    // Re-wrapping must not strip the interpolator's typed verdict:
                    // RifeCore throws `code: 'gpu_output_corrupt'` when a GPU that
                    // passed its pre-run test starts lying mid-run, and the page
                    // restarts on the CPU only if it can still see that code.
                    var wrapped = new Error('interpolator-failed: ' + ((e && e.message) || e));
                    if (e && typeof e === 'object') {
                        if (e.code) wrapped.code = e.code;
                        if (e.name === 'AbortError') wrapped.name = 'AbortError';
                        wrapped.cause = e;
                    }
                    throw wrapped;
                }
                if (!interpImgData || !interpImgData.data) throw new Error('interpolator-bad-result');
                // Put on output canvas + encode
                readCanvasOut.getContext('2d').putImageData(interpImgData, 0, 0);
                await emitPart(readCanvasOut);
            }
            // Roll: A := B (texture swap + ImageData reuse).
            var tmpTex = slotA; slotA = slotB; slotB = tmpTex;
            var tmpImg = lastImgDataA; lastImgDataA = lastImgDataB; lastImgDataB = tmpImg;
        } else {
            // GPU path (Fast tier): motion-compensated in-betweens, or the
            // cross-fade where the motion module is unavailable. Then B.
            var _inBetween = _pairT.length && _pairT[0] < 1;
            if (_motionI && _inBetween) {
                _motionI.prepare(slotA, slotB, _motionBTsI === _slotATsI);
                _motionBTsI = fts;
                // One look in 32 pairs at whether the motion read (a sync, so rare).
                if ((++_motionPairsI & 31) === 1) {
                    var _mv = _motionI.verdict();
                    _motionSampledI++; _motionAgreeI += _mv.agree;
                    if (_mv.unreadable) _motionUnreadableI++;
                }
            }
            for (var kk = 0; kk < _pairT.length; kk++) {
                if (_nthI < _skipI) { _nthI++; continue; }  // already in the encoder
                var tt = _pairT[kk];
                if (tt >= 1) renderTexToCanvas(slotB);
                else if (_motionI) _motionI.render(tt, null);
                else renderBlendToCanvas(slotA, slotB, tt);
                await emitPart(rig.canvas);
            }
            // Roll
            var tmpT = slotA; slotA = slotB; slotB = tmpT;
        }
        } catch (eB) {
            try { frame.close(); } catch(_){}
            if (_lostMidFrame(eB)) return;             // B not consumed: owed again, resumes after _partialDoneI
            throw eB;
        }
        _partialTsI = -Infinity; _partialDoneI = 0;
        _slotATsI = fts;
        if (fts > _decLastUsedTsI) _decLastUsedTsI = fts;
    }

    // ─── Decode loop (mirrors run()'s feed pattern) ────────────────────
    var decodedFrames = [];
    var decodeError = null;
    // The same guard as run()'s feed — see _decoderDied there.
    function _decoderDied() {
        if (decodeError) return true;
        var st = ''; try { st = decoder ? decoder.state : ''; } catch (_) {}
        if (st !== 'closed') return false;
        decodeError = new Error('decoder closed');
        return true;
    }
    /* In-place decoder rebuild (2026-09-27) — the shape run() got on 26.09
       (support #649, see _rebuildDecoder there). A decoder that has already
       WORKED and then dies is rebuilt with the config it accepted, fed again
       from the true IDR before the first frame still owed, and its re-emitted
       frames are dropped by timestamp. Prod, RU, 30 d: in ~100 mobile sessions
       this decoder died after the run had made progress (≥10 % → 58, ≥50 % →
       18, ≥90 % → 21); the rescue restarted them from zero on a software
       decoder the phone mostly lacks. Budget DEC_REBUILD_MAX_I; past it the
       error surfaces exactly as before. */
    var _decRebuildsI = 0, DEC_REBUILD_MAX_I = 3, _decOutTotalI = 0;
    var _decDropUpToI = -Infinity;       // re-emitted frames at or before this are dropped
    var _decLastUsedTsI = -Infinity;     // the last source frame this lane consumed
    var _slotATsI = -Infinity;           // the source frame whose pixels sit in slot A
    var _reprimeA = false;               // after a GL loss: re-upload slot A, emit nothing
    var _lastSentIdxI = -1, _decConfigChosenI = null, _decReordersI = null;
    var _gpuLostI = false, _gpuRevivalsI = 0;
    // The source frame being turned into outputs, and how many of its outputs
    // are already in the encoder — a context lost between two of them resumes
    // after the last one instead of emitting it twice.
    var _partialTsI = -Infinity, _partialDoneI = 0;
    function _glLostErrI() { var e = new Error('gpu-lost: context lost mid-frame'); e.__glLost = true; return e; }
    function _glLostI() {
        try { return !!(rig && rig.gl && typeof rig.gl.isContextLost === 'function' && rig.gl.isContextLost()); }
        catch (_) { return true; }
    }
    /* Close whatever this lane is still holding when it bails out.
       ⚠️ The three failure paths above/below used to call `_releaseAllSent()` —
       _runPipeline's helper, copied along with the error handling but NOT with
       the `_sentFrames` retention it operates on. It is declared in a sibling
       function, so those paths threw `ReferenceError: _releaseAllSent is not
       defined` and buried the real encoder-failed / muxer-failed / cancelled
       reason, while the decoded VideoFrames still queued here (GPU memory, not
       JS heap) were never closed. This lane retains nothing after `encode()` —
       `emitFrameFromCanvas` closes its own outFrame — so the only thing it can
       leak is the undrained decode queue. Found by scripts/js-scope-leak.mjs. */
    function _releaseHeldFrames() {
        while (decodedFrames.length) {
            var f = decodedFrames.shift();
            try { f.close(); } catch (_) {}
        }
    }
    // HW-decoder OUTPUT-stall watchdog. Caught live (2026-08-08, desktop
    // Chromium + resident ORT-WebGPU device): the hardware decoder accepted
    // all samples, delivered ~10 of 24 outputs, then went silent — no error,
    // and decoder.flush() never resolves. run()'s consumers survive this via
    // their blade-level no-tick watchdogs, but the interpolation entry had
    // no deadline at all → the AI lane hung forever at «0 из ?». Turning the
    // silent stall into a `decode: Decoder failure…` error (with the hw hint
    // attached) routes it into runInterpolation's EXISTING software-decode
    // rescue — one bounded retry on prefer-software, which is immune to the
    // GPU contention that wedges the hardware path.
    //
    // ⚠️ (2026-09-26) The watchdog measures the DECODER's silence, and two things
    // are not that:
    //   • a frozen or hidden tab — it used `performance.now()`, the wall clock.
    //     Android freezes a backgrounded page; the reporter of 2026-09-25 came
    //     back after 15 minutes and the first poll saw «no output for 15 min»
    //     and killed a healthy run at 99 %. Visible time only (`_visNow`), the
    //     rule the encoder's wedge watchdog already follows;
    //   • OUR consumer — the AI lane spends 0.3–30 s per frame in RIFE while up
    //     to 20 decoded frames wait for it, and a decoder whose output pool is
    //     full cannot emit. The clock restarts whenever we finish a frame, and
    //     a watchdog that fires while we still HOLD frames blames the decoder
    //     for our own queue (the «hungry decoder ≠ stalled decoder» class the
    //     frame extractor fixed on 2026-09-17; prod: «hw output stalled at
    //     58/96 during feed» on a desktop AI run at 832 ms/frame).
    var _lastDecOutAtI = -1, _lastConsumedAtI = -1, _decOutCountI = 0, _decFedCountI = 0, _feedStartAtI = -1;
    var DEC_STALL_MS = 30000;
    // Wakes the flush loop on the decoder's own events instead of a spin.
    var _decWakeI = null;
    function _wakeDecWaiterI() { var f = _decWakeI; _decWakeI = null; if (f) f(); }
    function _waitDecoderEventI(ms) {
        return new Promise(function (resolve) {
            var t = setTimeout(function () { if (_decWakeI === done) _decWakeI = null; resolve(); }, ms);
            function done() { clearTimeout(t); resolve(); }
            _decWakeI = done;
        });
    }
    function _throwIfDecoderStalled(where) {
        if (_decOutCountI >= _decFedCountI) return;               // nothing pending
        if (decodedFrames.length) return;                         // it is waiting on US
        var anchor = Math.max(_lastDecOutAtI, _lastConsumedAtI, _feedStartAtI);
        if (anchor < 0 || (_visNow() - anchor) < DEC_STALL_MS) return;
        var err = new Error('decode: Decoder failure (hw output stalled at ' + _decOutCountI + '/' + _decFedCountI + ' during ' + where + ')');
        err.decHint = decoderHwTag;
        _aeWebcodecsErr(aeSlug, 'decoder', err, {
            stage: 'interp', codec: demuxInfo.codec,
            stall_where: where, delivered: _decOutCountI, fed: _decFedCountI
        });
        throw err;
    }
    // One decoder per life: a callback from a decoder that was replaced (a late
    // error, a frame it still had in flight) must not touch the new one's state.
    function _makeDecoderI() {
        var self = new VideoDecoder({
            output: function (frame) {
                if (self !== decoder) { try { frame.close(); } catch(_){} return; }
                _decInitI.output(frame);
            },
            error: function (e) {
                if (self !== decoder) return;
                _decInitI.error(e);
            }
        });
        return self;
    }
    var _decInitI = {
        output: function(frame) {
            if (aborted) { try { frame.close(); } catch(_){} _wakeDecWaiterI(); return; }
            _lastDecOutAtI = _visNow();
            _decOutCountI++;
            _decOutTotalI++;
            // Already consumed before a rebuild — the new decoder re-emits it.
            if (_gpuLostI || frame.timestamp <= _decDropUpToI) { try { frame.close(); } catch(_){} _wakeDecWaiterI(); return; }
            decodedFrames.push(frame);
            _wakeDecWaiterI();
        },
        error: function(e) {
            decodeError = e;
            _aeWebcodecsErr(aeSlug, 'decoder', e, {
                stage: 'interp',
                codec: demuxInfo.codec,
                src_w: inW, src_h: inH,
                err_name: e && e.name,
                // mid-run or at the start — the field could not tell until 27.09
                decoded_so_far: _decOutTotalI,
                vis_state: (typeof document !== 'undefined' && document.visibilityState) || ''
            });
            _wakeDecWaiterI();
        }
    };
    var decoder = null;
    decoder = _makeDecoderI();
    // Same isConfigSupported pre-check + cascade as primary pipeline,
    // including the sanitizeAvcLevel hint repair for sub-1.0 level bytes.
    var decConfigBase = {
        codec: sanitizeAvcLevel(demuxInfo.codec),
        codedWidth: inW, codedHeight: inH,
        description: demuxInfo.avcC || demuxInfo.hvcC                                 // Wave 18 — HEVC sources now carry hvcC description
    };
    var decConfigured = false;
    var decoderHwTag = null;
    var decConfigureErrI = null;
    // Caller-overridable cascade (2026-07-31) — the interpolation rescue
    // wrapper pins ['prefer-software'] on retry, mirroring run()'s Wave 23.
    // 2026-08-08: when a frameInterpolator is present (AI lane) a resident
    // ORT-WebGPU device shares the GPU with the decoder, and on desktop the
    // hardware decoder intermittently stalls mid-delivery next to it (caught
    // live: ~10 of 24 outputs, then silence, flush never settles). A/B on the
    // same machine: hw-first = ~75% stall→30 s watchdog→SW rescue (42–47 s),
    // or 10–12 s when it survives; SW-first = 0 stalls, 1.7–2.2 s. Software
    // decode is also never the bottleneck in AI runs (RIFE inference is).
    // Desktop-only: on Android `prefer-software` LIES at configure (accepts,
    // dies on chunk 1 — measured 2026-08-05, rescue 2/115), so Android keeps
    // hw-first + the stall watchdog as its safety net.
    var _swFirstForAi = !!interpolator && !/Android/i.test((typeof navigator !== 'undefined' && navigator.userAgent) || '');
    var _iDecHints = (opts && opts._decodeHints)
        || (_swFirstForAi ? ['prefer-software', 'prefer-hardware', null] : DECODER_HINTS);
    for (var dh = 0; dh < _iDecHints.length; dh++) {
        try {
            var dcfg = Object.assign({}, decConfigBase);
            if (_iDecHints[dh]) dcfg.hardwareAcceleration = _iDecHints[dh];
            var dProbeI = await VideoDecoder.isConfigSupported(dcfg);
            if (!dProbeI || !dProbeI.supported) continue;
            decoder.configure(dProbeI.config || dcfg);
            _decConfigChosenI = dProbeI.config || dcfg;
            decConfigured = true;
            decoderHwTag = _iDecHints[dh] || 'no-hint';
            break;
        } catch (e) { decConfigureErrI = e; }
    }
    if (!decConfigured) {
        var dMsgI = (decConfigureErrI && decConfigureErrI.message)
            ? ('decoder-configure-failed: ' + decConfigureErrI.message)
            : ('decoder-configure-failed: no hint accepted ' + demuxInfo.codec);
        _aeWebcodecsErr(aeSlug, 'decoder', new Error(dMsgI), { stage: 'interp', codec: demuxInfo.codec });
        throw new Error(dMsgI);
    }

    var samples = demuxInfo.samples;
    var _lazyRdI = demuxInfo.lazy ? _lazySampleReader(demuxInfo) : null;
    function _samplesReorderI() {
        if (_decReordersI !== null) return _decReordersI;
        _decReordersI = false;
        for (var ro = 1; ro < samples.length && ro < 4000; ro++) {
            if (samples[ro].timestamp < samples[ro - 1].timestamp) { _decReordersI = true; break; }
        }
        return _decReordersI;
    }
    // A lost context is dead for good; a fresh rig gets a fresh one once the GPU
    // process is back. Slot A's pixels are re-uploaded by the re-prime.
    async function _reviveRigI() {
        for (var a = 0; a < 4; a++) {
            try {
                rig = makeFilterRig(outW, outH);
                gl = rig.gl;
                slotA = rig.texSrc; slotB = rig.texSrcB;
                _makeMotionI();   // the old one died with the old context
                _gpuLostI = false; _gpuRevivalsI++;
                return true;
            } catch (_) { await new Promise(function (r) { setTimeout(r, 500); }); }
        }
        return false;
    }
    // Returns the sample index to feed from, or -1 (the error then surfaces as before).
    async function _rebuildDecoderI(where) {
        if (!_lazyRdI || !_decConfigChosenI || _decOutTotalI <= 0 || aborted) return -1;
        if (_decRebuildsI >= DEC_REBUILD_MAX_I) return -1;
        _decRebuildsI++;
        var deadErr = decodeError;
        try { decoder.close(); } catch (_) {}
        if (!(await _waitForForeground(60000))) return -1;
        // Delivered but not yet consumed: owed again, from the new decoder.
        _releaseHeldFrames();
        var revived = _gpuLostI || _glLostI();
        if (revived && !(await _reviveRigI())) return -1;
        var owedTs = _decLastUsedTsI;
        var upTo = Math.min(Math.max(_lastSentIdxI, 0), samples.length - 1);
        var first = -1;
        for (var j = 0; j <= upTo; j++) { if (samples[j].timestamp > owedTs) { first = j; break; } }
        if (first < 0) first = Math.min(upTo + 1, samples.length - 1);
        // A lost context also owes slot A's frame: its texture died with it.
        var reprime = revived && hasA;
        if (reprime) {
            for (var ja = 0; ja <= first; ja++) { if (samples[ja].timestamp >= _slotATsI) { first = ja; break; } }
        }
        var k = first;
        while (k > 0 && !samples[k].is_sync) k--;
        if (_samplesReorderI() && k > 0) {
            var k2 = k - 1;
            while (k2 > 0 && !samples[k2].is_sync) k2--;
            if (samples[k2].is_sync) k = k2;
        }
        for (var guard = 0; guard < 400; guard++) {
            if (samples[k].is_sync && await _sampleStartsADecoder(demuxInfo.codec, samples[k], _lazyRdI)) break;
            if (k <= 0) return -1;
            k--;
            while (k > 0 && !samples[k].is_sync) k--;
        }
        if (!samples[k].is_sync) return -1;
        decodeError = null;
        // the fresh decoder starts cold at k: its leading pictures stay out (_leadingSkip)
        _leadSkipI = _leadingSkip(samples, k, samples.length);
        _decDropUpToI = reprime ? _slotATsI - 1 : owedTs;
        _reprimeA = reprime;
        try {
            decoder = _makeDecoderI();
            decoder.configure(_decConfigChosenI);
        } catch (e) { decodeError = e; return -1; }
        // A fresh decoder owes nothing yet: the stall watchdog counts from here.
        _decOutCountI = 0; _decFedCountI = 0; _feedStartAtI = -1; _lastDecOutAtI = -1;
        _aeWebcodecsErr(aeSlug, 'decoder_rebuilt', new Error((deadErr && deadErr.message) || 'decoder died'), {
            stage: 'interp', where: where, n: _decRebuildsI, decoded_so_far: _decOutTotalI,
            resume_from: k, first_owed: first, last_sent: _lastSentIdxI, lead: _leadSkipI.n || 0,
            src_w: inW, src_h: inH, vis_state: document.visibilityState || '',
            enc_dead: encoderError ? 1 : 0, gpu_revived: revived ? 1 : 0,
            reprime: reprime ? 1 : 0, ai: interpolator ? 1 : 0,
            out_frames: outFrameCount, owed_enc: _sentFramesI.size
        });
        return k;
    }
    async function drainOne() {
        // While an encoder rebuild is pending nothing more is rendered, so the
        // frames it will replay stay within the retention ceiling.
        if (_pendingEncRecovery || _gpuLostI) return false;
        var fr = decodedFrames.shift();
        if (!fr) return false;
        if (_editAppliedI && (fr.timestamp < _presStartUsI || fr.timestamp >= _presEndUsI)) {
            // Outside the edit list's presentation window (the camera's video
            // lead, an editor's trimmed tail): decoded for reference only,
            // never interpolated from — it was never on screen.
            if (fr.timestamp > _decLastUsedTsI) _decLastUsedTsI = fr.timestamp;
            try { fr.close(); } catch (_) {}
            _lastConsumedAtI = _visNow();
            return true;
        }
        await processNewDecodedFrame(fr);
        _lastConsumedAtI = _visNow();          // a slot is free again — the stall clock restarts
        return true;
    }
    var _feedResumeI = 0;
    // samples the current decoder must not be fed: its cold-start keyframe's leading pictures
    var _leadSkipI = _leadingSkip(samples, 0, samples.length);
    for (;;) {   // re-entered only when the decoder died during the final flush
    for (var i = _feedResumeI; i < samples.length; i++) {
        if (aborted) {
            _releaseHeldFrames();
            try { decoder.close(); } catch(_){}
            try { encoder.close(); } catch(_){}
            throw new DOMException('cancelled', 'AbortError');
        }
        if (_leadSkipI[i]) continue;
        var s = samples[i];
        var _sBytesI = s.data;
        if (!_sBytesI && _lazyRdI) {
            try { _sBytesI = await _lazyRdI.bytes(s, true); }
            catch (rdEI) { _releaseHeldFrames(); try { decoder.close(); } catch(_){} try { encoder.close(); } catch(_){} throw new Error('demux-read: ' + String((rdEI && rdEI.message) || rdEI).slice(0, 120)); }
            if (aborted) { _releaseHeldFrames(); try { decoder.close(); } catch(_){} try { encoder.close(); } catch(_){} throw new DOMException('cancelled', 'AbortError'); }
        }
        var _restartAtI = -1;
        while (_decoderDied() || decoder.decodeQueueSize > 30 || decodedFrames.length > 20) {
            await _yieldTick(0);
            while (await drainOne()) {}
            if (decodeError) {
                _restartAtI = await _rebuildDecoderI('feed');
                if (_restartAtI < 0) { var _deI = new Error('decode: ' + decodeError.message); _deI.decHint = decoderHwTag; throw _deI; }
                break;
            }
            _throwIfDecoderStalled('feed');
            if (_pendingEncRecovery) {
                _pendingEncRecovery = false;
                var okI = await _rebuildEncoderAfterReclaim();
                if (!okI) {
                    try { decoder.close(); } catch(_){}
                    { var _efI2 = new Error('encoder-failed: ' + (encoderError && (encoderError.message || encoderError) || 'reclaim-recovery-failed')); _efI2.encHint = encoderChosenHintI; throw _efI2; }
                }
            }
        }
        if (_restartAtI >= 0) { i = _restartAtI - 1; continue; }
        if (_feedStartAtI < 0) _feedStartAtI = _visNow();
        _decFedCountI++;
        _lastSentIdxI = i;
        decoder.decode(new EncodedVideoChunk({
            type: s.is_sync ? 'key' : 'delta',
            timestamp: s.timestamp,
            duration: s.duration,
            data: _sBytesI
        }));
        /* Release the compressed bytes once handed over — the exact pattern
           _runPipeline's feed loop earned on the 2 h video-flip export
           (steady heap −33%): demuxMp4 collects EVERY sample before decode
           starts, so without this a 235 MB source parks its whole compressed
           stream on the heap for the entire interpolation run. Each sample is
           fed exactly once and EncodedVideoChunk COPIES the bytes; the SW
           rescue re-demuxes from the file, so releasing here cannot break it.
           Sample 0 is kept for the chunk0_hex decoder diagnostic. */
        if (i > 0) s.data = null;
        await _yieldTick(0);
        while (await drainOne()) {}
        if (decodeError) {
            var _rkI = await _rebuildDecoderI('feed');
            if (_rkI < 0) { var _deI2 = new Error('decode: ' + decodeError.message); _deI2.decHint = decoderHwTag; throw _deI2; }
            i = _rkI - 1;
            continue;
        }
        if (_pendingEncRecovery) {
            _pendingEncRecovery = false;
            var okI2 = await _rebuildEncoderAfterReclaim();
            if (!okI2) {
                try { decoder.close(); } catch(_){}
                { var _efI3 = new Error('encoder-failed: ' + (encoderError && (encoderError.message || encoderError) || 'reclaim-recovery-failed')); _efI3.encHint = encoderChosenHintI; throw _efI3; }
            }
        }
    }
    // ── Flush — and TAKE the frames it produces while it runs (2026-09-26) ──
    // This used to race a bare `decoder.flush()` against the stall poller and
    // drain nothing until it settled. Every frame still in flight then piled
    // up unclosed in `decodedFrames`, and a hardware decoder's output pool is
    // finite: with more frames in flight than the pool holds, flush() never
    // settles, the poller turned the wedge into «hw output stalled … during
    // flush», and the rescue restarted the run from zero. `run()` was given
    // this exact shape on 2026-09-25; this entry kept the old one — and the
    // same day's hidden-tab yield (_yieldTick → MessageChannel) let the feed
    // loop run to its back-pressure limit, so a backgrounded run reached the
    // flush with up to 30 frames in flight: 3 of 4 hidden mobile runs of
    // /video-fps failed after that deploy (the reporter lost six minutes of
    // work at 99 %). Prod, 60 d, same signature: desktop short clips and
    // previews stuck at ~8 of 27 frames (the hardware pool), phones at the
    // end with 1–15.
    // The loop wakes on the decoder's OWN events (output, error, the flush
    // settling); the one-second timer only lets the watchdog look at the
    // visible-time clock — a hidden tab does not spin a core while it waits.
    // Flush rejections stay swallowed exactly as before.
    // A decoder that dies while flushing REJECTS the flush (its error callback
    // may land a task later) — that rejection is the death, kept, not swallowed.
    var _flushSettledI = false, _flushErrI = null;
    var _flushGenI = (typeof _flushGenI === 'number' ? _flushGenI : 0) + 1;
    (function (gen) {
        // A flush of a decoder the rebuild already replaced settles late — it
        // must not end the flush of the new one.
        decoder.flush().then(function(){ if (gen !== _flushGenI) return; _flushSettledI = true; _wakeDecWaiterI(); },
                             function(fe){ if (gen !== _flushGenI) return; _flushSettledI = true; _flushErrI = fe || new Error('flush rejected'); _wakeDecWaiterI(); });
    })(_flushGenI);
    for (;;) {
        while (await drainOne()) {}
        if (_flushSettledI) break;
        // Everything fed has come out and been taken: the flush owes nothing,
        // and a flush that still does not return has nothing left to deliver
        // (the old poller exited on this too).
        if (_decOutCountI >= _decFedCountI && !_pendingEncRecovery) break;
        if (aborted) throw new DOMException('cancelled', 'AbortError');
        if (decodeError) break;                   // the rebuild below owns it
        if (_pendingEncRecovery) {
            _pendingEncRecovery = false;
            if (!await _rebuildEncoderAfterReclaim()) {
                var _efF = new Error('encoder-failed: ' + (encoderError && (encoderError.message || encoderError) || 'reclaim-recovery-failed'));
                _efF.encHint = encoderChosenHintI;
                throw _efF;
            }
        }
        _throwIfDecoderStalled('flush');
        if (!decodedFrames.length && !_flushSettledI) await _waitDecoderEventI(1000);
    }
    // Take everything the flush delivered — through an encoder rebuild if one is
    // pending (drainOne stops while it is).
    for (var _fdgI = 0; _fdgI < 200000; _fdgI++) {
        while (await drainOne()) {}
        if (!decodedFrames.length || decodeError || aborted || _gpuLostI) break;
        if (_pendingEncRecovery) {
            _pendingEncRecovery = false;
            if (!await _rebuildEncoderAfterReclaim()) {
                var _efF2 = new Error('encoder-failed: ' + (encoderError && (encoderError.message || encoderError) || 'reclaim-recovery-failed'));
                _efF2.encHint = encoderChosenHintI;
                throw _efF2;
            }
            continue;
        }
        await _yieldTick(0);
    }
    if (aborted) throw new DOMException('cancelled', 'AbortError');
    if (!decodeError && _flushErrI && _flushErrI.name !== 'AbortError') decodeError = _flushErrI;
    if (decodeError) {
        var _rkFI = await _rebuildDecoderI('flush');
        if (_rkFI < 0) { var _deF = new Error('decode: ' + decodeError.message); _deF.decHint = decoderHwTag; throw _deF; }
        _feedResumeI = _rkFI;
        continue;
    }
    break;
    }   // re-entry loop
    try { decoder.close(); } catch(_) {}
    try { await encoder.flush(); } catch(_) {}
    while (encoderError && _encRecoverableI() && _encRebuildsI < ENC_REBUILD_MAX_I) {
        if (!(await _rebuildEncoderAfterReclaim())) break;
        try { await encoder.flush(); } catch(_) {}
    }
    try { encoder.close(); } catch(_) {}
    // Everything still held after flush() was never going to be confirmed.
    _releaseAllSentI();

    // Same root-cause surfacing as the primary pipeline — see comment there.
    if (encoderError) { var _efI4 = new Error('encoder-failed: ' + (encoderError.message || encoderError)); _efI4.encHint = encoderChosenHintI; throw _efI4; }
    if (muxerError) throw new Error('muxer-failed: ' + (muxerError.message || muxerError));
    if (!firstChunkSeen) {
        throw new Error('encoder-no-output: produced 0 video chunks (source may be invalid or decoder failed silently)');
    }
    // Gap check after a rebuild — see the long comment on the run() copy. This
    // path is REACHED in the field: 17 successful rebuilds on /video-fps in
    // 30 days (11 sessions), every one of them unverified until now. Kept
    // deliberately stricter than run()'s: /video-fps has no disclosure UI, so
    // any shortfall refuses the artifact and falls back to the exact software
    // lane rather than handing over a silently gappy interpolation.
    // ⚠️ `outFrameCount`, not `frameCount`: that name belongs to _runPipeline, a
    // sibling function. Copied verbatim, this guard threw ReferenceError instead
    // of running — so after ANY encoder rebuild the interpolation lane failed
    // with "frameCount is not defined" whether or not frames were lost, and the
    // telemetry meant to measure those 17 field rebuilds never fired once.
    if ((_encRecoveryDone || _decRebuildsI > 0) && _muxChunkIdx < outFrameCount) {
        _aeWebcodecsErr(aeSlug, 'encoder_recovery_gap', new Error('gap after rebuild'), {
            stage: 'interp', sent: outFrameCount, emitted: _muxChunkIdx,
            lost: outFrameCount - _muxChunkIdx, chunk_idx: _muxChunkIdx,
            retain_peak: _retainPeakI, retain_dropped: _retainDroppedI,
            dec_rebuilds: _decRebuildsI, enc_rebuilds: _encRebuildsI
        });
        throw new Error('encoder-failed: recovered stream is missing ' + (outFrameCount - _muxChunkIdx) + ' frame(s)');
    }

    // Audio passthrough (skipped entirely in slow-mo mode — original
    // audio timing would desync once video stretches; honest UI tells the
    // user audio is dropped).
    var audioMuxed = 0;
    if (demuxInfo.audio && !dropAudio && _audioNeedsTranscode) {
        // Not AAC-LC → decode → AAC re-encode (duration unchanged: speed 1,
        // no gain/fades). A failure throws, exactly like the edit path in
        // _runPipeline, so the caller's FFmpeg fallback produces the file
        // rather than us shipping a silent or undecodable audio track.
        audioMuxed = await muxSpeedAudio(file, muxer, 1, demuxInfo, function(){ return aborted; }, {
            leadSec: _audioLeadSecI,
            outRate: _audioOutI.sampleRate, outChannels: _audioOutI.numberOfChannels
        });
    } else if (demuxInfo.audio && demuxInfo.audio.samples && demuxInfo.audio.samples.length && !dropAudio) {
        // Copied as bytes, no codec API — see muxAudioPassthrough (Safari < 26).
        audioMuxed = (await muxAudioPassthrough(demuxInfo, muxer, {
            lead: _leadPlanI,
            onProgress: function (f) { onProgress(0.95 + 0.04 * f); },   // the tail is work (see run())
            slug: aeSlug, stage: 'interp'
        })).muxed;
    }

    muxer.finalize();

    var encodeSec = (performance.now() - t0) / 1000;
    onProgress(1.0);

    var newName = (file.name || 'video.mp4').replace(/(\.[^.]+)?$/, '_' + Math.round(actualOutputFps) + 'fps.mp4');
    muxTargetI.needBytes = _reserveBytesI;
    /* Интерполяция строит НОВУЮ шкалу времени, и файл играется ровно по своим
       меткам — в обоих режимах (плавность хранит длительность, замедление его
       уже растянуло). Это и говорится плеерам Apple: без ключа «Фото» на iPhone
       играют 240 к/с как slow-mo, в 8 раз медленнее (отзыв 31.10.2026). Значение
       источника тут не годится: slow-mo с iPhone (0), сделанный «плавнее»,
       иначе замедлился бы второй раз. */
    var _outI = await collectMuxOutput(muxTargetI, muxer, newName, demuxInfo.container === 'matroska' ? null : file, { playbackIntent: 1 });
    return {
        blob: _outI.blob,
        file: _outI.file,
        opfsName: _outI.opfsName,
        savedToPicked: !!_outI.savedToPicked, pickedName: _outI.pickedName || '',
        engine: engineLabel,
        srcFps: srcFps,
        outputFps: actualOutputFps,
        ratio: ratio,
        framesIn: totalSrcSamples,
        framesOut: outFrameCount,
        encode_sec: encodeSec,
        ms_per_frame: outFrameCount > 0 ? (performance.now() - t0) / outFrameCount : 0,
        decoder: decoderHwTag,
        encoder: encoderHwTag,
        audioMuxed: audioMuxed,
        audioExpected: demuxInfo.audio && demuxInfo.audio.samples ? demuxInfo.audio.samples.length : 0,
        // How the in-betweens were made: 'motion' | 'blend' | 'callback' (the AI lane)
        interp: interpolator ? 'callback' : (_motionI ? 'motion' : 'blend'),
        interpWhyNot: _motionWhyNotI,
        motionSampled: _motionSampledI,
        motionUnreadable: _motionUnreadableI,
        motionAgree: _motionSampledI ? Math.round(100 * _motionAgreeI / _motionSampledI) / 100 : null
    };
}

// ─────────────────────────────────────────────────────────────────────
// LIGHTWEIGHT CODEC PROBE
// ─────────────────────────────────────────────────────────────────────
// Feeds mp4box ONLY until onReady (the moov is parsed) and reads the video
// track's codec / dimensions / fps / duration — then stops. No sample
// extraction, no decode. mp4box is ~a few hundred KB; this lets a tool
// learn the codec (to decide HW routing) WITHOUT paying for a 32 MB
// FFmpeg-WASM load. Bug #459: enhance-video blocked its whole UI on an
// FFmpeg probe just to read a codec string — 652 sessions / 2 d waited
// >60 s for WASM init, one waited 122 s on a 5-second clip and gave up.
// mp4/mov/m4v only; resolves null for other containers (the caller falls
// back to FFmpeg there — and a non-mp4 file needs FFmpeg to export anyway).
/* КАКАЯ звуковая дорожка этого файла идёт в выход.
   До 17.09.2026 здесь стояло `tracks.find(type==='audio')` в шести местах —
   то есть «первая в moov», молча и без вариантов. У FFmpeg-лейна тех же
   тулов ответ был ДРУГОЙ (дорожка с флагом `(default)`), поэтому один и тот
   же файл на одной и той же кнопке давал разный ЯЗЫК в зависимости от того,
   какой движок достался машине (замер 17.09: rus/rus/eng с `default` на
   английской — FFmpeg отдавал eng, этот лейн rus). Индекс приходит снаружи,
   от общего владельца вопроса `window.__audioTracks`; без него поведение
   прежнее — первая дорожка. Порядок `filter` совпадает с порядком дорожек в
   moov, а `-map 0:a:N` у FFmpeg считает ровно так же, поэтому один и тот же
   номер означает в обоих лейнах одну и ту же дорожку. */
function audioTracksOf(tracks) {
    return (tracks || []).filter(function (t) { return t.type === 'audio' || _isPcmTrack(t); });
}
/* Несжатый звук камер (QuickTime `sowt`/`twos`/`in24`/…, ISO `ipcm`, G.711)
   mp4box типирует как 'metadata', но это звук — и `-map 0:a:N` у FFmpeg считает
   его звуком. Читает его дверь ядра `__mp4Pcm` (timbrica-core.js); нет двери —
   прежний счёт и прежний отказ. */
function _isPcmTrack(t) {
    var M = window.__mp4Pcm;
    return !!(t && t.type !== 'video' && t.type !== 'audio' && M && M.FOURCC && M.FOURCC.test(String(t.codec || '')));
}
/* Звук, который этот лейн несёт сам, — по имени кодека из пробы FFmpeg (паспорт
   файла у тулов говорит языком FFmpeg): AAC — копией, MP3/Opus/Vorbis/FLAC —
   реле muxAudioLazy, несжатый звук камер и G.711 в mp4/mov — дверью __mp4Pcm.
   Тул со своим гейтом быстрого пути спрашивает здесь, а не держит копию списка:
   /video-crop и /video-flip до 22.10.2026 слали каждый .mov камеры в x264. */
function carriesAudioCodec(name, file) {
    var c = String(name || '').toLowerCase();
    if (!c || c === 'aac' || /^mp4a/.test(c)) return true;
    if (/^(mp3|opus|vorbis|flac)$/.test(c)) return true;
    var mp4 = !file || /\.(mp4|m4v|mov)$/i.test(file.name || '') || /^video\/(mp4|quicktime)/i.test(file.type || '');
    return !!(window.__mp4Pcm && mp4 && /^pcm_(s16le|s16be|s24le|s24be|s32le|s32be|f32le|f32be|f64le|f64be|u8|s8|alaw|mulaw)$/.test(c));
}
function pickAudioTrack(tracks, idx) {
    var as = audioTracksOf(tracks);
    if (!as.length) return undefined;
    var i = (typeof idx === 'number' && idx >= 0 && idx < as.length) ? idx : 0;
    return as[i];
}

/* ЗВУК, КОТОРОГО ЭТОТ ЛЕЙН НЕ ВИДИТ (29.09.2026).
   mp4box 0.5.4 типирует как 'audio' только знакомые ему записи (mp4a, Opus,
   ac-3, ec-3, fLaC, mha1, mhm1, enca). Звуковая дорожка (обработчик 'soun') с
   любой другой записью — G.711 `alaw`/`ulaw` с IP-камер и регистраторов,
   QuickTime PCM `sowt`/`twos`/`lpcm`/`in24` с камер, `samr` из 3GP, `alac` —
   приходит как type 'metadata', `pickAudioTrack` её не находит, и run() честно
   собирал файл БЕЗ ЗВУКА: ни ошибки, ни события. Замер 29.09 в Chrome 154 на
   H.264 + pcm_alaw (форма выгрузки с камеры — обращение пользователя 10000977,
   iPhone): вход h264+pcm_alaw → выход только h264. /video-crop держал такие
   файлы на FFmpeg СВОИМ гейтом (`audio_pcm_*`, 17.09), остальные тулы лейна —
   нет (/video-speed, /enhance-video, /video-flip, /video-loop, /video-watermark,
   /video-compressor, /video-blur …).
   Ответ — тот же, что у звука, который не декодируется (`gateAudioDecodable`):
   типизированный отказ `audio-codec-unsupported-<тип>` ДО работы, и тул идёт
   своим FFmpeg-лейном, который этот звук читает. Там, где звук не нужен
   (замедление без звука, `dropAudio`), отказа нет. Событие `audio_track_unread`
   считает, сколько таких файлов приходит. */
function _unreadSoundTrack(mp4file, info) {
    try {
        var tracks = (info && info.tracks) || [];
        for (var i = 0; i < tracks.length; i++) {
            var t = tracks[i];
            if (!t || t.type === 'audio' || t.type === 'video') continue;
            var trak = mp4file && mp4file.getTrackById ? mp4file.getTrackById(t.id) : null;
            var h = trak && trak.mdia && trak.mdia.hdlr && trak.mdia.hdlr.handler;
            if (h === 'soun') return String(t.codec || 'soun').replace(/[^a-zA-Z0-9._ -]/g, '').trim().slice(0, 16) || 'soun';
        }
    } catch (_) {}
    return '';
}
function _refuseUnreadSound(demuxInfo, slug, soundNeeded) {
    if (!demuxInfo || demuxInfo.audio || !demuxInfo.soundUnread || soundNeeded === false) return;
    try {
        if (typeof window.__ae === 'function') window.__ae('feature_use', {
            slug: slug || 'webcodecs', action: 'audio_track_unread', codec: String(demuxInfo.soundUnread).slice(0, 16)
        });
    } catch (_) {}
    throw new Error('audio-codec-unsupported-' + demuxInfo.soundUnread);
}

async function probeCodec(file) {
    if (!file || !/\.(mp4|mov|m4v)$/i.test(file.name || '')) return null;
    try { await ensureMp4Box(); } catch (_) { return null; }
    if (!window.MP4Box) return null;
    return await new Promise(function(resolve){
        var inFile = MP4Box.createFile();
        var done = false;
        function finish(v){ if (done) return; done = true; resolve(v); }
        inFile.onError = function(){ finish(null); };
        // mp4box gives an RFC-6381 codec string (avc1.640028, hev1.1.6.…,
        // vp09.…, av01.…). Normalise to the short family name callers expect
        // (matches FFmpeg-probe output; nicer to display than avc1.F4000D).
        function shortCodec(c){
            c = (c || '').toLowerCase();
            if (c.indexOf('avc') === 0)  return 'h264';
            if (c.indexOf('hev') === 0 || c.indexOf('hvc') === 0) return 'hevc';
            if (c.indexOf('dvh') === 0 || c.indexOf('dva') === 0) return 'dolbyvision';
            if (c.indexOf('vp09') === 0 || c.indexOf('vp9') === 0) return 'vp9';
            if (c.indexOf('vp08') === 0 || c.indexOf('vp8') === 0) return 'vp8';
            if (c.indexOf('av01') === 0 || c.indexOf('av1') === 0) return 'av1';
            return c;
        }
        // Read the `colr` box off the video sample entry. Do NOT trust
        // getInfo()'s track object — it never carries colour at all, which is
        // why the old sniff for `colour_info` / `color_info` / `colr` there
        // always came up empty and this function silently degraded to a pure
        // codec-string guess (verified 2026-07-10: `colour === null` on every
        // fixture, including a real iPhone HLG .mov).
        //
        // mp4box parses the ISO flavour (`nclx`) into named fields but leaves
        // QuickTime's `nclc` — the one Apple actually writes — with only
        // `colour_type` set. Both carry the same three uint16 after the type,
        // so fall back to the raw payload bytes.
        function readColr(moov, trackId) {
            try {
                var traks = (moov && moov.traks) || [];
                for (var i = 0; i < traks.length; i++) {
                    var t = traks[i];
                    if (trackId && t.tkhd && t.tkhd.track_id !== trackId) continue;
                    var entries = t.mdia && t.mdia.minf && t.mdia.minf.stbl &&
                                  t.mdia.minf.stbl.stsd && t.mdia.minf.stbl.stsd.entries;
                    if (!entries || !entries.length) continue;
                    for (var e = 0; e < entries.length; e++) {
                        var entry = entries[e];
                        if (!entry || !/^(avc|hvc|hev|vp0|av0|dvh|dva)/i.test(entry.type || '')) continue;
                        var colr = entry.colr ||
                                   (entry.boxes || []).filter(function(b){ return b.type === 'colr'; })[0];
                        if (!colr) return null;
                        if (typeof colr.transfer_characteristics === 'number') {
                            return { primaries: colr.colour_primaries,
                                     transfer: colr.transfer_characteristics,
                                     matrix: colr.matrix_coefficients };
                        }
                        // nclc — parse the payload ourselves: 'nclc' then
                        // primaries / transfer / matrix as big-endian uint16.
                        var d = colr.data;
                        if (!d || d.length < 10) return null;
                        var u8 = (d instanceof Uint8Array) ? d : new Uint8Array(d.buffer || d, d.byteOffset || 0);
                        var be16 = function(o){ return (u8[o] << 8) | u8[o + 1]; };
                        return { primaries: be16(4), transfer: be16(6), matrix: be16(8) };
                    }
                }
            } catch (_) {}
            return null;
        }

        // HDR verdict, preferring measured colour over the codec-string guess.
        // Returns { isHdr, hdrFormat: 'hdr10'|'hlg'|'dolbyvision'|'main10'|null }.
        function detectHdr(codecFull, vTrack) {
            var c = (codecFull || '').toLowerCase();
            // Dolby Vision — codec family always implies HDR.
            if (/^(dvh1|dvhe|dva1|dvav)\./.test(c)) {
                return { isHdr: true, hdrFormat: 'dolbyvision' };
            }
            var colour = readColr(inFile.moov, vTrack && vTrack.id);
            // transfer_characteristics: 16 = SMPTE ST 2084 (PQ/HDR10),
            // 18 = ARIB STD-B67 (HLG). 1/6/13/14/15 = SDR curves.
            if (colour && typeof colour.transfer === 'number') {
                if (colour.transfer === 16) return { isHdr: true, hdrFormat: 'hdr10' };
                if (colour.transfer === 18) return { isHdr: true, hdrFormat: 'hlg' };
                // An explicit SDR transfer OUTRANKS the 10-bit heuristic below:
                // 10-bit BT.709 SDR exists (ProRes proxies, some Android
                // camcorders) and tone-mapping it would wash the picture.
                // 14/15 are the BT.2020 10/12-bit curves — wide gamut, but
                // still SDR: leave them alone rather than run a Hable curve
                // over content that never had highlights to compress.
                if ([1, 6, 13, 14, 15].indexOf(colour.transfer) !== -1) return { isHdr: false, hdrFormat: null };
                // bt2020 primaries with no recognised transfer — assume HDR.
                if (colour.primaries === 9) return { isHdr: true, hdrFormat: 'hdr10' };
            }
            // HEVC Main10 (hvc1.2 / hev1.2) — high probability HDR when
            // colour box is missing. False-positive risk: 10-bit BT.709
            // SDR HEVC exists but is rare in consumer files.
            if (/^(hvc1|hev1|hvc2|hev2)\.2\./.test(c)) {
                return { isHdr: true, hdrFormat: 'main10' };
            }
            // VP9 profile 2 / 3 = 10-bit / 12-bit. Same caveat.
            if (/^vp0?9\.0?[23]\./.test(c)) {
                return { isHdr: true, hdrFormat: 'main10' };
            }
            // AV1 Main 10/12-bit. RFC 6381 av1: av01.<profile>.<level><tier>.<bit-depth>.…
            if (/^av01\.0\.\d+[mh]\.(10|12)\./.test(c)) {
                return { isHdr: true, hdrFormat: 'main10' };
            }
            return { isHdr: false, hdrFormat: null };
        }
        inFile.onReady = function(info){
            try {
                var v = info && info.tracks && info.tracks.find(function(t){ return t.type === 'video'; });
                if (!v) return finish(null);
                /* Здесь сознательно ПЕРВАЯ дорожка: probeCodec отвечает на
                   вопрос «что за файл», и зовут его ДО того, как человек
                   выбрал дорожку. Номер приходит только в demux (ниже), где
                   он уже известен. Свой `audioTrackIndex` сюда не заводим —
                   неиспользуемая ручка хуже честного умолчания. */
                var a = pickAudioTrack(info.tracks);
                var ts = v.timescale || 1;
                var hdr = detectHdr(v.codec, v);
                finish({
                    videoCodec: shortCodec(v.codec),
                    videoCodecFull: v.codec || '',
                    audioCodec: a ? (a.codec || '') : '',
                    width:  (v.video && v.video.width)  || v.track_width  || 0,
                    height: (v.video && v.video.height) || v.track_height || 0,
                    duration: v.duration ? v.duration / ts
                              : (info.duration && info.timescale ? info.duration / info.timescale : 0),
                    fps: (v.nb_samples && v.duration) ? Math.round(v.nb_samples / (v.duration / ts)) : 0,
                    // The UNROUNDED rate. `fps` above is rounded because most callers
                    // want an integer (encoder hints, chunk-count estimates, target-fps
                    // pickers) — but rounding 30000/1001 to 30 turns a frame duration
                    // into a lie, and NTSC is a third of real-world footage. Anything
                    // computing a DURATION must use this. See fpsExact in run().
                    fpsExact: (v.nb_samples && v.duration) ? (v.nb_samples / (v.duration / ts)) : 0,
                    isHdr: hdr.isHdr,
                    hdrFormat: hdr.hdrFormat,
                    // Raw track list. `audioCodec` above is whatever mp4box
                    // typed as 'audio' — but QuickTime PCM (twos/sowt/lpcm)
                    // lands under type 'metadata', so a .mov with PCM audio
                    // reads as silent here. CodecMuxRules.audioCodecFromMp4Box
                    // knows that and wants the unfiltered list.
                    tracks: info.tracks || []
                });
            } catch (e) { finish(null); }
        };
        (async function(){
            try {
                var size = file.size, CHUNK = 4 * 1024 * 1024, off = 0, guard = 0;
                // Discovery feed — follow mp4box's nextFileStart hint, which
                // jumps straight to a moov-at-end (same approach as demuxMp4).
                while (!done && off < size && guard++ < 64) {
                    var end = Math.min(off + CHUNK, size);
                    var buf = await _readSliceWithRetry(file, off, end);
                    buf.fileStart = off;
                    var next = inFile.appendBuffer(buf);
                    if (done) return;
                    off = (typeof next === 'number' && next > off && next < size) ? next : end;
                }
                try { inFile.flush(); } catch (_) {}
            } catch (e) { /* fall through */ }
            finish(null);   // onReady never fired — not a parseable mp4
        })();
    });
}

// ─────────────────────────────────────────────────────────────────────
// avcC SANITISER (bug #458)
// ─────────────────────────────────────────────────────────────────────
// Firefox's WebCodecs H.264 encoder emits a `decoderConfig.description`
// (avcC) where EVERY SPS/PPS NAL has its 1-byte NAL header DUPLICATED:
// `67 67 42 c0 14…` instead of `67 42 c0 14…`, `68 68 ca…` instead of
// `68 ca…`. The in-band SPS inside the chunk data is fine — only the
// avcC is wrong. Muxed verbatim, the output mp4's avcC box is malformed:
// a player that trusts the avcC reads NAL-header `67`, then a bogus
// profile_idc `0x67` (=103), garbage-parses the rest → "sps_id out of
// range", invalid crop, wrong dimensions → corrupt decode over part of
// the frame. Bug #458 ("налепил водный знак на полэкрана" — Firefox 150).
// Chromium's avcC is clean. This detects the duplication (unambiguously,
// via SPS[0]: a valid SPS's RBSP byte 0 IS the avcC's profile_idc) and
// strips the stray byte. Returns the input untouched when it is clean.
function sanitizeAvcC(desc) {
    try {
        var b = desc instanceof Uint8Array ? desc : new Uint8Array(desc.buffer || desc);
        if (b.length < 7 || b[0] !== 1) return desc;        // not an avcC
        var avcProfile = b[1];
        var sps = [], pps = [], p = 6, i;
        var numSps = b[5] & 0x1F;
        for (i = 0; i < numSps && p + 2 <= b.length; i++) {
            var len = (b[p] << 8) | b[p + 1]; p += 2;
            if (p + len > b.length) return desc;             // truncated → bail
            sps.push(b.subarray(p, p + len)); p += len;
        }
        if (p >= b.length || !sps.length) return desc;
        var numPps = b[p++];
        for (i = 0; i < numPps && p + 2 <= b.length; i++) {
            var plen = (b[p] << 8) | b[p + 1]; p += 2;
            if (p + plen > b.length) return desc;
            pps.push(b.subarray(p, p + plen)); p += plen;
        }
        // Detect the Firefox duplicate-header bug via SPS[0]: a valid SPS
        // NAL is [0x67][profile_idc][…] and profile_idc === avcProfile.
        // Firefox's is [0x67][0x67][profile_idc][…] — byte 1 is a stray
        // NAL-header copy.
        var s0 = sps[0];
        var bug = s0.length >= 3 && (s0[0] & 0x1F) === 7 &&
                  s0[1] !== avcProfile && s0[2] === avcProfile;
        if (!bug) return desc;                               // clean — untouched
        function strip(nal) {                                // drop a duplicate header
            return (nal.length >= 2 && nal[1] === nal[0]) ? nal.subarray(1) : nal;
        }
        sps = sps.map(strip); pps = pps.map(strip);
        var size = 7, o = 0;
        sps.forEach(function(n){ size += 2 + n.length; });
        pps.forEach(function(n){ size += 2 + n.length; });
        var out = new Uint8Array(size);
        out[o++] = 1; out[o++] = b[1]; out[o++] = b[2]; out[o++] = b[3];
        out[o++] = b[4]; out[o++] = 0xE0 | (sps.length & 0x1F);
        sps.forEach(function(n){
            out[o++] = (n.length >> 8) & 0xFF; out[o++] = n.length & 0xFF;
            out.set(n, o); o += n.length;
        });
        out[o++] = pps.length & 0xFF;
        pps.forEach(function(n){
            out[o++] = (n.length >> 8) & 0xFF; out[o++] = n.length & 0xFF;
            out.set(n, o); o += n.length;
        });
        return out.buffer;
    } catch (e) { return desc; }
}

// Returns `meta` with a sanitised avcC description, or the same object if
// nothing needed fixing. Cheap no-op for the chunks that carry no config.
function sanitizeChunkMeta(meta) {
    if (meta && meta.decoderConfig && meta.decoderConfig.description) {
        var clean = sanitizeAvcC(meta.decoderConfig.description);
        if (clean !== meta.decoderConfig.description) {
            return Object.assign({}, meta, {
                decoderConfig: Object.assign({}, meta.decoderConfig, { description: clean })
            });
        }
    }
    return meta;
}

// ─────────────────────────────────────────────────────────────────────
// MP4Box DEMUX — feeds samples + avcC + audio info to the pipeline
// ─────────────────────────────────────────────────────────────────────

/* `onAlive` — LIVENESS, not progress. Demux reads the whole container before
   the pipeline can emit its first real tick, and every consumer guards the run
   with a "no progress for N seconds" watchdog. During that silent phase the
   watchdog has nothing to distinguish "reading a big file" from "wedged", so
   video-flip had to guess a size-scaled grace period — and the guess is wrong
   for the case that matters most: the SOFTWARE-DECODE RESCUE re-enters the
   pipeline from scratch, so its silent demux happens when the caller has
   already seen progress and switched to the tight 30 s gap. MEASURED over
   30 d: 538 rescue attempts, 82 successes (15 %), and 78 attempts provably
   killed by a caller's `hw-stalled` — our own recovery path, killed by our own
   watchdog, on exactly the broken-hardware cohort it exists for (user 4126,
   06.08: decoder error → rescue → hw-stalled → 29 min on FFmpeg).
   Liveness belongs HERE because only the pipeline knows it is alive. Callers
   already reset their timers inside `onProgress`, so a throttled `onProgress(0)`
   fixes all six consumers with no change on their side, and a real wedge is
   still caught: the ticks come from the feed loop itself, so if the loop stops
   the watchdog fires as before. */
// opts.headOnly — stop feeding once the first video KEYFRAME has been
// extracted. Only for probes that ask a question the head of the stream
// answers (decoder capability); the returned demuxInfo is then PARTIAL and
// must never be handed to run().
// ─────────────────────────────────────────────────────────────────────
// LAZY DEMUX — the sample TABLE now, the sample BYTES on demand (2026-09-17)
// ─────────────────────────────────────────────────────────────────────
// The eager demux below feeds the WHOLE file through mp4box and keeps a copy
// of every sample until the feed loop hands it to the decoder — so a long
// export parks its entire compressed stream on the JS heap (measured on a
// 2 h / 776 MB export: steady 1.66 GB, 3.3 GB peak), and the input size is
// the memory term that set every consumer's upload ceiling (the generic
// 'video' kind, 1 GiB — the refusal Blagodat wrote in about on 13.09 and
// 16.09.2026 with a 1.6 GB mp4 on /video-crop, «максимум 1024 МБ»).
//
// The bytes never had to be there. A File is disk-backed and the moov's
// sample table (stco/co64 + stsz) carries ABSOLUTE file offsets, so the
// decoder can be fed by slice-read: ftyp+moov are read (KB, ms), the table
// is built from mp4box's own parsed samples (getTrackSamplesInfo — the same
// objects onSamples would have handed over, minus `.data`), and the feed
// loops read 8 MB windows through _lazySampleReader as they go. Retention is
// O(window) instead of O(file). The same design already serves /video-merge,
// /slideshow-maker and /video-insert (video-merge-webcodecs.js demuxLazy,
// pinned byte-for-byte by tests/playwright/webcodecs-lazy-demux.mjs).
//
// When the lazy path cannot serve — no top-level moov (broken box sizes), a
// FRAGMENTED mp4 (moof/traf: the samples live outside the moov and the
// table in the moov is empty), or a table without finite offsets — it
// DECLINES and the eager path runs exactly as before. The two paths share
// onReady (decoder descriptions, audio ASC), the edit-list finalize and the
// resolve shape; only where the bytes come from differs.
//
// ⚠️ mp4box's stream must be contiguous from 0, and on any file not written
// with faststart (cameras, phones, ffmpeg's default) the moov sits BEHIND
// the mdat. ftyp and moov are therefore appended BACK TO BACK as a synthetic
// stream at fileStart 0 — chunk offsets in stco/co64 are absolute and stay
// correct wherever the moov is presented. Appending them at their true
// fileStarts leaves an mdat-sized hole and onReady never fires.
var LAZY_MOOV_MAX_BYTES = 256 * 1024 * 1024;     // a 4 h phone clip's moov is ~30 MB; anything past this is not a moov we trust
var LAZY_SPAN_BYTES = 8 * 1024 * 1024;            // one read serves a run of consecutive samples; bounded so interleaved audio cannot pull the mdat back in

// Top-level box headers only (8/16 bytes each) — a few reads whatever the
// file size. → { ftyp, moov, fragmented } or { why }.
async function _mp4TopLevel(file) {
    var boxes = [], off = 0, guard = 0, size = file.size;
    while (off < size && guard++ < 4096) {
        var hdr = await _readSliceWithRetry(file, off, Math.min(size, off + 16));
        var h = new DataView(hdr);
        if (h.byteLength < 8) break;
        var bsz = h.getUint32(0), hlen = 8;
        var type = String.fromCharCode(h.getUint8(4), h.getUint8(5), h.getUint8(6), h.getUint8(7));
        if (bsz === 1) {
            if (h.byteLength < 16) break;
            bsz = Number(h.getBigUint64(8)); hlen = 16;
        } else if (bsz === 0) {
            bsz = size - off;                       // "to end of file"
        }
        if (!(bsz >= hlen) || off + bsz > size) return { why: 'box-walk:' + type + '@' + off };
        boxes.push({ type: type, start: off, size: bsz });
        off += bsz;
    }
    var out = { ftyp: null, moov: null, fragmented: false };
    for (var i = 0; i < boxes.length; i++) {
        var b = boxes[i];
        if (b.type === 'ftyp' && !out.ftyp) out.ftyp = b;
        else if (b.type === 'moov' && !out.moov) out.moov = b;
        else if (b.type === 'moof') out.fragmented = true;
    }
    if (!out.moov) return { why: 'no-top-level-moov' };
    if (out.fragmented) return { why: 'fragmented' };
    if (out.moov.size > LAZY_MOOV_MAX_BYTES) return { why: 'moov-too-big' };
    return out;
}

// ftyp+moov as ONE buffer, fileStart 0 (see the contiguity note above).
async function _mp4HeadBuffer(file, top) {
    var wanted = top.ftyp ? [top.ftyp, top.moov] : [top.moov];
    var parts = [], total = 0;
    for (var k = 0; k < wanted.length; k++) {
        var u = new Uint8Array(await _readSliceWithRetry(file, wanted[k].start, wanted[k].start + wanted[k].size));
        parts.push(u); total += u.length;
    }
    var head = new Uint8Array(total), at = 0;
    for (var m = 0; m < parts.length; m++) { head.set(parts[m], at); at += parts[m].length; }
    return head.buffer;
}

// The sample tables from the parsed moov — the same sample objects mp4box's
// extractor hands to onSamples (cts/duration/is_sync come from the same
// stbl walk), carrying `offset`+`size` instead of `data`. Null when the
// video table is missing or any entry lacks a finite location.
function _lazySampleTables(inFile, videoTrackId, audioTrackId, vTscale, aTscale) {
    var vs = null, as = null;
    try { vs = inFile.getTrackSamplesInfo(videoTrackId); } catch (_) { vs = null; }
    if (!vs || !vs.length) return null;
    var video = new Array(vs.length);
    for (var i = 0; i < vs.length; i++) {
        var x = vs[i];
        if (!(x.size > 0) || !isFinite(x.offset) || x.offset < 0) return null;
        video[i] = { data: null, offset: x.offset, size: x.size,
                     timestamp: Math.round(x.cts * 1_000_000 / vTscale),
                     duration: Math.round(x.duration * 1_000_000 / vTscale),
                     is_sync: !!x.is_sync };
    }
    var audio = [];
    if (audioTrackId) {
        try { as = inFile.getTrackSamplesInfo(audioTrackId); } catch (_) { as = null; }
        if (as && as.length) {
            audio = new Array(as.length);
            for (var j = 0; j < as.length; j++) {
                var y = as[j];
                if (!(y.size > 0) || !isFinite(y.offset) || y.offset < 0) return null;
                audio[j] = { data: null, offset: y.offset, size: y.size,
                             timestamp: Math.round(y.cts * 1_000_000 / aTscale),
                             duration: Math.round(y.duration * 1_000_000 / aTscale),
                             is_sync: !!y.is_sync };
            }
        }
    }
    return { video: video, audio: audio };
}

// ─────────────────────────────────────────────────────────────────────
// MATROSKA / WEBM DEMUX — the same lazy contract as demuxMp4 (2026-09-25)
// ─────────────────────────────────────────────────────────────────────
// Every pipeline tool gated its fast lane on «mp4/mov/m4v», so a WebM or MKV
// input — Chrome/Yandex MediaRecorder takes, our own /screen-recorder on
// browsers without an H.264 encoder, NVR/CCTV web exports, OBS — went to
// single-thread x264 in WASM. Bug report 2026-09-21 (/video-speed, Edge,
// 4 GB): a 16-min 1080p VP9 CCTV export «processing for hours, no
// percentage», abandoned after 48 minutes at an unknown fraction.
//
// Matroska stores each frame whole inside its Block, at a fixed file offset,
// so the table the lazy mp4 demux builds ({offset, size, timestamp, duration,
// is_sync}) is exactly what the container already says. The EBML walk lives
// in ONE place — webm-copy-trim.js (trackTables), the trim door's parser —
// and this function only translates its answer into WebCodecs terms: codec
// strings, decoder descriptions, the audio track the gate and the relays read.
// The consuming loops (_lazySampleReader, muxAudioLazy, the video feed) are
// the mp4 ones, unchanged.
function isMatroskaFile(file) {
    var n = String((file && file.name) || '').toLowerCase(), t = String((file && file.type) || '').toLowerCase();
    return /\.(webm|mkv)$/.test(n) || /^video\/(webm|x-matroska|matroska)/.test(t);
}
function ensureWebmReader() {
    if (window.WebmCopyTrim && window.WebmCopyTrim.trackTables) return Promise.resolve();
    if (window.__webmTablesPromise) return window.__webmTablesPromise;
    var u = (typeof window.__jsSrc === 'function') ? window.__jsSrc('/js/webm-copy-trim.js') : '/js/webm-copy-trim.js';
    window.__webmTablesPromise = loadScript(u).catch(function (e) { window.__webmTablesPromise = null; throw e; });
    return window.__webmTablesPromise;
}
function _hex2(n) { return ('0' + (n & 255).toString(16)).slice(-2); }
// VP9 carries no profile in the container: read it from the first keyframe's
// uncompressed header (VP9 bitstream spec §6.2) — profile bits, then the sync
// code and, for profiles 2/3, the bit depth. Guessing profile 0 would hand a
// 10-bit HDR stream a decoder that rejects its first chunk.
function _vp9CodecString(bytes, w, h) {
    var prof = 0, depth = 8;
    try {
        var pos = 0;
        var bit = function () { var b = (bytes[pos >> 3] >> (7 - (pos & 7))) & 1; pos++; return b; };
        if (bytes && bytes.length >= 10 && bit() === 1 && bit() === 0) {
            var lo = bit(), hi = bit(); prof = (hi << 1) | lo;
            if (prof === 3) bit();
            if (!bit()) {                                   // show_existing_frame = 0
                var keyframe = bit() === 0; bit(); bit();   // frame_type, show_frame, error_resilient
                if (keyframe && prof >= 2) {
                    var sync = 0; for (var i = 0; i < 24; i++) sync = (sync << 1) | bit();
                    if (sync === 0x498342) depth = bit() ? 12 : 10;
                }
            }
        }
    } catch (_) {}
    var px = (w || 1920) * (h || 1080);
    var level = px <= 2228224 ? '41' : px <= 8912896 ? '51' : '61';
    return 'vp09.' + ('0' + prof).slice(-2) + '.' + level + '.' + (depth < 10 ? '08' : String(depth));
}
function _av1CodecString(p) {
    if (!p || p.length < 4) return 'av01.0.08M.08';
    var prof = (p[1] >> 5) & 7, lvl = p[1] & 31, tier = (p[2] >> 7) & 1, hbd = (p[2] >> 6) & 1, twelve = (p[2] >> 5) & 1;
    return 'av01.' + prof + '.' + ('0' + lvl).slice(-2) + (tier ? 'H' : 'M') + '.' + (twelve ? '12' : hbd ? '10' : '08');
}
function _hevcCodecString(p) {
    if (!p || p.length < 13) return null;
    var space = (p[1] >> 6) & 3, tier = (p[1] >> 5) & 1, prof = p[1] & 31;
    var c = ((p[2] << 24) | (p[3] << 16) | (p[4] << 8) | p[5]) >>> 0, rev = 0;
    for (var i = 0; i < 32; i++) { rev = (rev * 2) + (c & 1); c = c >>> 1; }
    var cons = []; for (var j = 6; j < 12; j++) cons.push(p[j]);
    while (cons.length && cons[cons.length - 1] === 0) cons.pop();
    return 'hvc1.' + ['', 'A', 'B', 'C'][space] + prof + '.' + (rev >>> 0).toString(16) + '.' + (tier ? 'H' : 'L') + p[12] +
        (cons.length ? '.' + cons.map(function (x) { return x.toString(16); }).join('.') : '');
}
// AAC in Matroska may come without CodecPrivate (A_AAC/MPEG4/LC …): the
// AudioSpecificConfig is then implied by the codec id, the rate and channels.
function _mkvAsc(t) {
    if (t.codecPrivate && t.codecPrivate.length >= 2) return t.codecPrivate;
    var sr = t.sampleRate || 48000, idx = AAC_SAMPLE_RATE_TABLE.indexOf(Math.round(sr));
    if (idx < 0) return null;
    var aot = /SBR/.test(t.codec) ? 5 : 2, ch = t.channels || 2;
    return new Uint8Array([(aot << 3) | (idx >> 1), ((idx & 1) << 7) | (ch << 3)]);
}
function _mkvAudioInfo(a) {
    var t = a.track, id = t.codec || '', ch = t.channels || 2;
    var sr = Math.round(t.outSampleRate || t.sampleRate || 48000);
    var info = { codec: id.toLowerCase(), channels: ch, sampleRate: sr, ascBytes: null, description: null };
    if (id === 'A_OPUS') { info.codec = 'opus'; info.sampleRate = 48000; info.description = t.codecPrivate || null; }
    else if (id === 'A_VORBIS') { info.codec = 'vorbis'; info.description = t.codecPrivate || null; }
    else if (id === 'A_FLAC') { info.codec = 'flac'; info.description = t.codecPrivate || null; }
    else if (id === 'A_MPEG/L3') info.codec = 'mp3';
    else if (/^A_AAC/.test(id)) {
        var asc = _mkvAsc(t);
        var aot = asc ? (asc[0] >> 3) : 2;
        info.codec = aot === 5 ? 'mp4a.40.5' : aot === 29 ? 'mp4a.40.29' : 'mp4a.40.2';
        info.ascBytes = asc;
    }
    info.codecDelayUs = t.codecDelayNs > 0 ? t.codecDelayNs / 1000 : 0;
    return info;
}
async function demuxMatroska(file, onAlive, opts) {
    if (opts && opts.mosh) throw new Error('matroska-mosh-unsupported');   // datamosh edits an EAGER mp4 sample list
    await ensureWebmReader();
    if (!window.WebmCopyTrim || !window.WebmCopyTrim.trackTables) throw new Error('matroska-reader-unavailable');
    var tt;
    try {
        tt = await window.WebmCopyTrim.trackTables(file, { audio: true, audioTrackIndex: opts && opts.audioTrackIndex, onAlive: onAlive });
    } catch (e) {
        throw new Error('matroska-demux: ' + ((e && (e.code || e.message)) || e));
    }
    var v = tt.video, vs = v.samples;
    if (!vs.length || !vs[0].key) throw new Error('matroska-demux: first-not-key');
    var id = v.codecId, priv = v.codecPrivate, codec = null, avcC = null, hvcC = null, av1C = null;
    if (id === 'V_VP9') {
        var k0 = new Uint8Array(await _readSliceWithRetry(file, vs[0].offset, vs[0].offset + Math.min(vs[0].size, 64)));
        codec = _vp9CodecString(k0, v.width, v.height);
    } else if (id === 'V_VP8') codec = 'vp8';
    else if (id === 'V_AV1') { codec = _av1CodecString(priv); av1C = (priv && priv.length) ? priv : null; }
    else if (id === 'V_MPEG4/ISO/AVC' && priv && priv.length >= 4) { codec = 'avc1.' + _hex2(priv[1]) + _hex2(priv[2]) + _hex2(priv[3]); avcC = priv; }
    else if (id === 'V_MPEGH/ISO/HEVC' && priv) { codec = _hevcCodecString(priv); hvcC = priv; }
    if (!codec) throw new Error('matroska-demux: codec ' + String(id || '?').slice(0, 24));

    var samples = new Array(vs.length), t0 = Infinity, tEnd = 0;
    for (var i = 0; i < vs.length; i++) {
        var x = vs[i];
        samples[i] = { data: null, offset: x.offset, size: x.size, timestamp: x.ts, duration: x.dur, is_sync: !!x.key };
        if (x.ts < t0) t0 = x.ts;
        if (x.ts + x.dur > tEnd) tEnd = x.ts + x.dur;
    }
    var spanUs = Math.max(1, tEnd - (isFinite(t0) ? t0 : 0));
    var fpsExact = vs.length / (spanUs / 1e6);
    var audio = null, aWhy = '';
    if (tt.audio) {
        if (tt.audio.why) aWhy = tt.audio.why;
        else {
            var ai = _mkvAudioInfo(tt.audio), as = tt.audio.samples, aEnd = 0, a0 = as.length ? as[0].ts : 0;
            var aSamples = new Array(as.length);
            for (var j = 0; j < as.length; j++) {
                var y = as[j];
                aSamples[j] = { data: null, offset: y.offset, size: y.size, timestamp: y.ts, duration: y.dur, is_sync: true };
                if (y.ts + y.dur > aEnd) aEnd = y.ts + y.dur;
            }
            audio = { codec: ai.codec, channels: ai.channels, sampleRate: ai.sampleRate, ascBytes: ai.ascBytes,
                      description: ai.description, codecDelayUs: ai.codecDelayUs,
                      durationSec: Math.max(0, aEnd - a0) / 1e6, samples: aSamples };
        }
    }
    try { window.__ae && window.__ae('feature_use', {
        slug: (opts && opts.slug) || 'webcodecs', action: 'demux_matroska',
        vcodec: String(id).slice(0, 20), acodec: tt.audio ? String(tt.audio.track.codec || '').slice(0, 20) : '',
        a_why: aWhy, frames: vs.length, aframes: audio ? audio.samples.length : 0,
        readMB: Math.round((tt.readBytes || 0) / 1048576), szMB: Math.round(file.size / 1048576)
    }); } catch (_) {}
    if (aWhy) throw new Error('matroska-demux: audio ' + aWhy);   // never drop a track silently — the caller's fallback keeps it
    return {
        lazy: true, file: file, container: 'matroska',
        _nalStats: avcC ? { sei: 0, ps: 0, samples: 0 } : null,
        width: v.width, height: v.height, rotation: 0,
        fps: Math.round(fpsExact) || 30, fpsExact: fpsExact,
        durationSec: (tt.durationUs > 0 ? tt.durationUs : spanUs) / 1e6,
        // undefined, not null: the decoder config reads `avcC || hvcC || av1C || vpcC`
        // as its description, and WebCodecs rejects a null description outright.
        codec: codec, avcC: avcC || undefined, hvcC: hvcC || undefined, av1C: av1C || undefined, vpcC: undefined,
        samples: samples, totalFrames: samples.length,
        edit: { applied: false },
        audio: audio
    };
}

// The fast lane writes MP4. A tool whose user asked for another container
// gets it here — ONE door (2026-09-26): mp4/m4v pass through, mkv/mov/avi are
// a copy remux of the same H.264/AAC (no second encode — 1–1.5 s on a 5-s
// clip, measured on /video-crop), webm is the VP9/Opus transcode of
// WebMEncoder. Before it, /video-crop and /video-speed each carried a private
// copy of the remux, and /video-compressor and /video-flip sent every MKV
// «same as source» to single-thread x264 because they had none (fmt_mkv).
//
// MKV and MOV go through LibavRemux.remuxFile (2026-10-08): libav.js in its own
// worker, the input read in 8 MB windows and the output written to OPFS, so the
// size of the result is not a memory term. FFmpeg's `-c copy` keeps its output
// in MEMFS — a 3.3 GB /enhance-video result died there with «Array buffer
// allocation failed» after 27 minutes of encoding (request #1848), and the tool
// then threw the encode away. Measured on the same machine, 300 MB: FFmpeg 7.1 s
// with +1 GB of memory, the worker 8.7 s with +110 MB. FFmpeg stays the
// fallback for what fits its heap, and the only lane for AVI (the libav build
// writes no AVI) — refused BEFORE it runs when the output cannot fit.
function _ffmpegCopyCeiling() {
    var v = +window.__WASM_OUTPUT_MAX;
    return v > 0 ? v : 1600 * 1048576;
}
// Resolves to the module itself, so a page takes `remuxFile` from the loader's
// answer instead of reaching for a global it never loaded by tag
// (PageLoadsTheModulesItCallsTest). A script that loaded without defining the
// module is a failure here, not a TypeError at the call site.
function ensureLibavRemux() {
    if (window.LibavRemux && window.LibavRemux.remuxFile) return Promise.resolve(window.LibavRemux);
    if (!window.__libavRemuxPromise) {
        var u = (typeof window.__jsSrc === 'function') ? window.__jsSrc('/js/libav-remux.js') : '/js/libav-remux.js';
        window.__libavRemuxPromise = loadScript(u, 30000).catch(function (e) { window.__libavRemuxPromise = null; throw e; });
    }
    return window.__libavRemuxPromise.then(function () {
        if (!window.LibavRemux || !window.LibavRemux.remuxFile) throw new Error('libav-remux: module-missing');
        return window.LibavRemux;
    });
}
async function _ffmpegCopyRemux(mp4Blob, fmt, opts, onP) {
    if (mp4Blob.size > _ffmpegCopyCeiling()) {
        var big = new Error('to-container: too-big-for-ffmpeg ' + Math.round(mp4Blob.size / 1048576) + ' MB');
        big.code = 'TOO_BIG';
        throw big;
    }
    var P = window.FFmpegProcessor;
    if (!P || typeof P.process !== 'function') throw new Error('to-container: no-ffmpeg');
    if (!P.isReady()) await P.init(function (p) { if (typeof p === 'number' && isFinite(p)) onP(Math.min(0.4, p * 0.4)); });
    if (opts.signal && opts.signal.aborted) throw new DOMException('cancelled', 'AbortError');
    var args = ['-c', 'copy'];
    if (fmt === 'mov') args.push('-movflags', '+faststart');
    P.__nextOperation = 'copy';
    try { P.onProgress(function (p) { onP(0.4 + Math.max(0, Math.min(1, p)) * 0.6); }); } catch (_) {}
    var r = await P.process(new File([mp4Blob], 'hw_out.mp4', { type: 'video/mp4' }), args, 'output.' + fmt);
    var out = r && (r.blob || r);
    if (!out || out.size < 1000) throw new Error('to-container: remux-empty');
    return out;
}
async function toContainer(mp4Blob, fmt, opts) {
    opts = opts || {};
    fmt = String(fmt || 'mp4').toLowerCase();
    if (fmt === 'mp4' || fmt === 'm4v') return mp4Blob;
    var onP = typeof opts.onProgress === 'function' ? opts.onProgress : function () {};
    var slug = opts.slug || (window.__TOOL_SLUG || 'webcodecs');
    var t0 = performance.now(), out, lane = '', why = '';
    if (fmt === 'webm') {
        if (!window.WebMEncoder || !window.WebMEncoder.fromVideoBlob) throw new Error('to-container: no-webm-encoder');
        out = await window.WebMEncoder.fromVideoBlob(mp4Blob, { crf: opts.crf, onProgress: onP, signal: opts.signal });
        if (!out || out.size < 1000) throw new Error('to-container: webm-empty');
        lane = 'webm';
    } else if (fmt === 'mkv' || fmt === 'mov') {
        try {
            await ensureLibavRemux();
            var src = (typeof File !== 'undefined' && mp4Blob instanceof File) ? mp4Blob : new File([mp4Blob], 'hw_out.mp4', { type: 'video/mp4' });
            var rr = await window.LibavRemux.remuxFile(src, { format: fmt, signal: opts.signal, onProgress: onP });
            out = rr.file;
            if (!out || out.size < 1000) throw new Error('to-container: remux-empty');
            lane = 'libav';
        } catch (e) {
            if ((e && e.name === 'AbortError') || (opts.signal && opts.signal.aborted)) throw e;
            why = String((e && (e.code || e.message)) || 'libav').slice(0, 80);
            out = await _ffmpegCopyRemux(mp4Blob, fmt, opts, onP);
            lane = 'ffmpeg';
        }
    } else if (fmt === 'avi') {
        out = await _ffmpegCopyRemux(mp4Blob, fmt, opts, onP);
        lane = 'ffmpeg';
    } else {
        throw new Error('to-container: unsupported ' + fmt);
    }
    try { window.__ae && window.__ae('feature_use', { slug: slug, action: 'hw_remux', fmt: fmt, out: out.size, ms: Math.round(performance.now() - t0), lane: lane, why: why || undefined }); } catch (_) {}
    return out;
}

// The ONE place that picks the demuxer for an input file — run() and
// runInterpolation() both come through here, so a container the pipeline
// learns to read is learned by every entry at once (the Matroska lane reached
// run() first and /video-fps's runInterpolation a day later; two copies of
// the choice is how that gap stays open).
function ensureInputDemuxer(file) { return isMatroskaFile(file) ? ensureWebmReader() : ensureMp4Box(); }
function demuxInput(file, onAlive, opts) {
    return isMatroskaFile(file) ? demuxMatroska(file, onAlive, opts) : demuxMp4(file, onAlive, opts);
}

/* Uncompressed sound (camera PCM, G.711 — demuxInfo.audio.pcm) → AudioBuffer,
   straight from the chunk table: the samples ARE the sound, no decoder, no
   whole-file read (22.10.2026). Starts exactly at the edit's cut — the edit
   list kept one chunk before it (a decoder warm-up PCM does not need), and
   `audioResidualUs` is how far into that chunk the cut lies. */
function _pcmSkipFrames(demuxInfo, rate) {
    var r = (demuxInfo.edit && demuxInfo.edit.applied && demuxInfo.edit.audioResidualUs) || 0;
    return r > 0 ? Math.round(r * rate / 1e6) : 0;
}
// Lazy units carry `size`; eager ones carry their bytes in `data`.
function _unitBytes(s) { return s.size > 0 ? s.size : ((s.data && s.data.byteLength) || 0); }
async function _decodePcmTable(demuxInfo, isAborted) {
    var a = demuxInfo.audio, L = a.pcm, M = window.__mp4Pcm;
    if (!M || typeof M.decode !== 'function' || typeof AudioBuffer === 'undefined') throw new Error('audio-pcm-unavailable');
    var units = a.samples, total = 0, u;
    for (u = 0; u < units.length; u++) total += Math.floor(_unitBytes(units[u]) / L.bytesPerFrame);
    var skip = Math.min(total, _pcmSkipFrames(demuxInfo, L.sampleRate));
    if (total - skip < 1) throw new Error('audio-pcm-empty');
    var buf = new AudioBuffer({ length: total - skip, numberOfChannels: L.channels, sampleRate: L.sampleRate });
    var planes = [];
    for (var c = 0; c < L.channels; c++) planes.push(buf.getChannelData(c));
    var rd = _lazySampleReader(demuxInfo), at = -skip, ch = L.channels;
    for (u = 0; u < units.length; u++) {
        if (isAborted && isAborted()) throw new DOMException('cancelled', 'AbortError');
        var d = M.decode(await rd.bytes(units[u], false), L);
        var k = 1 / (d.format === 's16' ? 32768 : 1), x = d.data;
        for (var f = 0; f < d.frames; f++, at++) {
            if (at < 0) continue;
            for (var c2 = 0, p = f * ch; c2 < ch; c2++, p++) planes[c2][at] = x[p] * k;
        }
        if ((u & 63) === 63) await _yieldTick(0);
    }
    try { buf.__via = 'pcm'; } catch (_) {}
    return buf;
}

/* The same sound relayed chunk by chunk into the AAC encoder — the plain case
   (same speed, no gain / fade / lead) of muxAudioLazy, with the decoder
   replaced by the bytes themselves. Sample-exact trim; nothing outlives its
   8 MB window. null = declined before any chunk reached the muxer (a rate the
   AAC encoder must not be handed without a resampler, no AudioEncoder) —
   muxSpeedAudio then serves through _decodePcmTable. */
async function _muxPcmLazy(demuxInfo, muxer, isAborted, edit) {
    var a = demuxInfo.audio, L = a.pcm, M = window.__mp4Pcm;
    if (!M || typeof M.decode !== 'function') return null;
    if (typeof AudioData === 'undefined' || typeof AudioEncoder === 'undefined') return null;
    var outRate = +edit.outRate > 0 ? +edit.outRate : L.sampleRate;
    var outCh = +edit.outChannels > 0 ? Math.min(2, +edit.outChannels) : Math.min(2, L.channels);
    if (outRate !== L.sampleRate) return null;
    var WAD = window.WebCodecsAudioDecode;
    if (outCh !== L.channels && !(WAD && typeof WAD.conformAudioData === 'function')) return null;
    await ensureAacAccessUnit();
    var rate = L.sampleRate, ch = L.channels;
    var tStart = +edit.trimStart > 0 ? +edit.trimStart : 0, tEnd = +edit.trimEnd > 0 ? +edit.trimEnd : 0;
    var fromF = Math.round(tStart * rate), toF = tEnd > tStart ? Math.round(tEnd * rate) : Infinity;
    var encErr = null, first = true, muxed = 0, fed = 0, t0 = Date.now();
    var aenc = window.AacAccessUnit.encoder({
        output: function (chunk, meta) {
            try { muxer.addAudioChunk(chunk, (first && meta) ? meta : undefined); muxed++; first = false; }
            catch (e) { encErr = encErr || e; }
        },
        error: function (e) { encErr = encErr || e; }
    }, { slug: window.__TOOL_SLUG });
    aenc.configure({ codec: 'mp4a.40.2', sampleRate: outRate, numberOfChannels: outCh, bitrate: 192000 });
    var rd = _lazySampleReader(demuxInfo), units = a.samples;
    var at = -_pcmSkipFrames(demuxInfo, rate);             // frame index of the next unit, 0 = the cut
    try {
        for (var i = 0; i < units.length && at < toF; i++) {
            if (isAborted && isAborted()) throw new DOMException('cancelled', 'AbortError');
            if (encErr) throw encErr;
            var n = Math.floor(_unitBytes(units[i]) / L.bytesPerFrame), uAt = at;
            at += n;
            if (at <= fromF || at <= 0) continue;           // wholly before the cut: not even read
            var d = M.decode(await rd.bytes(units[i], false), L);
            var lo = Math.max(0, fromF - uAt, -uAt), hi = Math.min(d.frames, toF - uAt);
            if (hi <= lo) continue;
            var data = new AudioData({ format: d.format, sampleRate: rate, numberOfFrames: hi - lo, numberOfChannels: ch,
                timestamp: Math.round((uAt + lo - fromF) * 1e6 / rate), data: d.data.subarray(lo * ch, hi * ch) });
            if (outCh !== ch) { var cf = WAD.conformAudioData(data, outRate, outCh); if (cf !== data) data.close(); data = cf; }
            aenc.encode(data); data.close(); fed++;
            if (typeof edit.onProgress === 'function' && (i & 63) === 0) { try { edit.onProgress(i / units.length); } catch (_) {} }
            while (aenc.encodeQueueSize > 16 && !encErr) await _yieldTick(0);
        }
        if (encErr) throw encErr;
        await aenc.flush();
    } finally {
        try { aenc.close(); } catch (_) {}
    }
    if (encErr) throw encErr;
    if (!muxed) throw new Error('audio-encode-no-output');
    try { if (typeof window.__ae === 'function') window.__ae('feature_use', { slug: edit.slug || (window.__currentSlug || 'webcodecs'),
        action: 'audio_pcm_relay', codec: String(L.codec), bits: L.bits, sr: rate, ch: ch, units: fed, muxed: muxed,
        reads: rd.stats().reads, ms: Date.now() - t0 }); } catch (_) {}
    return muxed;
}

// The whole audio track as ONE AudioBuffer, decoded from the sample table
// (AudioDecoder over 8 MB windows) — what muxSpeedAudio needs for a speed
// change, without reading the file into the heap. decodeAudioData wants the
// ENTIRE file as one ArrayBuffer (and was handed a second copy of it): for
// the 549 MB WebM in the bug report that is 1.1 GB of container bytes before
// a single sample is decoded, on a 4 GB machine. Here the PCM is the only big
// allocation, written straight into its AudioBuffer.
async function _decodeTableAudio(demuxInfo, isAborted) {
    var a = demuxInfo.audio;
    var cfg = lazyAudioDecoderConfig(a);
    if (!cfg && /^mp4a\.40\.2$/i.test(a.codec || '') && a.ascBytes) {
        cfg = { codec: 'mp4a.40.2', sampleRate: a.sampleRate, numberOfChannels: a.channels, description: a.ascBytes };
    }
    if (!cfg || typeof AudioDecoder === 'undefined') throw new Error('audio-table-codec:' + (a && a.codec));
    var sup = await AudioDecoder.isConfigSupported(cfg);
    if (!sup || !sup.supported) throw new Error('audio-table-unsupported:' + cfg.codec);
    var units = a.samples, durUs = 0;
    for (var u = 0; u < units.length; u++) durUs += units[u].duration || 0;
    var buf = null, rate = 0, chans = 0, filled = 0, extra = [], decErr = null;
    var dec = new AudioDecoder({
        output: function (d) {
            try {
                if (!buf) {
                    rate = d.sampleRate; chans = d.numberOfChannels;
                    buf = new AudioBuffer({ length: Math.max(1, Math.ceil(durUs * rate / 1e6) + rate), numberOfChannels: chans, sampleRate: rate });
                }
                var n = d.numberOfFrames, tmp = new Float32Array(n);
                for (var c = 0; c < chans; c++) {
                    d.copyTo(tmp, { planeIndex: c, format: 'f32-planar' });
                    if (filled + n <= buf.length) buf.getChannelData(c).set(tmp, filled);
                    else extra.push({ c: c, at: filled, pcm: tmp.slice() });
                }
                filled += n;
            } catch (e) { decErr = e; }
            finally { try { d.close(); } catch (_) {} }
        },
        error: function (e) { decErr = e; }
    });
    dec.configure(cfg);
    var rd = _lazySampleReader(demuxInfo);
    try {
        for (var i = 0; i < units.length; i++) {
            if (isAborted && isAborted()) throw new DOMException('cancelled', 'AbortError');
            if (decErr) break;
            var sm = units[i];
            dec.decode(new EncodedAudioChunk({ type: 'key', timestamp: sm.timestamp, duration: sm.duration || 0, data: sm.data || await rd.bytes(sm, false) }));
            while (dec.decodeQueueSize > 32) { await _yieldTick(0); if (decErr) break; }
        }
        await dec.flush();
    } finally { try { dec.close(); } catch (_) {} }
    if (decErr) throw new Error('audio-table-decode: ' + ((decErr && decErr.message) || decErr));
    if (!buf || !filled) throw new Error('audio-table-empty');
    // Opus/AAC encoder delay the container declares (CodecDelay) — the decoder
    // outputs it as leading samples; FFmpeg drops it, and so do we.
    var skip = a.codecDelayUs > 0 ? Math.min(filled - 1, Math.round(a.codecDelayUs * rate / 1e6)) : 0;
    var len = filled - skip;
    if (skip === 0 && len === buf.length && !extra.length) return buf;
    var outB = new AudioBuffer({ length: len, numberOfChannels: chans, sampleRate: rate });
    for (var ch = 0; ch < chans; ch++) {
        var src = buf.getChannelData(ch);
        outB.copyToChannel(src.subarray(skip, Math.min(buf.length, filled)), ch, 0);
    }
    for (var e2 = 0; e2 < extra.length; e2++) {
        var x2 = extra[e2], at2 = x2.at - skip;
        if (at2 < len) outB.copyToChannel(x2.pcm.subarray(0, Math.min(x2.pcm.length, len - at2)), x2.c, Math.max(0, at2));
    }
    return outB;
}

// One reader per consuming loop (video feed, audio passthrough): a sliding
// window over the file, read through the same slice door the eager demux
// uses (RAM tier when cached, live slice otherwise, durable-tier rescue on a
// revoked handle). Samples arrive in file order, so a run of them is usually
// ONE read. `bytes()` returns a view into the window — EncodedVideoChunk /
// EncodedAudioChunk COPY, so the window is free to move on. Video samples go
// through the same NAL sanitizer the eager demux applies at collection time.
function _lazySampleReader(demuxInfo) {
    var file = demuxInfo.file, avcC = demuxInfo.avcC || null, stats = demuxInfo._nalStats || null;
    var win = null, winLo = 0, winHi = 0, reads = 0, readBytes = 0;
    return {
        bytes: async function (s, isVideo) {
            if (s.data) return s.data;                                 // eager sample, or sample 0 kept for diagnostics
            var lo = s.offset, hi = s.offset + s.size;
            if (!(win && lo >= winLo && hi <= winHi)) {
                var end = Math.min(file.size, Math.max(hi, lo + LAZY_SPAN_BYTES));
                win = new Uint8Array(await _readSliceWithRetry(file, lo, end));
                winLo = lo; winHi = lo + win.length;
                reads++; readBytes += win.length;
                if (hi > winHi) throw new Error('demux-read: sample@' + lo + '+' + s.size + ' past EOF ' + file.size);
            }
            var view = win.subarray(lo - winLo, hi - winLo);
            return (isVideo && avcC) ? _sanitizeAvccSample(view, avcC, stats) : view;
        },
        stats: function () { return { reads: reads, bytes: readBytes }; }
    };
}

/* How EVEN the source's frame times are (2026-11-04). This lane keeps every
   frame's own time; an FFmpeg-WASM lane used to move it onto a 1/rate grid
   (FFmpegFrameTimingRule in ffmpeg-processor.js has the measurement), and the
   review that found it («скачанное видео дёргается», a 15 fps CCTV-like clip)
   left no trace of the source's cadence anywhere. Read from the sample table
   the demux already holds — no extra I/O; the first 30 000 frames.
     ts_med — median frame interval, ms;
     ts_jit — p95 |interval − median|, ms (0 for an even clip);
     ts_irr — % of intervals more than 25 % off the median. */
function _frameTimingOf(samples) {
    var n = Math.min((samples && samples.length) || 0, 30000);
    if (n < 8) return null;
    var t = new Float64Array(n);
    for (var i = 0; i < n; i++) t[i] = +samples[i].timestamp || 0;
    t.sort();
    var d = new Float64Array(n - 1);
    for (var j = 1; j < n; j++) d[j - 1] = t[j] - t[j - 1];
    var s = Float64Array.from(d).sort(), med = s[s.length >> 1];
    if (!(med > 0)) return null;
    var dev = new Float64Array(d.length), irr = 0;
    for (var k = 0; k < d.length; k++) { dev[k] = Math.abs(d[k] - med); if (dev[k] > med * 0.25) irr++; }
    dev.sort();
    return { ts_med: Math.round(med / 100) / 10, ts_jit: Math.round(dev[Math.floor(dev.length * 0.95)] / 100) / 10,
        ts_irr: Math.round(irr / d.length * 1000) / 10 };
}
function _aeDemuxLane(slug, demuxInfo, file) {
    try {
        if (typeof window.__ae !== 'function') return;
        var ev = {
            slug: slug || 'webcodecs', action: 'demux_lane',
            lane: (demuxInfo && demuxInfo.lazy) ? 'lazy' : 'eager',
            szMB: Math.round(((file && file.size) || 0) / 1048576),
            frames: (demuxInfo && demuxInfo.samples) ? demuxInfo.samples.length : 0
        };
        try { var ft = _frameTimingOf(demuxInfo && demuxInfo.samples); if (ft) Object.assign(ev, ft); } catch (_) {}
        window.__ae('feature_use', ev);
    } catch (_) {}
}

async function demuxMp4(file, onAlive, opts) {
    if (!window.MP4Box) throw new Error('mp4box-missing');
    var _stopEarly = !!(opts && opts.headOnly);
    // The edit-list reader (mp4-edit-list.js). Fail-open: without it the
    // finalize block below falls back to the pre-2026-09-15 per-track
    // min-normalisation and says so in `edit.applied = false`.
    var _editList = null;
    try { await ensureMp4EditList(); _editList = window.Mp4EditList || null; } catch (_) { _editList = null; }
    // Lazy first when asked; a decline (reason string) means "the eager
    // path serves this file", never an error the caller sees.
    if (opts && opts.lazy && !_stopEarly) {
        var lz = await _demuxMp4Once(file, onAlive, opts, _editList, true);
        if (lz && !lz.declined) return lz;
        try { if (typeof window.__ae === 'function') window.__ae('feature_use', {
            slug: (window.__currentSlug || (opts && opts.slug) || 'webcodecs'), action: 'demux_lazy_declined',
            why: String((lz && lz.declined) || '?').slice(0, 60), szMB: Math.round((file.size || 0) / 1048576) }); } catch (_) {}
    }
    return await _demuxMp4Once(file, onAlive, opts, _editList, false);
}

async function _demuxMp4Once(file, onAlive, opts, _editList, lazy) {
    var _stopEarly = !!(opts && opts.headOnly);
    var _aliveAt = 0;
    var _alive = function () {
        if (typeof onAlive !== 'function') return;
        var now = Date.now();
        if (now - _aliveAt < 1500) return;   // throttle: liveness, not a firehose
        _aliveAt = now;
        try { onAlive(); } catch (_) {}
    };
    return await new Promise(function(resolve, reject){
        var inFile = MP4Box.createFile();
        var done = false;
        var info = null;
        var videoSamples = [];
        var audioSamples = [];
        var videoTrackId, audioTrackId, vTscale, aTscale;
        var audioAsc = null, audioDurSec = 0;
        // Uncompressed sound (camera PCM, G.711): its layout from the core door
        // (__mp4Pcm, trak.pcm) — bits, byte order, channels, rate. null otherwise.
        var audioPcm = null;
        // The sample-entry type of a sound track this demux cannot see — see
        // _unreadSoundTrack. '' when there is none (or when an audio track is picked).
        var soundUnread = '';
        // All decoder-config-record byte buffers live at demuxMp4 scope so the
        // resolve() at the end of the outer feed IIFE can reference them. The
        // builders inside onReady ASSIGN to these (don't re-declare), so a
        // ReferenceError doesn't kill the entire HW pipeline at resolve time.
        // Pre-Wave-19-bugfix (~2026-06-04 to 2026-06-07): the hvcC/av1C/vpcC
        // bindings were `var`-declared INSIDE onReady, hoisted only to onReady's
        // scope, so resolve()'s `hvcC: hvcCBytes` threw ReferenceError on every
        // call (typeof returned 'undefined' which masked the bug from quick
        // probes). Result: 100 % HW pipeline failure across all codec sources
        // including AVC — every session looked like a 30 s stall to the user
        // because the Wave 2.C watchdog caught the silent rejection. 1479
        // hw_pipeline_stalled events / 3 d on prod, zero hw_pipeline_used.
        var avcCBytes = null;
        var hvcCBytes = null;
        var av1CBytes = null;
        var vpcCBytes = null;
        // Wave 23 — per-demux NAL sanitization counters ({sei, ps, samples}).
        // Reported once via __ae after the demux resolves (see below).
        var _nalSanStats = { sei: 0, ps: 0, samples: 0 };

        inFile.onError = function(e){ if (!done) { done = true; reject(new Error('demux:' + e)); } };
        inFile.onReady = function(_info) {
            info = _info;
            var v = (info.tracks || []).find(function(t){ return t.type === 'video'; });
            var a = pickAudioTrack(info.tracks, opts && opts.audioTrackIndex);
            if (!v) { done = true; return reject(new Error('no-video-track')); }
            /* Несжатый звук: дверь ядра разложила его таблицу по чанкам и
               записала формат в trak.pcm. Формата нет (раскладка, которую
               честно не прочесть) — это прежний «звук, которого лейн не видит». */
            if (a && _isPcmTrack(a)) {
                try { audioPcm = inFile.getTrackById(a.id).pcm || null; } catch (_) { audioPcm = null; }
                if (!audioPcm) { soundUnread = String(a.codec || 'soun').replace(/[^a-zA-Z0-9._ -]/g, '').trim().slice(0, 16) || 'soun'; a = null; }
            }
            if (!a && !soundUnread) soundUnread = _unreadSoundTrack(inFile, info);
            /* Прибор класса. Сколько многодорожечных файлов вообще приходит в
               видео-тулы, сайт не знал НИ ОДНОГО ДНЯ: ни одно событие не несло
               число дорожек, и «часто ли это» было предметом догадок. Здесь
               даром: демукс уже разобрал moov, счёт — одна строка. Именно по
               нему решается, какой тул подключать к выбору дорожки следующим. */
            try {
                var _aTr = audioTracksOf(info.tracks);
                if (_aTr.length > 1 && typeof window.__ae === 'function') window.__ae('feature_use', {
                    slug: (opts && opts.slug) || window.__currentSlug || 'webcodecs',
                    f: 'audio_tracks', lane: 'hw', n: _aTr.length,
                    langs: _aTr.map(function (t) { return (t.language || '?').slice(0, 3); }).join(',').slice(0, 40),
                    picked: (typeof (opts && opts.audioTrackIndex) === 'number') ? opts.audioTrackIndex : 0,
                    why: (opts && typeof opts.audioTrackIndex === 'number') ? 'asked' : 'first'
                });
            } catch (_) {}
            videoTrackId = v.id;
            vTscale = v.timescale;
            // Build avcDecoderConfigRecord bytes for VideoDecoder.configure({description}).
            // mp4box parses the avcC box into structured fields but does not
            // expose the raw bytes — VideoDecoder requires the binary record.
            // We rebuild it from the parsed SPS/PPS NAL units following the
            // ISO/IEC 14496-15 layout.
            try {
                var trak = inFile.getTrackById(v.id);
                var stsd = trak.mdia.minf.stbl.stsd;
                var entry = stsd.entries[0];
                var avcC = entry.avcC || (entry.boxes || []).find(function(b){ return b.type === 'avcC'; });
                if (avcC && avcC.SPS && avcC.SPS.length && avcC.PPS && avcC.PPS.length) {
                    // Pack ALL SPS + ALL PPS, not just SPS[0]/PPS[0].
                    // Source-of-bug (closes ~1000 events/7d cluster
                    // "decode: Unsupported configuration" with
                    // decoded_so_far=0): some sources carry multiple SPS
                    // and/or multiple PPS in avcC. In-band slices then
                    // reference (say) pic_parameter_set_id=1 — but if we
                    // only configured the decoder with PPS[0] the decoder
                    // can't resolve the reference and bails on the very
                    // first chunk. Our reconstruction now mirrors the
                    // standard ISO/IEC 14496-15 avcC layout for any count
                    // of SPS / PPS up to the 5-bit / 8-bit field limits.
                    var spsArr = avcC.SPS;
                    var ppsArr = avcC.PPS;
                    // Field-size guards (spec: SPS count is 5 bits → max 31,
                    // PPS count is 8 bits → max 255). In practice numbers
                    // above 4 are exotic; clip defensively so we never
                    // overflow the count byte.
                    var numSps = Math.min(spsArr.length, 31);
                    var numPps = Math.min(ppsArr.length, 255);
                    var len = 7;
                    var i;
                    for (i = 0; i < numSps; i++) len += 2 + spsArr[i].nalu.length;
                    len += 1;   // numOfPictureParameterSets byte
                    for (i = 0; i < numPps; i++) len += 2 + ppsArr[i].nalu.length;
                    var rec = new Uint8Array(len);
                    var po = 0;
                    rec[po++] = avcC.configurationVersion || 1;
                    rec[po++] = avcC.AVCProfileIndication;
                    rec[po++] = avcC.profile_compatibility;
                    rec[po++] = avcC.AVCLevelIndication;
                    rec[po++] = 0xFC | ((avcC.lengthSizeMinusOne != null ? avcC.lengthSizeMinusOne : 3) & 0x03);
                    rec[po++] = 0xE0 | (numSps & 0x1F);
                    for (i = 0; i < numSps; i++) {
                        var spsN = spsArr[i].nalu;
                        rec[po++] = (spsN.length >> 8) & 0xFF;
                        rec[po++] = spsN.length & 0xFF;
                        rec.set(spsN, po); po += spsN.length;
                    }
                    rec[po++] = numPps & 0xFF;
                    for (i = 0; i < numPps; i++) {
                        var ppsN = ppsArr[i].nalu;
                        rec[po++] = (ppsN.length >> 8) & 0xFF;
                        rec[po++] = ppsN.length & 0xFF;
                        rec.set(ppsN, po); po += ppsN.length;
                    }
                    avcCBytes = rec;
                }
            } catch (e) { /* avcC extraction failed; decoder.configure will fail and we'll fallback */ }

            // No avcC bytes = no usable `description` for VideoDecoder.configure.
            // Three real-world causes (per prod telemetry 7d, 290 events):
            //   1. HEVC source (`hev*`/`hvc*`)  — parameter sets live in `hvcC`,
            //      ~70 events/7d. Routed to FFmpeg fallback (has HEVC decode).
            //   2. AV1 source (`av01`)          — parameter sets in `av1C`,
            //      currently no extractor. FFmpeg fallback handles AV1 too.
            //   3. VP9 source (`vp09`)          — `vpcC` box, same situation.
            //   4. Corrupted/atypical avcC      — extraction threw silently.
            // Without this gate, all four paths forwarded `description: undefined`
            // to VideoDecoder.isConfigSupported → cryptic
            // "Failed to read the 'description' property" (~144 events/7d) or
            // "Type error" (~76/7d). Now: typed throw, blade's `catch (hwErr)`
            // routes to the FFmpeg-WASM fallback uniformly. Telemetry carries
            // the codec string so /pino can size each sub-cluster.
            // ──────────────────────────────────────────────────────────────
            // HEVC hvcC builder — Wave 18 (2026-06-04).
            //
            // Pre-Wave-18 EVERY hev*/hvc* source bailed with
            // `decoder-config-no-description (hevc:...) needs FFmpeg fallback`
            // (~70 events / 7 d on desktop Yandex alone; Artyom #621
            // 2026-06-04 hit this class — feedback "процесс зависает и не
            // завершается" on Yandex 26 / Win10 / 8 GB / mp4 < 1 GB). FFmpeg
            // WASM HEVC decode runs ~5-10× slower than realtime; a 5-min HEVC
            // mp4 takes 25-50 min to fall back, the user sees a frozen %, gives up.
            //
            // mp4box parses hvcC into the same kind of structured object as
            // avcC but the field names differ. Pack per ISO/IEC 14496-15 §8.3
            // — 23-byte fixed header + 1 byte numOfArrays + per-array
            // (1 byte completeness/type + 2 byte numNalus + nalus).
            // hvcCBytes hoisted to demuxMp4 scope — see top-of-function comment
            if (!avcCBytes) try {
                var trakH = inFile.getTrackById(v.id);
                var stsdH = trakH.mdia.minf.stbl.stsd;
                var entryH = stsdH.entries[0];
                var hvcC = entryH.hvcC || (entryH.boxes || []).find(function(b){ return b.type === 'hvcC'; });
                if (hvcC && hvcC.nalu_arrays && hvcC.nalu_arrays.length) {
                    var arrays = hvcC.nalu_arrays;
                    var totalLen = 23;                                              // fixed header
                    totalLen += 1;                                                  // numOfArrays
                    for (var aI = 0; aI < arrays.length; aI++) {
                        var arr = arrays[aI];
                        totalLen += 1 + 2;                                          // type byte + numNalus
                        for (var nI = 0; nI < arr.length; nI++) {
                            var nalu = arr[nI];
                            var naluBytes = nalu.data || nalu.nalu || new Uint8Array(0);
                            totalLen += 2 + naluBytes.length;
                        }
                    }
                    var hvc = new Uint8Array(totalLen);
                    var ho = 0;
                    hvc[ho++] = hvcC.configurationVersion != null ? hvcC.configurationVersion : 1;
                    // Byte 1: general_profile_space (2) + general_tier_flag (1) + general_profile_idc (5)
                    hvc[ho++] = ((hvcC.general_profile_space & 0x03) << 6)
                              | ((hvcC.general_tier_flag & 0x01) << 5)
                              | (hvcC.general_profile_idc & 0x1F);
                    // 4 bytes general_profile_compatibility (big-endian)
                    var gpc = hvcC.general_profile_compatibility >>> 0;
                    hvc[ho++] = (gpc >>> 24) & 0xFF;
                    hvc[ho++] = (gpc >>> 16) & 0xFF;
                    hvc[ho++] = (gpc >>> 8)  & 0xFF;
                    hvc[ho++] = gpc          & 0xFF;
                    // 6 bytes general_constraint_indicator. mp4box surfaces it as Uint8Array;
                    // pad/truncate defensively so the layout never overruns.
                    var gci = hvcC.general_constraint_indicator || new Uint8Array(6);
                    for (var ci = 0; ci < 6; ci++) hvc[ho++] = gci[ci] || 0;
                    hvc[ho++] = (hvcC.general_level_idc || 0) & 0xFF;
                    // 2 bytes reserved (4 bits all 1) + min_spatial_segmentation_idc (12 bits)
                    var mssi = (hvcC.min_spatial_segmentation_idc || 0) & 0x0FFF;
                    hvc[ho++] = 0xF0 | ((mssi >>> 8) & 0x0F);
                    hvc[ho++] = mssi & 0xFF;
                    // 1 byte: reserved (6 bits=1) + parallelismType (2 bits)
                    hvc[ho++] = 0xFC | ((hvcC.parallelismType || 0) & 0x03);
                    // 1 byte: reserved (6 bits=1) + chromaFormat (2 bits)
                    hvc[ho++] = 0xFC | ((hvcC.chroma_format_idc || 0) & 0x03);
                    // 1 byte: reserved (5 bits=1) + bitDepthLumaMinus8 (3 bits)
                    hvc[ho++] = 0xF8 | ((hvcC.bit_depth_luma_minus8 || 0) & 0x07);
                    // 1 byte: reserved (5 bits=1) + bitDepthChromaMinus8 (3 bits)
                    hvc[ho++] = 0xF8 | ((hvcC.bit_depth_chroma_minus8 || 0) & 0x07);
                    // 2 bytes avgFrameRate (big-endian)
                    var afr = (hvcC.avgFrameRate || 0) & 0xFFFF;
                    hvc[ho++] = (afr >>> 8) & 0xFF;
                    hvc[ho++] = afr & 0xFF;
                    // 1 byte: constantFrameRate (2 bits) + numTemporalLayers (3 bits)
                    //         + temporalIdNested (1 bit) + lengthSizeMinusOne (2 bits)
                    hvc[ho++] = ((hvcC.constantFrameRate & 0x03) << 6)
                              | (((hvcC.numTemporalLayers != null ? hvcC.numTemporalLayers : 1) & 0x07) << 3)
                              | (((hvcC.temporalIdNested != null ? hvcC.temporalIdNested : 0) & 0x01) << 2)
                              | (((hvcC.lengthSizeMinusOne != null ? hvcC.lengthSizeMinusOne : 3) & 0x03));
                    // numOfArrays
                    hvc[ho++] = arrays.length & 0xFF;
                    // Per array
                    for (var ai2 = 0; ai2 < arrays.length; ai2++) {
                        var arr2 = arrays[ai2];
                        // Try several field-name shapes seen in mp4box revisions for
                        // completeness/type metadata. Fall back to (completeness=1, type=32 VPS) for sanity.
                        var meta = arr2.metadata || arr2 || {};
                        var complete = (meta.array_completeness != null) ? meta.array_completeness
                                    : (meta.completeness != null) ? meta.completeness : 1;
                        var nalType = (meta.NAL_unit_type != null) ? meta.NAL_unit_type
                                    : (meta.nalu_type != null) ? meta.nalu_type
                                    : (arr2[0] && arr2[0].type != null) ? arr2[0].type : 32;
                        hvc[ho++] = ((complete & 0x01) << 7) | (nalType & 0x3F);
                        var nNum = arr2.length & 0xFFFF;
                        hvc[ho++] = (nNum >>> 8) & 0xFF;
                        hvc[ho++] = nNum & 0xFF;
                        for (var ni = 0; ni < arr2.length; ni++) {
                            var nu = arr2[ni];
                            var nuBytes = nu.data || nu.nalu || new Uint8Array(0);
                            var nuLen = nuBytes.length & 0xFFFF;
                            hvc[ho++] = (nuLen >>> 8) & 0xFF;
                            hvc[ho++] = nuLen & 0xFF;
                            hvc.set(nuBytes, ho); ho += nuLen;
                        }
                    }
                    hvcCBytes = hvc.subarray(0, ho);                                  // trim to actual written length
                }
            } catch (hvcErr) { /* hvcC extraction failed; we'll fall back below */ }

            // ──────────────────────────────────────────────────────────────
            // AV1 av1C + VP9 vpcC direct extraction — Wave 19 (2026-06-04).
            //
            // mp4box exposes the raw post-header payload of `av1C` and `vpcC`
            // boxes on `box.data` (verified against mp4box 0.5.4 fixtures —
            // av1C.data starts with `0x81` marker/version per AV1 §2.3.3,
            // vpcC.data starts with profile/level per VP Codec ISO §4.1, and
            // already strips the FullBox version+flags for VP9). The raw
            // payload IS exactly what VideoDecoder.configure({description})
            // expects. No per-codec reconstruction needed for these two —
            // saves ~80 lines of byte-packing vs. an avcC/hvcC-style builder
            // and is immune to mp4box-schema changes (avcC/hvcC fields could
            // be renamed across versions; .data is the raw box content).
            //
            // hvcC.data is NOT populated by mp4box (the shortcut sample-
            // entry parser doesn't keep raw bytes for the codecs it
            // structures into fields), so HEVC stays on the Wave 18
            // per-field builder above. Same for avcC.
            function _extractDataPayload(entry, boxType) {
                var box = entry[boxType] || (entry.boxes || []).find(function(b){ return b.type === boxType; });
                if (!box || !box.data) return null;
                return box.data instanceof Uint8Array ? box.data : new Uint8Array(box.data);
            }
            // av1CBytes / vpcCBytes hoisted to demuxMp4 scope — see top-of-function comment
            if (!avcCBytes && !hvcCBytes) {
                try {
                    var entryAVP = inFile.getTrackById(v.id).mdia.minf.stbl.stsd.entries[0];
                    av1CBytes = _extractDataPayload(entryAVP, 'av1C');
                    if (!av1CBytes) vpcCBytes = _extractDataPayload(entryAVP, 'vpcC');
                } catch (avpErr) { /* fall through */ }
            }

            if (!avcCBytes && !hvcCBytes && !av1CBytes && !vpcCBytes) {
                var codecStr = (v.codec || 'unknown').slice(0, 40);
                var familyKey = /^(hev|hvc)/i.test(codecStr) ? 'hevc'
                              : /^av01/i.test(codecStr) ? 'av1'
                              : /^vp09/i.test(codecStr) ? 'vp9'
                              : /^avc1/i.test(codecStr) ? 'avc-parse-fail'
                              : 'codec-unknown';
                if (!done) { done = true; reject(new Error('decoder-config-no-description (' + familyKey + ':' + codecStr + ') needs FFmpeg fallback')); }
                return;
            }

            if (a) {
                audioTrackId = a.id;
                aTscale = a.timescale;
                // The source's own AudioSpecificConfig + media duration ride
                // along: passthrough must ship the REAL esds (an AAC-LC one
                // fabricated from the header drops the SBR/PS signalling of an
                // HE-AAC stream), and the duration is the oracle the decode
                // path checks its result against. Both fail open (null / 0).
                try {
                    var _trak = inFile.getTrackById(a.id);
                    var _ents = _trak.mdia.minf.stbl.stsd.entries;
                    for (var _ei = 0; _ei < _ents.length && !audioAsc; _ei++) {
                        var _e = _ents[_ei];
                        var _dcd = _e.esds && _e.esds.esd && _e.esds.esd.descs && _e.esds.esd.descs[0];
                        var _dsi = _dcd && _dcd.descs && _dcd.descs[0] && _dcd.descs[0].data;
                        if (_dsi && _dsi.length) audioAsc = new Uint8Array(_dsi);
                    }
                } catch (_) { audioAsc = null; }
                if (a.duration > 0 && a.timescale > 0) audioDurSec = a.duration / a.timescale;
            }

            inFile.setExtractionOptions(v.id, null, { rapAlignement: true, nbSamples: 50 });
            if (a) inFile.setExtractionOptions(a.id, null, { nbSamples: 50 });
            inFile.start();
        };
        inFile.onSamples = function(track_id, user, samples) {
            var lastNum = 0;
            for (var i = 0; i < samples.length; i++) {
                var s = samples[i];
                var ts = s.cts * 1_000_000 / (track_id === videoTrackId ? vTscale : aTscale);
                var dur = s.duration * 1_000_000 / (track_id === videoTrackId ? vTscale : aTscale);
                if (track_id === videoTrackId) {
                    // Wave 23 (2026-07-08): sanitize AVCC samples — strip SEI +
                    // avcC-duplicate SPS/PPS NALs. Android MediaCodec rejects
                    // SEI-first / in-band-paramset samples in AVCC mode with the
                    // async "Unsupported configuration" OperationError on the
                    // FIRST decode() (808 events / 660 sessions / 30 d, 99.5 %
                    // mobile — the cluster flagged UNRESOLVED at the decoder
                    // cascade comment since 2026-05-28). Desktop FFmpeg-based
                    // decoders forgive this, which is why it never reproduced
                    // locally. AVC-only: the hex triangulation showed every
                    // cluster event is avc1.* (x264 user-data SEI UUID
                    // dc45e9bd… dominates chunk0 prefixes).
                    videoSamples.push({ data: avcCBytes ? _sanitizeAvccSample(s.data, avcCBytes, _nalSanStats) : s.data, timestamp: Math.round(ts), duration: Math.round(dur), is_sync: !!s.is_sync });
                } else if (track_id === audioTrackId) {
                    audioSamples.push({ data: s.data, timestamp: Math.round(ts), duration: Math.round(dur), is_sync: !!s.is_sync });
                }
                if (s.number > lastNum) lastNum = s.number;
            }
            // Critical — without releasing, mp4box accumulates internal buffers
            // and stops emitting new batches after a threshold (~2 batches in
            // our experience on a 1800-sample track). releaseUsedSamples lets
            // mp4box advance its sample cursor and free RAM.
            try { inFile.releaseUsedSamples(track_id, lastNum); } catch (_) {}
        };

        // Feed strategy (the same two-phase feed the old mp4box-trim.js used, gone 2026-09-17): head scan, optional tail
        // (for no-faststart files), then re-feed the body from offset 0 so
        // mdat sample data flows through onSamples after the sample table
        // is known.
        (async function feed(){
            var size = file.size;
            var CHUNK = 4 * 1024 * 1024;
            try {
                if (lazy) {
                    // ── lazy: ftyp+moov only, tables from the parsed moov ──
                    var top = await _mp4TopLevel(file);
                    if (!top || top.why) { done = true; return resolve({ declined: (top && top.why) || 'no-moov' }); }
                    var headBuf = await _mp4HeadBuffer(file, top);
                    headBuf.fileStart = 0;
                    inFile.appendBuffer(headBuf);
                    try { inFile.flush(); } catch(_){}
                    _alive();
                    if (done) return;                                  // onReady rejected — the same verdict the eager path gives
                    if (!info) { done = true; return resolve({ declined: 'moov-unparsed' }); }
                    var tables = _lazySampleTables(inFile, videoTrackId, audioTrackId, vTscale, aTscale);
                    if (!tables) { done = true; return resolve({ declined: 'no-sample-table' }); }
                    videoSamples = tables.video;
                    audioSamples = tables.audio;
                    // Sample 0 is read now: the decoder-error diagnostic reports
                    // its first bytes (chunk0_hex), and the decoder probes want
                    // a key sample in hand. One small read.
                    try { videoSamples[0].data = await _lazySampleReader({ file: file, avcC: avcCBytes, _nalStats: _nalSanStats }).bytes(videoSamples[0], true); }
                    catch (rdErr) { done = true; return resolve({ declined: 'sample0-read:' + String((rdErr && rdErr.message) || rdErr).slice(0, 40) }); }
                } else {
                // ───────────────────────────────────────────────────────
                // ROOT-CAUSE FIX (bug #442): feed mp4box in TWO clean phases
                // ───────────────────────────────────────────────────────
                // The previous 3-phase feed (head scan / tail scan / body
                // scan) silently TRUNCATED moov-at-end (non-faststart) mp4s.
                // The head scan honoured mp4box's `nextFileStart` hint, which
                // for a moov-at-end file points straight at the trailing
                // moov. The scan jumped there, `info` got set "in phase 1",
                // so the tail-scan branch (gated on `!info`) was skipped AND
                // the body scan (`while off < size`) had `off` already at
                // EOF — the entire mdat body between the head chunk and the
                // trailing moov was NEVER delivered to the sample extractor.
                // Result: only the first ~CHUNK bytes of samples extracted
                // (proven: 300 of 900 frames on a 30 s test clip → a video
                // truncated to one third, no error). Non-faststart mp4 is
                // extremely common (ffmpeg default output, many phone /
                // screen recorders) → bug #442 "ни один проект не доделан".
                //
                // Phase 1 — DISCOVERY: feed chunks until onReady delivers
                // `info`. mp4box's `next` hint efficiently jumps to a
                // trailing moov, so this costs ~2 appends for moov-at-end
                // and 1 for faststart — the dedicated tail scan is no longer
                // needed (its job is subsumed by following the hint).
                var off = 0;
                while (!done && !info && off < size) {
                    var end = Math.min(off + CHUNK, size);
                    var buf = await _readSliceWithRetry(file, off, end);
                    buf.fileStart = off;
                    var next = inFile.appendBuffer(buf);
                    _alive();
                    off = (typeof next === 'number' && next > off && next < size) ? next : end;
                }
                if (done) return;
                if (!info) { done = true; return reject(new Error('no-moov-head-or-tail')); }
                // Phase 2 — BODY: the sample table is now known, so feed the
                // WHOLE file [0..size] sequentially. This delivers every
                // mdat byte to the extractor regardless of where discovery's
                // `next`-hint jumps left `off`. Re-appending the discovery
                // region is safe — mp4box's per-track extraction cursor
                // never re-emits an already-delivered sample (verified: no
                // duplicates on either moov layout). releaseUsedSamples() in
                // onSamples keeps mp4box's retained buffers bounded as
                // samples stream out.
                off = 0;
                while (!done && off < size) {
                    var bEnd = Math.min(off + CHUNK, size);
                    var bBuf = await _readSliceWithRetry(file, off, bEnd);
                    bBuf.fileStart = off;
                    inFile.appendBuffer(bBuf);
                    _alive();
                    off = bEnd;
                    // Early stop for callers that only need the head of the
                    // stream — the 2026-08-15 decoder probe wants ONE keyframe, and
                    // reading the whole file for it costs ~13 ms/MB at every
                    // file pick (measured: 89 ms on 0.7 MB, 1107 ms on 86 MB,
                    // i.e. ~11 s on a 900 MB source — for a question the first
                    // few hundred KB already answer). Same demuxer, same
                    // parsing, just fed less: a second lean implementation is
                    // what we refuse to write.
                    if (_stopEarly && videoSamples.length > 0) {
                        for (var _si = 0; _si < videoSamples.length; _si++) {
                            if (videoSamples[_si].is_sync) { off = size; break; }
                        }
                    }
                }
                try { inFile.flush(); } catch(_){}
                if (done) return;
                if (!info) { done = true; return reject(new Error('no-moov')); }
                }   // end of the eager feed
                // Etalon hardening (2026-06-08): defer `done = true` until
                // resolve() actually returns. Pre-hardening, the flag was
                // flipped here at the top of the finalize block — so if any
                // synchronous throw happened in the rotation decode or the
                // resolve() object-literal evaluation (the Wave 18-19
                // ReferenceError lived here), the outer catch ran but the
                // `if (!done) reject(e)` guard saw done=true and SKIPPED
                // the reject, leaving the promise pending forever. That's
                // exactly the silent-hang signature that produced 1479
                // hw_pipeline_stalled events / 3 days. The Wave 2.C watchdog
                // at the caller catches the hang at 30 s; without this fix
                // every future synchronous throw in the finalize block has
                // the same pathology.
                var v = info.tracks.find(function(t){ return t.type === 'video'; });

                // ───────────────────────────────────────────────────────────
                // PRESENTATION TIMELINE: the edit lists decide where t=0 is
                // ───────────────────────────────────────────────────────────
                // Sample tables carry MEDIA time; what a player shows is
                // presentation time, and the per-track `elst` is the only box
                // relating the two. Every AAC track hides its encoder priming
                // behind one (21–48 ms), every B-frame video its reorder delay
                // (2 frames), and an iPhone camera clip hides the VIDEO it
                // captured before the audio session went live — hundreds of
                // ms. Feeding raw composition times downstream keeps every
                // hidden frame and plays the priming as content, so the two
                // tracks drift apart by the difference of their edits.
                //
                // From 2026-05-08 to 2026-09-15 this block normalised each
                // track to its own earliest timestamp instead, on the recorded
                // premise that per-track edit lists are «very rare» and the
                // error «milliseconds, below perception». Measured on flash+
                // beep fixtures through this very pipeline in Chrome
                // (tests/playwright/webcodecs-pipeline-honours-edit-lists.mjs):
                // every export 20 ms audio-late, the camera shape 330 ms
                // picture-late — bug report of 2026-09-14 on /auto-captions
                // («звук опережает видео», IMG_2465.mov: 946 media frames for
                // a 904-frame presentation, three burns, refund requested).
                //
                // Now: Mp4EditList maps both tracks onto the presentation
                // timeline. Video frames a player never shows keep a timestamp
                // below `edit.presStartUs` (or at/after `edit.presEndUs`) and
                // the lanes skip them after decoding; audio access units
                // wholly before the cut are dropped (one kept as decoder
                // warm-up) and the sub-unit remainder is reported as
                // `audioResidualUs` — the lanes hold the first video frame by
                // exactly that, because an MP4 without an edit list cannot
                // start an AAC track mid-unit. The muxer's «first chunk at 0»
                // rule (the reason the old block existed) is satisfied by
                // construction: the first shown frame and the first kept unit
                // both sit at 0.
                var a0 = pickAudioTrack(info.tracks, opts && opts.audioTrackIndex);
                var edit = { applied: false, video: null, audio: null, presStartUs: 0, presEndUs: Infinity,
                             presented: videoSamples.length, presDurUs: 0, hiddenHead: 0, hiddenTail: 0,
                             audioResidualUs: 0, audioLeadUs: 0, audioDropped: 0, audioDroppedTail: 0, audioUnitUs: 0 };
                var _legacyNormalise = function () {
                    // Pre-2026-09-15 behaviour, kept only for the reader being
                    // unavailable: each track to its own earliest timestamp.
                    var lists = [videoSamples, audioSamples];
                    for (var li = 0; li < lists.length; li++) {
                        var list = lists[li];
                        if (!list.length) continue;
                        var off = list[0].timestamp;
                        for (var i1 = 1; i1 < list.length; i1++) if (list[i1].timestamp < off) off = list[i1].timestamp;
                        if (off !== 0) for (var j1 = 0; j1 < list.length; j1++) list[j1].timestamp -= off;
                    }
                };
                if (_editList) {
                    try {
                        var vPlan = _editList.plan(v, info.timescale);
                        var vRes  = _editList.applyVideo(videoSamples, vPlan);
                        var aPlan = a0 ? _editList.plan(a0, info.timescale) : null;
                        var aRes  = (a0 && audioSamples.length) ? _editList.applyAudio(audioSamples, aPlan) : null;
                        edit.applied = true; edit.video = vPlan; edit.audio = aPlan;
                        edit.presStartUs = vRes.presStartUs; edit.presEndUs = vRes.presEndUs;
                        edit.presented = vRes.presented; edit.hiddenHead = vRes.hiddenHead; edit.hiddenTail = vRes.hiddenTail;
                        edit.presDurUs = _editList.presentationDurationUs(vPlan, (v.duration && vTscale) ? v.duration * 1e6 / vTscale : 0, videoSamples);
                        if (aRes) {
                            edit.audioResidualUs = aRes.residualUs; edit.audioLeadUs = aRes.leadUs;
                            edit.audioDropped = aRes.dropped; edit.audioDroppedTail = aRes.droppedTail; edit.audioUnitUs = aRes.unitUs;
                        }
                    } catch (editErr) {
                        edit.applied = false; edit.error = String((editErr && editErr.message) || editErr);
                        _legacyNormalise();
                    }
                } else {
                    edit.error = 'reader-missing';
                    _legacyNormalise();
                }

                // Decode display rotation from tkhd matrix. mp4 stores a 3x3
                // matrix in fixed-point (16.16 for elements 0-1,3-4,6-7;
                // 2.30 for elements 2,5,8). We only care about the top-left
                // 2x2 (a, b, c, d) which encodes rotation. Standard cases:
                //   0°:   a=1   b=0    c=0    d=1
                //   90°:  a=0   b=1    c=-1   d=0
                //   180°: a=-1  b=0    c=0    d=-1
                //   270°: a=0   b=-1   c=1    d=0
                // Phone-shot portrait videos (iPhone, Android) store landscape
                // pixels + rotation matrix; a player applies the matrix on
                // display. Pre-fix HW path: matrix was IGNORED — output came
                // out landscape regardless of source orientation. Comment #370
                // ("Переворачивает видео") was the smoking gun.
                // Telemetry: 299 sessions/7d went through HW path; estimated
                // ~30-50% had rotated phone sources silently outputting wrong.
                // Fix: extract rotation here, pass through to muxer's
                // tkhd.matrix (mp4-muxer.js:256-258 supports `track.info.rotation`
                // as a degrees number). Pixel data stays untouched (no per-frame
                // rotation cost) — output mp4 has the SAME matrix as source,
                // every player honours it. FFmpeg path's processWithRotationFix
                // physically rotates pixels via transpose filter; HW path takes
                // the metadata-preservation route which is cheaper and matches
                // what most pro tools (HandBrake, Shotcut, etc.) do for trim/
                // re-encode operations.
                /* Сама арифметика матрицы живёт в public/js/video-orientation.js —
                   там же, где ей пользуются все остальные лейны. Здесь была её
                   копия; копий было четыре, и ни одна не знала про остальные. */
                var rotation = window.VideoOrientation
                    ? window.VideoOrientation.fromTkhd(inFile, v.id)
                    : 0;

                // Wave 23 — one-shot sanitize telemetry so /pino can size how
                // many files carry SEI / duplicate param-sets and correlate
                // against the Unsupported-configuration cluster shrinking.
                if (!lazy && _nalSanStats.samples > 0) {
                    try {
                        if (typeof window.__ae === 'function') {
                            window.__ae('feature_use', {
                                slug: (window.__currentSlug || 'webcodecs'),
                                action: 'nal_sanitized',
                                sei: _nalSanStats.sei,
                                ps: _nalSanStats.ps,
                                samples: _nalSanStats.samples,
                                total: videoSamples.length
                            });
                        }
                    } catch (_) {}
                }

                resolve({
                    // Lazy: the bytes are read by the consuming loops through
                    // _lazySampleReader; `_nalStats` accumulates there (the
                    // eager path reports nal_sanitized above, at collection).
                    lazy: !!lazy,
                    file: lazy ? file : null,
                    _nalStats: lazy ? _nalSanStats : null,
                    width: v.video.width, height: v.video.height,
                    rotation: rotation,
                    fps: (v.nb_samples && v.duration) ? Math.round(v.nb_samples / (v.duration / vTscale)) : 30,
                    // Unrounded twin of `fps` — see the note at the other demux site.
                    fpsExact: (v.nb_samples && v.duration) ? (v.nb_samples / (v.duration / vTscale)) : 0,
                    // duration in seconds — used by crfToBitrate's source-
                    // bitrate floor (file.size × 8 ÷ duration = source bps).
                    // The PRESENTED span once the edit list is applied (what
                    // the <video> element reports); the mdhd media span only
                    // when the reader is unavailable.
                    durationSec: (edit.applied && edit.presDurUs > 0) ? edit.presDurUs / 1e6
                        : ((v.duration && vTscale) ? (v.duration / vTscale) : 0),
                    codec: v.codec,
                    avcC: avcCBytes,
                    hvcC: hvcCBytes,                                                  // Wave 18 — HEVC description bytes (null when source is H.264)
                    av1C: av1CBytes,                                                  // Wave 19 — AV1 description bytes
                    vpcC: vpcCBytes,                                                  // Wave 19 — VP9 description bytes
                    samples: videoSamples,
                    // Frames a player shows — hidden head/tail frames are still
                    // in `samples` (the decoder needs them) but never encoded.
                    totalFrames: edit.applied ? edit.presented : videoSamples.length,
                    // The edit-list outcome: presentation window, audio residual
                    // / lead, shapes — see the block above and mp4-edit-list.js.
                    edit: edit,
                    audio: audioTrackId ? {
                        codec: pickAudioTrack(info.tracks, opts && opts.audioTrackIndex).codec,
                        channels: audioPcm ? audioPcm.channels : pickAudioTrack(info.tracks, opts && opts.audioTrackIndex).audio.channel_count,
                        sampleRate: audioPcm ? audioPcm.sampleRate : pickAudioTrack(info.tracks, opts && opts.audioTrackIndex).audio.sample_rate,
                        ascBytes: audioAsc,          // real esds DecoderSpecificInfo, or null
                        durationSec: audioDurSec,    // mdhd media duration, or 0
                        // Uncompressed sound: units are file CHUNKS, read as
                        // samples by __mp4Pcm.decode — never decoded, never copied.
                        pcm: audioPcm,
                        samples: audioSamples
                    } : null,
                    // A sound track mp4box does not type as audio (G.711, QuickTime
                    // PCM, AMR, ALAC …): its sample-entry type, '' when none.
                    // run()/runInterpolation() refuse on it — _refuseUnreadSound.
                    soundUnread: audioTrackId ? '' : soundUnread
                });
                done = true;                                                  // mark finalized AFTER resolve, see hardening comment above
            } catch (e) {
                if (!done) { done = true; reject(e); }
            }
        })();
    });
}

// ─────────────────────────────────────────────────────────────────────
// LIVE STYLIZE RENDERER  (used by /video-filter for preview + thumbnails)
// ─────────────────────────────────────────────────────────────────────
//
// Returns a tiny WebGL2 renderer that draws ANY frame source (a playing
// <video>, a VideoFrame, an ImageBitmap, a canvas) through the EXACT same
// FRAG_STYLIZE shader run()'s export path uses. That single-source-of-truth
// is why the live preview is pixel-identical to the downloaded result.
//
//   var r = WebCodecsPipeline.createStylizeRenderer({ canvas: myCanvas });
//   r.resize(1280, 720);
//   r.render(videoEl, stylizeParams);   // call per rAF tick
//
// Returns null when WebGL2 is unavailable. ⚠️ ONE-SHOT: prefer
// createStylizeRendererRetry() on any path a person is waiting on — see the
// note above it for why the difference is measurable.
function createStylizeRenderer(opts) {
    opts = opts || {};
    var canvas = opts.canvas;
    if (!canvas) {
        canvas = (typeof OffscreenCanvas !== 'undefined')
            ? new OffscreenCanvas(opts.width || 320, opts.height || 240)
            : document.createElement('canvas');
    }
    var gl = null;
    try { gl = canvas.getContext('webgl2', { premultipliedAlpha: false, alpha: false }); } catch (_) {}
    if (!gl) { _stylizeFailure = { stage: 'context', log: '' }; return null; }
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    var prog;
    // The reason a renderer was refused used to be thrown away here, so a
    // browser WITH WebGL2 whose driver rejected this shader read in telemetry
    // exactly like a browser without WebGL2 (/video-filter #1941: iOS 18.1,
    // raw getContext('webgl2') fine, renderer null, seven exports refused).
    // stylizeFailure() hands the stage and the first words of the log out.
    try { prog = buildProgram(gl, VERT_QUAD, FRAG_STYLIZE); }
    catch (e) {
        var m = String((e && e.message) || e || '');
        _stylizeFailure = { stage: /^link/.test(m) ? 'link' : (gl.isContextLost && gl.isContextLost() ? 'lost' : 'compile'),
                            log: m.replace(/^(shader compile|link):\s*/, '').slice(0, 120) };
        return null;
    }
    _stylizeFailure = null;

    var vao = gl.createVertexArray();
    gl.bindVertexArray(vao);
    var buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
        -1, -1,  1, -1,  -1, 1,
        -1,  1,  1, -1,   1, 1
    ]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    gl.bindVertexArray(null);
    gl.bindAttribLocation(prog, 0, 'a_pos');

    var tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

    var u = collectStylizeUniforms(gl, prog);
    var disposed = false;
    var mipState = {};

    function resize(w, h) {
        w = Math.max(1, w | 0); h = Math.max(1, h | 0);
        if (canvas.width !== w)  canvas.width  = w;
        if (canvas.height !== h) canvas.height = h;
    }
    // ── Imported .cube grade ────────────────────────────────────────
    // Uploaded ONCE per cube, not per frame: a 33³ cube is 107 KB and a 64³ is
    // 786 KB, and pushing that every tick would cost more than the whole shader.
    // `lutKey` is what says "same cube as last time"; the caller supplies it.
    var lutTex = null, lutKey = null, lutSize = 0;
    function setLut(cube) {
        if (!cube || !cube.data) {
            if (lutTex) { try { gl.deleteTexture(lutTex); } catch (_) {} }
            lutTex = null; lutKey = null; lutSize = 0;
            return;
        }
        if (cube.key && cube.key === lutKey) return;      // already resident
        if (!lutTex) lutTex = gl.createTexture();
        gl.activeTexture(gl.TEXTURE1);
        gl.bindTexture(gl.TEXTURE_3D, lutTex);
        // RGB8 + LINEAR: filtering a FLOAT 3D texture needs
        // OES_texture_float_linear, which is not guaranteed, and an unfiltered
        // cube shows visible steps on a gradient. The source video is 8-bit, so
        // nothing is lost here that was present a step earlier.
        gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
        // ⚠️⚠️ UNPACK_FLIP_Y_WEBGL стоит true — он нужен видеокадру, — но для
        // texImage3D спецификация его ЗАПРЕЩАЕТ: вызов даёт INVALID_OPERATION,
        // текстура остаётся незалитой, и шейдер читает из неё нули. Наружу это
        // выглядит как «LUT красит всё в чёрное», то есть как ошибка цвета, а
        // не как ошибка загрузки. Снимаем на время куба и возвращаем сразу.
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
        gl.texImage3D(gl.TEXTURE_3D, 0, gl.RGB8, cube.size, cube.size, cube.size,
                      0, gl.RGB, gl.UNSIGNED_BYTE, cube.data);
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_R, gl.CLAMP_TO_EDGE);
        lutKey = cube.key || String(cube.size) + ':' + cube.data.length;
        lutSize = cube.size;
    }

    function render(source, su) {
        if (disposed || !source) return false;
        var w = canvas.width, h = canvas.height;
        // The cube travels with the params so the export path needs no extra
        // call: same object, same order, preview and download cannot drift.
        if (su && su.lutCube !== undefined) setLut(su.lutCube);
        if (lutTex) {
            gl.activeTexture(gl.TEXTURE1);
            gl.bindTexture(gl.TEXTURE_3D, lutTex);
        }
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, tex);
        try {
            uploadFrameTexture(gl, source);
            stylizeMips(gl, su, mipState);
        } catch (e) { return false; }     // video not ready yet (readyState < 2)
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.viewport(0, 0, w, h);
        gl.useProgram(prog);
        gl.bindVertexArray(vao);
        gl.uniform1i(u.tex, 0);
        applyStylizeUniforms(gl, u, su, w, h);
        // A mix with no cube resident would sample an unbound unit — black.
        gl.uniform1f(u.lutMix, lutTex ? (+((su && su.lutMix) || 0)) : 0);
        gl.uniform1f(u.lutSize, lutSize || 2);
        gl.drawArrays(gl.TRIANGLES, 0, 6);
        gl.bindVertexArray(null);
        return true;
    }
    function dispose() {
        if (disposed) return;
        disposed = true;
        try {
            gl.deleteProgram(prog); gl.deleteTexture(tex);
            if (lutTex) gl.deleteTexture(lutTex);
            gl.deleteVertexArray(vao); gl.deleteBuffer(buf);
            var lc = gl.getExtension('WEBGL_lose_context');
            if (lc) lc.loseContext();
        } catch (_) {}
    }
    return { canvas: canvas, gl: gl, render: render, resize: resize, dispose: dispose, setLut: setLut };
}

// Same renderer, with the SAME retry probeWebGL2() already uses: 3 attempts,
// 100 ms apart.
//
// probeWebGL2() was hardened in 2026 after telemetry showed that almost every
// `no-webgl2` skip on Yandex 24/26 and Samsung Internet 29 — browsers that DO
// support WebGL2 — was a TRANSIENT context-creation refusal (background tab,
// GPU-process race, driver hiccup), not a missing feature. createStylizeRenderer
// asks the very same question and never got the same answer: it stayed a
// one-shot getContext('webgl2').
//
// That left the two copies deciding opposite things about one browser. The
// hardened copy guards the EXPORT, which the person reaches last; the raw copy
// guards the PREVIEW and the 30-cell contact sheet, which is the entire page
// they see first. A transient refusal therefore painted a wall of black boxes
// and hid nothing about the export that would have worked. Measured 2026-08-22
// on /video-filter: 12 of 310 sessions in 30 days (3.9%), one of them clicking
// "re-shoot the sheet" 20 times in 24 seconds before leaving.
//
// Async because a retry needs to wait; resolves to null only when WebGL2 is
// genuinely unavailable, which is also the case where the export cannot run.
var _stylizeFailure = null;
/** Why the last createStylizeRenderer() returned null: {stage:'context'|'compile'|'link'|'lost', log}, or null. */
function stylizeFailure() { return _stylizeFailure; }

async function createStylizeRendererRetry(opts) {
    for (var attempt = 0; attempt < 3; attempt++) {
        var rend = createStylizeRenderer(opts);
        if (rend) return rend;
        if (attempt < 2) await new Promise(function (r) { setTimeout(r, 100); });
    }
    return null;
}

// ─────────────────────────────────────────────────────────────────────
// PUBLIC API
// ─────────────────────────────────────────────────────────────────────

/* Работа движка учитывается в window.__execWork — см. шапку
   _execWorkAdd в ffmpeg-processor.js (один контракт на все движки; сторож —
   ExecWorkIsMeteredTest). Оборачиваем на экспорте, а не в телах: run и
   runInterpolation — единственные двери, через которые тул получает кадры. */
function __timedExec(fn) {
    return async function () {
        var t0 = Date.now(), v0 = _visNow(), ok = 0;
        try { var res = await fn.apply(this, arguments); ok = 1; return res; }
        catch (e) { dropRunLanes(arguments[0]); throw e; }
        finally {
            try { var w = window.__execWork; if (w) { w.ms += Date.now() - t0; w.n++; } } catch (_) {}
            // Прогон, шедший в скрытой вкладке, — отдельной строкой: до 25.09.2026
            // такой прогон либо падал на изъятом кодере, либо полз по одному
            // образцу за пробуждение таймера (см. _yieldTick), и в поле это не
            // отличалось от «медленной машины». hid_s — сколько из wall_s вкладка
            // была скрыта; у здорового конвейера wall_s не растёт от hid_s.
            try {
                var wall = Date.now() - t0, hid = wall - (_visNow() - v0);
                if (hid > 5000 && typeof window.__ae === 'function') {
                    var o = arguments[0] || {};
                    window.__ae('feature_use', {
                        slug: (o.slug || window.__currentSlug || 'webcodecs'), action: 'wcp_hidden_run',
                        wall_s: Math.round(wall / 1000), hid_s: Math.round(hid / 1000), ok: ok,
                        analyze: (o.geometry2d && o.geometry2d.analyzeOnly) ? 1 : 0,
                    });
                }
            } catch (_) {}
        }
    };
}

/* A run that fails after its disk lane opened must give the room back NOW:
   the lane holds a reservation the size of the whole output, and until the
   stream is collected a retry of the same export would find the grant taken
   by its own corpse. Lanes are tracked on the call's opts (one object per
   call), and finish() / abort() are idempotent, so a finished lane is left
   alone. */
function trackRunLane(opts, muxTarget) {
    try { if (opts && muxTarget && muxTarget.opfs && muxTarget.opfs.lane) (opts.__muxLanes || (opts.__muxLanes = [])).push(muxTarget.opfs.lane); } catch (_) {}
    // The blocks of a failed run are the whole file in RAM: let go of them the same way
    // (release() after finish() is a no-op — a finished run keeps its Blob).
    try { if (opts && muxTarget && muxTarget.memory) (opts.__muxLanes || (opts.__muxLanes = [])).push({ abort: muxTarget.memory.release }); } catch (_) {}
}
function dropRunLanes(opts) {
    try {
        var l = opts && opts.__muxLanes;
        if (!l) return;
        for (var i = 0; i < l.length; i++) { try { l[i].abort(); } catch (_) {} }
        // Emptied, not replaced: a rescue re-run copies opts (Object.assign) and
        // so shares this array — its own lane lands here too.
        l.length = 0;
    } catch (_) {}
}

window.WebCodecsPipeline = {
    isSupported: isSupported,
    run: __timedExec(run),
    runInterpolation: __timedExec(runInterpolation),
    /* The door to window.__mp4Tags, loaded on demand — for a tool whose OTHER
       lane (FFmpeg) delivers an mp4 that must say the same things ours do. */
    ensureMp4Tags: ensureMp4Tags,
    createStylizeRenderer: createStylizeRenderer,
    createStylizeRendererRetry: createStylizeRendererRetry,
    stylizeFailure: stylizeFailure,
    paramsCovered: paramsCovered,
    renderStill: renderStill,
    probeCodec: probeCodec,
    verifyEncoderAt: verifyEncoderAt,
    verifyDecoderForFile: verifyDecoderForFile,   // ← use this one; owns mp4box
    verifyDecoderFor: verifyDecoderFor,           // demuxInfo in hand (run() path)
    demuxMp4: demuxMp4,
    /* The one rule for "which tracks of this file are sound", in the order
       pickAudioTrack() and FFmpeg's `-map 0:a:N` count them — for a tool that
       lists the tracks for window.__audioTracks and then hands the chosen
       number back as opts.audioTrackIndex (/video-stabilizer). A tool-side
       copy of the filter would be the second rule this file forbids. */
    audioTracksOf: audioTracksOf,
    // Can run() open this container? mp4/mov/m4v through mp4box, WebM/MKV
    // through the Matroska tables (demuxMatroska). Name/type only — the demux
    // itself is the verdict, and a file it declines throws a typed error.
    canDemux: function (file) {
        var n = String((file && file.name) || '').toLowerCase();
        return /\.(mp4|m4v|mov)$/.test(n) || /^video\/(mp4|quicktime)/.test(String((file && file.type) || '')) || isMatroskaFile(file);
    },
    demuxMatroska: demuxMatroska,
    toContainer: toContainer,
    ensureLibavRemux: ensureLibavRemux,
    /* The H.264 level table, for the hybrid lane (hybrid-encode-lane.js).
       It configures the same encoder on the same devices, and a wrong level is
       not a quality choice but a refusal: measured on an iPhone-class 2896×1630
       clip, a hardcoded `avc1.640028` (Level 4.0, max ~8192 macroblocks) made
       VideoEncoder.isConfigSupported answer false for every hint, and the lane
       fell over. One table, one place. */
    h264LevelHex: h264LevelHex,
    // The software rung's budget — one number for the fast lane and the hybrid lane.
    softwareRungBps: softwareRungBps,   // the software rung's bits for a run (0 — the table's rate)
    // Visible-time clock and the encoder-hint cascade, shared with the lanes
    // that drive their own VideoEncoder (hybrid-encode-lane) so they measure a
    // wedge the same way and escalate the same ladder — not a second copy.
    visibleNow: _visNow,
    encoderHintsAfter: _hintsAfter,
    ENC_WEDGE_MS: ENC_WEDGE_MS,
    // Firefox emits a malformed avcC in chunk metadata (#458) and reorders
    // chunks (B-frames) whatever `latencyMode` asks — both are muxer-facing,
    // so any lane that drives its own muxer needs the same repair, not a copy.
    sanitizeChunkMeta: sanitizeChunkMeta,
    /* Borrowed by the hybrid lane (hybrid-encode-lane.js), which muxes with the
       same library. ONE loader: it carries the version stamp, the CDN fallback
       and the in-flight promise that stops two callers fetching it twice, and a
       second copy would drift away from all three. */
    ensureMp4Muxer: ensureMp4Muxer,
    /* The source's bitrate re-expressed for an output of fewer pixels (pixel
       ratio^0.75, ×1.7 for an HEVC / VP9 / AV1 source) — the one downscale
       formula; /video-converter's explicit downscale bitrate
       (VideoConvertPlan.hwBitrate) asks it rather than keeping a copy. */
    sourceBpsForOutput: sourceBpsForOutput,
    // Exposed for debugging / test harnesses
    _runVideoBitrate: runVideoBitrate,   // tests: the whole bitrate decision of run()
    _muxAudioPassthrough: muxAudioPassthrough,   // tests: AAC copied without WebCodecs audio (Safari < 26)
    _silentLeadPlan: _silentLeadPlan,     // tests: a late-starting AAC-LC track as silent frames + copy
    _aacAscInfo: _aacAscInfo,             // tests: what an AudioSpecificConfig says (AOT, rate, channels, SBR)
    _pickEncoderConfig: pickEncoderConfig, // tests: the rung ladder (software rung's budget)
    _crfToBitrate: crfToBitrate,         // tests: the bounded CRF table both lanes share
    _frameTimingOf: _frameTimingOf,      // tests: how even the source's frame times are (demux_lane ts_*)
    _muxColorSpace: muxColorSpace,       // tests: the `colr` label of the encoded pixels
    _demuxMp4: demuxMp4,
    _lazySampleReader: _lazySampleReader,   // tests: read lazy samples the way the feed loops do
    _refuseUnreadSound: _refuseUnreadSound, // tests: the verdict on a sound track the demux cannot see
    _gateAudioDecodable: gateAudioDecodable, // tests: the audio verdict both lanes ask before the muxer
    _decodePcmTable: _decodePcmTable,       // tests: camera PCM / G.711 straight off the chunk table
    _muxPcmLazy: _muxPcmLazy,               // tests: the same sound relayed into the AAC encoder
    audioTranscodeLazy: true,               // tools: non-AAC audio no longer costs a whole-file read (muxAudioLazy)
    carriesAudioCodec: carriesAudioCodec,   // tools: does the fast lane carry this (FFmpeg-named) sound itself
    lazyAudioDecoderConfig: lazyAudioDecoderConfig,
    _mapParams: mapParams,
    _sanitizeAvcC: sanitizeAvcC,
    _sanitizeAvcLevel: sanitizeAvcLevel
};

})();
